// This file is auto-generated by @hey-api/openapi-ts

export type ClientOptions = {
    baseUrl: `${string}://pkg` | (string & {});
};

/**
 * AddReservableItemRequest
 *
 * Request model for adding a new reservable item
 */
export type AddReservableItemRequest = {
    name: string;
    bookable: string;
    description?: string | null;
};

/**
 * AdminAppliedQuotaResponse
 *
 * Response for ``GET /admin/item/user/appliedquota/{user_id}``.
 *
 * ``Quotas.get_applied_quota`` returns ``{quota, restriction_applied}``
 * where ``quota`` is either ``False`` or the quota dict and
 * ``restriction_applied`` is one of ``user_quota`` / ``group_quota`` /
 * ``category_quota``.
 */
export type AdminAppliedQuotaResponse = {
    quota: boolean | {
        [key: string]: unknown;
    };
    restriction_applied: string;
};

/**
 * AdminBastionConfigResponse
 *
 * Response model for admin bastion configuration overview
 */
export type AdminBastionConfigResponse = {
    /**
     * Whether bastion is currently enabled (both in config and database).
     */
    bastion_enabled: boolean;
    /**
     * Whether bastion is enabled in the environment configuration.
     */
    bastion_enabled_in_cfg: boolean;
    /**
     * Whether bastion is enabled in the database configuration.
     */
    bastion_enabled_in_db: boolean;
    /**
     * The bastion domain configured.
     */
    bastion_domain: string | null;
    /**
     * The SSH port for bastion connections. None if bastion is disabled.
     */
    bastion_ssh_port: string | null;
    /**
     * Whether domain verification is required for bastion domains.
     */
    domain_verification_required: boolean;
};

/**
 * AdminBastionConfigUpdateRequest
 *
 * Request model for updating bastion configuration
 */
export type AdminBastionConfigUpdateRequest = {
    /**
     * Whether to enable or disable bastion.
     */
    enabled: boolean;
    /**
     * The bastion domain to set.
     */
    bastion_domain: string;
    /**
     * Whether domain verification should be required.
     */
    domain_verification_required: boolean;
};

/**
 * AdminBastionDomainData
 *
 * Request body for updating bastion domain.
 */
export type AdminBastionDomainData = {
    bastion_domain: string | boolean | null;
};

/**
 * AdminBastionDomainResponse
 *
 * Response for the two ``/admin/item/category/{category_id}/bastion_domain``
 * endpoints.
 *
 * GET returns ``{bastion_domain: <str|False|None>}`` — string when set,
 * ``False`` when explicitly unset, ``None`` when missing. PUT echoes the
 * request body shape (same union).
 */
export type AdminBastionDomainResponse = {
    bastion_domain?: string | boolean | null;
};

/**
 * AdminBookingResponse
 *
 * Response model for admin bookings (includes username and category)
 */
export type AdminBookingResponse = {
    id: string;
    item_id: string;
    item_type: string;
    units: number;
    reservables: {
        [key: string]: unknown;
    };
    start: string;
    end: string;
    title: string;
    user_id: string;
    plans: Array<UserBookingPlan>;
    username: string;
    category: string;
};

/**
 * AdminBootProgressRequest
 *
 * Body for ``PUT /admin/item/hypervisor/{hyper_id}/boot_progress``.
 *
 * Carries a structured ``{step, total, label, error, timestamp}``
 * payload. Stored verbatim in RethinkDB (``hypervisors.boot_progress``)
 * and consumed by the changefeed / webapp for live progress reporting.
 */
export type AdminBootProgressRequest = {
    boot_progress: {
        [key: string]: unknown;
    };
};

/**
 * AdminBroadcastData
 *
 * Request body for broadcasting a message.
 */
export type AdminBroadcastData = {
    type: string;
    message: string;
};

/**
 * AdminBulkUserCreateData
 *
 * Request body for bulk user creation.
 */
export type AdminBulkUserCreateData = {
    users: Array<{
        [key: string]: unknown;
    }>;
    email_verified?: boolean;
};

/**
 * AdminCSVImportResponse
 *
 * Response for ``POST /admin/items/users/csv`` and ``/admin/items/bulk/user``.
 *
 * The route reshapes the service's ``{users, errors}`` into a count +
 * error list — keeping the shape so the OAS documents the same payload
 * the webapp consumes.
 */
export type AdminCsvImportResponse = {
    created: number;
    errors: Array<string>;
};

/**
 * AdminCSVUserEditData
 *
 * Request body for editing users via CSV (PUT ``/admin/items/users/csv``).
 */
export type AdminCsvUserEditData = {
    users: Array<AdminCsvUserEditRow>;
};

/**
 * AdminCSVUserEditRow
 *
 * One row of PUT ``/admin/items/users/csv`` — an enriched user record
 * returned by ``/admin/items/users/csv/validate`` (PUT). Webapp/k6
 * round-trip the validate output back into the edit endpoint; ``id``
 * is the only load-bearing field. Anything else is optional so a
 * minimal rename ``{"id": "...", "name": "new"}`` is also accepted.
 * Pre-typed so a missing ``id`` yields 422 at the boundary instead
 * of falling into the service and surfacing as 500.
 */
export type AdminCsvUserEditRow = {
    id: string;
    username?: string | null;
    name?: string | null;
    email?: string | null;
    role?: string | null;
    category?: string | null;
    group?: string | null;
    provider?: string | null;
    uid?: string | null;
    category_id?: string | null;
    group_id?: string | null;
    secondary_groups?: Array<string> | null;
    secondary_groups_names?: Array<string> | null;
    password?: string | null;
    photo?: string | null;
    active?: boolean | null;
    [key: string]: unknown | string | string | null | string | null | string | null | string | null | string | null | string | null | string | null | string | null | string | null | string | null | Array<string> | null | Array<string> | null | string | null | string | null | boolean | null | undefined;
};

/**
 * AdminCSVUserImportData
 *
 * Request body for importing new users from CSV (POST
 * ``/admin/items/users/csv``). Rows here have no ``id`` yet — that's
 * assigned by ``CommonUsers.generate_users``. Kept as ``List[dict]``
 * so the upstream validate-create flow's permissive shape still
 * applies.
 */
export type AdminCsvUserImportData = {
    users: Array<{
        [key: string]: unknown;
    }>;
};

/**
 * AdminCSVValidateCreateResponse
 *
 * Response for ``POST /admin/items/users/csv/validate`` — the bulk-create
 * pre-flight. Service returns the enriched row list under ``users`` and
 * a parallel ``errors`` list of human-readable strings for the rows that
 * were skipped. Rows are kept as ``list[dict]`` because each row mirrors
 * the permissive create-input shape (``UserFromCSV``); narrowing here
 * would 500 on edge fields the webapp tolerates.
 */
export type AdminCsvValidateCreateResponse = {
    users: Array<{
        [key: string]: unknown;
    }>;
    errors: Array<string>;
};

/**
 * AdminCategoryAuthenticationData
 *
 * Request body for updating category authentication.
 */
export type AdminCategoryAuthenticationData = {
    authentication: {
        [key: string]: unknown;
    };
};

/**
 * AdminCategoryCreateData
 *
 * Request body for creating a category.
 */
export type AdminCategoryCreateData = {
    name: string;
    description?: string;
    frontend?: boolean;
    custom_url_name?: string;
    uid?: string | null;
    photo?: string | null;
    storage_pool?: string | null;
    maintenance?: boolean;
    manager_permissions: ManagerPermissionsData;
    recycle_bin_cutoff_time?: number | null;
    ephimeral?: EphimeralDesktopsData | boolean | null;
    gpu_use_global_pool?: boolean | null;
};

/**
 * AdminCategoryDetailResponse
 *
 * Response for ``GET /admin/item/category/{category_id}`` and the create
 * endpoint.
 *
 * ``ApiAdmin.get_table_item("categories", ...)`` returns the full row;
 * the service then strips the authentication-secret fields and adds an
 * ``is_default`` flag. ``create_category`` returns the freshly built
 * document (id/name/description/frontend/custom_url_name/photo/uid +
 * the four-provider authentication shell). Kept permissive on quota /
 * limits / branding so legacy categories serialize cleanly.
 */
export type AdminCategoryDetailResponse = {
    id: string;
    name: string;
    description?: string | null;
    frontend?: boolean | null;
    custom_url_name?: string | null;
    uid?: string | null;
    photo?: string | null;
    authentication?: {
        [key: string]: unknown;
    } | null;
    is_default?: boolean | null;
    quota?: {
        [key: string]: unknown;
    } | boolean | null;
    limits?: {
        [key: string]: unknown;
    } | boolean | null;
    branding?: {
        [key: string]: unknown;
    } | null;
    manager_permissions?: {
        [key: string]: unknown;
    } | null;
    maintenance?: boolean | null;
    recycle_bin_cutoff_time?: number | null;
};

/**
 * AdminCategoryFrontendItem
 *
 * Row shape for ``GET /admin/items/categories/{frontend}``.
 *
 * Mirrors ``CategoriesProcessed.get_categories_frontend`` — pluck of
 * ``id``, ``name``, ``custom_url_name`` (and ``frontend`` on the
 * branding-domain branch). ``custom_url_name`` is always returned but
 * may be empty.
 */
export type AdminCategoryFrontendItem = {
    id: string;
    name: string;
    custom_url_name?: string | null;
    frontend?: boolean | null;
};

/**
 * AdminCategoryItem
 *
 * Row shape for ``GET /admin/items/categories``.
 *
 * ``UsersProcessed.categories_get`` plucks ``id``/``name``/``frontend``
 * only. Kept tight — the admin categories panel does not consume
 * additional fields from this endpoint.
 */
export type AdminCategoryItem = {
    id: string;
    name: string;
    frontend?: boolean | null;
};

/**
 * AdminCategoryNavItem
 *
 * Row shape for ``GET /admin/items/users/{nav}/categories``.
 *
 * ``UsersProcessed.admin_list_categories`` returns the categories row
 * (with ``quota``/``limits`` stripped on ``management``) and merges
 * ``media_size``/``domains_size`` on ``quotas_limits``. The trailing
 * fields stay optional so both nav variants serialise through the same
 * model.
 */
export type AdminCategoryNavItem = {
    id: string;
    name: string;
    description?: string | null;
    frontend?: boolean | null;
    custom_url_name?: string | null;
    uid?: string | null;
    photo?: string | null;
    authentication?: {
        [key: string]: unknown;
    } | null;
    manager_permissions?: {
        [key: string]: unknown;
    } | null;
    quota?: {
        [key: string]: unknown;
    } | boolean | null;
    limits?: {
        [key: string]: unknown;
    } | boolean | null;
    media_size?: number | null;
    domains_size?: number | null;
    bastion_domain?: string | null;
    ephimeral?: EphimeralDesktopsData | boolean | null;
    maintenance?: boolean | null;
};

/**
 * AdminCategoryUpdateData
 *
 * Request body for updating a category.
 */
export type AdminCategoryUpdateData = {
    id: string;
    name: string;
    description?: string | null;
    frontend?: boolean | null;
    custom_url_name?: string | null;
    uid?: string | null;
    recycle_bin_cutoff_time?: number | null;
    ephimeral?: EphimeralDesktopsData | boolean | null;
    maintenance?: boolean | null;
    manager_permissions?: ManagerPermissionsData | null;
    gpu_use_global_pool?: boolean | null;
};

/**
 * AdminCategoryUserItem
 *
 * Row shape for ``GET /admin/items/category/{category_id}/users``.
 *
 * ``UsersProcessed.list_by_category`` returns the user row enriched with
 * role/group/category names + a denormalised ``secondary_groups_data``
 * list — same shape as ``AdminUserFullDataResponse`` minus the
 * ``photo``/``api_key``/``vpn`` columns the admin list view does not
 * render.
 */
export type AdminCategoryUserItem = {
    id: string;
    name?: string | null;
    username?: string | null;
    role?: string | null;
    role_name?: string | null;
    category?: string | null;
    category_name?: string | null;
    group?: string | null;
    group_name?: string | null;
    secondary_groups?: Array<string> | null;
    secondary_groups_data?: Array<AdminSecondaryGroupRef> | null;
    email?: string | null;
    active?: boolean | null;
    provider?: string | null;
    uid?: string | null;
    accessed?: number | null;
};

/**
 * AdminCheckGroupCategoryData
 *
 * Request body for checking group/category association.
 */
export type AdminCheckGroupCategoryData = {
    category?: string | null;
    group?: string | null;
};

/**
 * AdminCheckMigratedData
 *
 * Request body for checking migrated users.
 */
export type AdminCheckMigratedData = {
    users: Array<string>;
};

/**
 * AdminCheckMigratedResponse
 *
 * Response for ``POST /admin/item/user/check/migrated``.
 *
 * Service returns a single boolean; the route wraps it as
 * ``{"migrated": <bool>}``.
 */
export type AdminCheckMigratedResponse = {
    migrated: boolean;
};

/**
 * AdminDeleteCheckDeployment
 */
export type AdminDeleteCheckDeployment = {
    id: string;
    name?: string | null;
    user?: string | null;
    user_name?: string | null;
    username?: string | null;
};

/**
 * AdminDeleteCheckDesktop
 *
 * Row shape for the ``desktops`` array of every delete-check response.
 *
 * ``UsersProcessed.user_delete_checks`` plucks ``id``/``name``/``kind``/
 * ``user``/``status``/``parents`` (+ ``persistent`` /
 * ``duplicate_parent_template`` on the user-table branch) and merges
 * ``username``/``user_name``. Optional everywhere because the merge
 * paths differ per kind.
 */
export type AdminDeleteCheckDesktop = {
    id: string;
    name?: string | null;
    kind?: string | null;
    user?: string | null;
    user_name?: string | null;
    username?: string | null;
    status?: string | null;
    parents?: Array<string> | null;
    persistent?: boolean | null;
    duplicate_parent_template?: string | boolean | null;
};

/**
 * AdminDeleteCheckGroup
 */
export type AdminDeleteCheckGroup = {
    id: string;
    name?: string | null;
};

/**
 * AdminDeleteCheckMedia
 */
export type AdminDeleteCheckMedia = {
    id: string;
    name?: string | null;
    user?: string | null;
    user_name?: string | null;
    username?: string | null;
};

/**
 * AdminDeleteCheckTemplate
 */
export type AdminDeleteCheckTemplate = {
    id: string;
    name?: string | null;
    kind?: string | null;
    user?: string | null;
    user_name?: string | null;
    username?: string | null;
    category?: string | null;
    group?: string | null;
    duplicate_parent_template?: string | boolean | null;
};

/**
 * AdminDeleteCheckUser
 */
export type AdminDeleteCheckUser = {
    id: string;
    name?: string | null;
    username?: string | null;
    provider?: string | null;
};

/**
 * AdminDeleteChecksData
 *
 * Request body for delete dependency checks.
 */
export type AdminDeleteChecksData = {
    ids: Array<string>;
};

/**
 * AdminDeleteChecksResponse
 *
 * Response for the three ``POST /admin/{user|group|category}/delete/check``
 * endpoints. Service helper ``UsersProcessed.user_delete_checks`` returns
 * six parallel arrays of cascaded items, plus ``storage_pools`` (count)
 * on the category branch.
 */
export type AdminDeleteChecksResponse = {
    desktops?: Array<AdminDeleteCheckDesktop>;
    templates?: Array<AdminDeleteCheckTemplate>;
    deployments?: Array<AdminDeleteCheckDeployment>;
    media?: Array<AdminDeleteCheckMedia>;
    users?: Array<AdminDeleteCheckUser>;
    groups?: Array<AdminDeleteCheckGroup>;
    storage_pools?: number | null;
};

/**
 * AdminDomainBastionTarget
 *
 * Subset of the ``targets`` row attached to a desktop list entry.
 */
export type AdminDomainBastionTarget = {
    id: string;
    domains?: Array<string>;
    http?: BastionHttpConfig | null;
    ssh?: BastionSshConfig | null;
};

/**
 * AdminDomainCreateDictSummary
 *
 * ``create_dict`` slice plucked for admin listings.
 */
export type AdminDomainCreateDictSummary = {
    hardware?: AdminDomainHardwareSummary | null;
    reservables?: {
        [key: string]: unknown;
    } | null;
};

/**
 * AdminDomainDetailsResponse
 *
 * Response of ``DesktopDetailsData`` (``detail`` + ``description``).
 */
export type AdminDomainDetailsResponse = {
    detail?: string | null;
    description?: string | null;
};

/**
 * AdminDomainHardwareDisk
 *
 * Disk entry as persisted in ``create_dict.hardware.disks``.
 */
export type AdminDomainHardwareDisk = {
    storage_id?: string | null;
    bus?: string | boolean | null;
    extension?: string | null;
    file?: string | null;
};

/**
 * AdminDomainHardwareInner
 *
 * Hardware payload returned by ``get_domain_details_hardware``.
 *
 * Mirrors the persisted ``create_dict.hardware`` plus the ``*_name``
 * look-ups added by the service. ``memory`` is divided by 1048576 in
 * the service (MiB → GiB).
 */
export type AdminDomainHardwareInner = {
    vcpus?: number | null;
    memory?: number | null;
    disk_bus?: string | null;
    boot_order?: Array<string> | null;
    videos?: Array<string> | null;
    graphics?: Array<string> | null;
    interfaces?: Array<AdminDomainHardwareInterface> | null;
    isos?: Array<AdminDomainHardwareIso> | null;
    floppies?: Array<AdminDomainHardwareIso> | null;
    disks?: Array<AdminDomainHardwareDisk> | null;
    virtualization_nested?: boolean | null;
    qos_disk_id?: string | boolean | null;
};

/**
 * AdminDomainHardwareInterface
 *
 * Interface enriched with its ``name`` from the ``interfaces`` table.
 */
export type AdminDomainHardwareInterface = {
    id: string;
    mac?: string | null;
    name?: string | null;
};

/**
 * AdminDomainHardwareIso
 *
 * Iso entry returned by ``get_domain_details_hardware``.
 */
export type AdminDomainHardwareIso = {
    id: string;
    name?: string | null;
};

/**
 * AdminDomainHardwareResponse
 *
 * Response of ``get_domain_hardware``.
 *
 * ``hardware`` is the enriched hardware block; the other ``create_dict``
 * keys (origin, reservables, xml_protected_sections, reservable_name…)
 * flow through here.
 */
export type AdminDomainHardwareResponse = {
    hardware: AdminDomainHardwareInner;
    origin?: unknown | null;
    reservables?: {
        [key: string]: unknown;
    } | null;
    reservable_name?: Array<string> | boolean | null;
    video_name?: Array<string> | null;
    boot_name?: Array<string> | null;
    xml_protected_sections?: Array<string> | null;
};

/**
 * AdminDomainHardwareSummary
 *
 * Subset of ``create_dict.hardware`` plucked for admin listings.
 */
export type AdminDomainHardwareSummary = {
    vcpus?: number | null;
    memory?: number | null;
};

/**
 * AdminDomainImage
 *
 * Image url subset used by admin domain listings.
 */
export type AdminDomainImage = {
    url?: string | null;
};

/**
 * AdminDomainListItem
 *
 * Row returned by ``ApiAdmin.list_desktops`` / ``ApiAdmin.list_templates`` /
 * ``DomainsProcessed.get_by_ids``.
 *
 * Different sources populate slightly different subsets — every field
 * is therefore optional. The shape is the union of the three
 * branches in ``admin_list_domains``.
 */
export type AdminDomainListItem = {
    id: string;
    name?: string | null;
    kind?: string | null;
    status?: string | null;
    description?: string | null;
    detail?: string | null;
    icon?: string | null;
    image?: AdminDomainImage | null;
    enabled?: boolean | null;
    accessed?: number | null;
    server?: boolean | null;
    server_autostart?: boolean | null;
    persistent?: boolean | null;
    hyp_started?: string | boolean | null;
    forced_hyp?: string | boolean | Array<unknown> | null;
    favourite_hyp?: string | boolean | Array<unknown> | null;
    booking_id?: string | boolean | null;
    role?: string | null;
    current_action?: string | boolean | null;
    user?: string | null;
    user_name?: string | null;
    username?: string | null;
    category?: string | null;
    category_name?: string | boolean | null;
    category_id?: string | null;
    group?: string | null;
    group_id?: string | null;
    group_name?: string | boolean | null;
    create_dict?: AdminDomainCreateDictSummary | null;
    bastion?: AdminDomainBastionTarget | null;
    derivates?: number | null;
    tag?: string | boolean | null;
};

/**
 * AdminDomainLiveXmlResponse
 *
 * Response of ``GET /admin/item/domain/{domain_id}/live_xml`` — the
 * engine's in-RAM live libvirt XML (incl. secrets) of a started desktop,
 * never stored in the DB.
 */
export type AdminDomainLiveXmlResponse = {
    /**
     * The live libvirt XML
     */
    xml: string;
    /**
     * The hypervisor currently running the desktop
     */
    hyp?: string | null;
};

/**
 * AdminDomainSearchCreateDict
 */
export type AdminDomainSearchCreateDict = {
    hardware: AdminDomainSearchHardware;
};

/**
 * AdminDomainSearchHardware
 */
export type AdminDomainSearchHardware = {
    disks?: Array<AdminDomainHardwareDisk>;
};

/**
 * AdminDomainSearchInfoResponse
 *
 * Response of ``get_domain_search_info`` (admin search panel).
 */
export type AdminDomainSearchInfoResponse = {
    id?: string | null;
    name?: string | null;
    status?: string | null;
    user?: string | null;
    kind?: string | null;
    create_dict: AdminDomainSearchCreateDict;
    owner_data?: AdminDomainSearchOwnerData | null;
};

/**
 * AdminDomainSearchOwnerData
 *
 * ``Caches.get_cached_user_with_names`` payload (subset).
 *
 * ``Caches`` re-uses the raw users-table row plus a handful of joined
 * name fields. All optional because the cache may return ``None``.
 */
export type AdminDomainSearchOwnerData = {
    id?: string | null;
    name?: string | null;
    username?: string | null;
    role?: string | null;
    role_name?: string | null;
    category?: string | null;
    category_name?: string | null;
    group?: string | null;
    group_name?: string | null;
    user_name?: string | null;
    secondary_groups?: Array<string> | null;
    secondary_groups_names?: Array<string> | null;
    secondary_groups_data?: Array<{
        [key: string]: unknown;
    }> | null;
};

/**
 * AdminDomainStatusItem
 *
 * Row returned by ``StorageProcessed.get_domains_delete_pending``
 * (admin/manager ``status == "delete_pending"``) plus the minimal
 * ``ApiAdmin.domains_status_minimal`` row used for other statuses.
 *
 * Optional everywhere because the two sources do not overlap.
 */
export type AdminDomainStatusItem = {
    id: string;
    name?: string | null;
    accessed?: number | null;
    type?: string | null;
    status?: string | null;
    directory_path?: string | null;
    parent?: string | null;
    user_id?: string | null;
    status_logs?: Array<{
        [key: string]: unknown;
    }> | null;
    last_domain_attached?: {
        [key: string]: unknown;
    } | null;
    user_name?: string | null;
    category_name?: string | null;
};

/**
 * AdminDomainStorageItem
 *
 * Row returned by ``ApiAdmin.get_domain_storage``.
 *
 * ``actual_size`` / ``virtual_size`` are merged in by the service in GiB.
 * The remaining fields are whatever ``r.table('storage')`` carries.
 */
export type AdminDomainStorageItem = {
    id?: string | null;
    type?: string | null;
    status?: string | null;
    parent?: string | null;
    user_id?: string | null;
    directory_path?: string | null;
    actual_size?: number | null;
    virtual_size?: number | null;
    has_pending_task?: boolean | null;
};

/**
 * AdminDomainStoragePathData
 *
 * Request body for updating domain storage path.
 */
export type AdminDomainStoragePathData = {
    old_path: string;
    new_path: string;
};

/**
 * AdminDomainStoragePathResponse
 *
 * Response of ``update_domain_storage_path``.
 *
 * The service returns the freshly-updated full domain dict; only
 * ``id`` is reliably populated — everything else mirrors a ``domains``
 * row, so it travels through this thin model untouched.
 */
export type AdminDomainStoragePathResponse = {
    id: string;
};

/**
 * AdminDomainTemplateTreeItem
 *
 * Row returned by ``DomainsProcessed.domain_template_tree``.
 */
export type AdminDomainTemplateTreeItem = {
    id: string;
    name?: string | null;
    user?: string | null;
    username?: string | null;
    category_name?: string | null;
    group_name?: string | null;
    parents_count?: number | null;
};

/**
 * AdminDomainViewerCreateDict
 *
 * ``create_dict`` slice in ``DesktopViewerData``.
 *
 * Only ``hardware.interfaces`` is enforced; all the other
 * ``create_dict`` keys (origin, reservables, xml_protected_sections, …)
 * flow through unchanged via this dict-typed remainder.
 */
export type AdminDomainViewerCreateDict = {
    hardware: AdminDomainViewerCreateDictHardware;
    origin?: unknown | null;
    reservables?: {
        [key: string]: unknown;
    } | null;
    xml_protected_sections?: Array<string> | null;
};

/**
 * AdminDomainViewerCreateDictHardware
 *
 * ``create_dict.hardware`` slice in the viewer-data response.
 */
export type AdminDomainViewerCreateDictHardware = {
    interfaces?: Array<string>;
};

/**
 * AdminDomainViewerDataResponse
 *
 * Response of ``DesktopViewerData``.
 */
export type AdminDomainViewerDataResponse = {
    guest_properties?: {
        [key: string]: unknown;
    } | null;
    create_dict: AdminDomainViewerCreateDict;
    viewer: AdminDomainViewerInfo;
};

/**
 * AdminDomainViewerInfo
 *
 * Subset of the ``viewer`` field exposed for the admin viewer page.
 */
export type AdminDomainViewerInfo = {
    guest_ip?: string | null;
};

/**
 * AdminDomainXmlCapabilitiesResponse
 *
 * Response of ``GET /admin/item/domains/xml_capabilities``.
 *
 * Libvirt domain capabilities are an arbitrary nested mapping (the
 * apiv4 helper just forwards the first non-empty hypervisor copy).
 * The wire shape is the raw mapping — the legacy webapp consumer
 * (``static/admin/js/xml_sections.js``) destructures it directly —
 * so we use a ``RootModel`` to keep the response model explicit
 * without changing the on-wire JSON.
 */
export type AdminDomainXmlCapabilitiesResponse = {
    [key: string]: unknown;
};

/**
 * AdminDomainXmlData
 *
 * Request body for updating domain XML.
 */
export type AdminDomainXmlData = {
    xml?: {
        [key: string]: unknown;
    } | null;
};

/**
 * AdminDomainXmlResponse
 *
 * Response of ``get_domain_xml`` / ``update_domain_xml``.
 *
 * The underlying ``DomainsProcessed.get_xml`` returns a raw string
 * (or ``None``); the route wraps it in a one-key dict to match v3.
 * Pydantic captures it explicitly here so non-string surprises 422.
 */
export type AdminDomainXmlResponse = {
    xml?: string | null;
};

/**
 * AdminDomainXmlSection
 *
 * One entry in a parsed XML sections list (see ``split_xml_sections``).
 */
export type AdminDomainXmlSection = {
    key: string;
    label: string;
    group?: string | null;
    xml: string;
    protected: boolean;
    protectable: boolean;
    derived: boolean;
};

/**
 * AdminDomainXmlSectionsGetResponse
 *
 * Response of ``GET /admin/item/domains/xml_sections/{domain_id}`` and
 * ``GET /admin/virt_install/xml_sections/{virt_id}``.
 */
export type AdminDomainXmlSectionsGetResponse = {
    sections?: Array<AdminDomainXmlSection>;
    xml_full?: string | null;
    /**
     * The domain's stored protected-section list (['raw'] when the domain is in RAW mode) so the editor can restore the mode
     */
    xml_protected_sections?: Array<string>;
};

/**
 * AdminDomainXmlSectionsParseResponse
 *
 * Response of ``POST /admin/item/domains/xml_sections/parse``.
 */
export type AdminDomainXmlSectionsParseResponse = {
    sections: Array<AdminDomainXmlSection>;
};

/**
 * AdminDomainXmlSectionsSaveData
 *
 * Request body for ``POST /admin/item/domains/xml_sections/{domain_id}``.
 *
 * The xml editor in the admin UI sends the edited per-section XML as
 * a dict keyed by section name plus the user's protected-section
 * selection (sections that survive engine rewrites).
 */
export type AdminDomainXmlSectionsSaveData = {
    /**
     * Edited XML sections, keyed by section name
     */
    sections?: {
        [key: string]: unknown;
    };
    /**
     * Section names that should not be regenerated by the engine, or exactly ['raw'] for RAW mode (the whole <domain> is authoritative; the engine applies only the isard-essentials)
     */
    protected_sections?: Array<string>;
    /**
     * RAW mode only: the full <domain> XML to store verbatim
     */
    raw_xml?: string | null;
};

/**
 * AdminDomainXmlSectionsSaveResponse
 *
 * Response body for ``POST /admin/item/domains/xml_sections/{domain_id}``.
 */
export type AdminDomainXmlSectionsSaveResponse = {
    /**
     * The rebuilt full domain XML
     */
    xml: string;
};

/**
 * AdminDomainsFieldResponse
 *
 * Distinct values for a domain field (``GET /admin/items/domains/{field}/{kind}``).
 *
 * Values are heterogeneous depending on the column: strings for
 * ``status``/``name``/``user``…, ints for ``vcpus``, floats for ``memory``.
 */
export type AdminDomainsFieldResponse = {
    /**
     * Distinct values found for the requested field.
     */
    field?: Array<unknown>;
};

/**
 * AdminFindStoragesByStatusResponse
 *
 * Response of ``find_storages_by_domain_status`` — one int counter.
 */
export type AdminFindStoragesByStatusResponse = {
    tasks_created: number;
};

/**
 * AdminGpuAppliedRequest
 *
 * Body for ``PUT /admin/item/hypervisor/{hyper_id}/gpu_applied``.
 *
 * A gpu-apply-capable hypervisor reports back, after registration, the
 * per-card profile it actually applied locally (+ the created mdev
 * pool): ``{pci_bus_id: {result, profile, mdevs, ...}}``. Persisted
 * into ``vgpus`` so the DB reflects reality and the engine reconcile
 * confirms instead of re-applying.
 */
export type AdminGpuAppliedRequest = {
    applied: {
        [key: string]: unknown;
    };
};

/**
 * AdminGpuForceProfilePreviewRequest
 *
 * Body for ``POST /admin/item/hypervisor/gpus/{card_id}/force_profile_preview``.
 */
export type AdminGpuForceProfilePreviewRequest = {
    target_profile: string;
};

/**
 * AdminGpuForceProfilePreviewResponse
 *
 * Read-only pre-flight for the admin force-profile dialog: which
 * running desktops would be stopped and which reservables would be
 * removed (no other card provides them) if this card is forced to
 * ``target_profile``. ``current_profile``/``target_profile`` are absent
 * when the card row does not exist.
 */
export type AdminGpuForceProfilePreviewResponse = {
    current_profile?: string | null;
    target_profile?: string | null;
    desktops_to_stop: Array<string>;
    resources_to_remove: Array<string>;
};

/**
 * AdminGroup
 */
export type AdminGroup = {
    id: string;
    uid: string;
    name: string;
    parent_category: string;
    auto?: boolean | {
        [key: string]: unknown;
    };
    description?: string;
    external_app_id?: string | null;
    external_gid?: string | null;
    limits?: boolean | {
        [key: string]: unknown;
    };
};

/**
 * AdminGroupCreateData
 *
 * Request body for creating a group.
 */
export type AdminGroupCreateData = {
    uid?: string | null;
    name: string;
    description?: string;
    parent_category?: string | null;
    external_app_id?: string | null;
    external_gid?: string | null;
};

/**
 * AdminGroupEnrollmentData
 *
 * Request body for group enrollment actions.
 */
export type AdminGroupEnrollmentData = {
    id: string;
    action: string;
    role?: string | null;
};

/**
 * AdminGroupEnrollmentResponse
 *
 * Response for ``POST /admin/item/group/enrollment``: the new enrollment
 * code (reset) or ``True`` (disable).
 */
export type AdminGroupEnrollmentResponse = {
    code?: string | boolean | null;
};

/**
 * AdminGroupFullDataResponse
 *
 * Response for ``GET /admin/item/group/{group_id}``.
 *
 * ``GroupsProcessed.group_get_full_data`` returns the row merged with
 * ``linked_groups_data``. Inherits ``AdminGroup`` and adds the two
 * enrichment fields.
 */
export type AdminGroupFullDataResponse = {
    id: string;
    uid: string;
    name: string;
    parent_category: string;
    auto?: boolean | {
        [key: string]: unknown;
    };
    description?: string;
    external_app_id?: string | null;
    external_gid?: string | null;
    limits?: boolean | {
        [key: string]: unknown;
    };
    linked_groups?: Array<string> | null;
    linked_groups_data?: Array<AdminSecondaryGroupRef> | null;
    quota?: {
        [key: string]: unknown;
    } | boolean | null;
    enrollment?: {
        [key: string]: unknown;
    } | null;
    ephimeral?: EphimeralDesktopsData | boolean | null;
};

/**
 * AdminGroupListItem
 *
 * Row shape for ``GET /admin/items/groups``.
 *
 * ``GroupsProcessed.admin_get_groups`` returns the full ``groups`` row
 * merged with ``linked_groups_data`` (a denormalised array of
 * ``{id, name}`` pairs from the linked groups). Inherits ``AdminGroup``
 * so the canonical group fields stay strict and only the two extra
 * columns are added on top.
 */
export type AdminGroupListItem = {
    id: string;
    uid: string;
    name: string;
    parent_category: string;
    auto?: boolean | {
        [key: string]: unknown;
    };
    description?: string;
    external_app_id?: string | null;
    external_gid?: string | null;
    limits?: boolean | {
        [key: string]: unknown;
    };
    linked_groups?: Array<string> | null;
    linked_groups_data?: Array<AdminSecondaryGroupRef> | null;
    quota?: {
        [key: string]: unknown;
    } | boolean | null;
    enrollment?: {
        [key: string]: unknown;
    } | null;
};

/**
 * AdminGroupNavItem
 *
 * Row shape for ``GET /admin/items/users/{nav}/groups``.
 *
 * ``UsersProcessed.admin_list_groups`` returns the full groups row with
 * extra merges: ``management`` adds ``linked_groups_data``
 * (with ``category_name``) and ``parent_category_name``;
 * ``quotas_limits`` adds ``media_size`` / ``domains_size``. Fields kept
 * optional + the ``without`` strip on ``quotas_limits`` is reflected by
 * making ``enrollment``/``external_*``/``linked_groups`` optional here.
 */
export type AdminGroupNavItem = {
    id: string;
    uid?: string | null;
    name: string;
    parent_category: string;
    auto?: {
        [key: string]: unknown;
    } | boolean | null;
    description?: string | null;
    external_app_id?: string | null;
    external_gid?: string | null;
    linked_groups?: Array<string> | null;
    linked_groups_data?: Array<AdminLinkedGroupNamed> | null;
    parent_category_name?: string | null;
    enrollment?: {
        [key: string]: unknown;
    } | null;
    quota?: {
        [key: string]: unknown;
    } | boolean | null;
    limits?: {
        [key: string]: unknown;
    } | boolean | null;
    media_size?: number | null;
    domains_size?: number | null;
};

/**
 * AdminGroupUpdateData
 *
 * Request body for updating a group.
 */
export type AdminGroupUpdateData = {
    id: string;
    name: string;
    description?: string | null;
    ephimeral?: EphimeralDesktopsData | boolean | null;
    linked_groups?: Array<string> | null;
};

/**
 * AdminGroupUserItem
 *
 * Row shape for ``GET /admin/items/group/{group_id}/users``.
 *
 * ``GroupsProcessed.get_users_in_group`` plucks ``id``, ``name``,
 * ``username`` and ``photo`` only.
 */
export type AdminGroupUserItem = {
    id: string;
    name?: string | null;
    username?: string | null;
    photo?: string | null;
};

/**
 * AdminHypervisor
 */
export type AdminHypervisor = {
    id: string;
    hostname: string;
    port: string;
    description: string;
    capabilities: AdminHypervisorCapabilities;
    only_forced: boolean;
    min_free_mem_gb: number;
    min_free_gpu_mem_gb: number;
    nvidia_enabled: boolean;
    force_get_hyp_info?: boolean;
    buffering_hyper: boolean;
    gpu_only: boolean;
    isard_hyper_vpn_host: string;
    kvm_module?: string | null;
    nested?: boolean | null;
    status?: HypervisorStatus;
    user?: string;
    enabled?: boolean;
    detail?: string;
    uri?: string;
    status_time?: number;
    info?: {
        [key: string]: unknown;
    };
    cap_status?: {
        [key: string]: unknown;
    };
    hypervisors_pools?: Array<string>;
    storage_pools?: Array<string>;
    enabled_storage_pools?: Array<string>;
    virt_pools?: Array<string>;
    enabled_virt_pools?: Array<string>;
    prev_status?: Array<unknown>;
    mountpoints?: Array<unknown>;
    stats?: {
        [key: string]: unknown;
    } | null;
    viewer?: AdminHypervisorViewer | null;
    viewer_status?: {
        [key: string]: unknown;
    } | null;
    vpn?: {
        [key: string]: unknown;
    } | null;
    desktops_started?: number;
    gpus?: Array<string>;
    physical_gpus?: Array<string>;
    orchestrator_managed?: boolean | null;
    boot_progress?: {
        [key: string]: unknown;
    } | null;
    health?: {
        [key: string]: unknown;
    } | null;
    degraded?: {
        [key: string]: unknown;
    } | null;
    libvirt_warning?: {
        [key: string]: unknown;
    } | null;
    icon?: string | null;
    isard_static_url?: string | null;
    isard_video_url?: string | null;
    isard_proxy_hyper_url?: string | null;
};

/**
 * AdminHypervisorCapabilities
 */
export type AdminHypervisorCapabilities = {
    disk_operations: boolean;
    hypervisor: boolean;
};

/**
 * AdminHypervisorCreateData
 *
 * Request body for creating/registering a hypervisor.
 */
export type AdminHypervisorCreateData = {
    hyper_id: string;
    hostname: string;
    user?: string;
    port?: string;
    cap_disk?: boolean;
    cap_hyper?: boolean;
    enabled?: boolean;
    browser_port?: string;
    spice_port?: string;
    isard_static_url?: string | null;
    isard_video_url?: string | null;
    isard_proxy_hyper_url?: string;
    isard_hyper_vpn_host?: string | null;
    description?: string;
    only_forced?: boolean;
    nvidia_enabled?: boolean;
    nvidia_gpus?: Array<{
        [key: string]: unknown;
    }> | null;
    force_get_hyp_info?: boolean;
    min_free_mem_gb?: number;
    min_free_gpu_mem_gb?: number;
    storage_pools?: string | null;
    virt_pools?: string | null;
    buffering_hyper?: boolean;
    gpu_only?: boolean;
    kvm_module?: string | null;
    nested?: boolean | null;
    hugepages_info?: {
        [key: string]: unknown;
    } | null;
    pci_devices?: {
        [key: string]: {
            [key: string]: unknown;
        };
    } | null;
};

/**
 * AdminHypervisorCreateResponse
 *
 * Response for ``POST /admin/item/hypervisor``.
 */
export type AdminHypervisorCreateResponse = {
    [key: string]: unknown;
};

/**
 * AdminHypervisorDisksFoundData
 *
 * Request body for reporting disks found on hypervisor.
 */
export type AdminHypervisorDisksFoundData = {
    disks: Array<unknown>;
};

/**
 * AdminHypervisorEnableData
 *
 * Request body for enabling/disabling a hypervisor.
 */
export type AdminHypervisorEnableData = {
    enabled?: boolean;
    numa_topology?: {
        [key: string]: unknown;
    } | null;
    machine_types?: {
        [key: string]: unknown;
    } | null;
};

/**
 * AdminHypervisorEnableResponse
 *
 * Response for ``PUT /admin/item/hypervisor/{hyper_id}``.
 */
export type AdminHypervisorEnableResponse = {
    [key: string]: unknown;
};

/**
 * AdminHypervisorHealthRequest
 *
 * A hypervisor's self-measured health: mount latency, hung mounts, NFS, D-state qemu, libvirt, load.
 */
export type AdminHypervisorHealthRequest = {
    health: {
        [key: string]: unknown;
    };
};

/**
 * AdminHypervisorMediaDeleteData
 *
 * Request body for deleting media paths.
 */
export type AdminHypervisorMediaDeleteData = {
    medias_paths: Array<unknown>;
};

/**
 * AdminHypervisorMediaFoundData
 *
 * Request body for reporting media found on hypervisor.
 */
export type AdminHypervisorMediaFoundData = {
    medias: Array<unknown>;
};

/**
 * AdminHypervisorMountpoint
 *
 * One row of ``GET /admin/items/hypervisor/mountpoints/{hyper_id}``.
 */
export type AdminHypervisorMountpoint = {
    [key: string]: unknown;
};

/**
 * AdminHypervisorStartedDomain
 *
 * One row of ``GET /admin/items/hypervisor/started_domains/{hyper_id}``.
 */
export type AdminHypervisorStartedDomain = {
    id?: string | null;
    name?: string | null;
    user?: string | null;
    [key: string]: unknown | string | null | string | null | string | null | undefined;
};

/**
 * AdminHypervisorStatusResponse
 *
 * Response for ``GET /admin/item/hypervisor/status/{hyper_id}`` —
 * the trimmed status row (id, status, only_forced).
 */
export type AdminHypervisorStatusResponse = {
    id?: string | null;
    status?: HypervisorStatus | null;
    only_forced?: boolean | null;
    [key: string]: unknown | string | null | HypervisorStatus | null | boolean | null | undefined;
};

/**
 * AdminHypervisorViewer
 */
export type AdminHypervisorViewer = {
    static?: string | null;
    proxy_video?: string | null;
    spice_ext_port?: string | null;
    html5_ext_port?: string | null;
    proxy_hyper_host?: string | null;
};

/**
 * AdminHypervisorVirtPool
 *
 * One row of ``GET /admin/items/hypervisor/{hyper_id}/virt_pools``.
 */
export type AdminHypervisorVirtPool = {
    id?: string | null;
    name?: string | null;
    enabled?: boolean | null;
    [key: string]: unknown | string | null | string | null | boolean | null | undefined;
};

/**
 * AdminHypervisorVirtPoolUpdateData
 *
 * Request body for updating hypervisor virt pool assignment.
 */
export type AdminHypervisorVirtPoolUpdateData = {
    /**
     * Virt pool ID
     */
    id: string;
    /**
     * Enable or disable the virt pool
     */
    enable_virt_pool: boolean;
};

/**
 * AdminHypervisorVpnResponse
 *
 * Response for ``GET /admin/item/hypervisor_vpn/{hyper_id}``.
 */
export type AdminHypervisorVpnResponse = {
    [key: string]: unknown;
};

/**
 * AdminHypervisorWgAddrData
 *
 * Request body for updating wireguard address.
 */
export type AdminHypervisorWgAddrData = {
    mac: string;
    ip: string;
};

/**
 * AdminHypervisorWgAddrResponse
 *
 * Response for ``GET /admin/item/hypervisor/vm/wg_addr`` — the wireguard
 * table lookup result for the hypervisor host.
 */
export type AdminHypervisorWgAddrResponse = {
    [key: string]: unknown;
};

/**
 * AdminLimitsUpdateData
 *
 * Request body for updating limits.
 */
export type AdminLimitsUpdateData = {
    limits: boolean | Limits;
    propagate?: boolean | null;
};

/**
 * AdminLinkedGroupNamed
 *
 * ``{id, name, category_name}`` lookup row used by the management nav
 * response for the ``linked_groups_data`` array.
 */
export type AdminLinkedGroupNamed = {
    id: string;
    name?: string | null;
    category_name?: string | null;
};

/**
 * AdminListDomainsData
 *
 * Request body for listing domains (desktops or templates).
 */
export type AdminListDomainsData = {
    kind?: 'desktop' | 'template';
    categories?: string | null;
    domain_ids?: Array<string> | null;
    status?: string | null;
    group?: string | null;
    user?: string | null;
    hyp_started?: string | null;
    server?: 'SERVER' | 'AUTO' | '-' | null;
    name?: Array<string> | null;
    operators?: {
        [key: string]: 'is' | 'is-not';
    } | null;
};

/**
 * AdminLoginConfigResponse
 *
 * Raw response for the admin login-config read endpoints.
 *
 * Returned by ``GET /admin/item/login-config`` (global) and
 * ``GET /admin/item/login-config/{category_id}`` (per-category — the
 * service falls back to the global config when the category has no
 * overrides). Permissive shape because the underlying
 * ``Configuration.login`` blob is admin-edited freeform; the public
 * ``LoginConfigResponse`` (in ``api.schemas.login``) typed each field
 * individually for the consumer side, but the admin edit modal needs
 * the unmerged raw payload.
 */
export type AdminLoginConfigResponse = {
    [key: string]: unknown;
};

/**
 * AdminMediaItem
 *
 * One row of ``GET /admin/items/media`` and ``GET /admin/items/media/{status}``.
 *
 * The service returns the raw media doc merged with display-friendly
 * joined fields (category_name, group_name, user_name, domains
 * count). Permissive (``ConfigDict(extra="allow")``) because legacy
 * rows may carry fields the merger doesn't know about — losing them
 * silently was the apiv3 behaviour and the webapp admin tolerates it.
 */
export type AdminMediaItem = {
    id: string;
    name?: string | null;
    description?: string | null;
    kind?: string | null;
    status?: string | null;
    category?: string | null;
    category_name?: string | null;
    group?: string | null;
    group_name?: string | null;
    user?: string | null;
    user_name?: string | null;
    username?: string | null;
    progress?: unknown | null;
    url_isard?: string | null;
    url_web?: string | null;
    icon?: string | null;
    domains?: number | null;
    accessed?: unknown | null;
    file?: string | null;
    [key: string]: unknown | string | string | null | string | null | string | null | string | null | string | null | string | null | string | null | string | null | string | null | string | null | string | null | unknown | null | string | null | string | null | string | null | number | null | unknown | null | string | null | undefined;
};

/**
 * AdminMediaStatusCount
 *
 * One row of ``GET /media/status``.
 *
 * The service emits ``{status, count}`` pairs from
 * ``MediaProcessed.admin_get_media_status_count``. Status values are
 * DB-driven (``MediaStatusEnum`` plus historical strings), so the
 * field stays loose ``str``.
 */
export type AdminMediaStatusCount = {
    status: string;
    count: number;
};

/**
 * AdminMigrationErrorsResponse
 *
 * Response for ``GET /admin/item/user/migrate/check/{user_id}/{target_user_id}``.
 *
 * Service returns the raw error list; the route wraps it as
 * ``{"errors": [...]}``.
 */
export type AdminMigrationErrorsResponse = {
    errors: Array<unknown>;
};

/**
 * AdminMigrationStartedResponse
 *
 * Response for ``PUT /admin/item/user/migrate/{user_id}/{target_user_id}``.
 *
 * On success the service returns ``({}, 200)``; on validation failure it
 * returns ``({"errors": [...]}, 428)``. Both shapes serialise through a
 * single optional-``errors`` model.
 */
export type AdminMigrationStartedResponse = {
    errors?: Array<unknown> | null;
};

/**
 * AdminMigrationsResponse
 *
 * Response for admin migrations listing.
 */
export type AdminMigrationsResponse = {
    migrations: Array<{
        [key: string]: unknown;
    }>;
};

/**
 * AdminMultipleActionsData
 *
 * Request body for performing actions on multiple domains.
 */
export type AdminMultipleActionsData = {
    action: string;
    ids: Array<string>;
};

/**
 * AdminNotifyDesktopsQueueRequest
 *
 * Top-level JSON array body for ``PUT /admin/item/notify/desktops/queue/{hyp_id}``.
 */
export type AdminNotifyDesktopsQueueRequest = Array<DesktopQueueItem>;

/**
 * AdminPasswordPolicyResponse
 *
 * Response for ``GET /admin/item/user/password-policy/{user_id}``.
 *
 * ``UserPolicies.get_user_policy(subtype="password", ...)`` returns the
 * policy dict (or ``False`` when no policy applies). The route only ever
 * calls it with ``subtype="password"``, so the shape is the password
 * policy. ``False`` is normalised to ``{}`` by the route's
 * ``isinstance(result, dict)`` guard so we don't have to model the
 * bool variant.
 */
export type AdminPasswordPolicyResponse = {
    length?: number | null;
    uppercase?: number | null;
    lowercase?: number | null;
    digits?: number | null;
    special_characters?: number | null;
    not_username?: boolean | null;
    expiration?: number | null;
    old_passwords?: number | null;
    digits_last_password?: number | null;
};

/**
 * AdminPasswordResetData
 *
 * Request body for resetting a user's password.
 */
export type AdminPasswordResetData = {
    user_id: string;
    password: string;
};

/**
 * AdminQuotaCategoryLimits
 *
 * Category- (or group-) scoped limits block returned by
 * ``QuotasProcess.process_category_limits`` /
 * ``get_manager_usage`` / ``get_admin_usage``.
 *
 * The two ``process_*_limits`` paths emit the full ``q``/``qp`` set;
 * the manager/admin-usage paths return only the counters
 * (``d``/``r``/``t``/``i``/``v``/``m``/``u``/``deployments``). All
 * ``q``/``qp`` fields kept optional so the lighter shapes don't 500
 * here.
 */
export type AdminQuotaCategoryLimits = {
    category?: {
        [key: string]: unknown;
    } | null;
    group?: {
        [key: string]: unknown;
    } | null;
    d: number;
    r: number;
    t: number;
    i: number;
    v: number;
    m: number;
    u?: number | null;
    deployments: number;
    dq?: number | null;
    dqp?: number | null;
    rq?: number | null;
    rqp?: number | null;
    tq?: number | null;
    tqp?: number | null;
    iq?: number | null;
    iqp?: number | null;
    vq?: number | null;
    vqp?: number | null;
    mq?: number | null;
    mqp?: number | null;
    uq?: number | null;
    uqp?: number | null;
    deploymentsq?: number | null;
    deploymentsqp?: number | null;
};

/**
 * AdminQuotaResponse
 *
 * Configured quota / limits for a user, category or group.
 *
 * The shape varies by ``kind``: user lookups return ``quota`` +
 * ``limits``; category lookups return ``quota`` + ``limits``; group
 * lookups additionally include ``grouplimits``. ``Quotas.Get*Quota``
 * in the common helper is the source of truth.
 */
export type AdminQuotaResponse = {
    /**
     * Quota dict (desktops, templates, vcpus, memory, ...) or ``False`` if no quota is configured.
     */
    quota: boolean | {
        [key: string]: unknown;
    };
    /**
     * Limits dict (the maximum the entity may set its own quota to) or ``False`` if no limits apply.
     */
    limits?: boolean | {
        [key: string]: unknown;
    };
    /**
     * Group-level limits, only present on group quota lookups.
     */
    grouplimits?: boolean | {
        [key: string]: unknown;
    } | null;
    [key: string]: unknown | boolean | {
        [key: string]: unknown;
    } | boolean | {
        [key: string]: unknown;
    } | boolean | {
        [key: string]: unknown;
    } | null | undefined;
};

/**
 * AdminQuotaUpdateData
 *
 * Request body for updating quotas.
 */
export type AdminQuotaUpdateData = {
    quota: boolean | Quota;
    propagate?: boolean | null;
    role?: string | null;
};

/**
 * AdminQuotaUserUsage
 *
 * Per-user quota usage block from ``QuotasProcess.process_user_quota``.
 *
 * All ``q``/``qp`` fields are emitted unconditionally — when the user
 * has no quota set the service writes ``9999`` / ``0`` placeholders,
 * so the int variant covers both cases.
 */
export type AdminQuotaUserUsage = {
    user: {
        [key: string]: unknown;
    };
    d: number;
    dq: number;
    dqp: number;
    r: number;
    rq: number;
    rqp: number;
    t: number;
    tq: number;
    tqp: number;
    i: number;
    iq: number;
    iqp: number;
    v: number;
    vq: number;
    vqp: number;
    m: number;
    mq: number;
    mqp: number;
    deployments: number;
    deploymentsq: number;
    deploymentsqp: number;
    dktpDeployment: number;
    dktpDeploymentq: number;
    dktpDeploymentqp: number;
    startDeploymentDktp: number;
    startDeploymentDktpq: number;
    startDeploymentDktpqp: number;
};

/**
 * AdminQuotasResponse
 *
 * Response for ``GET /admin/items/quotas``.
 *
 * ``QuotasProcess.get`` always returns a ``user`` block and adds either
 * ``limits`` (managers) or ``global`` (admins).
 */
export type AdminQuotasResponse = {
    user: AdminQuotaUserUsage;
    limits?: AdminQuotaCategoryLimits | null;
    global?: AdminQuotaCategoryLimits | null;
};

/**
 * AdminRegisterVlansRequest
 *
 * Body for ``POST /admin/items/vlans``.
 *
 * Sent by ``docker/hypervisor/src/vlans/vlans-db.py``: the hypervisor
 * reports the set of VLAN identifiers it has discovered so the API
 * can upsert the corresponding ``interfaces`` entries.
 */
export type AdminRegisterVlansRequest = {
    vlans: Array<string>;
};

/**
 * AdminRoleItem
 *
 * Row shape for ``GET /admin/items/roles``.
 *
 * ``UsersProcessed.get_roles`` plucks ``id``, ``name``,
 * ``description`` (and orders by ``sortorder``).
 */
export type AdminRoleItem = {
    id: string;
    name: string;
    description?: string | null;
};

/**
 * AdminRoleUpdateData
 *
 * Request body for updating a role.
 *
 * The route forces ``id`` from the URL path, so any ``id`` on the
 * body is ignored — declared optional so callers that include it for
 * symmetry don't 4xx, and the service receives the URL-derived id.
 */
export type AdminRoleUpdateData = {
    id?: string | null;
    name?: string | null;
    description?: string | null;
    sortorder?: number | null;
};

/**
 * AdminSecondaryGroupRef
 *
 * Lightweight ``{id, name}`` group reference used by the user-full-data
 * response. Same pluck as ``GroupsProcessed.get_secondary_groups_data``.
 */
export type AdminSecondaryGroupRef = {
    id: string;
    name?: string | null;
    category_name?: string | null;
};

/**
 * AdminSecondaryGroupsData
 *
 * Request body for secondary group operations.
 */
export type AdminSecondaryGroupsData = {
    ids: Array<string>;
    secondary_groups: Array<string>;
};

/**
 * AdminSecretCreateData
 *
 * Request body for creating a secret.
 */
export type AdminSecretCreateData = {
    id: string;
    description?: string | null;
    domain: string;
    category_id: string;
};

/**
 * AdminSecretCreateResponse
 *
 * Response for ``POST /admin/item/secret``.
 *
 * Service returns ``{secret: <raw>}`` only — the JWT-signing key is
 * surfaced once and never read back.
 */
export type AdminSecretCreateResponse = {
    secret: string;
};

/**
 * AdminSecretItem
 *
 * Row shape for ``GET /admin/items/secrets``.
 *
 * Backed by ``ApiAdmin.admin_table_list("secrets")`` — the secrets
 * table carries the same fields the create-endpoint advertises
 * (``id``/``kid``, ``category_id``, ``secret``, ``description``,
 * ``role_id``).
 */
export type AdminSecretItem = {
    id?: string | null;
    kid?: string | null;
    category_id?: string | null;
    secret?: string | null;
    description?: string | null;
    role_id?: string | null;
};

/**
 * AdminSocketioEmitRequest
 *
 * Top-level JSON array body for ``POST /admin/items/socketio``.
 */
export type AdminSocketioEmitRequest = Array<SocketioEmitRequest>;

/**
 * AdminStorageDomain
 *
 * One row of ``GET /admin/items/storage/domains/{id}`` and
 * ``GET /admin/items/media/domains/{id}``: the domain id + display name
 * the webapp admin lists. Permissive — joined fields differ.
 */
export type AdminStorageDomain = {
    id?: string | null;
    name?: string | null;
    [key: string]: unknown | string | null | string | null | undefined;
};

/**
 * AdminStorageFilterRequest
 *
 * Request body for filtering admin storage listings.
 */
export type AdminStorageFilterRequest = {
    /**
     * Category IDs to filter by (admin only)
     */
    categories?: Array<string> | null;
};

/**
 * AdminStorageInfo
 *
 * Response shape for ``GET /admin/item/storage/info/{id}`` and
 * ``GET /admin/item/storage/search-info/{id}``.
 *
 * Backed by ``AdminStorageService.get_storage_info`` /
 * ``get_storage_search_info`` — qemu-img blob plus owner joins.
 * Permissive because those service methods merge sub-objects whose
 * keys differ per backend.
 */
export type AdminStorageInfo = {
    [key: string]: unknown;
};

/**
 * AdminStorageItem
 *
 * One row of ``GET /admin/items/storage`` and the by-status / by-role
 * variants. Permissive (``ConfigDict(extra="allow")``) because the
 * storage doc carries varied per-status fields the webapp admin
 * renders verbatim.
 */
export type AdminStorageItem = {
    id?: string | null;
    status?: string | null;
    type?: string | null;
    user_id?: string | null;
    category?: string | null;
    [key: string]: unknown | string | null | string | null | string | null | string | null | string | null | undefined;
};

/**
 * AdminStorageRefreshResponse
 *
 * Result of the running-desktops qemu-img-info refresh sweep.
 */
export type AdminStorageRefreshResponse = {
    /**
     * Number of qemu-img-info refresh tasks enqueued.
     */
    enqueued: number;
};

/**
 * AdminStorageStatusCount
 *
 * One row of ``GET /storage/status``: ``{status, count}``.
 */
export type AdminStorageStatusCount = {
    status: string;
    count: number;
};

/**
 * AdminTemplateItem
 */
export type AdminTemplateItem = {
    id: string;
    name: string;
    icon?: string | null;
    user?: string | null;
    category?: string | null;
};

/**
 * AdminTemplateTreeNode
 *
 * Recursive node in ``ApiAdmin.get_template_tree_list``.
 */
export type AdminTemplateTreeNode = {
    id: string;
    title?: string | null;
    parent?: string | boolean | null;
    user?: string | null;
    category?: string | null;
    group?: string | null;
    kind?: string | null;
    duplicate_parent_template?: string | boolean | null;
    expanded?: boolean | null;
    selected?: boolean | null;
    unselectable?: boolean | null;
    children?: Array<AdminTemplateTreeNode> | null;
};

/**
 * AdminUser
 */
export type AdminUser = {
    id: string;
    name?: string | null;
    provider?: string | null;
    category?: string | null;
    uid?: string | null;
    username?: string | null;
    role?: string | null;
    group?: string | null;
    active?: boolean | null;
    secondary_groups?: Array<string> | null;
    email?: string | null;
    accessed?: number | null;
    email_verified?: boolean | number | null;
    disclaimer_acknowledged?: boolean | null;
    vpn?: AdminUserVpn | null;
    user_storage?: AdminUserStorage | null;
};

/**
 * AdminUserCreateData
 *
 * Request body for creating a user.
 */
export type AdminUserCreateData = {
    username: string;
    name: string;
    uid?: string | null;
    provider?: string;
    category: string;
    group: string;
    role: string;
    password: string;
    email?: string | null;
    email_verified?: boolean;
    photo?: string | null;
    bulk?: boolean;
    secondary_groups?: Array<string>;
};

/**
 * AdminUserDeleteData
 *
 * Request body for deleting users.
 */
export type AdminUserDeleteData = {
    user: Array<string>;
    delete_user?: boolean;
};

/**
 * AdminUserDeleteResponse
 */
export type AdminUserDeleteResponse = {
    exceptions?: Array<string> | null;
};

/**
 * AdminUserDesktopItem
 *
 * Row shape for ``GET /admin/items/user/{user_id}/desktops``.
 *
 * ``DomainsProcessed.list_by_kind_user("desktop", ...)`` plucks the
 * listed fields. ``status`` may be missing on freshly-created
 * desktops.
 */
export type AdminUserDesktopItem = {
    id: string;
    name?: string | null;
    status?: string | null;
    icon?: string | null;
    image?: DomainImageOutput | null;
    kind?: string | null;
};

/**
 * AdminUserDisplaysResponse
 *
 * Admin user notification displays response
 */
export type AdminUserDisplaysResponse = {
    displays: Array<NotificationDisplayEnum>;
};

/**
 * AdminUserFullDataResponse
 *
 * Response for ``GET /admin/item/user/{user_id}`` and ``/raw``.
 *
 * ``UsersProcessed.get_user_full_data`` enriches the cached user row with
 * ``category_name``, ``group_name`` and ``secondary_groups_data``. Inherits
 * every ``AdminUser`` field (so half-deleted rows still serialize) and
 * surfaces the two name lookups + the secondary-group expansion. The raw
 * variant goes through ``Caches.get_cached_user_with_names`` which already
 * merges the same name fields, so a single model covers both.
 */
export type AdminUserFullDataResponse = {
    id: string;
    name?: string | null;
    provider?: string | null;
    category?: string | null;
    uid?: string | null;
    username?: string | null;
    role?: string | null;
    group?: string | null;
    active?: boolean | null;
    secondary_groups?: Array<string> | null;
    email?: string | null;
    accessed?: number | null;
    email_verified?: boolean | number | null;
    disclaimer_acknowledged?: boolean | null;
    vpn?: AdminUserVpn | null;
    user_storage?: AdminUserStorage | null;
    category_name?: string | null;
    group_name?: string | null;
    secondary_groups_data?: Array<AdminSecondaryGroupRef> | null;
};

/**
 * AdminUserIdResponse
 *
 * Response for ``GET /admin/item/user/email-category/{email}/{category}``.
 *
 * Returns ``{id: <user-id-or-None>}`` — service may return ``None`` when
 * no user matches.
 */
export type AdminUserIdResponse = {
    id?: string | null;
};

/**
 * AdminUserImpersonateJwtResponse
 *
 * Response for ``GET /admin/item/jwt/{user_id}`` — a single signed JWT.
 */
export type AdminUserImpersonateJwtResponse = {
    jwt: string;
};

/**
 * AdminUserNavItem
 *
 * Row shape for ``GET /admin/items/users/{nav}/users``.
 *
 * ``UsersProcessed.admin_list_users`` plucks the ``AdminUser`` fields and,
 * depending on ``nav``, merges either the ``*_name`` lookups
 * (``management``) or quota usage counters (``quotas_limits``). Both nav
 * branches share the base id/name columns; the divergent fields are kept
 * optional so a single model covers both shapes.
 */
export type AdminUserNavItem = {
    id: string;
    name?: string | null;
    username?: string | null;
    role?: string | null;
    category?: string | null;
    group?: string | null;
    active?: boolean | null;
    provider?: string | null;
    uid?: string | null;
    secondary_groups?: Array<string> | null;
    email?: string | null;
    accessed?: number | null;
    email_verified?: boolean | number | null;
    disclaimer_acknowledged?: boolean | null;
    vpn?: AdminUserVpn | null;
    user_storage?: AdminUserStorage | null;
    group_name?: string | null;
    role_name?: string | null;
    category_name?: string | null;
    secondary_groups_names?: Array<string> | null;
    volatile?: number | null;
    desktops?: number | null;
    templates?: number | null;
    media_size?: number | null;
    domains_size?: number | null;
};

/**
 * AdminUserSchemaResponse
 *
 * Response for user schema (roles, categories, groups).
 */
export type AdminUserSchemaResponse = {
    role: Array<unknown>;
    category?: Array<unknown> | null;
    group?: Array<unknown> | null;
};

/**
 * AdminUserSearchData
 *
 * Request body for searching users.
 */
export type AdminUserSearchData = {
    term: string;
};

/**
 * AdminUserSearchItem
 *
 * Row shape for ``POST /admin/items/users/search``.
 *
 * ``Alloweds.get_table_term`` plucks ``id``/``name``/``uid``/``category_name`` for admins
 * and adds ``category`` for managers. Fields kept optional so a
 * single model handles either pluck variant.
 */
export type AdminUserSearchItem = {
    id: string;
    name?: string | null;
    uid?: string | null;
    category?: string | null;
    category_name?: string | null;
};

/**
 * AdminUserStorage
 */
export type AdminUserStorage = {
    provider_quota?: {
        [key: string]: unknown;
    } | null;
};

/**
 * AdminUserTemplateItem
 *
 * Row shape for ``GET /admin/items/user/{user_id}/templates``.
 */
export type AdminUserTemplateItem = {
    id: string;
    name: string;
    icon?: string | null;
    image?: DomainImageOutput | null;
    description?: string | null;
};

/**
 * AdminUserUpdateData
 *
 * Request body for updating one or more users.
 */
export type AdminUserUpdateData = {
    ids?: Array<string> | null;
    name?: string | null;
    email?: string | null;
    email_verified?: boolean | null;
    role?: string | null;
    category?: string | null;
    group?: string | null;
    password?: string | null;
    active?: boolean | null;
    quota?: boolean | Quota | null;
    secondary_groups?: Array<string> | null;
    bulk?: boolean;
};

/**
 * AdminUserVpn
 */
export type AdminUserVpn = {
    wireguard?: {
        [key: string]: unknown;
    } | null;
};

/**
 * AdminUserVpnFileResponse
 *
 * Response for the two VPN config/install endpoints.
 *
 * ``IsardVpn.vpn_data`` returns ``{kind: "file", name, ext, mime,
 * content}`` for both ``config`` (wireguard ``.conf``) and ``install``
 * (a ``sh``/``vb`` setup script).
 */
export type AdminUserVpnFileResponse = {
    kind: 'file';
    name: string;
    ext: string;
    mime: string;
    content: string;
};

/**
 * AdminVersionResponse
 */
export type AdminVersionResponse = {
    isardvdi_version: string;
    commit: string;
};

/**
 * AdminVirtInstallSaveResponse
 *
 * Response of ``POST /admin/item/domains/xml_sections/{domain_id}/save_virt_install``.
 *
 * Only ``id`` and ``name`` are returned to the route layer (see the
 * route's ``record["id"], record["name"]`` slice).
 */
export type AdminVirtInstallSaveResponse = {
    id: string;
    name: string;
};

/**
 * AdminVirtInstallXmlSectionsSaveResponse
 *
 * Response of ``POST /admin/virt_install/xml_sections/{virt_id}``.
 *
 * Mirrors ``save_virt_install_xml_sections`` which returns
 * ``{xml: <merged>, valid: True}``.
 */
export type AdminVirtInstallXmlSectionsSaveResponse = {
    xml: string;
    valid: boolean;
};

/**
 * AdminVpnConnectionsDisconnectRequest
 *
 * Top-level JSON array body for ``DELETE /admin/vpn_connections``.
 *
 * Aligned with the FastAPI operation id ``vpn_connections_disconnect``.
 */
export type AdminVpnConnectionsDisconnectRequest = Array<VpnDisconnectListItem>;

export type AllowedInput = {
    /**
     * List of allowed groups. If False, no groups are allowed.
     */
    groups?: boolean | Array<unknown>;
    /**
     * List of allowed users. If False, no users are allowed.
     */
    users?: boolean | Array<unknown>;
    /**
     * List of allowed categories. If False, no categories are allowed.
     */
    categories?: boolean | Array<unknown>;
    /**
     * List of allowed roles. If False, no roles are allowed.
     */
    roles?: boolean | Array<unknown>;
};

/**
 * AllowedBase
 */
export type AllowedBase = {
    /**
     * List of allowed groups. If False, no groups are allowed.
     */
    groups?: boolean | Array<unknown>;
    /**
     * List of allowed users. If False, no users are allowed.
     */
    users?: boolean | Array<unknown>;
};

/**
 * AllowedGetRequest
 *
 * Request body for getting allowed access list for a table item.
 */
export type AllowedGetRequest = {
    /**
     * Item ID to get allowed access for
     */
    id: string | number;
};

/**
 * AllowedReservableItem
 */
export type AllowedReservableItem = {
    allowed: ApiSchemasAllowedAllowedOutput;
    description: string;
    id: string;
    name: string;
    editable: boolean;
    /**
     * Anonymized indices of the hypervisor groups that can host this vGPU
     */
    hypervisor_groups?: Array<number>;
    /**
     * NUMA nodes per hypervisor-group index (keyed by group index)
     */
    numa_by_group?: {
        [key: string]: Array<number>;
    };
    /**
     * Hypervisor names hosting this vGPU (admins/managers only)
     */
    hypervisors?: Array<string>;
    /**
     * NUMA nodes per hypervisor name (admins/managers only)
     */
    numa_by_hypervisor?: {
        [key: string]: Array<number>;
    };
};

/**
 * AllowedReservablesResponse
 */
export type AllowedReservablesResponse = {
    vgpus: Array<AllowedReservableItem>;
};

/**
 * AllowedResponse
 */
export type AllowedResponse = {
    /**
     * List of currently selected allowed users and groups
     */
    selected: SelectedAllowed;
    /**
     * List of groups that the user is allowed to access and share items with
     */
    available_groups: boolean | Array<ApiSchemasAllowedAvailableGroup>;
    /**
     * List of groups that have some, but not all, users allowed
     */
    indeterminate_groups: boolean | Array<IndeterminateGroup>;
    /**
     * Details of the users in selected.users
     */
    selected_users?: Array<AvailableUser>;
};

/**
 * AllowedTableEntityItem
 *
 * Enriched roles/categories row from ``Alloweds.get_allowed`` —
 * pluck'd to ``id, name, uid, parent_category``.
 */
export type AllowedTableEntityItem = {
    id: string;
    name?: string | null;
    uid?: string | null;
    parent_category?: string | null;
};

/**
 * AllowedTableGroupItem
 *
 * Enriched group row from ``Alloweds.get_allowed`` — pluck'd to
 * ``id, name, uid, parent_category`` and merged with ``category_name``.
 */
export type AllowedTableGroupItem = {
    id: string;
    name?: string | null;
    uid?: string | null;
    parent_category?: string | null;
    category_name?: string | null;
};

/**
 * AllowedTableResponse
 *
 * Response model for ``POST /allowed/table/{table}``.
 *
 * Mirrors the dict returned by ``Alloweds.get_allowed`` — each
 * bucket is either ``False`` (allow-none) or a list of enriched
 * rows. Buckets are individually optional because the underlying
 * ``allowed`` dict may not declare every key on legacy rows.
 */
export type AllowedTableResponse = {
    roles?: boolean | Array<AllowedTableEntityItem> | null;
    categories?: boolean | Array<AllowedTableEntityItem> | null;
    groups?: boolean | Array<AllowedTableGroupItem> | null;
    users?: boolean | Array<AllowedTableUserItem> | null;
};

/**
 * AllowedTableUserItem
 *
 * Enriched user row from ``Alloweds.get_allowed`` — pluck'd to
 * ``id, name, uid, username, photo`` and merged with
 * ``category_name``/``group_name``.
 */
export type AllowedTableUserItem = {
    id: string;
    name?: string | null;
    uid?: string | null;
    username?: string | null;
    photo?: string | null;
    group_name?: string | null;
    category_name?: string | null;
};

/**
 * AllowedTermItem
 *
 * Single autocomplete row returned by
 * ``POST /admin/allowed/term/{table}``.
 *
 * The service overrides the pluck per-table:
 * ``users`` adds ``uid, role, username, category_name, group_name``;
 * ``groups`` adds ``parent_category, category_name``. The webapp's
 * select2 templates render those fields (e.g.
 * ``static/js/snippets/alloweds.js`` shows
 * ``item.name [item.uid] (item.category_name)``), so the model must
 * let them pass through — ``extra='allow'`` preserves them; without
 * it Pydantic strips every field except ``id``/``name`` and the
 * select2 labels become ``undefined``.
 */
export type AllowedTermItem = {
    id: string;
    name?: string | null;
    [key: string]: unknown | string | string | null | undefined;
};

/**
 * AllowedTermRequest
 *
 * Request body for searching table items by term.
 */
export type AllowedTermRequest = {
    /**
     * Search term (2+ characters)
     */
    term: string;
    /**
     * Category to filter by
     */
    category?: string | null;
    /**
     * Role to exclude from user results
     */
    exclude_role?: string | null;
    /**
     * Media kind filter (isos, floppies)
     */
    kind?: string | null;
};

/**
 * AllowedUpdate
 */
export type AllowedUpdate = {
    /**
     * List of allowed groups. If None, the field is not updated.
     */
    groups?: boolean | Array<unknown>;
    /**
     * List of allowed users. If None, the field is not updated.
     */
    users?: boolean | Array<unknown>;
};

/**
 * AllowedUpdateRequest
 *
 * Request body for updating allowed access on a table item.
 */
export type AllowedUpdateRequest = {
    /**
     * Item ID to update
     */
    id: string;
    /**
     * Allowed access configuration with roles, categories, groups, users
     */
    allowed: {
        [key: string]: unknown;
    };
};

/**
 * AnalyticsCategoriesRequest
 *
 * Request body for analytics endpoints that filter by categories.
 */
export type AnalyticsCategoriesRequest = {
    /**
     * List of category IDs to filter
     */
    categories?: Array<string> | null;
};

/**
 * AnalyticsGraphConfigResponse
 *
 * Row in the ``analytics`` table — see ``AnalyticsGraphCreateRequest``
 * for the writeable fields. ``id`` is auto-assigned by RethinkDB and
 * ``grouping_name`` is resolved by the list endpoint.
 *
 * Every field is optional: rows written before the create/update schemas
 * carried these fields persisted without them.
 */
export type AnalyticsGraphConfigResponse = {
    id?: string | null;
    title?: string | null;
    subtitle?: string | null;
    grouping?: string | null;
    grouping_name?: string | null;
    priority?: number | null;
    x_axis_days?: number | null;
};

/**
 * AnalyticsGraphCreateRequest
 *
 * Request body for creating an analytics graph configuration.
 *
 * ``id`` is absent on purpose: the admin form strips it and RethinkDB
 * assigns the primary key.
 */
export type AnalyticsGraphCreateRequest = {
    /**
     * Grouping ID
     */
    grouping: string;
    /**
     * Render order, ascending
     */
    priority: number;
    /**
     * Days of history to plot
     */
    x_axis_days: number;
    /**
     * Graph title
     */
    title: string;
    /**
     * Graph subtitle
     */
    subtitle?: string | null;
};

/**
 * AnalyticsGraphUpdateRequest
 *
 * Request body for updating an analytics graph configuration.
 *
 * ``id`` is absent on purpose: the edit form posts it in the body and
 * it must not reach the update.
 */
export type AnalyticsGraphUpdateRequest = {
    /**
     * Grouping ID
     */
    grouping?: string | null;
    /**
     * Render order, ascending
     */
    priority?: number | null;
    /**
     * Days of history to plot
     */
    x_axis_days?: number | null;
    /**
     * Graph title
     */
    title?: string | null;
    /**
     * Graph subtitle
     */
    subtitle?: string | null;
};

/**
 * AnalyticsSuggestedRemovalsRequest
 *
 * Request body for suggested removals endpoint.
 */
export type AnalyticsSuggestedRemovalsRequest = {
    /**
     * List of category IDs to filter
     */
    categories?: Array<string> | null;
    /**
     * Number of months without use to consider for removal
     */
    months_without_use: number;
};

/**
 * ApiVersion
 */
export type ApiVersion = {
    name: string;
    api_version: string;
    usage: string | null;
};

/**
 * AutoDeleteConfigResponse
 *
 * Auto delete configuration
 */
export type AutoDeleteConfigResponse = {
    older_than?: number | null;
    queue_registries?: Array<string> | null;
    enabled?: boolean;
};

/**
 * AutoDeleteEnabledRequest
 *
 * Request to set auto delete enabled
 */
export type AutoDeleteEnabledRequest = {
    enabled: boolean;
};

/**
 * AutoDeleteEnabledResponse
 *
 * Response shape for ``PUT /admin/item/queues/old_tasks/config/enabled``.
 */
export type AutoDeleteEnabledResponse = {
    enabled: boolean;
};

/**
 * AutoDeleteMaxTimeResponse
 *
 * Response shape for ``PUT /admin/item/queues/old_tasks/config/max_time/{max_time}``.
 */
export type AutoDeleteMaxTimeResponse = {
    older_than: number;
};

/**
 * AutoDeleteQueueRegistriesResponse
 *
 * Response shape for ``PUT /admin/item/queues/old_tasks/config/queue_registries``.
 */
export type AutoDeleteQueueRegistriesResponse = {
    queue_registries: Array<string>;
};

/**
 * AutoRegisterRequest
 */
export type AutoRegisterRequest = {
    role_id: string;
    group_id: string;
    secondary_groups?: Array<string> | null;
};

/**
 * AutoRegisterResponse
 */
export type AutoRegisterResponse = {
    id: string;
};

/**
 * AvailabilityResponse
 *
 * Response model for availability
 */
export type AvailabilityResponse = {
    /**
     * The start time of the availability period.
     */
    start: string;
    /**
     * The end time of the availability period.
     */
    end: string;
    /**
     * Type of availability event.
     */
    event_type: 'available' | 'unavailable' | 'overridable';
    /**
     * Number of units available
     */
    units: 'Enough' | number;
};

/**
 * AvailableReservable
 *
 * Available reservable item
 */
export type AvailableReservable = {
    id: string;
    name: string;
    description: string;
    max_booking_date: string;
};

/**
 * AvailableReservablesResponse
 *
 * Response model for available reservables
 */
export type AvailableReservablesResponse = {
    reservables_available: Array<AvailableReservable> | null;
};

/**
 * AvailableUser
 */
export type AvailableUser = {
    /**
     * User ID
     */
    id: string;
    /**
     * User full name
     */
    name: string;
    /**
     * User username
     */
    username: string;
    /**
     * User photo URL
     */
    photo?: string | null;
    /**
     * User primary group ID
     */
    group?: string | null;
    /**
     * User secondary group IDs
     */
    secondary_groups?: Array<string> | null;
};

/**
 * BacklogRollupRow
 *
 * One ``(pool, category, tier)`` backlog rollup row — the ``GET
 * /admin/items/queues/backlog`` payload is a bare list of these (P2.4 §2.4).
 */
export type BacklogRollupRow = {
    pool: string;
    category_id?: string | null;
    category_name?: string | null;
    tier: string;
    queued?: number;
    started?: number;
    started_over_timeout?: number;
    failed?: number;
    deferred?: number;
    oldest_queued_age_seconds?: number | null;
    has_consumer?: boolean;
    coverage_known?: boolean;
    stranded?: boolean;
};

/**
 * BackupConfigEnabled
 *
 * Enabled flag per backup scope.
 */
export type BackupConfigEnabled = {
    db?: boolean;
    redis?: boolean;
    stats?: boolean;
    config?: boolean;
    disks?: boolean;
    pools?: boolean;
};

/**
 * BackupConfigResponse
 *
 * Response shape for ``GET /admin/item/backups/config``.
 *
 * Mirrors the dict returned by ``AdminBackupsService.get_backup_config``.
 */
export type BackupConfigResponse = {
    schedule: BackupConfigSchedule;
    enabled: BackupConfigEnabled;
    main_schedule_hour: number;
};

/**
 * BackupConfigSchedule
 *
 * Schedule hours per backup scope (``None`` = unscheduled).
 */
export type BackupConfigSchedule = {
    db?: number | null;
    redis?: number | null;
    stats?: number | null;
    config?: number | null;
    disks?: number | null;
    pools?: number | null;
};

/**
 * BackupIntegrityResponse
 *
 * Response shape for ``GET /admin/item/backups/integrity`` and the matching PUT.
 */
export type BackupIntegrityResponse = {
    integrity_enabled: boolean;
};

/**
 * BackupIntegritySetRequest
 *
 * Request body for the weekly borg integrity check toggle.
 */
export type BackupIntegritySetRequest = {
    /**
     * Enable or disable the weekly run
     */
    integrity_enabled: boolean;
};

/**
 * BackupItem
 *
 * One row of ``GET /admin/items/backups``.
 *
 * Permissive (``ConfigDict(extra="allow")``) because legacy backup
 * rows carry assorted free-form metadata in ``details`` that the
 * webapp admin renders verbatim. The required fields are the ones
 * the route depends on for sorting / display.
 */
export type BackupItem = {
    id?: string | null;
    timestamp?: string | null;
    status?: string | null;
    type?: string | null;
    scope?: string | null;
    received_at?: string | null;
    created_at?: string | null;
    details?: {
        [key: string]: unknown;
    } | null;
    [key: string]: unknown | string | null | string | null | string | null | string | null | string | null | string | null | string | null | {
        [key: string]: unknown;
    } | null | undefined;
};

/**
 * BackupReportInsertResponse
 *
 * Response shape for ``POST /backups`` (backupninja ingestion).
 */
export type BackupReportInsertResponse = {
    id: string;
    status: string;
    message: string;
};

/**
 * BackupReportRequest
 *
 * Request body for backup report submission.
 *
 * Permissive (``ConfigDict(extra="allow")``) to mirror ``BackupItem``: the
 * backupninja parser sends rich, free-form report fields on top of the ones
 * declared below (``disk_types``, ``actions``, the ``*_actions`` counts,
 * ``duration``, ``summary``, ``backup_types_status``, ``filesystem_metrics``,
 * ...). The webapp backups view renders several of these verbatim
 * (``total_actions``/``successful_actions``/``warning_actions``/
 * ``failed_actions``, ``duration``, ``backup_types_status``). Without
 * ``extra="allow"`` pydantic silently dropped every one of them, leaving the
 * stored record — and the admin view — with only status/type/scope/timestamp.
 */
export type BackupReportRequest = {
    /**
     * Backup timestamp
     */
    timestamp: unknown;
    /**
     * Backup status
     */
    status: string;
    /**
     * Backup type: 'automated' or 'manual'
     */
    type: string;
    /**
     * Backup scope: 'full', 'db', 'redis', 'stats', 'config', 'disks', or 'pools'
     */
    scope: string;
    /**
     * Backup details with checks, warnings, time_breakdown
     */
    details?: {
        [key: string]: unknown;
    } | null;
    /**
     * Creation timestamp
     */
    created_at?: unknown | null;
    [key: string]: unknown | string | {
        [key: string]: unknown;
    } | null | unknown | null | undefined;
};

/**
 * BastionActiveResponse
 *
 * Lightweight, read-only bastion status for a desktop.
 *
 * Returned by the non-mutating ``/bastion/active`` endpoint used by the
 * desktop-card "Bastion" entry: it never creates a target and carries just
 * enough to decide whether to open the read-only access modal (``ssh.enabled``
 * / ``http.enabled``) and to render the access links.
 */
export type BastionActiveResponse = {
    /**
     * Whether a bastion target exists for this desktop.
     */
    exists?: boolean;
    /**
     * The bastion target ID.
     */
    id?: string | null;
    /**
     * Custom domains, if any.
     */
    domains?: Array<string>;
    ssh?: BastionActiveSshStatus;
    http?: BastionHttpConfig;
    /**
     * The global bastion domain.
     */
    bastion_domain?: string | null;
    /**
     * The global bastion SSH port (None if disabled).
     */
    bastion_ssh_port?: string | null;
    /**
     * Whether bastion is globally enabled.
     */
    bastion_enabled?: boolean;
};

/**
 * BastionActiveSshStatus
 *
 * SSH access status without exposing the authorized keys.
 */
export type BastionActiveSshStatus = {
    enabled?: boolean;
    port?: number;
};

/**
 * BastionAuthorizedKeysRequest
 *
 * Request model for updating bastion SSH authorized keys
 */
export type BastionAuthorizedKeysRequest = {
    /**
     * List of SSH public keys to authorize for bastion SSH access.
     */
    authorized_keys: Array<string>;
};

/**
 * BastionAuthorizedKeysUpdateRequest
 */
export type BastionAuthorizedKeysUpdateRequest = {
    /**
     * List of SSH public keys to be authorized for the bastion SSH access. If empty, the authorized keys will be cleared.
     */
    authorized_keys?: Array<string>;
};

/**
 * BastionDirectViewerResponse
 *
 * Read-only bastion connection info for the direct viewer.
 *
 * Reachable by anyone holding the share link, so it carries only what is
 * needed to connect — target id, domains and ready-to-use URLs — and no key
 * material nor owner data. The URLs are built server-side because the direct
 * viewer cannot read the config (``bastion_domain``, ports) they need.
 */
export type BastionDirectViewerResponse = {
    /**
     * Whether there is any bastion access to show. False when bastion is globally disabled, the desktop has no target, or neither SSH nor HTTP access is enabled on it.
     */
    enabled?: boolean;
    /**
     * The bastion target ID.
     */
    id?: string | null;
    /**
     * Custom domains configured for the target. The access URLs above are only built from the first one.
     */
    custom_domains?: Array<string>;
    /**
     * Whether bastion SSH access is enabled.
     */
    ssh_enabled?: boolean;
    /**
     * Ready-to-run SSH command, if SSH access is enabled.
     */
    ssh_command?: string | null;
    /**
     * Whether bastion HTTP access is enabled.
     */
    http_enabled?: boolean;
    /**
     * Public HTTP URL, if HTTP access is enabled.
     */
    http_url?: string | null;
    /**
     * Public HTTPS URL, if HTTP access is enabled.
     */
    https_url?: string | null;
};

/**
 * BastionDomainUpdateRequest
 */
export type BastionDomainUpdateRequest = {
    /**
     * The domain name to be set for the bastion target of the desktop.
     */
    domain_name: string | null;
};

/**
 * BastionDomainVerificationConfigResponse
 *
 * Response model for bastion domain verification configuration
 */
export type BastionDomainVerificationConfigResponse = {
    /**
     * Whether domain verification is required for bastion domains.
     */
    domain_verification_required: boolean;
};

/**
 * BastionDomainsRequest
 *
 * Request model for updating bastion custom domains
 */
export type BastionDomainsRequest = {
    /**
     * List of custom domain names for the bastion target. Maximum 10.
     */
    domains: Array<string>;
};

/**
 * BastionDomainsUpdateRequest
 *
 * Bulk update of the bastion ``domains`` list (up to 10).
 */
export type BastionDomainsUpdateRequest = {
    /**
     * Up to 10 bastion domain names to set for the desktop. An empty list clears the domains.
     */
    domains?: Array<string>;
};

/**
 * BastionHttpConfig
 */
export type BastionHttpConfig = {
    /**
     * If true, the bastion HTTP access will be enabled for the desktop.
     */
    enabled?: boolean;
    /**
     * HTTP port for the bastion.
     */
    http_port?: number;
    /**
     * HTTPS port for the bastion.
     */
    https_port?: number;
    /**
     * If true, the bastion sends a PROXY protocol v2 header to the guest, preserving the real client IP.
     */
    proxy_protocol?: boolean;
};

/**
 * BastionRequest
 */
export type BastionRequest = {
    /**
     * Bastion HTTP configuration. If not provided, the bastion HTTP configuration will not be modified.
     */
    http?: BastionHttpConfig | null;
    /**
     * Bastion SSH configuration. If not provided, the bastion SSH configuration will not be modified.
     */
    ssh?: BastionSshConfig | null;
    /**
     * Custom domain names to access the bastion (up to 10). If the bastion target hasn't yet been created and domain verification is required, the domains cannot be set.
     */
    domains?: Array<string>;
};

/**
 * BastionResponse
 *
 * Response model for bastion configuration
 */
export type BastionResponse = {
    desktop_id: string;
    http: BastionHttpConfig;
    id: string;
    ssh: BastionSshConfig;
    user_id: string;
    domains?: Array<string>;
};

/**
 * BastionSshConfig
 */
export type BastionSshConfig = {
    /**
     * If true, the bastion SSH access will be enabled for the desktop.
     */
    enabled?: boolean;
    /**
     * SSH port for the bastion.
     */
    port?: number;
    /**
     * List of SSH public keys to be authorized for the bastion SSH access. If empty, the authorized keys will be cleared.
     */
    authorized_keys?: Array<string | null>;
};

/**
 * BookableItemResponse
 *
 * A bookable vGPU reservable for the admin Bookables list.
 *
 * ``categories`` are the distinct names of the categories that delegated GPU
 * cards backing this reservable belong to (empty when only global/undelegated
 * cards back it). The webapp renders them as green delegated-category tags.
 */
export type BookableItemResponse = {
    id: string;
    name: string;
    description?: string | null;
    priority_id?: string | null;
    brand?: string | null;
    model?: string | null;
    profile?: string | null;
    units?: number | null;
    total_units?: number | null;
    categories?: Array<string>;
};

/**
 * BookingEventReservables
 *
 * Reservables associated with a booking event
 */
export type BookingEventReservables = {
    /**
     * List of vGPU profile IDs associated with the booking event
     */
    vgpus: Array<string>;
};

/**
 * BookingEventResponse
 *
 * Response model for booking events
 */
export type BookingEventResponse = {
    id: string;
    item_id: string;
    item_type: string;
    units: number;
    reservables: BookingEventReservables;
    start: string;
    end: string;
    title: string;
    user_id: string;
    editable: boolean;
};

/**
 * BookingPlanResponse
 *
 * Response model for a booking plan
 */
export type BookingPlanResponse = {
    id: string;
    item_type: string;
    item_id: string;
    subitem_id: string;
    units: number;
    start: string;
    end: string;
    user_id: string;
    event_type: string;
    item: string;
};

/**
 * BookingPriorityDesktopResponse
 *
 * Response model for booking priority desktop.
 *
 * The dict returned by ``Bookings.get_user_priority(...)`` (per-vgpu
 * priorities + the most-restrictive ``forbid_time/max_time/max_items``)
 * merged with the desktop ``name``. ``max_time`` and ``max_items`` may
 * be ``None`` when no priority rule matched.
 */
export type BookingPriorityDesktopResponse = {
    priority: {
        [key: string]: number;
    };
    forbid_time: number;
    max_time?: number | null;
    max_items?: number | null;
    name: string;
    [key: string]: unknown | {
        [key: string]: number;
    } | number | number | null | number | null | string | undefined;
};

/**
 * BookingPriorityUser
 *
 * One row of ``POST /items/bookings/priorities``.
 */
export type BookingPriorityUser = {
    id?: string | null;
    username?: string | null;
    name?: string | null;
    [key: string]: unknown | string | null | string | null | string | null | undefined;
};

/**
 * BookingProvisioningRequest
 *
 * Request model for booking provisioning
 */
export type BookingProvisioningRequest = {
    subitems: {
        [key: string]: unknown;
    };
    units: number;
    priority: {
        [key: string]: unknown;
    };
    block_interval: number;
};

/**
 * BrandingDomain
 *
 * Branding domain configuration.
 */
export type BrandingDomain = {
    enabled?: boolean;
    name?: string | null;
    certificate_source?: 'acme' | 'custom' | null;
    certificate_data?: string | null;
};

/**
 * BrandingLogo
 *
 * Branding logo configuration. data is a base64 data URL.
 */
export type BrandingLogo = {
    enabled?: boolean;
    data?: string | null;
};

/**
 * BrandingResponse
 *
 * Response shape for ``GET /admin/item/category/{id}/branding``.
 *
 * Mirrors the request shape — ``BrandingUpdateData`` — but is a
 * distinct class because the response side of branding is the
 * persisted value (i.e. ``Category.branding``), and the response
 * model is what FastAPI uses to filter the wire payload. Keeps both
 * sub-objects optional because freshly-created categories may have
 * no branding row at all.
 */
export type BrandingResponse = {
    domain?: BrandingDomain | null;
    logo?: BrandingLogo | null;
    logo_collapsed?: BrandingLogo | null;
};

/**
 * BrandingUpdateData
 *
 * Request body for updating category branding.
 */
export type BrandingUpdateData = {
    domain?: BrandingDomain | null;
    logo?: BrandingLogo | null;
    logo_collapsed?: BrandingLogo | null;
};

/**
 * BrowserRDPValues
 */
export type BrowserRdpValues = {
    vmName: string;
    vmHost: string;
    vmUsername: string;
    vmPassword: string;
    host: string;
    port: string;
    exp: number;
};

/**
 * BrowserRDPViewer
 */
export type BrowserRdpViewer = {
    kind: ViewerKind;
    protocol: ViewerProtocol;
    viewer?: string | null;
    urlp?: string | null;
    cookie?: string | null;
    values?: BrowserRdpValues | null;
};

/**
 * BrowserVNCValues
 */
export type BrowserVncValues = {
    vmName: string;
    vmHost: string;
    vmPort: string;
    host: string;
    port: string;
    token: string;
    exp: number;
};

/**
 * BrowserVNCViewer
 */
export type BrowserVncViewer = {
    kind: ViewerKind;
    protocol: ViewerProtocol;
    viewer: string;
    urlp: string;
    cookie: string;
    values: BrowserVncValues;
};

/**
 * BulkCreatePersistentDesktopsRequest
 *
 * Request body for ``POST /items/desktops/bulk-create``.
 *
 * Creates many persistent desktops from a single template for a set
 * of users / groups / categories / roles. The actual fan-out happens
 * inside ``CommonDesktops.bulk_create_desktops``.
 */
export type BulkCreatePersistentDesktopsRequest = {
    /**
     * ID of the template to clone from.
     */
    template_id: string;
    /**
     * Base name for the created desktops.
     */
    name: string;
    /**
     * Description.
     */
    description?: string;
    /**
     * Set of users / groups / categories / roles to fan out to. Each member receives one desktop.
     */
    allowed: AllowedInput;
};

/**
 * BulkDeleteDeploymentsErrorResponse
 */
export type BulkDeleteDeploymentsErrorResponse = {
    /**
     * List of error messages for deployments that could not be deleted
     */
    exceptions: Array<string>;
};

/**
 * BulkDeleteDeploymentsRequest
 */
export type BulkDeleteDeploymentsRequest = {
    /**
     * List of deployment IDs to delete
     */
    ids: Array<string>;
    /**
     * Whether to permanently delete the deployments
     */
    permanent?: boolean;
};

/**
 * BulkEditDesktopsRequest
 *
 * Request body for ``PUT /items/desktops/bulk-edit``.
 *
 * The route applies a partial update to every desktop in ``ids``; the
 * remaining fields are forwarded to ``CommonDesktops.update_desktop``
 * in bulk mode.
 */
export type BulkEditDesktopsRequest = {
    /**
     * List of desktop IDs to update.
     */
    ids: Array<string>;
    /**
     * New desktop name
     */
    name?: string | null;
    /**
     * New desktop description
     */
    description?: string | null;
    /**
     * Updated guest properties (credentials, viewers, fullscreen)
     */
    guest_properties?: DomainGuestPropertiesInput | null;
    /**
     * Updated hardware spec
     */
    hardware?: DomainHardware | null;
    /**
     * Updated bookable resources (vGPUs).
     */
    reservables?: ReservablesInput | null;
    [key: string]: unknown | Array<string> | string | null | string | null | DomainGuestPropertiesInput | null | DomainHardware | null | ReservablesInput | null | undefined;
};

/**
 * CardResponse
 *
 * Single card response.
 *
 * Permissive (``ConfigDict(extra="allow")``) because card payloads
 * carry varied per-source fields (stock vs user vs generated) and
 * the mock fixtures historically used ``name`` where the production
 * rows use ``url``.
 */
export type CardResponse = {
    id?: string | null;
    url?: string | null;
    type?: string | null;
    [key: string]: unknown | string | null | string | null | string | null | undefined;
};

/**
 * CategoriesUsersSearchResponse
 */
export type CategoriesUsersSearchResponse = {
    users: Array<AvailableUser>;
    total: number;
};

/**
 * CategoryAuthenticationResponse
 *
 * Response shape for ``GET /admin/item/category/{id}/authentication``.
 *
 * Permissive (``ConfigDict(extra="allow")``) because the underlying
 * ``Category.authentication`` blob is provider-specific freeform
 * (``local`` / ``ldap`` / ``saml`` / ``google``), and the service
 * pre-strips the secret keys (password, client_secret) before
 * returning. The response is consumed by the admin edit-modal which
 * inspects fields per-provider.
 */
export type CategoryAuthenticationResponse = {
    [key: string]: unknown;
};

/**
 * CategoryGauge
 *
 * Per-category fair-tier gauge (bulk/background only — reserved tiers have
 * NO per-tenant inflight signal). ``category_id`` is ``_nocat`` for the
 * null-owner sentinel lane; ``cap`` is ``None`` when uncapped (weighted-RR
 * only).
 */
export type CategoryGauge = {
    category_id: string;
    category_name?: string | null;
    inflight?: number;
    cap?: number | null;
    at_cap?: boolean;
    weight?: number;
    leaked?: number;
    starved?: boolean;
    backlog?: {
        [key: string]: number;
    };
    oldest_queued_age_seconds?: {
        [key: string]: number;
    };
    failed?: {
        [key: string]: number;
    };
};

/**
 * CategoryGroupingLogRow
 *
 * Row in the ``category_grouping`` view of ``query_paginated``.
 */
export type CategoryGroupingLogRow = {
    total: number;
    count: number;
    owner_category_name?: string | null;
    owner_category_id?: string | null;
};

/**
 * CategoryItem
 *
 * Category item model
 */
export type CategoryItem = {
    id: string;
    name: string;
    custom_url_name: string;
};

/**
 * CategoryLoginNotificationData
 *
 * Request body for updating per-category login notification.
 */
export type CategoryLoginNotificationData = {
    cover?: {
        [key: string]: unknown;
    } | null;
    form?: {
        [key: string]: unknown;
    } | null;
};

/**
 * CategoryLoginNotificationEnableData
 *
 * Request body for enabling/disabling a category login notification.
 */
export type CategoryLoginNotificationEnableData = {
    enabled: boolean;
};

/**
 * CategoryResponse
 *
 * Single category response model
 */
export type CategoryResponse = {
    id: string;
    name: string;
};

/**
 * CategoryResponseList
 *
 * List of categories response model
 */
export type CategoryResponseList = {
    categories: Array<CategoryItem>;
};

/**
 * CheckCategoryAvailabilityRequest
 *
 * Request body for checking category availability
 */
export type CheckCategoryAvailabilityRequest = {
    categories: Array<string>;
    storage_pool_id?: string | null;
};

/**
 * CheckCategoryAvailabilityResponse
 *
 * Response model for category availability check
 */
export type CheckCategoryAvailabilityResponse = {
    available: boolean;
};

/**
 * CheckLastResponse
 *
 * Response model for check last subitem/item
 */
export type CheckLastResponse = {
    last: Array<boolean>;
    desktops: Array<{
        [key: string]: unknown;
    }>;
    plans: Array<{
        [key: string]: unknown;
    }>;
    bookings: Array<{
        [key: string]: unknown;
    }>;
    deployments: Array<{
        [key: string]: unknown;
    }>;
};

/**
 * CheckQuotaRequest
 */
export type CheckQuotaRequest = {
    /**
     * Allowed users and groups to check quota against
     */
    allowed?: AllowedBase | null;
};

/**
 * CoOwnerUser
 *
 * Co-owner entry returned by the get co-owners endpoint.
 */
export type CoOwnerUser = {
    /**
     * User ID
     */
    id: string;
    /**
     * User full name
     */
    name: string;
    /**
     * User uid
     */
    uid?: string | null;
    /**
     * User photo URL
     */
    photo?: string | null;
};

/**
 * CoOwnersRequest
 */
export type CoOwnersRequest = {
    /**
     * List of user IDs to set as co-owners
     */
    co_owners: Array<string>;
};

/**
 * CoOwnersResponse
 */
export type CoOwnersResponse = {
    /**
     * Primary owner of the deployment
     */
    owner: CoOwnerUser;
    /**
     * List of co-owners of the deployment
     */
    co_owners: Array<CoOwnerUser>;
};

/**
 * CreateBookingEventRequest
 *
 * Request model for creating a booking event.
 *
 * Vue 2 (``old-frontend``) historically posts ``element_id`` /
 * ``element_type`` while Vue 3 + new clients post ``item_id`` /
 * ``item_type``. ``populate_by_name=True`` plus ``AliasChoices``
 * accepts both wire shapes so the legacy form keeps working.
 */
export type CreateBookingEventRequest = {
    /**
     * ID of the reservable item (e.g., desktop ID or deployment ID)
     */
    item_id: string;
    /**
     * Type of the reservable item (e.g., 'desktop', 'deployment')
     */
    item_type?: 'desktop' | 'deployment';
    /**
     * Start datetime for the booking event
     */
    start: string;
    /**
     * End datetime for the booking event
     */
    end: string;
    /**
     * Title of the booking event
     */
    title?: string | null;
    /**
     * If true, the booking starts now and the start field is ignored
     */
    now?: boolean | null;
};

/**
 * CreateDeploymentRequest
 */
export type CreateDeploymentRequest = {
    /**
     * Name of the deployment
     */
    name: string;
    /**
     * Description of the deployment
     */
    description?: string | null;
    /**
     * Who can access the deployment. At least one user or group must be provided.
     */
    allowed: AllowedBase;
    /**
     * Whether to create a desktop for the user creating the deployment.
     */
    create_owner_desktop?: boolean;
    /**
     * Whether the deployment is visible to it's users.
     */
    visible?: boolean;
    /**
     * List of user IDs that will be co-owners of the deployment.
     */
    co_owners?: Array<string>;
    /**
     * List of permissions the user creating the deployment has over it.
     */
    user_permissions?: Array<DeploymentPermissions>;
    /**
     * Image to use for the deployment. If not provided, the image from the first desktop will be used.
     */
    image?: DomainImageInput | null;
    /**
     * List of different desktop types to create in the deployment. Each entry will create a number of desktops with the same configuration. All desktops will be persistent and without bastion target.
     */
    desktops: Array<CreateDesktopRequest>;
    /**
     * UNIMPLEMENTED: List of resources to attach to the deployment
     */
    resources?: Array<Resource>;
};

/**
 * CreateDesktopFromMedia
 */
export type CreateDesktopFromMedia = {
    media_id: string;
    kind: MediaKindEnum;
    os_template: string;
    /**
     * Name of the desktop to be created. Must be unique within the user desktops.
     */
    name: string;
    /**
     * Description of the desktop to be created.
     */
    description?: string;
    guest_properties: DomainGuestPropertiesInput;
    hardware: MediaHardware;
    /**
     * Image to be used for the desktop. If not provided, a default stock card will be assigned.
     */
    image?: DomainImageInput | null;
};

/**
 * CreateDesktopRequest
 */
export type CreateDesktopRequest = {
    /**
     * ID of the template to use for creating the desktop.
     */
    template_id: string;
    /**
     * Name of the desktop to be created. Must be unique within the user desktops.
     */
    name: string;
    /**
     * Description of the desktop to be created.
     */
    description?: string | null;
    /**
     * If true, the desktop will be persistent and will not be deleted after shutdown. Otherwise, it will be non-persistent and will be deleted after shutdown.
     */
    persistent?: boolean;
    /**
     * Guest properties to be set for the desktop. If not provided, the template guest properties will be inherited.
     */
    guest_properties?: DomainGuestPropertiesInput | null;
    /**
     * Hardware configuration for the desktop. If not provided, the template hardware will be inherited.
     */
    hardware?: DomainHardware | null;
    /**
     * The domain bookable resources. If None, no reservables are available.
     */
    reservables?: ReservablesInput | null;
    /**
     * Image to be used for the desktop. If not provided, the template image will be inherited.
     */
    image?: DomainImageInput | null;
    /**
     * Bastion configuration for the desktop. If not provided, the bastion configuration will not be modified.
     */
    bastion_target?: BastionRequest | null;
    /**
     * End of the booking to create for a temporal desktop with a vGPU.
     */
    booking_end?: string | null;
};

/**
 * CreateMediaRequest
 *
 * Request model for creating media
 */
export type CreateMediaRequest = {
    /**
     * Name of the media.
     */
    name: string;
    /**
     * Description of the media.
     */
    description?: string;
    allowed: AllowedInput;
    kind: MediaKindEnum;
    url: string;
    hypervisors_pools: Array<string>;
};

/**
 * CreatePlanRequest
 *
 * Request model for creating a plan
 */
export type CreatePlanRequest = {
    item_type: string;
    item_id: string;
    subitem_id: string;
    start: string;
    end: string;
};

/**
 * CreatePlanningRequest
 *
 * Request model for creating a planning
 */
export type CreatePlanningRequest = {
    /**
     * Type of the reservable item (e.g., 'gpus')
     */
    item_type: string;
    /**
     * ID of the reservable item (e.g vGPU ID)
     */
    item_id: string;
    /**
     * ID of the subitem within the reservable item (e.g vGPU profile ID)
     */
    subitem_id: string;
    /**
     * Start datetime for the planning
     */
    start: string;
    /**
     * End datetime for the planning
     */
    end: string;
};

/**
 * DataTablesColumn
 *
 * Column descriptor in a DataTables request.
 */
export type DataTablesColumn = {
    data?: string | null;
    name?: string | null;
    searchable?: boolean | null;
    orderable?: boolean | null;
    search?: DataTablesSearch | null;
};

/**
 * DataTablesOrder
 *
 * Single ordering rule (``column`` is the index into ``columns``).
 */
export type DataTablesOrder = {
    column: number;
    dir: 'asc' | 'desc';
};

/**
 * DataTablesSearch
 *
 * ``search`` block sent by DataTables — global or per-column.
 */
export type DataTablesSearch = {
    value?: string;
    regex?: boolean;
};

/**
 * DeadRowSetResponse
 */
export type DeadRowSetResponse = {
    destroy_time: string;
};

/**
 * DeleteActionEnum
 *
 * Action to perform when deleting items.
 */
export const DeleteActionEnum = { MOVE: 'move', DELETE: 'delete' } as const;

/**
 * DeleteActionEnum
 *
 * Action to perform when deleting items.
 */
export type DeleteActionEnum = typeof DeleteActionEnum[keyof typeof DeleteActionEnum];

/**
 * DeleteOldTasksRequest
 *
 * Request to delete old tasks
 */
export type DeleteOldTasksRequest = {
    older_than: number;
};

/**
 * DeleteOldTasksResult
 *
 * Result of deleting old tasks
 */
export type DeleteOldTasksResult = {
    ok: Array<string>;
    errors: Array<string>;
};

/**
 * DeleteResponse
 */
export type DeleteResponse = {
    message?: string | null;
    message_code?: string | null;
    tasks_ids?: Array<string> | null;
};

/**
 * DeploymentBastionConfig
 *
 * Deployment-level bastion configuration applied to all its desktops.
 */
export type DeploymentBastionConfig = {
    ssh?: DeploymentBastionSsh;
    http?: DeploymentBastionHttp;
};

/**
 * DeploymentBastionHttp
 *
 * Deployment-level bastion HTTP options.
 */
export type DeploymentBastionHttp = {
    enabled?: boolean;
    http_port?: number;
    https_port?: number;
};

/**
 * DeploymentBastionSsh
 *
 * Deployment-level bastion SSH options (no authorized keys — the bastion
 * resolves each user's profile key at connection time).
 */
export type DeploymentBastionSsh = {
    enabled?: boolean;
    port?: number;
};

/**
 * DeploymentDesktop
 *
 * One ``deployments.create_dict`` entry -- a desktop of the deployment.
 *
 * Every allowed user gets exactly one instance of each, so this list is
 * the column axis of the visibility matrix.
 */
export type DeploymentDesktop = {
    /**
     * Stable id of this desktop within the deployment. Null on legacy deployments created before release 184.
     */
    tag_desktop_id?: string | null;
    /**
     * Name given to this desktop
     */
    name: string;
    /**
     * Source template id
     */
    template?: string | null;
    /**
     * Desktop image
     */
    image?: Image | null;
};

/**
 * DeploymentDesktopEditRequest
 */
export type DeploymentDesktopEditRequest = {
    /**
     * Name of the desktop to be created. Must be unique within the user desktops. If not provided, the current value will be kept.
     */
    name?: string;
    /**
     * Description of the desktop to be created. If not provided, the current value will be kept.
     */
    description?: string;
    /**
     * Guest properties to be set for the desktop. If not provided, the current value will be kept.
     */
    guest_properties?: DomainGuestPropertiesInput;
    /**
     * Hardware configuration for the desktop. If not provided, the current value will be kept.
     */
    hardware?: DomainHardware;
    /**
     * The domain bookable resources. If None, no reservables are available. If not provided, the current value will be kept.
     */
    reservables?: ReservablesInput;
    /**
     * Image to be used for the desktop. If None or not provided, the current value will be kept.
     */
    image?: DomainImageInput | null;
    /**
     * Bastion configuration for the desktop. If not provided, the bastion configuration will not be modified.
     */
    bastion_target?: BastionRequest;
    /**
     * List of hypervisor IDs to force the desktop onto, or False to clear it. Requires manager or admin role. If not provided, the current value will be kept.
     */
    forced_hyp?: Array<string> | false;
    /**
     * List of preferred hypervisor IDs, or False to clear it. Requires manager or admin role. If not provided, the current value will be kept.
     */
    favourite_hyp?: Array<string> | false;
    /**
     * If true, mark the desktop as a server. Requires manager or admin role. If not provided, the current value will be kept.
     */
    server?: boolean;
    /**
     * If true, the server desktop is autostarted. Requires manager or admin role and server=True. If not provided, the current value will be kept.
     */
    server_autostart?: boolean;
    /**
     * Unique identifier for the desktop within the deployment.
     */
    tag_desktop_id: string;
};

/**
 * DeploymentDetail
 *
 * Detailed deployment model
 */
export type DeploymentDetail = {
    /**
     * ID of the deployment
     */
    id: string;
    /**
     * Name of the deployment
     */
    name: string;
    /**
     * Description of the deployment
     */
    description?: string | null;
    /**
     * Indicates if the deployment is visible
     */
    tag_visible?: boolean;
    /**
     * Number of desktops that have been started
     */
    started_desktops: number;
    /**
     * Number of visible desktops associated with the deployment
     */
    visible_desktops: number;
    /**
     * Total number of users associated with the deployment
     */
    total_users: number;
    /**
     * Total number of desktops associated with the deployment
     */
    total_desktops: number;
    /**
     * Number of desktops assigned to each user
     */
    desktops_each_user: number;
    /**
     * Indicates if the requesting user is a co-owner of the deployment
     */
    co_owner?: boolean;
    /**
     * The deployment's desktops -- the column axis of the visibility matrix
     */
    desktops?: Array<DeploymentDesktop>;
    /**
     * Desktop rows that actually exist. Differs from total_desktops (desktops x users, the expected count) when desktops are missing and a recreate is due.
     */
    existing_desktops?: number;
};

/**
 * DeploymentEditRequest
 */
export type DeploymentEditRequest = {
    /**
     * Name of the deployment
     */
    name?: string;
    /**
     * Description of the deployment
     */
    description?: string | null;
    /**
     * Image to use for the deployment. If not provided, the image from the first desktop will be used.
     */
    image?: DomainImageInput | null;
    /**
     * Whether the deployment is visible to it's users.
     */
    visible?: boolean;
    /**
     * Who can access the deployment. At least one user or group must be provided.
     */
    allowed?: AllowedBase;
    /**
     * List of permissions the user creating the deployment has over it.
     */
    user_permissions?: Array<DeploymentPermissions>;
    /**
     * List of different desktop types to edit in the deployment. If a desktop is present in both edit and delete lists, it will be deleted from the edit list.
     */
    desktops_to_edit?: Array<DeploymentDesktopEditRequest>;
    /**
     * List of desktop IDs to delete from the deployment. The IDs must correspond to the `tag_desktop_id` field of the desktops in the deployment. If a desktop is present in both edit and delete lists, it will be deleted. At least one desktop must remain in the deployment.
     */
    desktops_to_delete?: Array<string>;
    /**
     * List of different desktop types to create in the deployment. Each entry will create a number of desktops with the same configuration. All desktops will be persistent and without bastion target.
     */
    desktops_to_create?: Array<CreateDesktopRequest>;
    /**
     * **UNIMPLEMENTED**: List of resources to attach to the deployment
     */
    resources?: Array<Resource>;
    /**
     * Legacy: name to apply to every desktop in the deployment.
     */
    desktop_name?: string | null;
    /**
     * Legacy: description to apply to every desktop in the deployment.
     */
    desktop_description?: string | null;
    /**
     * Legacy: hardware override applied to every desktop.
     */
    hardware?: {
        [key: string]: unknown;
    } | null;
    /**
     * Legacy: bookable resources (vGPU profiles) applied to every desktop.
     */
    reservables?: ReservablesInput | null;
    /**
     * Legacy: guest_properties override applied to every desktop.
     */
    guest_properties?: {
        [key: string]: unknown;
    } | null;
};

/**
 * DeploymentEditUsersRequest
 *
 * Request body for editing a deployment's allowed users/groups.
 */
export type DeploymentEditUsersRequest = {
    /**
     * Allowed users and groups dict with keys: groups (list|false), users (list|false)
     */
    allowed: {
        [key: string]: unknown;
    };
};

/**
 * DeploymentGroup
 *
 * User model with necessary fields to show in the avatar
 */
export type DeploymentGroup = {
    id: string;
    name: string;
    category_id: string;
    category_name: string;
};

/**
 * DeploymentHardware
 */
export type DeploymentHardware = {
    virtualization_nested?: boolean;
    boot_order?: Array<'iso' | 'floppy' | 'disk' | 'pxe'> | null;
    qos_disk_id?: string | boolean | null;
    vcpus: number;
    memory: number;
    graphics?: Array<string>;
    videos: Array<string>;
    interfaces: Array<DeploymentInterface>;
    disk_bus?: 'default' | 'ide' | 'sata' | 'virtio';
    isos?: Array<DeploymentMedia> | null;
    floppies?: Array<DeploymentMedia> | null;
    disks?: Array<Disk> | null;
};

/**
 * DeploymentHardwareResponse
 *
 * Response model for ``GET /item/deployment/{id}/hardware``.
 *
 * ``hardware.interfaces``/``isos``/``floppies`` are resolved by
 * ``get_deployment_details_hardware`` to ``{id, name}`` entries, so the
 * response uses ``DeploymentHardware`` instead of the raw ``Hardware``.
 */
export type DeploymentHardwareResponse = {
    guest_properties: GuestProperties;
    hardware: DeploymentHardware;
    image: Image;
    name: string;
    description?: string | null;
    reservables: {
        [key: string]: unknown;
    };
    template: string;
    video_name?: Array<string>;
    boot_name?: Array<string>;
    reservable_name?: Array<string> | boolean;
};

/**
 * DeploymentInfoResponse
 *
 * Response model for ``GET /item/deployment/{id}/info``.
 *
 * Combines the deployment's ``create_dict[0]`` with the deployment's
 * ``allowed``/``tag``/``tag_name``/``tag_visible`` fields and the
 * deployment ``id``. ``hardware`` arrays are resolved as in
 * ``DeploymentHardwareResponse`` and quotas are applied to the
 * hardware section before the response is built.
 */
export type DeploymentInfoResponse = {
    id: string;
    guest_properties: GuestProperties;
    hardware: DeploymentHardware;
    image: Image;
    name: string;
    description?: string | null;
    reservables: {
        [key: string]: unknown;
    };
    template: string;
    allowed?: ApiSchemasAllowedAllowedOutput | null;
    tag?: string | null;
    tag_name?: string | null;
    tag_description?: string | null;
    tag_visible?: boolean | null;
};

/**
 * DeploymentInterface
 */
export type DeploymentInterface = {
    id: string;
    name?: string | null;
};

/**
 * DeploymentMedia
 */
export type DeploymentMedia = {
    id: string;
    name?: string | null;
};

/**
 * DeploymentPermissions
 */
export const DeploymentPermissions = { RECREATE: 'recreate' } as const;

/**
 * DeploymentPermissions
 */
export type DeploymentPermissions = typeof DeploymentPermissions[keyof typeof DeploymentPermissions];

/**
 * DeploymentRecreateCountResponse
 *
 * How many desktops a recreate would create, shown before confirming.
 */
export type DeploymentRecreateCountResponse = {
    /**
     * Number of desktops that a recreate would create for the deployment's currently allowed and active users
     */
    desktops_to_create: number;
};

/**
 * DeploymentResponse
 *
 * Deployment response model
 */
export type DeploymentResponse = {
    /**
     * Detailed information about the deployment
     */
    info: DeploymentDetail;
    /**
     * List of users associated with the deployment
     */
    users: Array<DeploymentUserDetail>;
};

/**
 * DeploymentStopRequest
 */
export type DeploymentStopRequest = {
    /**
     * If true, started desktops in the deployment are stopped forcefully (straight to Stopping), and desktops already shutting down are killed too. If false, started desktops are stopped gracefully (Shutting-down first) and the ones already shutting down are left alone.
     */
    force?: boolean;
};

/**
 * DeploymentUserDesktop
 *
 * A single (user, desktop) cell of the visibility matrix.
 */
export type DeploymentUserDesktop = {
    /**
     * Deployment desktop this instance came from; null on legacy rows
     */
    tag_desktop_id?: string | null;
    /**
     * Desktop id -- the unit a visibility SET targets
     */
    desktop_id: string;
    /**
     * Desktop name
     */
    name: string;
    /**
     * tag_visible of this desktop
     */
    visible?: boolean;
    /**
     * Desktop status
     */
    status?: string | null;
};

/**
 * DeploymentUserDetail
 *
 * Detailed deployment user model
 */
export type DeploymentUserDetail = {
    /**
     * ID of the user
     */
    id: string;
    /**
     * Name of the user
     */
    name: string;
    /**
     * Username of the user
     */
    username: string;
    /**
     * Photo of the user
     */
    photo?: string | null;
    /**
     * Whether the user account is enabled; a deployment recreate skips the allowed users that are not
     */
    active?: boolean;
    /**
     * List of desktop statuses associated with the user
     */
    desktops_statuses?: Array<DesktopStatus>;
    /**
     * Indicates if the user has visible desktops
     */
    visible?: boolean;
    /**
     * Timestamp of the last access of the user
     */
    last_access?: number | null;
    /**
     * One entry per deployment desktop this user actually has. Shorter than info.desktops when a desktop is missing.
     */
    desktops?: Array<DeploymentUserDesktop>;
};

/**
 * DeploymentVideowallResponse
 */
export type DeploymentVideowallResponse = {
    allowed: ApiSchemasAllowedAllowedOutput;
    /**
     * List of user IDs that are co-owners of the deployment.
     */
    co_owners?: Array<string>;
    /**
     * Description of the deployment
     */
    description?: string | null;
    /**
     * ID of the deployment
     */
    id: string;
    /**
     * Name of the deployment
     */
    name: string;
    /**
     * Image associated with the deployment (may be missing on legacy rows)
     */
    image?: Image | null;
    /**
     * Tag of the deployment
     */
    tag: string;
    tag_visible: boolean;
    /**
     * ID of the user that owns the deployment
     */
    user: string;
    /**
     * List of permissions the user has over the deployment.
     */
    user_permissions?: Array<string>;
    total_desktops: number;
    visibleDesktops: number;
    startedDesktops: number;
    creatingDesktops: number;
    visible: boolean;
    desktop_name: string;
    template: string;
    needs_booking: boolean;
    next_booking_start: string | null;
    next_booking_end: string | null;
    booking_id: string | boolean | null;
    desktops: Array<UserDeploymentDesktop>;
    total_users: number;
    desktops_each_user: number;
};

/**
 * DeploymentVisibilityResponse
 *
 * Recomputed aggregate, so the caller needs no follow-up GET.
 */
export type DeploymentVisibilityResponse = {
    /**
     * Deployment id
     */
    id: string;
    /**
     * Recomputed deployment master flag
     */
    tag_visible: boolean;
    /**
     * Desktops with tag_visible == True after the write
     */
    visible_desktops: number;
    /**
     * Desktop rows that actually exist for this deployment
     */
    existing_desktops: number;
    /**
     * Rows whose tag_visible actually changed
     */
    updated: number;
    /**
     * Desktop ids that were sent a stop event
     */
    stopped?: Array<string>;
};

/**
 * Desktop
 */
export type Desktop = {
    /**
     * Image of the desktop. May be None for desktops without a custom image assigned.
     */
    image?: DomainImageOutput | null;
    /**
     * Name of the desktop.
     */
    name: string;
    /**
     * ID of the desktop.
     */
    id: string;
    /**
     * Status of the desktop.
     */
    status: DesktopStatusEnum;
    /**
     * IP address of the desktop. If None, the desktop is not running or does not have an IP assigned.
     */
    ip?: string | null;
    /**
     * Description of the desktop.
     */
    description?: string | null;
    /**
     * If true, the desktop will be visible.
     */
    tag_visible?: boolean;
    /**
     * Timestamp of the last access to the desktop.
     */
    accessed: number;
    /**
     * ID of the user who owns the desktop.
     */
    user: string;
};

/**
 * DesktopAnalyticsRequest
 *
 * Request body for desktop analytics endpoints.
 */
export type DesktopAnalyticsRequest = {
    /**
     * Number of days to look back
     */
    days_before?: number;
    /**
     * Maximum number of results
     */
    limit?: number;
    /**
     * Skip desktops with this directory path prefix
     */
    not_in_directory_path?: string | null;
    /**
     * Filter by desktop status
     */
    status?: string | null;
};

/**
 * DesktopAnalyticsRow
 *
 * Shared row shape for the three desktop-analytics endpoints.
 *
 * ``less_used`` / ``recently_used`` produce ``last_accessed`` (and no
 * ``start_count``); ``most_used`` produces ``start_count`` (and no
 * ``last_accessed``). Every other column is common: identifiers, the
 * backing storage join, and the GiB-normalised size.
 */
export type DesktopAnalyticsRow = {
    desktop_id?: string | null;
    desktop_status?: string | null;
    desktop_category?: string | null;
    storage_id?: string | null;
    storage_status?: string | null;
    directory_path?: string | null;
    storage_path?: string | null;
    /**
     * Storage size in GiB
     */
    size?: number;
    /**
     * Last access time (less_used / recently_used only)
     */
    last_accessed?: unknown | null;
    /**
     * Start count within window (most_used only)
     */
    start_count?: number | null;
};

/**
 * DesktopAttachedMediaItem
 *
 * Single media item attached to a desktop's hardware (iso or floppy).
 *
 * Returned by ``GET /admin/item/desktop/{id}/media-list`` (replaces the v3
 * ``POST /desktops/media_list`` shim). Mirrors the v3
 * ``api_media.List()`` shape.
 */
export type DesktopAttachedMediaItem = {
    id: string;
    name: string;
    kind: 'iso' | 'floppy';
    size?: string | null;
};

/**
 * DesktopBastionResponse
 */
export type DesktopBastionResponse = {
    /**
     * ID of the bastion configuration for the desktop.
     */
    id: string;
    /**
     * ID of the user who owns the desktop.
     */
    user_id: string;
    /**
     * ID of the desktop associated with the bastion configuration.
     */
    desktop_id: string;
    /**
     * Domains associated with the bastion configuration. Empty if the bastion is not associated with any domain.
     */
    domains?: Array<string>;
    /**
     * HTTP configuration for the bastion.
     */
    http: BastionHttpConfig;
    /**
     * SSH configuration for the bastion.
     */
    ssh: BastionSshConfig;
};

/**
 * DesktopDetailsResponse
 */
export type DesktopDetailsResponse = {
    /**
     * ID of the desktop.
     */
    id: string;
    /**
     * Name of the desktop.
     */
    name: string;
    /**
     * Description of the desktop.
     */
    description?: string | null;
    /**
     * IP address of the desktop.
     */
    ip?: string | null;
    /**
     * Number of virtual CPUs allocated to the desktop.
     */
    vcpu?: number;
    /**
     * Amount of memory (RAM) allocated to the desktop in GiB.
     */
    memory?: number;
    /**
     * ID and name of the disk bus used by the desktop, e.g., 'virtio', 'scsi'.
     */
    disk_bus: DesktopNamedResource;
    /**
     * List of IDs and names with the boot order that will be followed when booting the domain. Each ID will be a valid boot id.
     */
    boot_order: Array<DesktopNamedResource>;
    /**
     * List of disks that will be attached to the domain. Each disk will have a storage_id and its size.
     */
    disks: Array<DesktopStorage>;
    /**
     * List of IDs and names with the videos order that will be followed. Each ID will be a valid video id.
     */
    videos: Array<DesktopNamedResource>;
    /**
     * List of viewers that will be available to access the desktop.
     */
    viewers?: Array<DomainViewerEnum>;
    /**
     * If true, the desktop will be opened in fullscreen mode by default.
     */
    fullscreen?: boolean;
    /**
     * List of ISO images that will be attached to the desktop. Each ISO will have an ID and a name.
     */
    isos?: Array<DesktopNamedResource> | null;
    /**
     * List of floppy images that will be attached to the desktop. Each floppy will have an ID and a name.
     */
    floppies?: Array<DesktopNamedResource> | null;
    /**
     * The domain bookable resources.
     */
    reservables?: ApiSchemasDomainsHardwareReservables;
    /**
     * Status of the desktop.
     */
    status: DesktopStatusEnum;
    /**
     * List of network interfaces attached to the desktop. Each interface will have an ID, a name, and a MAC address.
     */
    interfaces: Array<DesktopNetwork>;
    /**
     * Credentials to access the desktop, if available. The keys of the dictionary will be the types of credentials, such as 'rdp', 'vnc', 'ssh', and the values will be the corresponding credentials, such as the password or the private key.
     */
    credentials?: ApiSchemasDomainsHardwareDomainGuestPropertiesGuestPropertiesCredentials | null;
    /**
     * Template used to create the desktop, if available.
     */
    template?: DesktopTemplate | null;
};

/**
 * DesktopDirectViewerDetailsResponse
 */
export type DesktopDirectViewerDetailsResponse = {
    /**
     * ID of the desktop.
     */
    id: string;
    /**
     * Name of the desktop.
     */
    name: string;
    /**
     * Description of the desktop.
     */
    description?: string | null;
    /**
     * IP address of the desktop.
     */
    ip?: string | null;
    /**
     * Number of virtual CPUs allocated to the desktop.
     */
    vcpu?: number;
    /**
     * Amount of memory (RAM) allocated to the desktop in GiB.
     */
    memory?: number;
    /**
     * ID and name of the disk bus used by the desktop, e.g., 'virtio', 'scsi'.
     */
    disk_bus: DesktopNamedResource;
    /**
     * List of IDs and names with the boot order that will be followed when booting the domain. Each ID will be a valid boot id.
     */
    boot_order: Array<DesktopNamedResource>;
    /**
     * List of disks that will be attached to the domain. Each disk will have a storage_id and its size.
     */
    disks: Array<DesktopStorage>;
    /**
     * List of IDs and names with the videos order that will be followed. Each ID will be a valid video id.
     */
    videos: Array<DesktopNamedResource>;
    /**
     * List of viewers that will be available to access the desktop.
     */
    viewers?: Array<DomainViewerEnum>;
    /**
     * If true, the desktop will be opened in fullscreen mode by default.
     */
    fullscreen?: boolean;
    /**
     * List of ISO images that will be attached to the desktop. Each ISO will have an ID and a name.
     */
    isos?: Array<DesktopNamedResource> | null;
    /**
     * List of floppy images that will be attached to the desktop. Each floppy will have an ID and a name.
     */
    floppies?: Array<DesktopNamedResource> | null;
    /**
     * The domain bookable resources.
     */
    reservables?: ApiSchemasDomainsHardwareReservables;
    /**
     * Status of the desktop.
     */
    status: DesktopStatusEnum;
    /**
     * List of network interfaces attached to the desktop. Each interface will have an ID, a name, and a MAC address.
     */
    interfaces: Array<DesktopNetwork>;
    /**
     * Credentials to access the desktop, if available. The keys of the dictionary will be the types of credentials, such as 'rdp', 'vnc', 'ssh', and the values will be the corresponding credentials, such as the password or the private key.
     */
    credentials?: ApiSchemasDomainsHardwareDomainGuestPropertiesGuestPropertiesCredentials | null;
    /**
     * Template used to create the desktop, if available.
     */
    template?: DesktopTemplate | null;
    /**
     * Read-only bastion access info for the desktop, if enabled.
     */
    bastion?: BastionDirectViewerResponse;
};

/**
 * DesktopEditRequest
 */
export type DesktopEditRequest = {
    /**
     * Name of the desktop to be created. Must be unique within the user desktops. If not provided, the current value will be kept.
     */
    name?: string;
    /**
     * Description of the desktop to be created. If not provided, the current value will be kept.
     */
    description?: string;
    /**
     * Guest properties to be set for the desktop. If not provided, the current value will be kept.
     */
    guest_properties?: DomainGuestPropertiesInput;
    /**
     * Hardware configuration for the desktop. If not provided, the current value will be kept.
     */
    hardware?: DomainHardware;
    /**
     * The domain bookable resources. If None, no reservables are available. If not provided, the current value will be kept.
     */
    reservables?: ReservablesInput;
    /**
     * Image to be used for the desktop. If None or not provided, the current value will be kept.
     */
    image?: DomainImageInput | null;
    /**
     * Bastion configuration for the desktop. If not provided, the bastion configuration will not be modified.
     */
    bastion_target?: BastionRequest;
    /**
     * List of hypervisor IDs to force the desktop onto, or False to clear it. Requires manager or admin role. If not provided, the current value will be kept.
     */
    forced_hyp?: Array<string> | false;
    /**
     * List of preferred hypervisor IDs, or False to clear it. Requires manager or admin role. If not provided, the current value will be kept.
     */
    favourite_hyp?: Array<string> | false;
    /**
     * If true, mark the desktop as a server. Requires manager or admin role. If not provided, the current value will be kept.
     */
    server?: boolean;
    /**
     * If true, the server desktop is autostarted. Requires manager or admin role and server=True. If not provided, the current value will be kept.
     */
    server_autostart?: boolean;
};

/**
 * DesktopGetViewerResponse
 *
 * Response model for desktop viewer connection string
 */
export type DesktopGetViewerResponse = {
    /**
     * Type of viewer - 'browser' or 'file'
     */
    kind: 'browser' | 'file';
    /**
     * Protocol used for the connection
     */
    protocol: 'rdp' | 'vnc' | 'rdpvpn' | 'rdpgw' | 'spice';
    /**
     * URL to the browser viewer
     */
    viewer?: string | null;
    /**
     * URL with parameters for the viewer
     */
    urlp?: string | null;
    /**
     * JWT token for authentication
     */
    cookie?: string | null;
    /**
     * Decoded values from the cookie
     */
    values?: {
        [key: string]: string | number | number | boolean;
    } | null;
    /**
     * Name of the file to be downloaded
     */
    name?: string | null;
    /**
     * File extension
     */
    ext?: string | null;
    /**
     * MIME type of the file
     */
    mime?: string | null;
    /**
     * File content
     */
    content?: string | null;
};

/**
 * DesktopGroupingLogRow
 *
 * Row in the ``desktop_grouping`` view of ``query_paginated``.
 */
export type DesktopGroupingLogRow = {
    count: number;
    desktop_name?: string | null;
    desktop_id?: string | null;
    owner_user_name?: string | null;
    owner_user_id?: string | null;
    owner_group_name?: string | null;
    owner_group_id?: string | null;
    owner_category_name?: string | null;
    owner_category_id?: string | null;
    starting_time?: number | string | null;
};

/**
 * DesktopImageType
 */
export const DesktopImageType = { STOCK: 'stock' } as const;

/**
 * DesktopImageType
 */
export type DesktopImageType = typeof DesktopImageType[keyof typeof DesktopImageType];

/**
 * DesktopImagesResponse
 */
export type DesktopImagesResponse = {
    images: Array<DomainImageOutput>;
};

/**
 * DesktopLogRow
 *
 * Raw row from ``r.table('logs_desktops')``.
 *
 * Fields are populated conditionally by ``Logging._logs_domain_start``
 * and the various stop/event updaters in
 * ``isardvdi_common.helpers.logging``, so most are optional.
 */
export type DesktopLogRow = {
    id: string;
    starting_time?: number | string | null;
    starting_by?: string | null;
    starting_user?: string | null;
    started_time?: number | string | null;
    stopping_time?: number | string | boolean | null;
    stopping_by?: string | null;
    stopping_user?: string | null;
    stopping_ip?: string | null;
    stopping_agent_browser?: string | null;
    stopping_agent_platform?: string | null;
    stopped_time?: number | string | boolean | null;
    stopped_by?: string | null;
    stopped_status?: string | null;
    desktop_id?: string | null;
    desktop_name?: string | null;
    desktop_template_hierarchy?: Array<string> | null;
    deployment_id?: string | null;
    deployment_name?: string | null;
    owner_user_id?: string | null;
    owner_user_name?: string | null;
    owner_category_id?: string | null;
    owner_category_name?: string | null;
    owner_group_id?: string | null;
    owner_group_name?: string | null;
    owner_role_id?: string | null;
    hyp_started?: string | boolean | null;
    hyp_forced?: string | Array<string> | null;
    hyp_favourite?: string | Array<string> | null;
    hardware_vcpus?: number | null;
    hardware_memory?: number | null;
    hardware_bookables_vgpus?: Array<string> | null;
    booking_id?: string | boolean | null;
    booking_start?: number | string | null;
    booking_end?: number | string | null;
    events?: Array<{
        [key: string]: unknown;
    }> | null;
    request_ip?: string | null;
    request_agent_browser?: string | null;
    request_agent_platform?: string | null;
};

/**
 * DesktopNameExistsErrorParams
 */
export type DesktopNameExistsErrorParams = {
    users?: Array<string> | null;
    name?: string | null;
};

/**
 * DesktopNameExistsErrorResponse
 */
export type DesktopNameExistsErrorResponse = {
    error: string;
    msg: string;
    description_code: string;
    function: string;
    function_call: string;
    description: string;
    debug: string;
    request: string;
    data: string;
    params: DesktopNameExistsErrorParams | null;
};

/**
 * DesktopNamedResource
 */
export type DesktopNamedResource = {
    /**
     * ID of the resource, such as a disk, video, or ISO.
     */
    id: string;
    /**
     * Name of the resource, such as a disk, video, or ISO.
     */
    name: string;
};

/**
 * DesktopNetwork
 */
export type DesktopNetwork = {
    /**
     * ID of the network interface.
     */
    id: string;
    /**
     * Name of the network interface.
     */
    name: string;
    /**
     * MAC address of the network interface.
     */
    mac: string;
};

/**
 * DesktopNetworksResponse
 */
export type DesktopNetworksResponse = {
    /**
     * List of networks available for the desktop.
     */
    networks?: Array<DesktopNetwork>;
};

/**
 * DesktopNotBookedErrorParams
 */
export type DesktopNotBookedErrorParams = {
    start?: string | null;
};

/**
 * DesktopNotBookedErrorResponse
 */
export type DesktopNotBookedErrorResponse = {
    error: string;
    msg: string;
    description_code: string;
    function: string;
    function_call: string;
    description: string;
    debug: string;
    request: string;
    data: string;
    params: DesktopNotBookedErrorParams | null;
};

/**
 * DesktopQueueItem
 *
 * Single desktop queue entry.
 *
 * Matches the exact shape produced by
 * ``hyp_worker_thread.get_positioned_items``
 * (``engine/.../hyp_worker_thread.py``): the engine always emits
 * ``desktop_id``, ``event``, ``priority`` and ``position`` — these
 * four fields are the complete contract. Unknown keys are rejected
 * (``extra="forbid"``) so a new field on the engine side surfaces
 * as a 422 here and forces us to update this schema in lockstep
 * rather than silently drifting.
 */
export type DesktopQueueItem = {
    desktop_id: string;
    event?: string | null;
    priority?: number | null;
    position?: number | null;
};

/**
 * DesktopSearchFields
 */
export const DesktopSearchFields = {
    NAME: 'name',
    DESCRIPTION: 'description',
    GROUP_NAME: 'group_name'
} as const;

/**
 * DesktopSearchFields
 */
export type DesktopSearchFields = typeof DesktopSearchFields[keyof typeof DesktopSearchFields];

/**
 * DesktopShareLinkResponse
 */
export type DesktopShareLinkResponse = {
    /**
     * Link to share the desktop. None if no share link has been generated yet.
     */
    link?: string | null;
};

/**
 * DesktopStatus
 *
 * Desktop status count model
 */
export type DesktopStatus = {
    /**
     * Status of the desktops
     */
    status: string;
    /**
     * Number of desktops with the status
     */
    amount: number;
};

/**
 * DesktopStatusEnum
 */
export const DesktopStatusEnum = {
    CREATING: 'Creating',
    CREATING_DISK: 'CreatingDisk',
    CREATING_AND_STARTING: 'CreatingAndStarting',
    CREATING_TEMPLATE: 'CreatingTemplate',
    CREATING_DOMAIN: 'CreatingDomain',
    CREATING_DOMAIN_FROM_DISK: 'CreatingDomainFromDisk',
    STARTING: 'Starting',
    STARTING_PAUSED: 'StartingPaused',
    STARTING_DOMAIN_DISPOSABLE: 'StartingDomainDisposable',
    STARTED: 'Started',
    STOPPED: 'Stopped',
    FAILED: 'Failed',
    SHUTTING_DOWN: 'Shutting-down',
    DOWNLOADING: 'Downloading',
    DOWNLOAD_STARTING: 'DownloadStarting',
    DOWNLOADED: 'Downloaded',
    DOWNLOAD_FAILED: 'DownloadFailed',
    DOWNLOAD_ABORTING: 'DownloadAborting',
    RESET_DOWNLOADING: 'ResetDownloading',
    DISK_NEW: 'DiskNew',
    CREATING_DISK_FROM_SCRATCH: 'CreatingDiskFromScratch',
    UPDATING: 'Updating',
    DELETING: 'Deleting',
    DELETING_DOMAIN_DISK: 'DeletingDomainDisk',
    DISK_DELETED: 'DiskDeleted',
    FORCE_DELETING: 'ForceDeleting',
    SUSPENDED: 'Suspended',
    MAINTENANCE: 'Maintenance',
    STOPPING: 'Stopping',
    PAUSED: 'Paused',
    RESETTING: 'Resetting',
    UNKNOWN: 'Unknown',
    WAITING_IP: 'WaitingIP',
    DELETED: 'deleted',
    CRASHED: 'Crashed'
} as const;

/**
 * DesktopStatusEnum
 */
export type DesktopStatusEnum = typeof DesktopStatusEnum[keyof typeof DesktopStatusEnum];

/**
 * DesktopStorage
 */
export type DesktopStorage = {
    /**
     * ID of the storage where the disk is located.
     */
    id: string;
    /**
     * Size of the storage in GB.
     */
    size: number;
};

/**
 * DesktopTemplate
 */
export type DesktopTemplate = {
    /**
     * ID of the template.
     */
    id: string;
    /**
     * Name of the template.
     */
    name: string;
};

/**
 * DesktopTypeEnum
 */
export const DesktopTypeEnum = { PERSISTENT: 'persistent', NONPERSISTENT: 'nonpersistent' } as const;

/**
 * DesktopTypeEnum
 */
export type DesktopTypeEnum = typeof DesktopTypeEnum[keyof typeof DesktopTypeEnum];

/**
 * DesktopUpdateShareLinkRequest
 */
export type DesktopUpdateShareLinkRequest = {
    /**
     * If false, the share link will be disabled. If true, the share link will be enabled or regenerated.
     */
    enabled?: boolean;
};

/**
 * DesktopViewerResponse
 */
export type DesktopViewerResponse = {
    /**
     * ID of the desktop.
     */
    id: string;
    /**
     * JWT token to access the desktop.
     */
    jwt: string;
    /**
     * Name of the desktop.
     */
    name: string;
    /**
     * Description of the desktop.
     */
    description?: string;
    /**
     * Status of the desktop.
     */
    status: DesktopStatusEnum;
    /**
     * IP address of the desktop. None until the guest reports it.
     */
    ip?: string | null;
    /**
     * Type of the desktop.
     */
    type: DesktopTypeEnum;
    /**
     * Scheduled actions for the desktop viewer.
     */
    scheduled?: DesktopViewerScheduled | null;
    /**
     * Available viewers for the desktop keyed by viewer ID.
     */
    viewers: ViewersModel;
    /**
     * Desktop image (stock id or uploaded URL) used by the card.
     */
    image?: Image | null;
    /**
     * If true, the desktop needs to be booked.
     */
    needs_booking?: boolean | null;
    /**
     * Start time of the next booking.
     */
    next_booking_start?: string | null;
    /**
     * End time of the next booking.
     */
    next_booking_end?: string | null;
    /**
     * Read-only bastion access info for the desktop, if enabled.
     */
    bastion?: BastionDirectViewerResponse;
};

/**
 * DesktopViewerScheduled
 */
export type DesktopViewerScheduled = {
    /**
     * When a shutdown is scheduled, contains the UTC end time as a '%Y-%m-%dT%H:%M%z' string. False when no shutdown is scheduled.
     */
    shutdown?: boolean | string;
};

/**
 * DesktopsPaginationResponse
 */
export type DesktopsPaginationResponse = {
    /**
     * List of desktop items for the current page
     */
    rows: Array<ApiSchemasDomainsDesktopsUserDesktop>;
    /**
     * Total number of items across all pages
     */
    total: number;
};

/**
 * DesktopsStopRequest
 */
export type DesktopsStopRequest = {
    /**
     * If true, the desktop will be stopped forcefully. If false, the desktop will be stopped gracefully.
     */
    force?: boolean | null;
};

/**
 * DirectViewerDocsResponse
 */
export type DirectViewerDocsResponse = {
    /**
     * URL to the documentation of the direct viewer page.
     */
    direct_viewer_documentation_url: string;
};

/**
 * Disk
 */
export type Disk = {
    storage_id?: string | null;
    bus?: string | null;
    extension?: string | null;
};

/**
 * DomainFilterFieldEnum
 *
 * Domain columns the webapp filters can request distinct values for.
 */
export const DomainFilterFieldEnum = {
    STATUS: 'status',
    NAME: 'name',
    VCPUS: 'vcpus',
    MEMORY: 'memory'
} as const;

/**
 * DomainFilterFieldEnum
 *
 * Domain columns the webapp filters can request distinct values for.
 */
export type DomainFilterFieldEnum = typeof DomainFilterFieldEnum[keyof typeof DomainFilterFieldEnum];

export type DomainGuestPropertiesInput = {
    /**
     * Credentials for accessing the desktop using RDP. If not provided, the desktop will not be accessible.
     */
    credentials?: GuestPropertiesCredentialsInput | null;
    /**
     * If true, the desktop will be opened in fullscreen mode by default.
     */
    fullscreen?: boolean;
    /**
     * Configuration for various viewers that will be available to access the desktop. Only the configured viewers will be available.
     */
    viewers?: GuestPropertiesViewersInput | null;
};

export type DomainGuestPropertiesOutput = {
    /**
     * Credentials for accessing the desktop using RDP. If not provided, the desktop will not be accessible.
     */
    credentials?: ApiSchemasDomainsHardwareDomainGuestPropertiesGuestPropertiesCredentials | null;
    /**
     * If true, the desktop will be opened in fullscreen mode by default.
     */
    fullscreen?: boolean;
    /**
     * Configuration for various viewers that will be available to access the desktop. Only the configured viewers will be available.
     */
    viewers?: ApiSchemasDomainsHardwareDomainGuestPropertiesGuestPropertiesViewers | null;
};

/**
 * DomainHardware
 */
export type DomainHardware = {
    /**
     * Amount of memory (RAM) allocated to the domain in GB (GiB). Minimum: 0.025 GB.
     */
    memory?: number | null;
    /**
     * Number of virtual CPUs allocated to the domain. Minimum: 1 vCPU.
     */
    vcpus?: number | null;
    /**
     * List of boot devices in the order they will be used to boot the domain. Each device must be one of 'iso', 'floppy', 'disk', or 'pxe'.
     */
    boot_order?: Array<'iso' | 'floppy' | 'disk' | 'pxe'> | null;
    /**
     * The ID of the disk bus that will be used for the domain disks. The disk bus must be a valid disk bus id.
     */
    disk_bus?: string | null;
    /**
     * List of floppy disk images to be attached to the domain. Each floppy must be provided as an object with id and name.
     */
    floppies?: Array<DomainHardwareResource> | null;
    /**
     * List of network interfaces to be used by the domain. Each interface can be a string ID or an Interface object.
     */
    interfaces?: Array<string> | null;
    /**
     * List of ISO images to be attached to the domain. Each ISO must be provided as an object with id and name.
     */
    isos?: Array<DomainHardwareResource> | null;
    /**
     * List of video devices to be used by the domain. Each device must be a valid video device ID.
     */
    videos?: Array<string> | null;
    /**
     * If true, the host CPU is passed through so the domain can run its own hypervisor. Only admins and managers can change it; it is ignored for other roles.
     */
    virtualization_nested?: boolean | null;
};

/**
 * DomainHardwareResource
 */
export type DomainHardwareResource = {
    /**
     * ID of the hardware resource.
     */
    id: string;
    /**
     * Name of the hardware resource.
     */
    name: string;
};

export type DomainImageInput = {
    /**
     * ID of the image to be used for the domain.
     */
    id: string;
    /**
     * Type of the image. This string can be either 'stock' or 'user'.
     */
    type: string;
    /**
     * URL of the image.
     */
    url?: string | null;
    /**
     * Optional file payload used to upload a new user card on desktop edit. When set, the backend saves the image and replaces the desktop's card with it (see `isardvdi_common.helpers.cards.Cards.upload`). Excluded from JSON serialization (write-only).
     */
    file?: DomainImageFile | null;
};

export type DomainImageOutput = {
    /**
     * ID of the image to be used for the domain.
     */
    id: string;
    /**
     * Type of the image. This string can be either 'stock' or 'user'.
     */
    type: string;
    /**
     * URL of the image.
     */
    url?: string | null;
};

/**
 * DomainImageFile
 */
export type DomainImageFile = {
    /**
     * Base64-encoded image data (without the 'data:<mime>;base64,' prefix).
     */
    data: string;
    /**
     * Original filename of the uploaded image.
     */
    filename: string;
};

/**
 * DomainInfoBastionResponse
 */
export type DomainInfoBastionResponse = {
    /**
     * ID of the bastion configuration for the desktop.
     */
    id: string;
    /**
     * Custom CNAMEs configured on this bastion target.
     */
    domains?: Array<string>;
    /**
     * HTTP configuration for the bastion.
     */
    http: BastionHttpConfig;
    /**
     * SSH configuration for the bastion.
     */
    ssh: BastionSshConfig;
    /**
     * Global bastion domain. Used by the admin info modal to compose the per-target subdomain.
     */
    bastion_domain?: string | null;
    /**
     * Global bastion SSH port. None when bastion is disabled.
     */
    ssh_port?: string | null;
};

/**
 * DomainInfoHardware
 */
export type DomainInfoHardware = {
    virtualization_nested?: boolean;
    boot_order?: Array<'iso' | 'floppy' | 'disk' | 'pxe'> | null;
    qos_disk_id?: string | boolean | null;
    vcpus: number;
    memory: number;
    graphics?: Array<string>;
    videos: Array<string>;
    interfaces: Array<string> | Array<Interface>;
    disk_bus?: 'default' | 'ide' | 'sata' | 'virtio';
    isos?: Array<DomainInfoMedia> | null;
    floppies?: Array<DomainInfoMedia> | null;
    disks?: Array<Disk> | null;
};

/**
 * DomainInfoMedia
 */
export type DomainInfoMedia = {
    id: string;
    name?: string | null;
};

/**
 * DomainInfoResponse
 */
export type DomainInfoResponse = {
    id: string;
    kind: string;
    name: string;
    description?: string;
    image?: DomainImageOutput | null;
    guest_properties?: DomainGuestPropertiesOutput | null;
    hardware?: DomainInfoHardware | null;
    reservables?: ApiSchemasDomainsHardwareReservables | null;
    limited_hardware?: {
        [key: string]: unknown;
    } | null;
    removed_viewers?: Array<string> | null;
    bastion_target?: DomainInfoBastionResponse | null;
    status?: string | null;
    hyp_started?: string | null;
    guest_ip?: string | null;
    deployment_name?: string | null;
    storage_id?: string | null;
    owner?: DomainOwnerResponse | null;
    interfaces?: Array<DomainInterfaceResponse> | null;
};

/**
 * DomainInterfaceResponse
 */
export type DomainInterfaceResponse = {
    id: string;
    name?: string | null;
    mac?: string | null;
};

/**
 * DomainKindEnum
 */
export const DomainKindEnum = { DESKTOP: 'desktop', TEMPLATE: 'template' } as const;

/**
 * DomainKindEnum
 */
export type DomainKindEnum = typeof DomainKindEnum[keyof typeof DomainKindEnum];

/**
 * DomainOwnerResponse
 */
export type DomainOwnerResponse = {
    /**
     * Owner user ID.
     */
    id: string;
    username?: string | null;
    name?: string | null;
    email?: string | null;
    role?: string | null;
    category?: string | null;
    category_name?: string | null;
    group?: string | null;
    group_name?: string | null;
};

/**
 * DomainViewerEnum
 */
export const DomainViewerEnum = {
    FILE_RDPGW: 'file_rdpgw',
    BROWSER_RDP: 'browser_rdp',
    BROWSER_VNC: 'browser_vnc',
    FILE_RDPVPN: 'file_rdpvpn',
    FILE_SPICE: 'file_spice'
} as const;

/**
 * DomainViewerEnum
 */
export type DomainViewerEnum = typeof DomainViewerEnum[keyof typeof DomainViewerEnum];

/**
 * DownloadActionResponse
 *
 * Outcome of a download/abort/delete action on one catalogue item.
 *
 * The action used to answer 204 because it only wrote a status and
 * hoped a consumer would act on it. It now performs the operation, so
 * it reports what it did and the task doing the work when there is one.
 */
export type DownloadActionResponse = {
    id?: string | null;
    kind?: string | null;
    action?: string | null;
    task_id?: unknown | null;
};

/**
 * DownloadItem
 *
 * One row of ``GET /admin/items/downloads/{kind}``.
 *
 * The shape varies per kind (domains/media merge in registry rows,
 * virt_install/videos/viewers come straight from the upstream
 * updates server). Permissive (``ConfigDict(extra="allow")``) so all
 * five kinds round-trip without schema fragmentation; the webapp
 * admin renders fields per-kind.
 */
export type DownloadItem = {
    id?: string | null;
    name?: string | null;
    description?: string | null;
    [key: string]: unknown | string | null | string | null | string | null | undefined;
};

/**
 * DownloadsOverviewResponse
 *
 * Response shape for ``GET /admin/items/downloads``.
 *
 * The service returns ``{}`` after the registration check passes;
 * keeping a typed empty model documents the contract explicitly so
 * callers don't expect a payload.
 */
export type DownloadsOverviewResponse = {
    [key: string]: unknown;
};

/**
 * DuplicateTemplateRequest
 */
export type DuplicateTemplateRequest = {
    /**
     * Name of the template. Must be unique within the user's templates.
     */
    name: string;
    /**
     * Description of the template. If not provided, an empty string is used.
     */
    description?: string;
    /**
     * Permissions for the template.
     */
    allowed: AllowedBase;
    /**
     * Whether the template is enabled or not.
     */
    enabled?: boolean;
    /**
     * Image to be used for the duplicate. If None or not provided, the source template's image is inherited.
     */
    image?: DomainImageInput | null;
};

/**
 * EchartDailyItemsResponse
 *
 * Response shape for ``POST /admin/item/echart/daily_items``.
 *
 * The service returns ``{x: [iso-dates], series: {<date_field>: [counts]}}``
 * matching the eChart contract — a single dict, not a list. The
 * other ``echart`` views return ``list[{value, name}]``; this is
 * why daily_items has its own route + response model.
 */
export type EchartDailyItemsResponse = {
    x?: Array<string>;
    series?: {
        [key: string]: Array<number>;
    };
};

/**
 * EchartGroupedItem
 *
 * Row shape produced by ``get_grouped_data`` /
 * ``get_grouped_unique_data`` / ``get_nested_array_grouped_data``.
 *
 * All three return ``list[{value: int, name: <group key>}]`` where
 * ``name`` is the grouping value (which may be a string, int, bool,
 * list of nested keys, etc., depending on the source field) and
 * ``value`` is the count or distinct-count for that bucket.
 */
export type EchartGroupedItem = {
    /**
     * Count for this group
     */
    value: number;
    /**
     * Group key — can be any JSON-serialisable value
     */
    name: unknown;
};

/**
 * EchartRequest
 *
 * Request body for echart data endpoints.
 */
export type EchartRequest = {
    /**
     * Database table name
     */
    table: string;
    /**
     * Date field for daily items
     */
    date_field?: string | null;
    /**
     * Field to group by
     */
    group_field?: string | null;
    /**
     * Field for unique counting
     */
    unique_field?: string | null;
    /**
     * Nested array field name
     */
    nested_array_field?: string | null;
};

/**
 * EmptyDeploymentRow
 *
 * One row returned by ``AnalyticsProcessed.get_empty_deployments``.
 *
 * The query ``zip``s ``deployments`` (all fields) with ``users`` plucked
 * fields (``group``, ``category``, ``username``) and merges the resolved
 * ``category_name`` / ``group_name`` plus the (always 0) ``domains`` count.
 * Deployment columns are not pinned in the schema, so non-essential
 * fields stay optional.
 */
export type EmptyDeploymentRow = {
    id?: string | null;
    name?: string | null;
    user?: string | null;
    username?: string | null;
    group?: string | null;
    group_name?: string | null;
    category?: string | null;
    category_name?: string | null;
    /**
     * Number of domains attached (always 0)
     */
    domains?: number;
};

/**
 * EmptyResponse
 *
 * Empty response for operations that don't return data
 */
export type EmptyResponse = {
    [key: string]: unknown;
};

/**
 * EnableReservableRequest
 *
 * Request model for enabling/disabling a reservable subitem
 */
export type EnableReservableRequest = {
    enabled: boolean;
};

/**
 * EphimeralDesktopsData
 */
export type EphimeralDesktopsData = {
    minutes: number;
    action: 'Stopping' | 'StoppingAndDeleting';
};

/**
 * ErrorResponse
 */
export type ErrorResponse = {
    error: string;
    msg: string;
    description_code: string;
    function: string;
    function_call: string;
    description: string;
    debug: string;
    request: string;
    data: string;
    params: {
        [key: string]: unknown;
    } | null;
};

/**
 * FaroConfig
 *
 * Faro client configuration delivered to the browser at startup.
 */
export type FaroConfig = {
    /**
     * Whether the Faro SDK should initialize
     */
    enabled: boolean;
    /**
     * Receiver endpoint. Relative paths target the local HAProxy route; absolute URLs point to an external Faro receiver.
     */
    url?: string | null;
    /**
     * Fraction of successful HTTP requests reported as request_completed events. 1.0 reports them all.
     */
    http_sampling?: number;
};

/**
 * FileRDPGWViewer
 */
export type FileRdpgwViewer = {
    kind: ViewerKind;
    protocol: ViewerProtocol;
    name?: string | null;
    ext?: string | null;
    mime?: string | null;
    content?: string | null;
};

/**
 * FileSpiceViewer
 */
export type FileSpiceViewer = {
    kind: ViewerKind;
    protocol: ViewerProtocol;
    name: string;
    ext: string;
    mime: string;
    content: string;
};

/**
 * GenerateCardRequest
 *
 * Request to generate a default card
 */
export type GenerateCardRequest = {
    desktop_id: string;
    desktop_name: string;
};

/**
 * GetUsersPrioritiesRequest
 *
 * Request model for getting users by priority rule
 */
export type GetUsersPrioritiesRequest = {
    /**
     * The ID of the priority rule.
     */
    rule_id: string;
};

/**
 * GovernorCounters
 *
 * Durable shed / defer event counters. ``total`` is monotonic since the
 * counter key was created (answers "did this EVER happen", which a gauge
 * cannot); ``recent`` is the rolling ``window_minutes`` sum — the rate that
 * makes a storm visible in one sample. High-cardinality dimensions (pool ids,
 * worker names) appear only as ``last_*`` context, never as counters.
 */
export type GovernorCounters = {
    total?: number;
    recent?: number;
    window_minutes?: number;
    by_reason?: {
        [key: string]: number;
    };
    by_tier?: {
        [key: string]: number;
    };
    last_ts?: number | null;
    last_seconds_ago?: number | null;
    last_reason?: string | null;
    last_pool?: string | null;
    last_tier?: string | null;
    last_worker?: string | null;
};

/**
 * GovernorGaugesResponse
 *
 * Composite storage-governor gauge document — the ``GET
 * /admin/items/queues/governor`` payload AND the stats-go scrape source
 * (P2.4 §2.4). Degrades to honesty fields (``redis.up=false``, high
 * ``data_age_seconds``, ``multitenancy_active="unknown"``, empty pools/workers)
 * on transient Redis/rdb failure instead of raising.
 */
export type GovernorGaugesResponse = {
    generated_at: number;
    data_age_seconds?: number;
    multitenancy_active?: boolean | string;
    config_enabled?: boolean;
    config_mirrored?: boolean;
    psi_limit?: number;
    redis?: RedisHealth;
    streams?: StreamsGauge;
    heavy?: HeavyGauge;
    shed?: GovernorCounters;
    defer?: GovernorCounters;
    pools?: Array<PoolGauge>;
    workers?: Array<WorkerHealthRow>;
    warnings?: Array<GovernorWarning>;
    truncated_lanes?: number;
    truncated_inflight?: number;
    config?: StorageSchedulerConfigResponse | null;
};

/**
 * GovernorWarning
 *
 * A single governor warning. ``kind`` selects which of the (all-Optional)
 * fields are populated: ``stranded_lane`` (pool/tier/lane/backlog/
 * coverage_known), ``leaked_inflight`` (scope/counted/live) and
 * ``category_starved`` (pool/category_id).
 */
export type GovernorWarning = {
    kind: string;
    pool?: string | null;
    tier?: string | null;
    lane?: string | null;
    category_id?: string | null;
    backlog?: number | null;
    coverage_known?: boolean | null;
    scope?: string | null;
    counted?: number | null;
    live?: number | null;
    count?: number | null;
    max_lateness_seconds?: number | null;
};

/**
 * GpuForecastProfile
 *
 * GPU forecast for a specific profile
 */
export type GpuForecastProfile = {
    brand: string;
    model: string;
    profile: string;
    now: GpuForecastUnit;
    to_create: GpuForecastUnit;
    to_destroy: GpuForecastUnit;
};

/**
 * GpuForecastUnit
 *
 * GPU forecast unit with units count and date
 */
export type GpuForecastUnit = {
    units: number;
    date: string;
};

/**
 * GroupUsersResponse
 */
export type GroupUsersResponse = {
    users: Array<AvailableUser>;
};

/**
 * GroupsInCategoryResponse
 */
export type GroupsInCategoryResponse = {
    /**
     * List of available groups in the category
     */
    available_groups: Array<ApiSchemasCategoriesAvailableGroup>;
};

/**
 * GroupsUsersCountPutData
 *
 * Request body for counting users in groups.
 */
export type GroupsUsersCountPutData = {
    /**
     * List of group IDs to count users for.
     */
    groups: Array<string>;
};

/**
 * GroupsUsersCountResponse
 *
 * Response for groups users count.
 */
export type GroupsUsersCountResponse = {
    /**
     * Total number of users in the specified groups.
     */
    quantity: number;
};

/**
 * GuestProperties
 */
export type GuestProperties = {
    credentials?: IsardvdiCommonSchemasSharedHardwareGuestPropertiesGuestPropertiesCredentials;
    fullscreen?: boolean;
    viewers: IsardvdiCommonSchemasSharedHardwareGuestPropertiesGuestPropertiesViewers;
};

/**
 * HTTPValidationError
 */
export type HttpValidationError = {
    detail?: Array<ValidationError>;
};

/**
 * HardwareItem
 */
export type HardwareItem = {
    allowed: IsardvdiCommonSchemasSharedAllowedAllowed;
    /**
     * Description of the hardware item
     */
    description: string;
    /**
     * Unique identifier for the hardware item
     */
    id: string;
    /**
     * Display name of the hardware item
     */
    name: string;
    /**
     * Whether this item can be edited
     */
    editable: boolean;
};

/**
 * HeavyGauge
 *
 * Global heavy-admission gauge: ``SCARD(governor:heavy_running)`` vs the
 * effective ``max_heavy`` cap, plus the read-only leak delta (catalog #5/#12).
 */
export type HeavyGauge = {
    running?: number;
    cap?: number;
    at_cap?: boolean;
    leaked?: number;
};

/**
 * HypervisorActionResponse
 *
 * Result of ``PUT /admin/item/operations/hypervisor/{id}`` (start) and
 * ``DELETE /admin/item/operations/hypervisor/{id}`` (stop). The fields
 * come straight from the operations gRPC ``CreateHypervisorResponse``
 * / ``DestroyHypervisorResponse`` proto.
 */
export type HypervisorActionResponse = {
    state?: string | null;
    msg?: string | null;
};

/**
 * HypervisorStatus
 *
 * Closed status set enforced by the engine's ``update_hyp_status``
 * whitelist (plus the ``Offline``/``Deleting`` writes in
 * ``HypervisorsProcessed``).
 */
export const HypervisorStatus = {
    ONLINE: 'Online',
    OFFLINE: 'Offline',
    ERROR: 'Error',
    DELETING: 'Deleting'
} as const;

/**
 * HypervisorStatus
 *
 * Closed status set enforced by the engine's ``update_hyp_status``
 * whitelist (plus the ``Offline``/``Deleting`` writes in
 * ``HypervisorsProcessed``).
 */
export type HypervisorStatus = typeof HypervisorStatus[keyof typeof HypervisorStatus];

/**
 * Image
 */
export type Image = {
    type: 'user' | 'stock';
    id?: string | null;
    url?: string | null;
    file?: ImageFile | null;
};

/**
 * ImportUserRequest
 *
 * Request model for importing user data for migration.
 */
export type ImportUserRequest = {
    /**
     * JWT token containing the user's data for migration.
     */
    token: string;
};

/**
 * IndeterminateGroup
 */
export type IndeterminateGroup = {
    /**
     * Group ID
     */
    id: string;
    /**
     * Group name
     */
    name: string;
    /**
     * Group description
     */
    description?: string | null;
};

/**
 * Interface
 */
export type Interface = {
    id: string;
    mac: string;
};

/**
 * ItemBookingsResponse
 */
export type ItemBookingsResponse = Array<UserBookingResponse | AvailabilityResponse>;

/**
 * Limits
 */
export type Limits = {
    desktops: number;
    desktops_disk_size: number;
    isos: number;
    vcpus: number;
    memory: number;
    running: number;
    templates: number;
    users: number;
    total_size: number;
    total_soft_size: number;
    deployments_total: number;
};

/**
 * Locale
 *
 * Locale model
 */
export type Locale = {
    default?: string | null;
    hide?: boolean;
    available_locales?: Array<string> | null;
};

/**
 * LoginConfigInfo
 */
export type LoginConfigInfo = {
    title?: string | null;
};

/**
 * LoginConfigLogo
 */
export type LoginConfigLogo = {
    hide?: boolean;
};

/**
 * LoginConfigMaintenance
 */
export type LoginConfigMaintenance = {
    title?: string | null;
    description?: string | null;
};

/**
 * LoginConfigResponse
 *
 * Login configuration response model.
 *
 * ``notification_cover`` and ``notification_form`` are lists so the
 * per-category endpoint can return both the global and category
 * notifications side-by-side. The global-only endpoint returns
 * 1-item lists. Pre-feature single-dict notifications are accepted
 * for backwards compatibility and wrapped server-side.
 */
export type LoginConfigResponse = {
    notification_cover?: Array<LoginNotification | null> | LoginNotification | null;
    notification_form?: Array<LoginNotification | null> | LoginNotification | null;
    info?: LoginConfigInfo | null;
    locale?: Locale | null;
    providers?: Providers | null;
    logo?: LoginConfigLogo | null;
    maintenance?: LoginConfigMaintenance | null;
};

/**
 * LoginNotification
 *
 * Login notification model
 */
export type LoginNotification = {
    title?: string | null;
    description?: string | null;
    button?: LoginNotificationButton | null;
    enabled?: boolean;
    icon?: string | null;
    extra_styles?: string | null;
};

/**
 * LoginNotificationButton
 *
 * Login notification button model
 */
export type LoginNotificationButton = {
    extra_styles?: string | null;
    text?: string | null;
    url?: string | null;
};

/**
 * LoginNotificationEnableRequest
 *
 * Request to enable or disable a login notification
 */
export type LoginNotificationEnableRequest = {
    enabled: boolean;
};

/**
 * LoginNotificationUpdateRequest
 *
 * Request to update login notification configuration
 */
export type LoginNotificationUpdateRequest = {
    cover?: {
        [key: string]: unknown;
    } | null;
    form?: {
        [key: string]: unknown;
    } | null;
};

/**
 * LogsDataTablesRange
 *
 * Time-range filter injected by the page-level ``pipeline`` plugin.
 */
export type LogsDataTablesRange = {
    field: string;
    start: string;
    end: string;
};

/**
 * LogsDataTablesRequest
 *
 * Request body for the logs DataTables routes.
 *
 * Mirrors the DataTables wire shape plus the project-specific extras
 * (``filter_field`` / ``filter_value`` / ``range`` / ``pluck``) that
 * ``LogsProcessed._build_query`` consumes. ``extra='ignore'`` keeps
 * the contract forward-compatible with DataTables versions that add
 * fields we don't read.
 */
export type LogsDataTablesRequest = {
    draw?: number;
    start?: number;
    length?: number;
    columns?: Array<DataTablesColumn>;
    order?: Array<DataTablesOrder>;
    search?: DataTablesSearch | null;
    range?: LogsDataTablesRange | null;
    filter_field?: string | null;
    filter_value?: string | null;
    pluck?: Array<string> | null;
};

/**
 * LogsDataTablesResponse[DesktopLogRow]
 */
export type LogsDataTablesResponseDesktopLogRow = {
    draw?: number;
    recordsTotal?: number;
    recordsFiltered?: number;
    data?: Array<DesktopLogRow>;
    indexs?: Array<unknown>;
};

/**
 * LogsDataTablesResponse[Union[DesktopLogRow, DesktopGroupingLogRow, CategoryGroupingLogRow]]
 */
export type LogsDataTablesResponseUnionDesktopLogRowDesktopGroupingLogRowCategoryGroupingLogRow = {
    draw?: number;
    recordsTotal?: number;
    recordsFiltered?: number;
    data?: Array<DesktopLogRow | DesktopGroupingLogRow | CategoryGroupingLogRow>;
    indexs?: Array<unknown>;
};

/**
 * LogsDataTablesResponse[Union[UserLogRow, UserGroupingLogRow, CategoryGroupingLogRow]]
 */
export type LogsDataTablesResponseUnionUserLogRowUserGroupingLogRowCategoryGroupingLogRow = {
    draw?: number;
    recordsTotal?: number;
    recordsFiltered?: number;
    data?: Array<UserLogRow | UserGroupingLogRow | CategoryGroupingLogRow>;
    indexs?: Array<unknown>;
};

/**
 * LogsDataTablesResponse[UserLogRow]
 */
export type LogsDataTablesResponseUserLogRow = {
    draw?: number;
    recordsTotal?: number;
    recordsFiltered?: number;
    data?: Array<UserLogRow>;
    indexs?: Array<unknown>;
};

/**
 * LogsRetentionConfigResponse
 *
 * Response of ``get_logs_*_old_entries_config`` and the matching
 * ``set_logs_*_max_time`` / ``set_logs_*_action`` PUTs.
 *
 * Both desktop-logs and user-logs retention endpoints share this shape
 * (see ``ApiAdmin.get_logs_desktops_old_entries_config`` /
 * ``get_logs_users_old_entries_config``).
 */
export type LogsRetentionConfigResponse = {
    max_time?: number | null;
    action?: string | null;
};

/**
 * MaintenanceStatusResponse
 *
 * Maintenance status response model
 */
export type MaintenanceStatusResponse = {
    /**
     * Indicates whether the maintenance mode is enabled or not.
     */
    enabled?: boolean;
};

/**
 * MaintenanceStatusUpdate
 *
 * Maintenance status update model
 */
export type MaintenanceStatusUpdate = {
    /**
     * Indicates whether the maintenance mode is enabled or not.
     */
    enabled?: boolean;
};

/**
 * MaintenanceTextGetResponse
 *
 * Response model for GET maintenance text (admin)
 */
export type MaintenanceTextGetResponse = {
    text: {
        [key: string]: unknown;
    };
};

/**
 * MaintenanceTextResponse
 */
export type MaintenanceTextResponse = {
    /**
     * Indicates whether the custom maintenance text is set or not.
     */
    enabled?: boolean;
    /**
     * Title to display during maintenance mode.
     */
    title?: string | null;
    /**
     * Description to display during maintenance mode.
     */
    body?: string | null;
};

/**
 * MaintenanceTextUpdate
 *
 * Maintenance text update model
 */
export type MaintenanceTextUpdate = {
    /**
     * Title to display during maintenance mode. If not provided, the existing title will be used.
     */
    title?: string | null;
    /**
     * Text to display during maintenance mode. If not provided, the existing text will be used.
     */
    body?: string | null;
};

/**
 * ManagerPermissionsData
 */
export type ManagerPermissionsData = {
    authentication?: boolean;
    branding?: boolean;
    login_notification?: boolean;
    plannings?: boolean;
};

/**
 * MaxBookingDateResponse
 *
 * Response model for max booking date
 */
export type MaxBookingDateResponse = {
    max_booking_date: string;
};

/**
 * MediaCheckResponse
 *
 * Response for the schedule-check action.
 *
 * The action is a no-op when the media is not currently downloaded
 * (``task_id`` is ``None``); otherwise the queued RQ task id is
 * returned so the caller can poll its progress.
 */
export type MediaCheckResponse = {
    task_id?: string | null;
};

/**
 * MediaDesktopResponse
 *
 * Desktop using media response model
 */
export type MediaDesktopResponse = {
    create_dict: {
        [key: string]: unknown;
    };
    id: string;
    kind?: DomainKindEnum;
    name: string;
    status: DesktopStatusEnum;
    user: string;
    user_name: string;
};

/**
 * MediaHardware
 */
export type MediaHardware = {
    boot_order: Array<'iso' | 'floppy' | 'disk' | 'pxe'>;
    disk_bus: string;
    /**
     * Size of the disk to create/attach in GB (GiB). Minimum: 1 GB.
     */
    disk_size: number;
    interfaces: Array<string>;
    /**
     * Amount of memory (RAM) allocated in GB (GiB). Minimum: 0.025 GB.
     */
    memory: number;
    /**
     * Number of virtual CPUs allocated. Minimum: 1 vCPU.
     */
    vcpus: number;
    videos: Array<string>;
    reservables?: ReservablesInput | null;
};

/**
 * MediaHardwareItem
 *
 * An ISO/floppy option, tagged with its owner.
 *
 * Media is shareable, so the hardware pickers list items belonging to
 * other users; the owner tells apart same-named images.
 */
export type MediaHardwareItem = {
    allowed: IsardvdiCommonSchemasSharedAllowedAllowed;
    /**
     * Description of the hardware item
     */
    description: string;
    /**
     * Unique identifier for the hardware item
     */
    id: string;
    /**
     * Display name of the hardware item
     */
    name: string;
    /**
     * Whether this item can be edited
     */
    editable: boolean;
    /**
     * Name of the user owning the media
     */
    user_name?: string | null;
    /**
     * Username of the user owning the media
     */
    username?: string | null;
};

/**
 * MediaItemResponse
 *
 * Media item response model for user media list
 */
export type MediaItemResponse = {
    id: string;
    name: string;
    description: string;
    kind: MediaKindEnum;
    url_isard: boolean | string;
    url_web: boolean | string;
    status: MediaStatusEnum;
    progress?: MediaProgress;
    user: string;
    category: string;
    group: string;
    accessed?: number | null;
    icon: string;
    editable: boolean;
};

/**
 * MediaKindEnum
 */
export const MediaKindEnum = {
    ISO: 'iso',
    FLOPPY: 'floppy',
    QCOW2: 'qcow2'
} as const;

/**
 * MediaKindEnum
 */
export type MediaKindEnum = typeof MediaKindEnum[keyof typeof MediaKindEnum];

/**
 * MediaProgress
 *
 * Media download progress model.
 *
 * Mirrors the dict written by the engine into the RethinkDB ``progress``
 * column (see ``services/media.py:create_media``). Fields are Optional with
 * defaults so partial documents (legacy rows, freshly-inserted media before
 * the engine populates) don't 500 the entire ``GET /items/media`` response.
 */
export type MediaProgress = {
    received?: string;
    received_percent?: number;
    total?: string;
    total_percent?: number;
    speed_current?: string;
    speed_download_average?: string;
};

/**
 * MediaResponse
 */
export type MediaResponse = {
    id: string;
    name: string;
    category: string;
    allowed: ApiSchemasAllowedAllowedOutput;
    description: string;
    status: MediaStatusEnum;
    user: string;
    username: string;
    kind: MediaKindEnum;
    accessed?: number | null;
    group?: string | null;
};

/**
 * MediaStatusEnum
 */
export const MediaStatusEnum = {
    DOWNLOADING: 'Downloading',
    DOWNLOADED: 'Downloaded',
    DELETING: 'Deleting',
    DELETED: 'deleted',
    DOWNLOAD_FAILED: 'DownloadFailed',
    DOWNLOAD_FAILED_INVALID_FORMAT: 'DownloadFailedInvalidFormat',
    DOWNLOAD_STARTING: 'DownloadStarting',
    DOWNLOAD: 'Download',
    DOWNLOAD_ABORTING: 'DownloadAborting',
    RESET_DOWNLOADING: 'ResetDownloading',
    MAINTENANCE: 'maintenance'
} as const;

/**
 * MediaStatusEnum
 */
export type MediaStatusEnum = typeof MediaStatusEnum[keyof typeof MediaStatusEnum];

/**
 * MediaUser
 */
export type MediaUser = {
    /**
     * ID of the user that allowed the media
     */
    id: string;
    /**
     * Name of the user that allowed the media
     */
    name: string;
    /**
     * Photo of the user that allowed the media
     */
    photo?: string | null;
};

/**
 * MigrationCheckResponse
 *
 * How many pre-flight integrity checks a "check disks first" queued.
 */
export type MigrationCheckResponse = {
    queued: number;
    total: number;
};

/**
 * MigrationConfigData
 *
 * Admin-set per-job knobs.
 */
export type MigrationConfigData = {
    bwlimit_kbs?: number;
    parallelism?: number;
    window?: MigrationWindowData | null;
    verify?: boolean;
    force_stop_desktops?: boolean;
    recurring?: boolean;
    rescan_cadence?: 'edge' | 'edge_on_drain' | 'continuous';
    failure_policy?: 'retry_quarantine' | 'pause' | 'retry_forever';
    quarantine_after?: number;
    on_damaged?: 'pause' | 'continue' | 'repair_leaks' | 'repair_all';
    max_bytes_per_occurrence?: number;
    order?: 'none' | 'oldest_first' | 'newest_first';
    min_free_bytes?: number;
    source_disposition?: 'system' | 'recycle_bin' | 'delete';
    min_free_pct?: number;
    usage_age_days?: number | null;
    include_never_used?: boolean;
    load_policy?: MigrationLoadPolicyData | null;
};

/**
 * MigrationConfigResponse
 *
 * Response model for migration configuration.
 */
export type MigrationConfigResponse = {
    /**
     * Whether quota checks are enforced during migration.
     */
    check_quotas?: boolean;
};

/**
 * MigrationConfigUpdateData
 *
 * A partial update: only the fields sent are applied. Weakening a
 * guarantee on a live job needs ``confirm_weakening``.
 */
export type MigrationConfigUpdateData = {
    bwlimit_kbs?: number;
    parallelism?: number;
    window?: MigrationWindowData | null;
    verify?: boolean;
    force_stop_desktops?: boolean;
    recurring?: boolean;
    rescan_cadence?: 'edge' | 'edge_on_drain' | 'continuous';
    failure_policy?: 'retry_quarantine' | 'pause' | 'retry_forever';
    quarantine_after?: number;
    on_damaged?: 'pause' | 'continue' | 'repair_leaks' | 'repair_all';
    max_bytes_per_occurrence?: number;
    order?: 'none' | 'oldest_first' | 'newest_first';
    min_free_bytes?: number;
    source_disposition?: 'system' | 'recycle_bin' | 'delete';
    min_free_pct?: number;
    usage_age_days?: number | null;
    include_never_used?: boolean;
    load_policy?: MigrationLoadPolicyData | null;
    confirm_weakening?: boolean;
};

/**
 * MigrationConfigUpdateRequest
 *
 * Request model for updating migration configuration.
 */
export type MigrationConfigUpdateRequest = {
    /**
     * Whether quota checks should be enforced during migration.
     */
    check_quotas?: boolean | null;
};

/**
 * MigrationCreateData
 */
export type MigrationCreateData = {
    selection: MigrationSelectionData;
    config?: MigrationConfigData;
};

/**
 * MigrationDeleteResponse
 *
 * Result of deleting a terminal or never-started migration and its ledger.
 */
export type MigrationDeleteResponse = {
    id: string;
    status: string;
    deleted_items?: number;
};

/**
 * MigrationException
 *
 * One row of ``GET /authentication/migrations/exceptions``.
 */
export type MigrationException = {
    id?: string | null;
    item_type?: string | null;
    item_ids?: Array<string> | null;
    created_at?: string | null;
    [key: string]: unknown | string | null | string | null | Array<string> | null | string | null | undefined;
};

/**
 * MigrationExceptionCreateRequest
 *
 * Request to add a migration exception
 */
export type MigrationExceptionCreateRequest = {
    item_type: string;
    item_ids: Array<string>;
};

/**
 * MigrationExportResponse
 *
 * Response model for exporting user data for migration.
 */
export type MigrationExportResponse = {
    /**
     * JWT token containing the user's data for migration.
     */
    token: string;
};

/**
 * MigrationItemSummary
 *
 * One disk in the per-job ledger, for the UI's per-tree expand.
 */
export type MigrationItemSummary = {
    id?: string | null;
    storage_id: string;
    tree_id: string;
    topo_index?: number;
    kind?: string | null;
    state: string;
    size_bytes?: number;
    error?: string | null;
    dst_path?: string | null;
};

/**
 * MigrationItemsPageResponse
 *
 * One page of disks for a tree's expand (server-paginated, index-sliced).
 */
export type MigrationItemsPageResponse = {
    items?: Array<MigrationItemSummary>;
    total?: number;
    page?: number;
    per_page?: number;
};

/**
 * MigrationListDeployment
 */
export type MigrationListDeployment = {
    /**
     * Item ID
     */
    id: string;
    /**
     * Item name
     */
    name: string;
    /**
     * User ID who owns the item
     */
    user: string;
    /**
     * Username of the owner
     */
    username: string;
    /**
     * Display name of the user
     */
    user_name: string;
};

/**
 * MigrationListDesktop
 */
export type MigrationListDesktop = {
    /**
     * Item ID
     */
    id: string;
    /**
     * Item name
     */
    name: string;
    /**
     * Type of item.
     */
    kind: 'desktop';
    /**
     * User ID who owns the item
     */
    user: string;
    /**
     * Username of the owner
     */
    username: string;
    /**
     * Display name of the user
     */
    user_name: string;
    /**
     * Whether the item is persistent
     */
    persistent: boolean;
};

/**
 * MigrationListError
 */
export type MigrationListError = {
    /**
     * Human-readable error description
     */
    description: string;
    /**
     * Machine-readable error code
     */
    description_code: string;
};

/**
 * MigrationListItem
 *
 * A list row: the job aggregate plus the few live fields the table renders,
 * all read off the job row so the list needs no per-disk load.
 */
export type MigrationListItem = {
    id: string;
    status: string;
    selection?: {
        [key: string]: unknown;
    };
    config?: {
        [key: string]: unknown;
    };
    totals?: MigrationTotalsResponse;
    pause_reason?: string | null;
    load_state?: {
        [key: string]: unknown;
    };
    created_by?: string | null;
    created_at?: number | null;
    updated_at?: number | null;
    last_activity_at?: number | null;
    state_counts?: {
        [key: string]: unknown;
    };
    eta_seconds?: number | null;
    current_window?: {
        [key: string]: unknown;
    } | null;
    recurring?: boolean;
    days?: Array<number>;
    next_run_seconds?: number | null;
};

/**
 * MigrationListItemsResponse
 *
 * Response model for listing items available for migration.
 */
export type MigrationListItemsResponse = {
    /**
     * Desktops available for migration.
     */
    desktops: Array<MigrationListDesktop>;
    /**
     * Templates available for migration.
     */
    templates: Array<MigrationListTemplate>;
    /**
     * Media available for migration.
     */
    media: Array<MigrationListMedia>;
    /**
     * Deployments available for migration.
     */
    deployments: Array<MigrationListDeployment>;
    /**
     * Origin user(s) of the migration.
     */
    users: Array<MigrationListUser>;
    /**
     * Quota errors detected during migration validation.
     */
    quota_errors?: Array<MigrationListError>;
    /**
     * Action that will be performed on the user account after migration. ``null`` means the account is left untouched.
     */
    action_after_migrate?: 'disable' | 'delete' | null;
};

/**
 * MigrationListMedia
 */
export type MigrationListMedia = {
    /**
     * Item ID
     */
    id: string;
    /**
     * Item name
     */
    name: string;
    /**
     * User ID who owns the item
     */
    user: string;
    /**
     * Username of the owner
     */
    username: string;
    /**
     * Display name of the user
     */
    user_name: string;
};

/**
 * MigrationListResponse
 */
export type MigrationListResponse = {
    migrations?: Array<MigrationListItem>;
};

/**
 * MigrationListTemplate
 */
export type MigrationListTemplate = {
    /**
     * Item ID
     */
    id: string;
    /**
     * Item name
     */
    name: string;
    /**
     * Type of item.
     */
    kind: 'template';
    /**
     * User ID who owns the item
     */
    user: string;
    /**
     * Category ID
     */
    category: string;
    /**
     * Group ID
     */
    group: string;
    /**
     * Username of the owner
     */
    username: string;
    /**
     * Display name of the user
     */
    user_name: string;
    /**
     * Parent template ID if duplicated
     */
    duplicate_parent_template?: string | null;
};

/**
 * MigrationListUser
 */
export type MigrationListUser = {
    /**
     * User ID
     */
    id: string;
    /**
     * Display name of the user
     */
    name: string;
    /**
     * Username of the user
     */
    username: string;
    /**
     * Authentication provider of the user
     */
    provider: string;
};

/**
 * MigrationLoadPolicyData
 *
 * Adaptive-load throttle. ``static`` (the default) keeps a fixed
 * ``parallelism``; ``adaptive`` samples real load each tick and steps
 * parallelism within [min, max], soft-pausing above ``pause_above`` Started
 * desktops. The parallelism-in-range check runs on the merged config in the
 * service (a partial update sends the whole load_policy object).
 */
export type MigrationLoadPolicyData = {
    mode?: 'static' | 'adaptive';
    parallelism_min?: number;
    parallelism_max?: number;
    pause_above?: number;
    baseline_window?: number;
};

/**
 * MigrationPathPrefixesResponse
 *
 * Real, selectable source path-prefixes for the ``path`` selection kind —
 * the distinct ``storage.directory_path`` values, optionally scoped to a source
 * pool. Drives the UI dropdown (no free text).
 */
export type MigrationPathPrefixesResponse = {
    prefixes?: Array<string>;
};

/**
 * MigrationPlanData
 *
 * Dry-run plan preview request — nothing is persisted.
 *
 * The config is optional and only two of its knobs change the preview
 * (`order` and `max_bytes_per_occurrence`), but they change it decisively:
 * without them the preview cannot say which trees would fall outside this
 * occurrence's budget, and an admin would be approving a job whose actual
 * effect is invisible until it runs.
 */
export type MigrationPlanData = {
    selection: MigrationSelectionData;
    config?: MigrationConfigData;
};

/**
 * MigrationPlanResponse
 *
 * Dry-run preview: what would move, grouped by root tree.
 */
export type MigrationPlanResponse = {
    trees?: Array<MigrationTreeSummary>;
    totals?: MigrationTotalsResponse;
};

/**
 * MigrationProviderEnabledResponse
 *
 * Response model for provider migration export/import enabled status.
 */
export type MigrationProviderEnabledResponse = {
    /**
     * Whether the provider allows export or import migrations.
     */
    enabled: boolean;
};

/**
 * MigrationResponse
 */
export type MigrationResponse = {
    id: string;
    status: string;
    selection?: {
        [key: string]: unknown;
    };
    config?: {
        [key: string]: unknown;
    };
    totals?: MigrationTotalsResponse;
    pause_reason?: string | null;
    load_state?: {
        [key: string]: unknown;
    };
    created_by?: string | null;
    created_at?: number | null;
    updated_at?: number | null;
    last_activity_at?: number | null;
};

/**
 * MigrationSelectionData
 *
 * What a migration job moves and where to.
 */
export type MigrationSelectionData = {
    kind?: 'pool' | 'path' | 'category';
    src_pool_id?: string | null;
    dst_pool_id?: string | null;
    category_id?: string | null;
    path_prefix?: string | null;
    tree_ids?: Array<string>;
    item_kinds?: Array<'desktop' | 'template' | 'media'>;
};

/**
 * MigrationStatusResponse
 */
export type MigrationStatusResponse = {
    id: string;
    status: string;
    created_at?: number | null;
    last_activity_at?: number | null;
    selection?: {
        [key: string]: unknown;
    };
    totals?: MigrationTotalsResponse;
    state_counts?: {
        [key: string]: unknown;
    };
    trees?: Array<MigrationTreeSummary>;
    config?: {
        [key: string]: unknown;
    };
    current_window?: {
        [key: string]: unknown;
    } | null;
    pause_reason?: string | null;
    load_state?: {
        [key: string]: unknown;
    };
    eta_seconds?: number | null;
    recurring?: boolean;
    days?: Array<number>;
    next_run_seconds?: number | null;
    items?: Array<MigrationItemSummary>;
    check_census?: {
        [key: string]: unknown;
    };
    not_moving_disks?: Array<{
        [key: string]: unknown;
    }>;
};

/**
 * MigrationTotalsResponse
 */
export type MigrationTotalsResponse = {
    trees?: number;
    derivative_templates?: number;
    desktops?: number;
    media?: number;
    items_total?: number;
    items_by_kind?: {
        [key: string]: unknown;
    };
    bytes_total?: number;
    bytes_by_kind?: {
        [key: string]: unknown;
    };
    bytes_done?: number;
    bytes_copied?: number;
    done?: number;
    migrated?: number;
    completed?: number;
    state_counts?: {
        [key: string]: unknown;
    };
    not_moving_total?: number;
    not_moving_by_kind?: {
        [key: string]: unknown;
    };
    not_moving_disks?: Array<{
        [key: string]: unknown;
    }>;
    excluded_trees?: Array<{
        [key: string]: unknown;
    }>;
    excluded_disks_total?: number;
    order?: string;
    order_trees_without_usage?: number;
    bytes_within_budget?: number;
    trees_within_budget?: number;
    usage_age_days?: number | null;
    usage_age_excluded_trees?: number;
    usage_age_excluded_disks?: number;
    usage_age_excluded_bytes?: number;
};

/**
 * MigrationTreeSummary
 */
export type MigrationTreeSummary = {
    tree_id: string;
    root_storage_id: string;
    order_key?: number | null;
    within_budget?: boolean;
    derivative_templates?: number;
    desktops?: number;
    media?: number;
    items_total?: number;
    bytes_total?: number;
    done?: number;
    migrated?: number;
    completed?: number;
    bytes_done?: number;
    bytes_copied?: number;
    state_counts?: {
        [key: string]: unknown;
    };
};

/**
 * MigrationTreesPageResponse
 *
 * One page of per-tree summaries for a job's expand (server-paginated).
 */
export type MigrationTreesPageResponse = {
    trees?: Array<MigrationTreeSummary>;
    total?: number;
    page?: number;
    per_page?: number;
};

/**
 * MigrationWindowData
 */
export type MigrationWindowData = {
    start?: string | null;
    end?: string | null;
    tz?: string;
    days?: Array<number>;
};

/**
 * NewNonpersistentDesktopRequest
 *
 * Minimal request body for the v3-parity non-persistent desktop
 * endpoint (``POST /item/desktop/new-nonpersistent``).
 *
 * v3 accepted a form-encoded body with just a ``template`` field
 * and generated the rest server-side. This schema mirrors that
 * shape as JSON.
 */
export type NewNonpersistentDesktopRequest = {
    /**
     * ID of the template to create a non-persistent desktop from.
     */
    template_id: string;
    /**
     * vGPU profiles to use instead of the template's.
     */
    reservables?: ReservablesInput | null;
    /**
     * End of the booking to create when the template has a vGPU.
     */
    booking_end?: string | null;
};

/**
 * NewTemplateRequest
 */
export type NewTemplateRequest = {
    /**
     * ID of the desktop to template.
     */
    desktop_id: string;
    /**
     * Name of the template. Must be unique within the user's templates.
     */
    name: string;
    /**
     * Description of the template. If not provided, an empty string is used.
     */
    description?: string;
    /**
     * Permissions for the template.
     */
    allowed: AllowedInput;
    /**
     * Whether the template is enabled or not.
     */
    enabled?: boolean;
    /**
     * Image to be used for the template. If None or not provided, a stock card derived from the new template ID is assigned.
     */
    image?: DomainImageInput | null;
};

/**
 * NotificationActionsResponse
 *
 * List of notification actions
 */
export type NotificationActionsResponse = {
    actions: Array<{
        [key: string]: unknown;
    }>;
};

/**
 * NotificationCreateRequest
 *
 * Request to create a notification
 */
export type NotificationCreateRequest = {
    name?: string | null;
    description?: string | null;
    trigger?: NotificationTriggerEnum | null;
    display?: Array<NotificationDisplayEnum> | null;
    template_id?: string | null;
    action_id?: string | null;
    item_type?: string | null;
    order?: number | null;
    enabled?: boolean | null;
    force_accept?: boolean | null;
    compute?: unknown | null;
    ignore_after?: string | null;
    keep_time?: number | null;
    allowed?: AllowedInput;
};

/**
 * NotificationDataListResponse
 *
 * List of notification data entries
 */
export type NotificationDataListResponse = {
    data: Array<{
        [key: string]: unknown;
    }>;
};

/**
 * NotificationDeleteRequest
 *
 * Request body for deleting a notification
 */
export type NotificationDeleteRequest = {
    delete_logs?: boolean;
};

/**
 * NotificationDetailResponse
 *
 * Full notification row. RootModel so datetime fields (``ignore_after``)
 * go through Pydantic's JSON serializer instead of Starlette's default
 * encoder.
 */
export type NotificationDetailResponse = {
    [key: string]: unknown;
};

/**
 * NotificationDisplayEnum
 */
export const NotificationDisplayEnum = {
    FULLPAGE: 'fullpage',
    MODAL: 'modal',
    BANNER: 'banner'
} as const;

/**
 * NotificationDisplayEnum
 */
export type NotificationDisplayEnum = typeof NotificationDisplayEnum[keyof typeof NotificationDisplayEnum];

/**
 * NotificationFlatItem
 *
 * Flat notification item ready to render, with template already resolved.
 */
export type NotificationFlatItem = {
    id: string;
    title?: string;
    body?: string;
    footer?: string | null;
    force_accept?: boolean;
};

/**
 * NotificationGroupedDataResponse
 *
 * Grouped notification data response
 */
export type NotificationGroupedDataResponse = {
    data: Array<{
        [key: string]: unknown;
    }>;
};

/**
 * NotificationItem
 *
 * Individual notification item
 */
export type NotificationItem = {
    id: string;
    vars?: {
        [key: string]: string;
    } | null;
    text?: string | null;
    title?: string | null;
    body?: string | null;
    footer?: string | null;
};

/**
 * NotificationListResponse
 *
 * List of notifications
 */
export type NotificationListResponse = {
    notifications: Array<{
        [key: string]: unknown;
    }>;
};

/**
 * NotificationResponse
 *
 * Single notification response
 */
export type NotificationResponse = {
    id?: string | null;
};

/**
 * NotificationStatusesResponse
 *
 * List of notification statuses
 */
export type NotificationStatusesResponse = {
    statuses: Array<string>;
};

/**
 * NotificationTemplate
 *
 * Notification template
 */
export type NotificationTemplate = {
    body: string;
    footer: string;
    title: string;
};

/**
 * NotificationTriggerEnum
 */
export const NotificationTriggerEnum = {
    LOGIN: 'login',
    START_DESKTOP: 'start_desktop',
    START_DESKTOP_BASTION: 'start_desktop_bastion'
} as const;

/**
 * NotificationTriggerEnum
 */
export type NotificationTriggerEnum = typeof NotificationTriggerEnum[keyof typeof NotificationTriggerEnum];

/**
 * NotificationUpdateRequest
 *
 * Request to update a notification
 */
export type NotificationUpdateRequest = {
    name?: string | null;
    description?: string | null;
    trigger?: NotificationTriggerEnum | null;
    display?: Array<NotificationDisplayEnum> | null;
    template_id?: string | null;
    action_id?: string | null;
    item_type?: string | null;
    order?: number | null;
    enabled?: boolean | null;
    force_accept?: boolean | null;
    compute?: unknown | null;
    ignore_after?: string | null;
    keep_time?: number | null;
    allowed?: AllowedInput | null;
};

/**
 * NotificationUserData
 *
 * User notification data
 */
export type NotificationUserData = {
    display: Array<string>;
    action_id: string;
    template_id: string;
    force_accept: boolean;
    notifications: Array<NotificationItem>;
    template?: NotificationTemplate | null;
};

/**
 * NotificationsUserDisplaysTriggerResponse
 *
 * User notification displays for a trigger response model
 */
export type NotificationsUserDisplaysTriggerResponse = {
    displays?: Array<string>;
};

/**
 * NotificationsUserTriggerDisplayFlatResponse
 *
 * User trigger notifications as a flat, ordered list.
 *
 * The server has already resolved the user's language template against
 * each item and sorted by the notification's ``order`` field, so the
 * client can render the list as-is.
 */
export type NotificationsUserTriggerDisplayFlatResponse = {
    notifications?: Array<NotificationFlatItem>;
};

/**
 * NotificationsUserTriggerDisplayResponse
 *
 * User notification displays response model
 */
export type NotificationsUserTriggerDisplayResponse = {
    notifications?: {
        [key: string]: {
            [key: string]: NotificationUserData;
        };
    };
};

/**
 * NotifyDesktopRequest
 *
 * Request to notify a desktop
 */
export type NotifyDesktopRequest = {
    desktop_id: string;
    type: string;
    msg_code?: string | null;
    params?: {
        [key: string]: unknown;
    } | null;
};

/**
 * NotifyUserDesktopRequest
 *
 * Request to notify a user about a desktop
 */
export type NotifyUserDesktopRequest = {
    user_id: string;
    type: string;
    msg_code?: string | null;
    params?: {
        [key: string]: unknown;
    } | null;
};

/**
 * OldEntriesActionEnum
 *
 * Action for old recycle bin entries cleanup.
 */
export const OldEntriesActionEnum = { DELETE: 'delete', KEEP: 'keep' } as const;

/**
 * OldEntriesActionEnum
 *
 * Action for old recycle bin entries cleanup.
 */
export type OldEntriesActionEnum = typeof OldEntriesActionEnum[keyof typeof OldEntriesActionEnum];

/**
 * OperationsHypervisorResponse
 *
 * Operations hypervisor info
 */
export type OperationsHypervisorResponse = {
    id: string;
    state: string;
    isard_state?: string | null;
    orchestrator_managed?: unknown | null;
    only_forced?: unknown | null;
    buffering_hyper?: unknown | null;
    destroy_time?: unknown | null;
    gpu_only?: unknown | null;
    desktops_started?: unknown | null;
    cpu?: number | null;
    ram?: number | null;
    capabilities?: Array<string> | null;
    gpus?: Array<string> | null;
    destroy_allowed?: boolean | null;
};

/**
 * OrchestratorConfigResponse
 *
 * Current orchestrator configuration.
 */
export type OrchestratorConfigResponse = {
    enabled: boolean;
};

/**
 * OrchestratorConfigUpdateRequest
 *
 * Request to enable or disable the orchestrator.
 */
export type OrchestratorConfigUpdateRequest = {
    enabled: boolean;
};

/**
 * OrchestratorHypervisor
 */
export type OrchestratorHypervisor = {
    id: string;
    status: HypervisorStatus;
    only_forced: boolean;
    buffering_hyper: boolean;
    destroy_time: string | null;
    stats: OrchestratorHypervisorStats | null;
    orchestrator_managed: boolean;
    min_free_mem_gb: number;
    gpu_only: boolean;
    desktops_started: number;
    bookings_end_time: string | null;
    gpus: Array<OrchestratorHypervisorGpu>;
    cpu: OrchestratorResourceLoad;
    ram: OrchestratorResourceLoad;
};

/**
 * OrchestratorHypervisorGPU
 */
export type OrchestratorHypervisorGpu = {
    id: string;
    total_units: number;
    used_units: number;
    free_units: number;
    brand: string;
    model: string;
    profile: string;
};

/**
 * OrchestratorHypervisorStats
 */
export type OrchestratorHypervisorStats = {
    cpu_current: OrchestratorHypervisorStatsCpu;
    cpu_5min: OrchestratorHypervisorStatsCpu;
    cpu_15min: OrchestratorHypervisorStatsCpu;
    cpu_1min: OrchestratorHypervisorStatsCpu;
    mem_stats: OrchestratorHypervisorStatsMem;
    mem_stats_1min: OrchestratorHypervisorStatsMem;
    mem_stats_5min: OrchestratorHypervisorStatsMem;
    mem_stats_15min: OrchestratorHypervisorStatsMem;
    time: number;
    positioned_items: Array<unknown>;
};

/**
 * OrchestratorHypervisorStatsCPU
 */
export type OrchestratorHypervisorStatsCpu = {
    idle: number;
    iowait: number;
    kernel: number;
    used: number;
    user: number;
};

/**
 * OrchestratorHypervisorStatsMem
 */
export type OrchestratorHypervisorStatsMem = {
    available: number;
    buffers: number;
    cached: number;
    free: number;
    total: number;
    used: number;
    hugepages_total_kb: number | null;
    hugepages_free_kb: number | null;
    hugepages_used_kb: number | null;
};

/**
 * OrchestratorManagedHypervisor
 */
export type OrchestratorManagedHypervisor = {
    id: string;
    info: {
        [key: string]: unknown;
    } | null;
    stats: {
        [key: string]: unknown;
    } | null;
    status: HypervisorStatus;
    destroy_time: string | null;
    status_time: string | number | number | null;
    desktops_started: number;
};

/**
 * OrchestratorOnlyForcedData
 *
 * Request body for setting a hypervisor's only_forced flag.
 */
export type OrchestratorOnlyForcedData = {
    only_forced: boolean;
};

/**
 * OrchestratorResourceLoad
 *
 * CPU/RAM load computed by ``HypervisorsProcessed.calc_resource_load``.
 */
export type OrchestratorResourceLoad = {
    total: number;
    used: number;
    free: number;
};

/**
 * OrphanLane
 *
 * A single storage queue lane holding jobs with no consumer.
 */
export type OrphanLane = {
    queue: string;
    queued: number;
};

/**
 * OrphanPool
 *
 * A pool (or move-lane pair) whose queued jobs are served by no worker.
 */
export type OrphanPool = {
    pool: string;
    queued: number;
    lanes: Array<OrphanLane>;
};

/**
 * OwnedDeployment
 */
export type OwnedDeployment = {
    /**
     * ID of the deployment
     */
    id: string;
    /**
     * Name of the deployment
     */
    name: string;
    /**
     * Description of the deployment
     */
    description?: string | null;
    /**
     * Image associated with the deployment (may be missing on legacy rows)
     */
    image?: Image | null;
    /**
     * List of desktop names associated with the deployment
     */
    desktop_names: Array<string>;
    template_names: Array<string>;
    /**
     * Number of desktops that have been started
     */
    started_desktops: number;
    /**
     * Indicates if the deployment is visible
     */
    tag_visible?: boolean;
    /**
     * Total number of desktops associated with the deployment
     */
    total_desktops: number;
    /**
     * Number of visible desktops associated with the deployment
     */
    visible_desktops: number;
    /**
     * Total number of users associated with the deployment
     */
    total_users: number;
    /**
     * Indicates if the user is a co-owner of the deployment
     */
    co_owner: boolean;
    /**
     * Indicates if the deployment needs booking
     */
    needs_booking: boolean;
    /**
     * Start time of the next booking for the desktop. If None, no booking is scheduled.
     */
    next_booking_start?: string | null;
    /**
     * End time of the next booking for the desktop. If None, no booking is scheduled.
     */
    next_booking_end?: string | null;
    /**
     * Current booking id. Only set when there is an active booking.
     */
    booking_id?: string | boolean | null;
};

/**
 * OwnedDeploymentsResponse
 */
export type OwnedDeploymentsResponse = {
    /**
     * List of deployments owned and co-owned by the user
     */
    deployments: Array<OwnedDeployment>;
};

/**
 * OwnerTaskItem
 *
 * One row of "the tasks this storage or media has had".
 *
 * Deliberately not ``TaskResponse``. That model is shaped by the single-task
 * detail path: its observability fields are filled by an enrichment step a
 * listing must never run (a Redis fetch per row), and its ``storage_id``
 * comes off a Task property that ``to_dict`` excludes — so reusing it here
 * would put a dozen permanently null keys on the wire and imply a chain view
 * this listing does not compute. Every field below is read from the job hash
 * the batch already fetched.
 *
 * ``job_status`` is this job's own status, not its chain's: the chain status
 * would mean walking dependencies, one round trip per member.
 */
export type OwnerTaskItem = {
    id: string;
    task?: string | null;
    queue?: string | null;
    job_status?: string | null;
    user_id?: string | null;
    category_id?: string | null;
    storage_id?: string | null;
    media_id?: string | null;
    enqueued_at?: number | null;
    started_at?: number | null;
    ended_at?: number | null;
};

/**
 * PasswordPolicyErrorParams
 */
export type PasswordPolicyErrorParams = {
    num?: number | null;
};

/**
 * PasswordPolicyErrorResponse
 */
export type PasswordPolicyErrorResponse = {
    error: string;
    msg: string;
    description_code: string;
    function: string;
    function_call: string;
    description: string;
    debug: string;
    request: string;
    data: string;
    params: PasswordPolicyErrorParams | null;
};

/**
 * PlanningItem
 *
 * Model for a planning item
 */
export type PlanningItem = {
    id: string;
    item_id: string;
    subitem_id: string;
    start: string;
    end: string;
    item_type?: string | null;
    units?: number | null;
    priority?: number | null;
};

/**
 * PlanningListResponse
 *
 * Response model for listing plannings
 */
export type PlanningListResponse = {
    plannings: Array<PlanningItem>;
};

/**
 * PolicyCreateDisclaimerRequest
 */
export type PolicyCreateDisclaimerRequest = {
    template?: string | null;
};

/**
 * PolicyCreateRequest
 *
 * Request to create an authentication policy
 */
export type PolicyCreateRequest = {
    category: string;
    role: string;
    type: string;
    disclaimer?: PolicyCreateDisclaimerRequest | boolean | null;
    email_verification?: boolean | null;
    password?: {
        [key: string]: unknown;
    } | null;
};

/**
 * PolicyEditRequest
 *
 * Request to edit an authentication policy
 */
export type PolicyEditRequest = {
    category?: string | null;
    role?: string | null;
    type?: string | null;
    disclaimer?: boolean | {
        [key: string]: unknown;
    } | null;
    email_verification?: boolean | null;
    password?: {
        [key: string]: unknown;
    } | null;
};

/**
 * PolicyResponse
 *
 * One row of ``GET /admin/items/authentication/policies`` and the matching
 * per-id read. The persisted policy doc is freeform and may carry
 * fields the schema doesn't enumerate.
 */
export type PolicyResponse = {
    id?: string | null;
    category?: string | null;
    role?: string | null;
    type?: string | null;
    disclaimer?: boolean | {
        [key: string]: unknown;
    } | null;
    email_verification?: boolean | null;
    password?: {
        [key: string]: unknown;
    } | null;
    [key: string]: unknown | string | null | string | null | string | null | string | null | boolean | {
        [key: string]: unknown;
    } | null | boolean | null | {
        [key: string]: unknown;
    } | null | undefined;
};

/**
 * PoolGauge
 *
 * Per-pool rollup: per-tier backlog / oldest-queued-age / started-over-
 * timeout / failed, plus the fair-tier per-category breakdown.
 */
export type PoolGauge = {
    pool: string;
    backlog?: {
        [key: string]: number;
    };
    oldest_queued_age_seconds?: {
        [key: string]: number;
    };
    started_over_timeout?: {
        [key: string]: number;
    };
    failed?: {
        [key: string]: number;
    };
    categories?: Array<CategoryGauge>;
};

/**
 * PoolPlanResponse
 *
 * Pool-scoped aggregation for the admin overview.
 */
export type PoolPlanResponse = {
    pool_id: string;
    trees?: Array<MigrationTreeSummary>;
    totals?: MigrationTotalsResponse;
};

/**
 * PriorityRuleResponse
 *
 * Response model for priority rules
 */
export type PriorityRuleResponse = {
    rule_id: string;
};

/**
 * ProblemTask
 *
 * One row of the bounded, filterable problem-task listing (``GET
 * /admin/items/queues/tasks/problems`` — P2.4 §2.4).
 *
 * ``kind`` is one of ``failed`` / ``stuck_running`` / ``deferred_orphan``.
 * Timestamps are the tz-aware rq-job datetimes flattened to epoch-float wire
 * form (``None`` when the job has not reached that lifecycle point).
 * ``age_seconds`` is measured from the most-relevant timestamp for the kind
 * (failed / deferred -> ``enqueued_at``; stuck_running -> ``started_at``).
 * ``exc_string`` is the traceback of the latest FAILED result (never a metric
 * label). ``retryable`` is True only for the failed kind; ``cancelable`` is
 * True for all (admin cancel has no status gate).
 */
export type ProblemTask = {
    id: string;
    kind: string;
    action?: string | null;
    queue?: string | null;
    pool?: string | null;
    category_id?: string | null;
    category_name?: string | null;
    tier?: string | null;
    job_status?: string | null;
    pending?: boolean | null;
    retries_left?: number | null;
    enqueued_at?: number | null;
    started_at?: number | null;
    ended_at?: number | null;
    age_seconds?: number | null;
    exc_string?: string | null;
    retryable?: boolean;
    cancelable?: boolean;
};

/**
 * ProblemTasksResponse
 *
 * The ``GET /admin/items/queues/tasks/problems`` payload (P2.4 §2.4).
 *
 * Degrades to ``count=0`` / empty ``tasks`` on transient Redis failure rather
 * than raising — a polled 500 would eject the operator mid-incident.
 * ``truncated`` is set when any lane hit its per-lane scan cap or the merged
 * pre-slice count exceeded the requested window.
 */
export type ProblemTasksResponse = {
    generated_at: number;
    truncated?: boolean;
    count?: number;
    tasks?: Array<ProblemTask>;
};

/**
 * ProviderAllDetails
 *
 * Details for 'all' providers model
 */
export type ProviderAllDetails = {
    description?: string | null;
    hide_categories_dropdown?: boolean;
    display_providers?: Array<string> | null;
};

/**
 * ProviderConfigResponse
 *
 * Response shape for ``GET /authentication/provider/{provider}``.
 *
 * The provider config is the raw ``Configuration.<provider>`` blob
 * plus a derived ``migration`` block. Permissive — each provider has
 * its own field set.
 */
export type ProviderConfigResponse = {
    status?: ProviderStatus | null;
    [key: string]: unknown | ProviderStatus | null | undefined;
};

/**
 * ProviderConfigUpdateRequest
 *
 * Update a provider configuration.
 *
 * ``extra: allow`` passes the full provider block (``enabled``,
 * ``<provider>_config``, …) through to ``update_provider_config``, which does
 * the validation; without it Pydantic would drop ``enabled``.
 */
export type ProviderConfigUpdateRequest = {
    migration?: {
        [key: string]: unknown;
    } | null;
    [key: string]: unknown | {
        [key: string]: unknown;
    } | null | undefined;
};

/**
 * ProviderDetails
 *
 * Provider details model
 */
export type ProviderDetails = {
    description?: string | null;
    hide_categories_dropdown?: boolean;
    hide_forgot_password?: boolean;
    submit_extra_styles?: string | null;
    submit_icon?: string | null;
    submit_text?: string | null;
};

/**
 * ProviderEntry
 *
 * Enabled flag of one authentication provider.
 */
export type ProviderEntry = {
    enabled?: boolean;
};

/**
 * ProviderQuotaModel
 *
 * Provider quota information model
 */
export type ProviderQuotaModel = {
    used?: number | null;
    relative?: number | null;
    quota?: number | null;
    total?: number | null;
    free?: number | null;
};

/**
 * ProviderStatus
 *
 * Health status of an authentication provider, written by the
 * authentication service and read by the admin UI. Mirrors the Go
 * ``model.ProviderStatus`` struct.
 */
export type ProviderStatus = {
    healthy?: boolean;
    msg?: string;
    last_updated?: string | null;
};

/**
 * ProviderWithNameEntry
 *
 * Provider entry with the optional display name read from
 * ``config.auth.<provider>.<provider>_config.name``.
 */
export type ProviderWithNameEntry = {
    enabled?: boolean;
    name?: string | null;
};

/**
 * Providers
 *
 * Providers model
 */
export type Providers = {
    all?: ProviderAllDetails | null;
    form?: ProviderDetails | null;
    saml?: ProviderDetails | null;
    google?: ProviderDetails | null;
};

/**
 * ProvidersResponse
 *
 * Response shape for ``GET /admin/items/authentication/providers``.
 *
 * The service returns a dict mapping provider name (``local`` /
 * ``google`` / ``saml`` / ``ldap``) to its enabled flag, plus the
 * optional display name for the nameable providers (``local`` can
 * never have one). Modelled with explicit fields so the OpenAPI
 * schema is self-documenting.
 */
export type ProvidersResponse = {
    local?: ProviderEntry;
    google?: ProviderWithNameEntry;
    saml?: ProviderWithNameEntry;
    ldap?: ProviderWithNameEntry;
};

/**
 * Qcow2GeometryResponse
 *
 * The installation-wide qcow2 geometry policy resolved by apiv4.
 */
export type Qcow2GeometryResponse = {
    cluster_size: string;
    extended_l2: string;
    lazy_refcounts: string;
    preallocation: string;
};

/**
 * QosDiskCreateRequest
 *
 * Request body for creating a QoS disk profile.
 */
export type QosDiskCreateRequest = {
    /**
     * QoS disk profile name
     */
    name: string;
    /**
     * QoS disk profile description
     */
    description?: string;
    /**
     * IO tune parameters
     */
    iotune: {
        [key: string]: unknown;
    };
    /**
     * Allowed access configuration
     */
    allowed?: AllowedInput;
};

/**
 * QosDiskUpdateRequest
 *
 * Request body for updating a QoS disk profile.
 */
export type QosDiskUpdateRequest = {
    /**
     * QoS disk profile ID
     */
    id: string;
    /**
     * QoS disk profile name
     */
    name: string;
    /**
     * QoS disk profile description
     */
    description?: string;
    /**
     * IO tune parameters
     */
    iotune?: {
        [key: string]: unknown;
    };
    /**
     * Allowed access configuration
     */
    allowed?: AllowedInput;
};

/**
 * QueueConsumerResponse
 *
 * Queue consumer/worker info.
 *
 * Back-compatible superset of the legacy pubsub-derived shape (``id``/
 * ``queue``/``priority``/``subscribers``/``status``) PLUS the reworked
 * heartbeat-truth fields sourced from the bounded ``rq:workers`` SET and each
 * ``rq:worker:<name>`` hash (and, once published in a later step, the
 * ``governor:worker:<name>`` hash — until then PSI/served-lanes degrade to
 * ``null``/``served_known=false``). Every new field is Optional so an old
 * consumer of this endpoint keeps working.
 */
export type QueueConsumerResponse = {
    id: string;
    queue: string;
    queue_id?: string | null;
    priority_id?: string | null;
    priority?: number | null;
    subscribers?: Array<string> | null;
    status?: string | null;
    name?: string | null;
    pool?: string | null;
    kind?: string | null;
    state?: string | null;
    up?: boolean | null;
    hash_present?: boolean | null;
    last_heartbeat?: number | null;
    heartbeat_age_seconds?: number | null;
    current_lane?: string | null;
    served_lanes?: Array<string> | null;
    served_known?: boolean | null;
    psi_available?: boolean | null;
    psi_cpu?: number | null;
    psi_io?: number | null;
    psi_mem?: number | null;
    deferring?: boolean | null;
    at_cap?: boolean | null;
    floor?: boolean | null;
    multitenancy?: boolean | null;
    last_job_id?: string | null;
    last_job_action?: string | null;
};

/**
 * QueueJobsResponse
 *
 * Queue jobs summary
 */
export type QueueJobsResponse = {
    id: string;
    queued?: number;
    started?: number;
    finished?: number;
    failed?: number;
    deferred?: number;
    scheduled?: number;
    canceled?: number;
};

/**
 * QueueRegistriesRequest
 *
 * Request to set queue registries
 */
export type QueueRegistriesRequest = {
    queue_registries?: Array<string> | null;
};

/**
 * QueueTierHealth
 *
 * Compact per-tier queued rollup for the user health summary.
 */
export type QueueTierHealth = {
    tier?: string | null;
    queued?: number;
    stranded?: boolean;
};

/**
 * QueuesHealthResponse
 *
 * User-facing storage-queue health summary (no per-worker detail).
 */
export type QueuesHealthResponse = {
    degraded?: boolean;
    stranded?: boolean;
    tiers?: Array<QueueTierHealth>;
};

/**
 * Quota
 */
export type Quota = {
    desktops: number;
    volatile: number;
    desktops_disk_size: number;
    isos: number;
    vcpus: number;
    memory: number;
    running: number;
    templates: number;
    users?: number | null;
    media_size?: number | null;
    total_size: number;
    total_soft_size: number;
    deployments_total: number;
    deployment_desktops: number;
    deployment_users: number;
    started_deployment_desktops: number;
};

/**
 * RecoverStuckRequest
 *
 * Request to recover recycle bin entries stranded mid-delete.
 */
export type RecoverStuckRequest = {
    older_than_minutes?: number;
};

/**
 * RecoverStuckResponse
 *
 * Result of a manual stuck-entry recovery (ids re-enqueued).
 */
export type RecoverStuckResponse = {
    recovered?: Array<string>;
    count?: number;
};

/**
 * RecycleBinBulkRequest
 *
 * Request for bulk recycle bin operations.
 */
export type RecycleBinBulkRequest = {
    recycle_bin_ids: Array<string>;
};

/**
 * RecycleBinBulkResponse
 *
 * Response for bulk recycle bin operations.
 *
 * The route handler is fire-and-forget — it schedules the per-id work
 * on a background task and returns the list of ids it accepted. The
 * pre-fix schema only had ``success`` / ``failed`` fields and the
 * route's ``RecycleBinBulkResponse(recycle_bin_ids=ids)`` call had
 * its kwarg silently dropped by Pydantic, so the wire response was
 * always ``{success: [], failed: []}`` regardless of input. Replace
 * with the canonical ``recycle_bin_ids`` echoing what was scheduled.
 */
export type RecycleBinBulkResponse = {
    recycle_bin_ids?: Array<string>;
};

/**
 * RecycleBinCategoryCutoffTime
 *
 * Manager view: the category's effective cutoff and the system ceiling.
 */
export type RecycleBinCategoryCutoffTime = {
    category?: number | null;
    system?: number | null;
};

/**
 * RecycleBinCutoffTimeResponse
 *
 * Recycle bin cutoff time response model.
 *
 * Emits the value under both spellings: the apiv3 contract used
 * ``recycle_bin_cuttoff_time`` (double-t typo) and the Vue 2
 * frontend still reads it that way. Vue 3 and other Pythonic
 * consumers expect the corrected ``recycle_bin_cutoff_time``.
 * Returning both keeps both fronts working without a breaking
 * change.
 */
export type RecycleBinCutoffTimeResponse = {
    recycle_bin_cutoff_time: number;
    recycle_bin_cuttoff_time: number;
};

/**
 * RecycleBinEntriesResponse
 *
 * List of recycle bin item counts
 */
export type RecycleBinEntriesResponse = {
    entries: Array<RecycleBinEntry>;
};

/**
 * RecycleBinEntry
 */
export type RecycleBinEntry = {
    accessed: number;
    agent_category_id?: string | null;
    agent_category_name?: string | null;
    agent_group_id?: string | null;
    agent_group_name?: string | null;
    agent_id: string;
    agent_name: string;
    agent_role?: string | null;
    agent_type: string;
    categories: number;
    deployments: number;
    desktops: number;
    groups: number;
    id: string;
    item_name?: string | null;
    item_type: string;
    last: RecycleBinLastAction | null;
    owner_category_id?: string | null;
    owner_category_name?: string | null;
    owner_group_id?: string | null;
    owner_group_name?: string | null;
    owner_id?: string | null;
    owner_name?: string | null;
    owner_role?: string | null;
    size: number;
    status: RecycleBinStatusEnum;
    storages: number;
    targets?: Array<{
        [key: string]: unknown;
    }>;
    templates: number;
    users: number;
};

/**
 * RecycleBinLastAction
 *
 * Last action performed on recycle bin item
 */
export type RecycleBinLastAction = {
    action: string;
    agent_category_id?: string | null;
    agent_category_name?: string | null;
    agent_id: string;
    agent_name: string;
    agent_role?: string | null;
    agent_type: string;
    time: number;
};

/**
 * RecycleBinOldEntriesConfig
 *
 * Configuration for old entries cleanup.
 */
export type RecycleBinOldEntriesConfig = {
    max_time?: number | null;
    action?: string | null;
};

/**
 * RecycleBinResponse
 */
export type RecycleBinResponse = {
    accessed: number;
    agent_category_id?: string | null;
    agent_category_name?: string | null;
    agent_group_id?: string | null;
    agent_group_name?: string | null;
    agent_id: string;
    agent_name: string;
    agent_role?: string | null;
    agent_type: string;
    categories: Array<{
        [key: string]: unknown;
    }>;
    deployments: Array<{
        [key: string]: unknown;
    }>;
    desktops: Array<{
        [key: string]: unknown;
    }>;
    groups: Array<{
        [key: string]: unknown;
    }>;
    id: string;
    item_name: string;
    item_type: string;
    logs: Array<{
        [key: string]: unknown;
    }>;
    owner_category_id: string;
    owner_category_name: string;
    owner_group_id: string;
    owner_group_name: string;
    owner_id: string;
    owner_name: string;
    owner_role: string;
    size: number;
    status: RecycleBinStatusEnum;
    storages: Array<{
        [key: string]: unknown;
    }>;
    targets?: Array<{
        [key: string]: unknown;
    }>;
    tasks: Array<{
        [key: string]: unknown;
    }> | null;
    templates: Array<{
        [key: string]: unknown;
    }>;
    users: Array<{
        [key: string]: unknown;
    }>;
};

/**
 * RecycleBinRestoreErrorParams
 */
export type RecycleBinRestoreErrorParams = {
    user?: string | null;
};

/**
 * RecycleBinRestoreErrorResponse
 */
export type RecycleBinRestoreErrorResponse = {
    error: string;
    msg: string;
    description_code: string;
    function: string;
    function_call: string;
    description: string;
    debug: string;
    request: string;
    data: string;
    params: RecycleBinRestoreErrorParams | null;
};

/**
 * RecycleBinSetDefaultDeleteRequest
 *
 * Request to set default delete behavior.
 */
export type RecycleBinSetDefaultDeleteRequest = {
    rb_default: boolean;
};

/**
 * RecycleBinStatusEnum
 */
export const RecycleBinStatusEnum = {
    RECYCLED: 'recycled',
    RESTORED: 'restored',
    QUEUED: 'queued',
    DELETING: 'deleting',
    DELETED: 'deleted'
} as const;

/**
 * RecycleBinStatusEnum
 */
export type RecycleBinStatusEnum = typeof RecycleBinStatusEnum[keyof typeof RecycleBinStatusEnum];

/**
 * RecycleBinStatusResponse
 *
 * Recycle bin status overview.
 */
export type RecycleBinStatusResponse = {
    total?: number;
    by_status?: {
        [key: string]: unknown;
    };
};

/**
 * RecycleBinSystemCutoffTimeResponse
 *
 * System-wide recycle bin cutoff time.
 *
 * Admins get a bare int; managers get the {category, system} breakdown the
 * webapp uses to cap the dropdown (recyclebin_domains.js:839-848).
 */
export type RecycleBinSystemCutoffTimeResponse = {
    recycle_bin_cuttoff_time: number | RecycleBinCategoryCutoffTime | null;
};

/**
 * RecycleBinUpdateCutoffTimeRequest
 *
 * Request to update system cutoff time.
 */
export type RecycleBinUpdateCutoffTimeRequest = {
    recycle_bin_cuttoff_time: number;
};

/**
 * RedisHealth
 *
 * Broker (RQ Redis) health sampled once per governor read (catalog #15).
 */
export type RedisHealth = {
    up?: boolean;
    ping_ms?: number | null;
    used_memory?: number | null;
    maxmemory?: number | null;
    used_ratio?: number | null;
    evicted_keys?: number | null;
};

/**
 * RegisterPostData
 */
export type RegisterPostData = {
    code: string;
};

/**
 * RemoteVpnResponse
 *
 * Response shape for the ``/admin/item/remote_vpn/{vpn_id}/{kind}[/{os}]``
 * endpoints. ``kind`` switches between config text and an installer
 * payload, so the model is permissive.
 */
export type RemoteVpnResponse = {
    [key: string]: unknown;
};

/**
 * RequiredCheckResponse
 */
export type RequiredCheckResponse = {
    required: boolean;
};

/**
 * ReservableDetailResponse
 *
 * Response model for reservable detail list
 */
export type ReservableDetailResponse = {
    items: Array<ReservableItemResponse>;
};

/**
 * ReservableItemResponse
 *
 * Response model for individual reservable item details
 */
export type ReservableItemResponse = {
    id: string;
    name: string;
    description: string;
    brand: string;
    model: string;
    memory: string;
    architecture: string;
    active_profile?: string | null;
    changing_to_profile?: string | null;
    physical_device?: string | null;
    category?: string | null;
    category_name?: string | null;
    passthrough_variant?: string | null;
    profiles_enabled: Array<string>;
    plans: ReservablePlans;
    desktops_started?: Array<string>;
    available_units?: number;
    gpu_warnings?: Array<string>;
};

/**
 * ReservablePlans
 *
 * Model for booking plans data
 */
export type ReservablePlans = {
    current: number;
    active: boolean;
    profile?: string | null;
};

/**
 * ReservableProfileResponse
 *
 * Response model for reservable profiles
 */
export type ReservableProfileResponse = {
    id: string;
    brand: string;
    model: string;
    description: string;
    memory: string;
    architecture: string;
    profiles: Array<{
        [key: string]: unknown;
    }>;
};

/**
 * ReservableVgpuItem
 *
 * A bookable vGPU plus the hypervisor/NUMA grouping the hardware selector
 * uses to group passthrough cards by socket/hypervisor (so otherwise-identical
 * cards on different sockets or hosts are distinguishable).
 *
 * ``hypervisor_groups``/``numa_by_group`` are anonymized and present for every
 * role; ``hypervisors``/``numa_by_hypervisor`` carry real hypervisor names and
 * are populated for admins/managers only. All default empty for backward
 * compatibility.
 */
export type ReservableVgpuItem = {
    allowed: IsardvdiCommonSchemasSharedAllowedAllowed;
    /**
     * Description of the hardware item
     */
    description: string;
    /**
     * Unique identifier for the hardware item
     */
    id: string;
    /**
     * Display name of the hardware item
     */
    name: string;
    /**
     * Whether this item can be edited
     */
    editable: boolean;
    /**
     * Anonymized indices of the hypervisor groups that can host this vGPU
     */
    hypervisor_groups?: Array<number>;
    /**
     * NUMA nodes per hypervisor-group index (keyed by group index)
     */
    numa_by_group?: {
        [key: string]: Array<number>;
    };
    /**
     * Hypervisor names hosting this vGPU (admins/managers only)
     */
    hypervisors?: Array<string>;
    /**
     * NUMA nodes per hypervisor name (admins/managers only)
     */
    numa_by_hypervisor?: {
        [key: string]: Array<number>;
    };
};

export type ReservablesInput = {
    /**
     * List of IDs of vGPUs that can be reserved for the desktop. If None, no vGPUs can be reserved nor used. Each ID must be a valid vGPU ID.
     */
    vgpus?: Array<string> | null;
};

/**
 * ReservablesListResponse
 *
 * Response model for reservables list
 */
export type ReservablesListResponse = {
    reservables: Array<string>;
};

/**
 * Resource
 *
 * Deployment resource model
 */
export type Resource = {
    resource_file: string | null;
};

/**
 * ResourceCountResponse
 *
 * Response shape for ``POST /analytics/resources/count``.
 *
 * Service ``AnalyticsProcessed.resource_count`` writes one key per
 * resource: desktops, templates, media, users, groups, deployments.
 */
export type ResourceCountResponse = {
    desktops?: number;
    templates?: number;
    media?: number;
    users?: number;
    groups?: number;
    deployments?: number;
};

/**
 * RetryAllTasksResponse
 *
 * Per-outcome counts of a bulk retry; ``skipped`` are typed refusals.
 */
export type RetryAllTasksResponse = {
    retried?: number;
    skipped?: number;
    errors?: number;
};

/**
 * RoleResponse
 *
 * Single role response
 */
export type RoleResponse = {
    id: string;
    name?: string | null;
    description?: string | null;
    sortorder?: number | null;
};

/**
 * SchedulerBookingsJob
 *
 * One row of ``GET /admin/items/scheduler/jobs/bookings``.
 *
 * Same shape as ``SchedulerSystemJob`` plus the ``kwargs`` blob the
 * bookings scheduler stores per job.
 */
export type SchedulerBookingsJob = {
    id: string;
    name?: string | null;
    kind?: string | null;
    /**
     * UTC epoch seconds — the apiv3 contract returned a float here
     */
    next_run_time?: number | null;
    kwargs?: {
        [key: string]: unknown;
    } | null;
};

/**
 * SchedulerSystemJob
 *
 * One row of ``GET /admin/items/scheduler/jobs/system``.
 *
 * Plucked from the ``scheduler_jobs`` table by
 * ``AdminSchedulerService.get_system_jobs``.
 */
export type SchedulerSystemJob = {
    id: string;
    name?: string | null;
    kind?: string | null;
    /**
     * UTC epoch seconds — the apiv3 contract returned a float here
     */
    next_run_time?: number | null;
};

/**
 * SecondaryGroupsData
 */
export type SecondaryGroupsData = {
    id: string;
    name: string;
};

/**
 * SelectedAllowed
 */
export type SelectedAllowed = {
    groups: boolean | Array<string>;
    users: boolean | Array<string>;
};

/**
 * SetDeploymentVisibilityRequest
 *
 * Idempotent SET of ``tag_visible`` over a selected subset.
 *
 * The selectors are ANDed and ``None``/omitted means "all", so one
 * route covers the whole deployment, a desktop column, a user row, a
 * single matrix cell, or an arbitrary set.
 */
export type SetDeploymentVisibilityRequest = {
    /**
     * Target visibility. Idempotent: this is a SET, not a toggle.
     */
    visible: boolean;
    /**
     * Restrict to these user ids. null/omitted means every user of the deployment.
     */
    users?: Array<string> | null;
    /**
     * Restrict to these deployment desktops (create_dict[*].tag_desktop_id). null/omitted means every one.
     */
    tag_desktop_ids?: Array<string> | null;
    /**
     * Restrict to these exact desktop ids; when present the other two selectors are ignored. Ids outside the deployment are dropped. Used by the frontend Undo, which has to restore a prior state that is not a users x desktops rectangle.
     */
    desktop_ids?: Array<string> | null;
    /**
     * When hiding, also force-stop the affected Started desktops. Ignored when visible=true. Defaults to False -- unlike the legacy /toggle-visibility, whose apiv3 contract defaults to True -- because a matrix cell/row/column edit must never stop a desktop implicitly.
     */
    stop_started_domains?: boolean;
};

/**
 * SharedDeployment
 *
 * Shared deployment response model
 */
export type SharedDeployment = {
    /**
     * ID of the shared deployment
     */
    id: string;
    /**
     * Name of the shared deployment
     */
    name: string;
    /**
     * Description of the shared deployment
     */
    description: string | null;
    /**
     * Image associated with the shared deployment (may be missing on legacy rows)
     */
    image?: Image | null;
    /**
     * Owner of the shared deployment
     */
    user: SharedDeploymentUser;
    /**
     * Total number of desktops in the shared deployment
     */
    total_desktops: number;
    /**
     * Number of started desktops in the shared deployment
     */
    started_desktops: number;
    /**
     * If true, the desktop needs to be booked before use. If false, no booking is required.
     */
    needs_booking?: boolean;
    /**
     * Start time of the next booking for the desktop. If None, no booking is scheduled.
     */
    next_booking_start?: string | null;
    /**
     * End time of the next booking for the desktop. If None, no booking is scheduled.
     */
    next_booking_end?: string | null;
    /**
     * Current booking id. Only set when there is an active booking.
     */
    booking_id?: string | boolean | null;
};

/**
 * SharedDeploymentUser
 *
 * Owner info for a shared deployment
 */
export type SharedDeploymentUser = {
    /**
     * Name of the deployment owner
     */
    name?: string | null;
    /**
     * Photo URL of the deployment owner
     */
    photo?: string | null;
};

/**
 * SharedDeploymentsResponse
 */
export type SharedDeploymentsResponse = {
    /**
     * List of deployments that have been shared with the user
     */
    deployments: Array<SharedDeployment>;
};

/**
 * SharedStorageStallResponse
 *
 * The install-level shared-storage condition the engine declares.
 */
export type SharedStorageStallResponse = {
    active?: boolean;
    since?: number | null;
    cleared_at?: number | null;
    hypervisors?: Array<string>;
    mounts?: Array<string>;
    online?: number | null;
};

/**
 * SharedTemplateUserInfo
 */
export type SharedTemplateUserInfo = {
    /**
     * ID of the user that allowed the template
     */
    id: string;
    /**
     * Name of the user that allowed the template
     */
    name: string;
    /**
     * Photo of the user that allowed the template
     */
    photo?: string | null;
};

/**
 * SimpleResponse
 *
 * To be returned in response of creation, update or delete
 */
export type SimpleResponse = {
    id: string;
};

/**
 * SimpleResponsePlural
 *
 * To be returned in response of creation, update or delete
 */
export type SimpleResponsePlural = {
    ids: Array<unknown>;
};

/**
 * SmtpConfigRequest
 *
 * Request for SMTP configuration
 */
export type SmtpConfigRequest = {
    host?: string | null;
    port?: number | null;
    username?: string | null;
    password?: string | null;
    enabled?: boolean | null;
    from?: string | null;
};

/**
 * SmtpConfigResponse
 *
 * Response shape for ``GET /smtp`` and ``PUT /smtp``.
 *
 * ``password`` and ``from`` are only emitted to internal service callers
 * (notifier needs them to SMTP-AUTH); admin-UI sessions get the redacted
 * shape, enforced by the route stripping those keys before constructing
 * the model. ``response_model_exclude_none=True`` keeps the redacted
 * shape free of ``"password": null`` noise.
 */
export type SmtpConfigResponse = {
    host?: string | null;
    port?: number | null;
    username?: string | null;
    password?: string | null;
    from?: string | null;
    enabled?: boolean | null;
};

/**
 * SmtpTestResponse
 *
 * Response for SMTP test
 */
export type SmtpTestResponse = {
    result: boolean;
    error?: string | null;
};

/**
 * SocketioEmitRequest
 *
 * Single socketio event.
 */
export type SocketioEmitRequest = {
    event?: string | null;
    data?: unknown | null;
    namespace?: string | null;
    room?: string | null;
};

/**
 * StatsCategoriesDeploymentsResponse
 */
export type StatsCategoriesDeploymentsResponse = {
    categories: {
        [key: string]: number;
    };
};

/**
 * StatsCategoriesResponse
 */
export type StatsCategoriesResponse = {
    category: {
        [key: string]: StatsCategoryDetail;
    };
};

/**
 * StatsCategoryDesktopsDetail
 */
export type StatsCategoryDesktopsDetail = {
    total: number;
    status: {
        [key: string]: number;
    };
};

/**
 * StatsCategoryDetail
 */
export type StatsCategoryDetail = {
    users: StatsCategoryUsersDetail;
    desktops: StatsCategoryDesktopsDetail;
    templates: StatsCategoryTemplatesDetail;
};

/**
 * StatsCategoryTemplatesDetail
 */
export type StatsCategoryTemplatesDetail = {
    total: number;
    status: {
        [key: string]: number;
    };
};

/**
 * StatsCategoryUsersDetail
 */
export type StatsCategoryUsersDetail = {
    total: number;
    status: {
        [key: string]: number;
    };
    roles: {
        [key: string]: number;
    };
};

/**
 * StatsDomainsStatusResponse
 */
export type StatsDomainsStatusResponse = {
    desktop?: {
        [key: string]: number;
    };
    template?: {
        [key: string]: number;
    };
};

/**
 * StatsGenericResponse
 *
 * Permissive response shape for the stats endpoints whose service
 * methods return varied / nested dicts (general stats, desktop status,
 * category status, category limits, kind/state breakdowns, started-
 * count). The webapp admin renders these per-key.
 */
export type StatsGenericResponse = {
    [key: string]: unknown;
};

/**
 * StatsKindDesktop
 */
export type StatsKindDesktop = {
    id: string;
    user: string;
};

/**
 * StatsKindHypervisor
 */
export type StatsKindHypervisor = {
    id: string;
    status: string;
    only_forced: boolean;
    degraded?: boolean | null;
    degraded_reason?: string | null;
};

/**
 * StatsKindTemplate
 */
export type StatsKindTemplate = {
    id: string;
};

/**
 * StatsKindUser
 */
export type StatsKindUser = {
    id: string;
    role?: string | null;
    category?: string | null;
    group?: string | null;
};

/**
 * StatusBarNotificationResponse
 *
 * Status bar notification response model (can be None)
 */
export type StatusBarNotificationResponse = {
    text?: string | null;
    level?: string | null;
    migration_config?: {
        [key: string]: unknown;
    } | null;
};

/**
 * StorageBatchIdsRequest
 *
 * Request body for batch operations with storage IDs
 */
export type StorageBatchIdsRequest = {
    /**
     * List of storage IDs
     */
    ids: Array<string>;
};

/**
 * StorageConvertRequest
 *
 * Request body for converting a storage
 */
export type StorageConvertRequest = {
    /**
     * New storage type: qcow2, vmdk, raw
     */
    new_storage_type: string;
    /**
     * Status for the new storage
     */
    new_storage_status?: string;
    /**
     * Whether to compress the output
     */
    compress?: boolean;
    /**
     * Task priority
     */
    priority?: string;
};

/**
 * StorageConvertResponse
 *
 * Response for storage conversion
 */
export type StorageConvertResponse = {
    new_storage_id: string;
    task_id: string;
};

/**
 * StorageCreateRequest
 *
 * Request body for creating a new storage
 */
export type StorageCreateRequest = {
    /**
     * Usage: desktop, template
     */
    usage: string;
    /**
     * Disk format: qcow2, vmdk
     */
    storage_type: string;
    /**
     * Storage ID to be used as backing file
     */
    parent: string;
    /**
     * Size of new storage like qemu-img command
     */
    size: string;
    /**
     * User ID of the owner. If not specified, the JWT user_id is used
     */
    user_id?: string | null;
};

/**
 * StorageCreateResponse
 *
 * Response for storage creation
 */
export type StorageCreateResponse = {
    storage_id: string;
    task_id: string;
};

/**
 * StorageDerivativesResponse
 *
 * Response for has-derivatives check
 */
export type StorageDerivativesResponse = {
    derivatives: number;
};

/**
 * StorageDetailResponse
 *
 * Raw storage row returned by ``GET /item/storage/{storage_id}``.
 *
 * Mirrors the well-known columns of the ``storage`` RethinkDB table.
 * Unknown extras are dropped (default ``extra='ignore'``); the
 * hyphenated ``qemu-img-info`` key is aliased so the model can be
 * instantiated directly from the raw row.
 */
export type StorageDetailResponse = {
    id: string;
    type?: string | null;
    status?: string | null;
    directory_path?: string | null;
    parent?: string | null;
    user_id?: string | null;
    perms?: Array<string> | null;
    status_logs?: Array<StorageStatusLog> | null;
    has_pending_task?: boolean | null;
    'qemu-img-info'?: StorageQemuImgInfo | null;
    storages_with_uuid?: Array<StoragesWithUuidEntry> | null;
};

/**
 * StorageDomain
 *
 * Domain using storage
 */
export type StorageDomain = {
    id: string;
    name: string;
    status: DesktopStatusEnum;
};

/**
 * StorageItem
 *
 * Storage item model.
 *
 * ``actual_size`` / ``virtual_size`` are populated by
 * ``StorageProcessed.parse_disks`` from the row's ``qemu-img-info``
 * sub-document; rows with no ``qemu-img-info`` (e.g. disks queued
 * for creation, or disks whose qemu-img info hasn't been refreshed
 * yet) drop the keys entirely. Likewise ``last`` is the most recent
 * ``status_logs`` timestamp — disks with no status history don't
 * carry it. Declaring all three required surfaced as a 500 from
 * ``GET /api/v4/items/storage/get-ready`` (Bug 34) the moment the
 * user table contained any such row.
 */
export type StorageItem = {
    category: string;
    domains: Array<StorageDomain>;
    id: string;
    user_id: string;
    user_name: string;
    actual_size?: number | null;
    virtual_size?: number | null;
    last?: number | null;
};

/**
 * StorageLaneHealthResponse
 *
 * Lane-centric health of the storage queues: orphan lanes (jobs but no
 * consumer, so tasks stall). ``healthy`` is true when there are none.
 */
export type StorageLaneHealthResponse = {
    orphan_pools: Array<string>;
    orphans: Array<OrphanPool>;
    healthy: boolean;
    truncated_lanes?: number;
    coverage_known?: boolean;
};

/**
 * StorageMaintenanceRequest
 *
 * Request body for setting storage to maintenance
 */
export type StorageMaintenanceRequest = {
    action?: string;
};

/**
 * StorageMoveByPathRequest
 *
 * Request body for moving storage by path
 */
export type StorageMoveByPathRequest = {
    /**
     * Absolute destination path
     */
    dest_path: string;
    /**
     * Task priority
     */
    priority?: string;
};

/**
 * StoragePathRequest
 *
 * Request body for set/delete path operations
 */
export type StoragePathRequest = {
    /**
     * File path
     */
    path: string;
    /**
     * Task priority
     */
    priority?: string;
    /**
     * Number of retries
     */
    retry?: number;
};

/**
 * StoragePoolByPathRequest
 *
 * Request body for getting a storage pool by path
 */
export type StoragePoolByPathRequest = {
    path: string;
};

/**
 * StoragePoolCreateRequest
 *
 * Request body for creating a storage pool
 */
export type StoragePoolCreateRequest = {
    id?: string | null;
    name: string;
    description: string;
    mountpoint: string;
    enabled: boolean;
    categories?: Array<string>;
    paths?: {
        [key: string]: Array<{
            [key: string]: unknown;
        }>;
    } | null;
    allowed?: {
        [key: string]: unknown;
    } | null;
    read?: boolean | null;
    write?: boolean | null;
    startable?: boolean | null;
    unused_desktops_cutoff_time?: number | null;
    qos_disk_id?: string | null;
};

/**
 * StoragePoolListResponse
 *
 * Response model for a list of storage pools
 */
export type StoragePoolListResponse = {
    storage_pools: Array<StoragePoolResponse>;
};

/**
 * StoragePoolMutationResponse
 *
 * Response for create/update: advisory warnings (never blocks the write).
 */
export type StoragePoolMutationResponse = {
    warnings?: Array<StoragePoolWarning>;
};

/**
 * StoragePoolPendingResponse
 *
 * Drain-status of a pool: whether it is quiescent enough to disable/delete.
 */
export type StoragePoolPendingResponse = {
    id: string;
    enabled: boolean;
    categories: number;
    disks: number;
    queued_tasks: number;
    coverage: number;
    drained: boolean;
};

/**
 * StoragePoolResponse
 *
 * Response model for a storage pool
 */
export type StoragePoolResponse = {
    id: string;
    name?: string | null;
    description?: string | null;
    mountpoint?: string | null;
    enabled?: boolean | null;
    enabled_virt?: boolean | null;
    categories?: Array<string> | null;
    paths?: {
        [key: string]: unknown;
    } | null;
    allowed?: {
        [key: string]: unknown;
    } | null;
    read?: boolean | null;
    write?: boolean | null;
    startable?: boolean | null;
    unused_desktops_cutoff_time?: number | null;
    is_default?: boolean | null;
    categories_names?: Array<{
        [key: string]: string;
    }> | null;
    storages?: number | null;
    hypers?: number | null;
    qos_disk_id?: string | null;
    physical_usage?: {
        [key: string]: unknown;
    } | null;
    physical_usage_devices?: Array<{
        [key: string]: unknown;
    }> | null;
    physical_usage_reason?: string | null;
};

/**
 * StoragePoolUpdateRequest
 *
 * Request body for updating a storage pool
 */
export type StoragePoolUpdateRequest = {
    id?: string | null;
    name?: string | null;
    description?: string | null;
    mountpoint?: string | null;
    enabled?: boolean | null;
    enabled_virt?: boolean | null;
    categories?: Array<string> | null;
    paths?: {
        [key: string]: Array<{
            [key: string]: unknown;
        }>;
    } | null;
    allowed?: {
        [key: string]: unknown;
    } | null;
    read?: boolean | null;
    write?: boolean | null;
    startable?: boolean | null;
    unused_desktops_cutoff_time?: number | null;
    qos_disk_id?: string | null;
};

/**
 * StoragePoolWarning
 *
 * A non-blocking advisory returned by a storage-pool mutation.
 */
export type StoragePoolWarning = {
    code: string;
    message: string;
    disks?: number | null;
    queued_tasks?: number | null;
};

/**
 * StorageQemuImgInfo
 *
 * The ``qemu-img-info`` sub-document on a storage row.
 *
 * Stored in RethinkDB with hyphenated keys; aliased here so callers can
 * populate the model from the raw row via ``**row``.
 */
export type StorageQemuImgInfo = {
    'actual-size'?: number | null;
    'virtual-size'?: number | null;
    'backing-filename'?: string | null;
    'full-backing-filename'?: string | null;
    'cluster-size'?: number | null;
    'dirty-flag'?: boolean | null;
    filename?: string | null;
    format?: string | null;
};

/**
 * StorageReadyResponse
 *
 * List of ready storage items
 */
export type StorageReadyResponse = {
    items: Array<StorageItem>;
};

/**
 * StorageRecreateRequest
 *
 * Request body for recreating a storage
 */
export type StorageRecreateRequest = {
    /**
     * Task priority
     */
    priority?: string;
    /**
     * Number of retries for the task
     */
    retry?: number;
};

/**
 * StorageRepairBatchResponse
 *
 * How many disks a batch repair queued, and how many it skipped because they
 * have descendants (never repaired automatically).
 */
export type StorageRepairBatchResponse = {
    queued: number;
    skipped_has_descendants?: number;
};

/**
 * StorageRsyncToPathRequest
 *
 * Request body for rsyncing storage to a path
 */
export type StorageRsyncToPathRequest = {
    /**
     * Absolute destination path
     */
    destination_path: string;
    /**
     * Bandwidth limit in KBytes/s
     */
    bwlimit?: number | null;
    /**
     * Remove source file after rsync
     */
    remove_source_file?: boolean;
    /**
     * Task priority
     */
    priority?: string;
};

/**
 * StorageRsyncToStoragePoolRequest
 *
 * Request body for rsyncing storage to a storage pool
 */
export type StorageRsyncToStoragePoolRequest = {
    /**
     * Destination storage pool ID
     */
    destination_storage_pool_id: string;
    /**
     * Bandwidth limit in KBytes/s
     */
    bwlimit?: number | null;
    /**
     * Remove source file after rsync
     */
    remove_source_file?: boolean;
    /**
     * Task priority
     */
    priority?: string;
};

/**
 * StorageSchedulerConfigRequest
 *
 * Partial update of the live storage-governor config; only the provided
 * keys are written (siblings preserved). Bounds are enforced at the edge so an
 * out-of-range value is rejected rather than bricking the elastic workers.
 */
export type StorageSchedulerConfigRequest = {
    enabled?: boolean | null;
    psi_limit?: number | null;
    max_heavy?: number | null;
    backoff?: number | null;
    category_weights?: {
        [key: string]: number;
    } | null;
    category_max_inflight?: {
        [key: string]: number;
    } | null;
    category_default_max_inflight?: number | null;
};

/**
 * StorageSchedulerConfigResponse
 *
 * Live storage-governor config (``config[1].storage_scheduler``).
 */
export type StorageSchedulerConfigResponse = {
    enabled?: boolean;
    psi_limit?: number;
    max_heavy?: number;
    backoff?: number;
    category_weights?: {
        [key: string]: number;
    };
    category_max_inflight?: {
        [key: string]: number;
    };
    category_default_max_inflight?: number | null;
};

/**
 * StorageStatusDomain
 *
 * A domain entry in a storage's statuses payload.
 */
export type StorageStatusDomain = {
    id: string;
    status: string;
    kind: string;
};

/**
 * StorageStatusLog
 *
 * A single status-log entry on a storage row
 */
export type StorageStatusLog = {
    status: string;
    time: number;
};

/**
 * StorageStatusesResponse
 *
 * Status of a storage and its associated domains.
 *
 * Mirrors ``Storage.statuses``: a single object (not a list), holding the
 * storage's own status plus the status of every domain that uses it.
 */
export type StorageStatusesResponse = {
    id: string;
    status: string;
    path: string;
    pool: string;
    domains: Array<StorageStatusDomain>;
};

/**
 * StorageSweepConfigRequest
 *
 * Partial update of the sweep config; omitted keys keep their stored value.
 */
export type StorageSweepConfigRequest = {
    enabled?: boolean | null;
    hour?: number | null;
    minute?: number | null;
    max_disks?: number | null;
    max_bytes?: number | null;
    sparsify?: boolean | null;
    repair_leaks?: boolean | null;
    check_integrity?: boolean | null;
    check_max_age_days?: number | null;
};

/**
 * StorageSweepConfigResponse
 *
 * Effective storage-sweep config: stored values over presentation defaults.
 */
export type StorageSweepConfigResponse = {
    enabled: boolean;
    hour: number;
    minute: number;
    max_disks: number;
    max_bytes: number;
    sparsify: boolean;
    repair_leaks: boolean;
    check_integrity: boolean;
    check_max_age_days: number;
};

/**
 * StorageSweepRunRequest
 *
 * Per-pass budget/action overrides for a manual sweep run (all optional).
 */
export type StorageSweepRunRequest = {
    max_disks?: number | null;
    max_bytes?: number | null;
    sparsify?: boolean | null;
    repair_leaks?: boolean | null;
    check_integrity?: boolean | null;
};

/**
 * StorageSweepRunResponse
 *
 * Summary of one sweep pass: per-action selected/skipped counts.
 */
export type StorageSweepRunResponse = {
    actions: {
        [key: string]: unknown;
    };
};

/**
 * StorageUsageResponse
 *
 * Response shape for ``POST /analytics/storage``.
 *
 * Service ``AnalyticsProcessed.storage_usage`` always returns both
 * ``media`` and ``domains`` totals in GiB.
 */
export type StorageUsageResponse = {
    /**
     * Total media usage (GiB)
     */
    media?: number;
    /**
     * Total per-user storage usage (GiB)
     */
    domains?: number;
};

/**
 * StorageVirtWinRegRequest
 *
 * Request body for applying a Windows registry patch
 */
export type StorageVirtWinRegRequest = {
    /**
     * Registry patch content
     */
    registry_patch: string;
    /**
     * Number of retries
     */
    retry?: number;
};

/**
 * StoragesWithUuidEntry
 *
 * Phantom-storage diagnostic entry.
 *
 * Shape produced when a ``find`` task discovers extra files matching
 * a storage UUID (see the ``handle_storage_update_pool`` body in
 * ``change-handler/.../task_results/storage.py``):
 * ``{"status": ..., "path": ...}`` per row, with ``id`` merged in by
 * the global aggregation queries (``get_storages_with_uuid``) and
 * ``count`` returned instead of ``path`` by the per-status grouping
 * query (``get_storages_with_uuid_status``). One model serves all
 * four endpoints; only ``status`` is universal.
 */
export type StoragesWithUuidEntry = {
    status: string;
    path?: string | null;
    id?: string | null;
    count?: number | null;
};

/**
 * StreamGroupGauge
 *
 * One consumer group of one redis stream.
 *
 * ``lag`` is the entries the group has never read; ``pending`` the ones it
 * read and has not ACKed. A backlog here is invisible to every queue gauge:
 * the job that produced the entry has already left its rq queue, so a chain
 * waiting to be finalized shows up nowhere else.
 */
export type StreamGroupGauge = {
    stream: string;
    group: string;
    lag?: number;
    pending?: number;
    consumers?: number;
};

/**
 * StreamsGauge
 *
 * The task streams' own health.
 *
 * Not derivable from the queue gauges, and the failure it exposes — the
 * workers keeping up while nothing settles — is otherwise indistinguishable
 * from the change-handler being dead.
 */
export type StreamsGauge = {
    up?: boolean;
    results_length?: number;
    progress_length?: number;
    dead_length?: number;
    groups?: Array<StreamGroupGauge>;
};

/**
 * StuckRecycleBinEntry
 *
 * A recycle bin entry stranded mid-delete ('deleting' or 'queued').
 */
export type StuckRecycleBinEntry = {
    id: string;
    status: RecycleBinStatusEnum;
    owner_id?: string | null;
    item_type?: string | null;
    storages_count?: number;
    last_log?: {
        [key: string]: unknown;
    } | null;
};

/**
 * SuggestedRemovalsResponse
 *
 * Response shape for ``POST /analytics/suggested_removals``.
 */
export type SuggestedRemovalsResponse = {
    empty_deployments?: Array<EmptyDeploymentRow>;
    unused_desktops?: UnusedDesktopsResult;
};

/**
 * SystemLogoResponse
 *
 * Response shape for ``GET /admin/item/logo``.
 *
 * ``data`` is always omitted here — the actual image is served
 * directly by the public ``/logo`` and ``/logo-collapsed`` endpoints,
 * this only reports whether a custom one is currently set.
 */
export type SystemLogoResponse = {
    logo?: BrandingLogo;
    logo_collapsed?: BrandingLogo;
};

/**
 * SystemLogoUpdateData
 *
 * Request body for updating the system-wide default logo.
 */
export type SystemLogoUpdateData = {
    logo?: BrandingLogo | null;
    logo_collapsed?: BrandingLogo | null;
};

/**
 * TableItem
 *
 * Generic admin-table item used by ``GET`` / ``POST`` /admin/items/table.
 *
 * The shape is per-table (interfaces, graphics, qos, …) and the
 * plucked field set varies with the request, so the model is
 * permissive (``ConfigDict(extra="allow")``).
 *
 * ``id`` accepts both ``str`` and ``int`` because RethinkDB's
 * ``config`` singleton row is stored with ``id=1`` (integer), and
 * the same admin route serves every table — see
 * ``Pydantic model vs DB convention`` recurring pattern in the
 * apiv4-migration skill: DB conventions use sentinel types
 * (``False`` / ``int`` / ``None``); Pydantic models added later
 * must accept them rather than forcing the DB to change shape.
 */
export type TableItem = {
    id?: string | number | null;
    [key: string]: unknown | string | number | null | undefined;
};

/**
 * TableListRequest
 *
 * Request body for listing/filtering table items.
 */
export type TableListRequest = {
    /**
     * Item ID or secondary index value
     */
    id?: string | null;
    /**
     * Secondary index to use with id
     */
    index?: string | null;
    /**
     * Field to order results by
     */
    order_by?: string | null;
    /**
     * Fields to pluck from each item
     */
    pluck?: Array<unknown> | {
        [key: string]: unknown;
    } | string | null;
    /**
     * Fields to exclude from each item
     */
    without?: Array<unknown> | {
        [key: string]: unknown;
    } | string | null;
};

/**
 * TaskIdResponse
 *
 * Response containing a task ID
 */
export type TaskIdResponse = {
    task_id: string;
};

/**
 * TaskResponse
 *
 * Single task response
 */
export type TaskResponse = {
    id: string;
    user_id?: string | null;
    task?: string | null;
    status?: string | null;
    progress?: number | null;
    queue?: string | null;
    position?: number | null;
    result?: unknown | null;
    exc_info?: string | null;
    pending?: boolean | null;
    depending_status?: string | null;
    job_status?: string | null;
    storage_id?: string | null;
    args?: Array<unknown> | null;
    kwargs?: {
        [key: string]: unknown;
    } | null;
    dependencies?: Array<{
        [key: string]: unknown;
    }> | null;
    dependents?: Array<{
        [key: string]: unknown;
    }> | null;
    category_id?: string | null;
    category_name?: string | null;
    tier?: string | null;
    enqueued_at?: number | null;
    started_at?: number | null;
    ended_at?: number | null;
    age_seconds?: number | null;
    retries_left?: number | null;
    exc_string?: string | null;
    effective_position?: number | null;
    eta_seconds?: number | null;
    has_consumer?: boolean | null;
    stranded?: boolean | null;
};

/**
 * TemplateCreateRequest
 *
 * Request to create a notification template
 */
export type TemplateCreateRequest = {
    language: string;
    title: string;
    body: string;
    footer: string;
    name?: string | null;
    description?: string | null;
    default?: string | null;
    kind?: string | null;
};

/**
 * TemplateDetailsResponse
 */
export type TemplateDetailsResponse = {
    /**
     * Name of the template.
     */
    name: string;
    /**
     * Description of the template.
     */
    description?: string;
    /**
     * Image associated with the template.
     */
    image: DomainImageOutput;
    /**
     * Number of virtual CPUs allocated to the template.
     */
    vcpu?: number;
    /**
     * Amount of memory (RAM) allocated to the template in GB.
     */
    memory?: number;
    /**
     * List of IDs and names with the boot order that will be followed when booting the template. Each ID will be a valid boot id.
     */
    boot_order: Array<TemplateNamedResource>;
    /**
     * ID and name of the disk bus used by the template, e.g., 'virtio', 'scsi'.
     */
    disk_bus: TemplateNamedResource;
    /**
     * List of IDs and names with the network interfaces that will be attached to the template. Each ID will be a valid network interface id.
     */
    interfaces: Array<TemplateNamedResource>;
    /**
     * List of disks that will be attached to the template. Each disk will have a storage_id and its size.
     */
    disks: Array<TemplateStorage>;
    /**
     * List of IDs and names with the videos order that will be followed. Each ID will be a valid video id.
     */
    videos: Array<TemplateNamedResource>;
    /**
     * List of viewers that will be available to access the template.
     */
    viewers?: Array<DomainViewerEnum>;
    /**
     * If true, the template will be opened in fullscreen mode by default.
     */
    fullscreen?: boolean;
    /**
     * List of ISO images that will be attached to the template. Each ISO will have an ID and a name.
     */
    isos?: Array<TemplateNamedResource> | null;
    /**
     * The template bookable resources.
     */
    reservables?: ApiSchemasDomainsHardwareReservables;
    /**
     * Credentials that will be set in the guest OS of the template.
     */
    credentials: ApiSchemasDomainsHardwareDomainGuestPropertiesGuestPropertiesCredentials;
};

/**
 * TemplateEditRequest
 */
export type TemplateEditRequest = {
    /**
     * Name of the template. Must be unique within the user's templates. If not provided, the current value will be kept.
     */
    name?: string;
    /**
     * Description of the template. If not provided, the current value will be kept.
     */
    description?: string;
    /**
     * Guest properties to be set for the template. If not provided, the current value will be kept.
     */
    guest_properties?: DomainGuestPropertiesInput;
    /**
     * Hardware configuration for the template. If not provided, the current value will be kept.
     */
    hardware?: DomainHardware;
    /**
     * The domain bookable resources. If None, no reservables are available. If not provided, the current value will be kept.
     */
    reservables?: ReservablesInput;
    /**
     * Image to be used for the template. If None or not provided, the current value will be kept.
     */
    image?: DomainImageInput | null;
    /**
     * If set, the desktops will only be able to start on the specified hypervisors.
     */
    forced_hyp?: Array<string> | false;
    /**
     * If set, the desktops will start on the specified hypervisors if available, but can start on other hypervisors if the favourite ones are not available.
     */
    favourite_hyp?: Array<string> | false;
};

/**
 * TemplateListResponse
 *
 * List of notification templates
 */
export type TemplateListResponse = {
    templates: Array<{
        [key: string]: unknown;
    }>;
};

/**
 * TemplateNamedResource
 */
export type TemplateNamedResource = {
    /**
     * ID of the resource, such as a disk, video, or ISO.
     */
    id: string;
    /**
     * Name of the resource, such as a disk, video, or ISO.
     */
    name: string;
};

/**
 * TemplateNonpersistentDesktopResponse
 */
export type TemplateNonpersistentDesktopResponse = {
    /**
     * ID of the non-persistent desktop the user already has from this template and that would be reused instead of creating a new one. None when a new one would be created.
     */
    desktop_id?: string | null;
};

/**
 * TemplatePreviewRequest
 *
 * Request to preview a notification template rendering
 */
export type TemplatePreviewRequest = {
    event: string;
    user_id?: string | null;
    data?: {
        [key: string]: unknown;
    };
};

/**
 * TemplatePreviewResponse
 *
 * Preview of rendered template
 */
export type TemplatePreviewResponse = {
    title?: string | null;
    body?: string | null;
    footer?: string | null;
    channels?: Array<string> | null;
};

/**
 * TemplateResponse
 *
 * Single notification template response
 */
export type TemplateResponse = {
    id?: string | null;
    name?: string | null;
    description?: string | null;
    kind?: string | null;
    default?: string | null;
    lang?: {
        [key: string]: unknown;
    } | null;
    system?: {
        [key: string]: unknown;
    } | null;
    vars?: {
        [key: string]: unknown;
    } | null;
};

/**
 * TemplateSetEnabledRequest
 */
export type TemplateSetEnabledRequest = {
    /**
     * Whether the template is enabled (usable for creating desktops).
     */
    enabled: boolean;
};

/**
 * TemplateStatusEnum
 */
export const TemplateStatusEnum = {
    CREATING: 'Creating',
    CREATING_TEMPLATE: 'CreatingTemplate',
    CREATING_DOMAIN: 'CreatingDomain',
    STARTING_PAUSED: 'StartingPaused',
    STOPPED: 'Stopped',
    FAILED: 'Failed',
    PAUSED: 'Paused',
    UNKNOWN: 'Unknown',
    UPDATING: 'Updating',
    MAINTENANCE: 'Maintenance',
    DOWNLOADING: 'Downloading',
    DOWNLOAD_STARTING: 'DownloadStarting',
    DOWNLOADED: 'Downloaded',
    DOWNLOAD_FAILED: 'DownloadFailed',
    DOWNLOAD_ABORTING: 'DownloadAborting'
} as const;

/**
 * TemplateStatusEnum
 */
export type TemplateStatusEnum = typeof TemplateStatusEnum[keyof typeof TemplateStatusEnum];

/**
 * TemplateStorage
 */
export type TemplateStorage = {
    /**
     * ID of the storage where the disk is located.
     */
    id: string;
    /**
     * Size of the storage in GB.
     */
    size: number;
};

/**
 * TemplateToDesktopRequest
 */
export type TemplateToDesktopRequest = {
    /**
     * Name of the desktop. Must be unique within the user's desktops. If not provided, the template name will be used.
     */
    name?: string | null;
};

/**
 * TemplateTreeDomains
 */
export type TemplateTreeDomains = {
    /**
     * ID of the domain (desktop or template).
     */
    id: string;
    /**
     * Kind of the domain (desktop or template).
     */
    kind: string;
    /**
     * Name of the domain.
     */
    name: string;
    /**
     * ID of the user that owns the domain.
     */
    user: string;
};

/**
 * TemplateTreeResponse
 */
export type TemplateTreeResponse = {
    /**
     * List of desktops and templates that depend on this template.
     */
    domains: Array<TemplateTreeDomains | {
        [key: string]: unknown;
    }>;
    /**
     * List of deployments that depend on this template.
     */
    deployments?: Array<TemplateTreeDomains | {
        [key: string]: unknown;
    }>;
    /**
     * Whether the template has pending desktops or templates.
     */
    pending: boolean;
    /**
     * Whether the template is a duplicate of another template.
     */
    is_duplicated: boolean;
    /**
     * Whether the template has derivatives in other categories.
     */
    cross_category?: boolean;
};

/**
 * TemplateUpdateRequest
 *
 * Request to update a notification template
 */
export type TemplateUpdateRequest = {
    language: string;
    title: string;
    body: string;
    footer: string;
    name?: string | null;
    description?: string | null;
    default?: string | null;
};

/**
 * ToggleVisibilityRequest
 *
 * Optional body for /item/deployment/{id}/toggle-visibility.
 *
 * Vue 2 sends ``{"stop_started_domains": bool}`` so the user can
 * pick whether hiding a deployment also stops its Started desktops;
 * Vue 3 may PUT with no body. Default to True (apiv3 contract).
 */
export type ToggleVisibilityRequest = {
    stop_started_domains?: boolean;
};

/**
 * UnusedDesktopRow
 *
 * One row returned by ``AnalyticsProcessed.get_unused_desktops``.
 *
 * Final pluck restricts output to: id, name, category_name, group_name,
 * username, size.
 */
export type UnusedDesktopRow = {
    id?: string | null;
    name?: string | null;
    category_name?: string | null;
    group_name?: string | null;
    username?: string | null;
    /**
     * Storage size in GiB
     */
    size?: number;
};

/**
 * UnusedDesktopsResult
 *
 * Wrapper produced by ``AnalyticsProcessed.get_unused_desktops``.
 */
export type UnusedDesktopsResult = {
    /**
     * Total size of unused desktops (GiB)
     */
    size?: number;
    desktops?: Array<UnusedDesktopRow>;
};

/**
 * UnusedItemTimeoutRule
 *
 * A single unused item timeout rule.
 *
 * Mirrors apiv3 ``main:api/src/api/schemas/unused_item_timeout.yml``:
 * ``op`` selects which scheduler hook the rule applies to (e.g.
 * ``send_unused_desktops_to_recycle_bin``); ``cutoff_time`` is the
 * threshold in months (scheduler multiplies by 30 days) after which the
 * action fires (nullable for "never"); ``priority`` orders rules with
 * overlapping allowed-sets;
 * ``allowed`` scopes the rule by users/groups/categories/roles.
 */
export type UnusedItemTimeoutRule = {
    id?: string | null;
    name?: string;
    description?: string;
    op?: string;
    cutoff_time?: number | null;
    priority?: number;
    allowed?: ApiSchemasAllowedAllowedOutput;
};

/**
 * UnusedItemTimeoutRuleCreateRequest
 *
 * Request to create an unused item timeout rule.
 *
 * Field set matches the apiv3 ``Cerberus`` schema and the webapp
 * admin form (``recycle_bin_config.js``) which posts
 * ``{name, description, op, cutoff_time, priority}``.
 */
export type UnusedItemTimeoutRuleCreateRequest = {
    name: string;
    description?: string;
    op: string;
    cutoff_time?: 1 | 2 | 3 | 4 | 5 | 6 | 12 | null;
    priority: number;
    allowed?: AllowedInput | null;
};

/**
 * UnusedItemTimeoutRuleUpdateRequest
 *
 * Request to update an unused item timeout rule.
 *
 * Webapp PUT sends the full set of editable fields with optional
 * ``cutoff_time`` (nullable) so the schema mirrors that.
 */
export type UnusedItemTimeoutRuleUpdateRequest = {
    name?: string | null;
    description?: string | null;
    op?: string | null;
    cutoff_time?: 1 | 2 | 3 | 4 | 5 | 6 | 12 | null;
    priority?: number | null;
    allowed?: AllowedInput | null;
};

/**
 * UnusedItemTimeoutRulesResponse
 *
 * List of unused item timeout rules.
 */
export type UnusedItemTimeoutRulesResponse = {
    rules?: Array<UnusedItemTimeoutRule>;
};

/**
 * UpdateBookingEventRequest
 *
 * Request model for updating a booking event.
 *
 * Form payload: ``title``/``start``/``end``. ``element_type`` is
 * preserved for backwards compatibility but unused on the server side.
 */
export type UpdateBookingEventRequest = {
    /**
     * New title for the booking
     */
    title?: string;
    /**
     * New start datetime for the booking
     */
    start: string;
    /**
     * New end datetime for the booking
     */
    end: string;
    /**
     * Deprecated: unused on the server side.
     */
    element_type?: DomainKindEnum | null;
};

/**
 * UpdateReservableItemRequest
 *
 * Request model for updating a reservable item's name and description,
 * and/or delegating it to a category (upstream MR !4546): an empty string
 * clears the delegation (back to admin-only/global).
 */
export type UpdateReservableItemRequest = {
    name?: string | null;
    description?: string | null;
    category?: string | null;
};

/**
 * UsageConsumersCountResponse
 *
 * Count of rows in the ``usage_consumption`` table.
 */
export type UsageConsumersCountResponse = {
    /**
     * Total consumption rows.
     */
    count: number;
};

/**
 * UsageConsumptionRequest
 *
 * Request body for usage consumption between dates.
 */
export type UsageConsumptionRequest = {
    /**
     * Start date in YYYY-MM-DD format
     */
    start_date?: string | null;
    /**
     * End date in YYYY-MM-DD format
     */
    end_date?: string | null;
    /**
     * List of item IDs to filter
     */
    items_ids?: Array<string> | null;
    /**
     * List of item IDs for ownership checks
     */
    item_ids?: Array<string> | null;
    /**
     * Type of item
     */
    item_type?: string | null;
    /**
     * Grouping parameters
     */
    grouping?: Array<string> | null;
};

/**
 * UsageCreditCreateRequest
 *
 * Request body for creating a usage credit.
 */
export type UsageCreditCreateRequest = {
    /**
     * List of item IDs
     */
    item_ids: Array<string>;
    /**
     * Consumer type
     */
    item_consumer: string;
    /**
     * Item type
     */
    item_type: string;
    /**
     * Grouping ID
     */
    grouping_id: string;
    /**
     * Limit ID
     */
    limit_id: string;
    /**
     * Start date in YYYY-MM-DD format
     */
    start_date: string;
    /**
     * End date in YYYY-MM-DD format (null for no end)
     */
    end_date?: string | null;
};

/**
 * UsageCreditIntervalResponse
 *
 * Slice of the credit timeline returned by ``find_in_period``.
 *
 * The endpoint produces a chain of ``before``/``inner``/``after``
 * intervals; intervals without an active credit carry ``limits=None``
 * and just bracket the dates of the gap.
 */
export type UsageCreditIntervalResponse = {
    /**
     * Credit ID, when present
     */
    id?: string | null;
    /**
     * Item ID
     */
    item_id?: string | null;
    /**
     * Consumer type
     */
    item_consumer?: string | null;
    /**
     * Item type
     */
    item_type?: string | null;
    /**
     * Grouping ID
     */
    grouping_id?: string | null;
    /**
     * Interval start (string).
     */
    start_date: string;
    /**
     * Interval end (string).
     */
    end_date: string;
    /**
     * Limit values for the interval; ``None`` for gap rows.
     */
    limits?: UsageLimitValuesResponse | null;
    limits_id?: string | null;
    limits_name?: string | null;
    limits_desc?: string | null;
};

/**
 * UsageCreditResponse
 *
 * Row of the ``usage_credit`` table.
 *
 * The list endpoint merges ``category_name``/``item_description``/
 * ``grouping_name`` onto the row; the by-id endpoint omits them — both
 * are declared optional here so the same model serves both.
 */
export type UsageCreditResponse = {
    /**
     * Credit ID
     */
    id?: string | null;
    /**
     * Item ID
     */
    item_id: string;
    /**
     * Consumer type
     */
    item_consumer?: string | null;
    /**
     * Item type
     */
    item_type: string;
    /**
     * Grouping ID
     */
    grouping_id: string;
    /**
     * Credit start (datetime or string).
     */
    start_date?: unknown | null;
    /**
     * Credit end (datetime or string, may be null).
     */
    end_date?: unknown | null;
    /**
     * Denormalised limit values.
     */
    limits?: UsageLimitValuesResponse | null;
    /**
     * Source limit id.
     */
    limits_id?: string | null;
    /**
     * Source limit name.
     */
    limits_name?: string | null;
    /**
     * Source limit description.
     */
    limits_desc?: string | null;
    /**
     * Category name (joined; only on the list endpoint).
     */
    category_name?: string | null;
    /**
     * Item description (joined; only on the list endpoint).
     */
    item_description?: string | null;
    /**
     * Grouping name (joined; only on the list endpoint).
     */
    grouping_name?: string | null;
};

/**
 * UsageCreditUpdateRequest
 *
 * Request body for updating a usage credit.
 */
export type UsageCreditUpdateRequest = {
    /**
     * Credit ID
     */
    id: string;
    /**
     * Item ID
     */
    item_id?: string | null;
    /**
     * Item type
     */
    item_type?: string | null;
    /**
     * Grouping ID
     */
    grouping_id?: string | null;
    /**
     * Start date
     */
    start_date?: string | null;
    /**
     * End date
     */
    end_date?: string | null;
    /**
     * Limit values
     */
    limits?: {
        [key: string]: unknown;
    } | null;
};

/**
 * UsageDistinctItemResponse
 *
 * Distinct-item dropdown row from the ``item_consumer`` index.
 */
export type UsageDistinctItemResponse = {
    /**
     * Item ID
     */
    item_id: string;
    /**
     * Item name
     */
    item_name?: string | null;
    /**
     * Category name (joined from ``categories``).
     */
    category_name?: string | null;
    /**
     * Owner username when the consumer is desktop/template.
     */
    username?: string | null;
    /**
     * Consumer category id (only present when filtered).
     */
    item_consumer_category_id?: string | null;
};

/**
 * UsageGroupingCreateRequest
 *
 * Request body for creating a usage grouping.
 */
export type UsageGroupingCreateRequest = {
    /**
     * Grouping name
     */
    name: string;
    /**
     * Grouping description
     */
    desc?: string | null;
    /**
     * Item type
     */
    item_type: string;
    /**
     * List of parameter IDs
     */
    parameters: Array<string>;
};

/**
 * UsageGroupingResponse
 *
 * Row of the ``usage_grouping`` table or a synthetic system grouping.
 */
export type UsageGroupingResponse = {
    /**
     * Grouping ID
     */
    id: string;
    /**
     * Grouping name
     */
    name: string;
    /**
     * Grouping description
     */
    desc?: string | null;
    /**
     * Item type
     */
    item_type: string;
    /**
     * System pseudo-grouping kind (all / system / custom).
     */
    item_sub_type?: string | null;
    /**
     * Parameter IDs in this grouping.
     */
    parameters?: Array<string>;
};

/**
 * UsageGroupingUpdateRequest
 *
 * Request body for updating a usage grouping.
 */
export type UsageGroupingUpdateRequest = {
    /**
     * Grouping ID
     */
    id: string;
    /**
     * Grouping name
     */
    name?: string | null;
    /**
     * Grouping description
     */
    desc?: string | null;
    /**
     * Item type
     */
    item_type?: string | null;
    /**
     * List of parameter IDs
     */
    parameters?: Array<string> | null;
};

/**
 * UsageGroupingsDropdownResponse
 *
 * Dropdown shape for ``/admin/items/usage/groupings_dropdown``.
 *
 * Inner dicts are keyed by item_type (``desktop``/``media``/``storage``/
 * ``user``) and carry the system pseudo-groupings + the user-defined
 * rows for that item type.
 */
export type UsageGroupingsDropdownResponse = {
    system?: {
        [key: string]: Array<UsageGroupingResponse>;
    };
    custom?: {
        [key: string]: Array<UsageGroupingResponse>;
    };
};

/**
 * UsageItemConsumption
 *
 * A single ``(date, item)`` consumption row used by the consumption
 * list and the start/end comparison endpoints.
 *
 * ``inc`` and ``abs`` are dicts keyed by parameter id (``{param_id:
 * float}``) and vary in shape with the requested ``grouping_params``,
 * so they are typed permissively as ``Dict[str, Any]``.
 */
export type UsageItemConsumption = {
    /**
     * Item name
     */
    name?: string | null;
    /**
     * Bucket date (datetime or date string).
     */
    date?: unknown | null;
    /**
     * Per-parameter increment values.
     */
    inc?: {
        [key: string]: unknown;
    };
    /**
     * Per-parameter absolute values.
     */
    abs?: {
        [key: string]: unknown;
    };
    /**
     * Item ID
     */
    item_id?: string | null;
    /**
     * Item name
     */
    item_name?: string | null;
    /**
     * Item type
     */
    item_type?: string | null;
    /**
     * Consumer type
     */
    item_consumer?: string | null;
    /**
     * Bucket granularity tier (daily / weekly / monthly).
     */
    granularity?: string | null;
};

/**
 * UsageLimitCreateRequest
 *
 * Request body for creating a usage limit.
 */
export type UsageLimitCreateRequest = {
    /**
     * Limit name
     */
    name: string;
    /**
     * Limit description
     */
    desc: string;
    /**
     * Limit values
     */
    limits: UsageLimitsValues;
};

/**
 * UsageLimitResponse
 *
 * Row of the ``usage_limit`` table.
 */
export type UsageLimitResponse = {
    /**
     * Limit ID
     */
    id?: string | null;
    /**
     * Limit name
     */
    name: string;
    /**
     * Limit description
     */
    desc?: string | null;
    /**
     * Limit values
     */
    limits: UsageLimitValuesResponse;
};

/**
 * UsageLimitUpdateRequest
 *
 * Request body for updating a usage limit.
 */
export type UsageLimitUpdateRequest = {
    /**
     * Limit name
     */
    name: string;
    /**
     * Limit description
     */
    desc: string;
    /**
     * Limit values
     */
    limits: UsageLimitsValues;
};

/**
 * UsageLimitValuesResponse
 *
 * Validated limit values stored on a ``usage_limit`` row.
 */
export type UsageLimitValuesResponse = {
    /**
     * Hard limit value
     */
    hard: number;
    /**
     * Soft limit value
     */
    soft: number;
    /**
     * Expected minimum value
     */
    exp_min: number;
    /**
     * Expected maximum value
     */
    exp_max: number;
};

/**
 * UsageLimitsValues
 *
 * Limits values.
 */
export type UsageLimitsValues = {
    /**
     * Hard limit value
     */
    hard: number;
    /**
     * Soft limit value
     */
    soft: number;
    /**
     * Expected minimum value
     */
    exp_min: number;
    /**
     * Expected maximum value
     */
    exp_max: number;
};

/**
 * UsageOverlappingResponse
 *
 * Response for ``/admin/item/usage/check/overlapping/...``.
 *
 * ``check_overlapping`` returns ``None`` (no conflict) or a descriptor
 * of the conflicting credit and the trim/delete action; ``None`` is
 * coerced to ``{}`` by the route, so every field is optional.
 */
export type UsageOverlappingResponse = {
    /**
     * The conflicting credit's id.
     */
    credit_id?: string | null;
    /**
     * Reconciliation verb (``cut`` or ``deleted``).
     */
    action?: string | null;
    /**
     * New start_date when ``action='cut'`` and the tail moves.
     */
    start_date?: unknown | null;
    /**
     * New end_date when ``action='cut'`` and the head moves.
     */
    end_date?: unknown | null;
};

/**
 * UsageParameterCreateRequest
 *
 * Request body for creating a usage parameter.
 */
export type UsageParameterCreateRequest = {
    /**
     * Parameter ID
     */
    id: string;
    /**
     * Parameter name
     */
    name: string;
    /**
     * Parameter description
     */
    desc: string;
    /**
     * Whether this is a custom parameter
     */
    custom: boolean;
    /**
     * Parameter formula
     */
    formula: string;
    /**
     * Item type this parameter applies to
     */
    item_type: string;
    /**
     * Parameter units
     */
    units: string;
};

/**
 * UsageParameterIdsRequest
 *
 * Request body for listing usage parameters by IDs.
 */
export type UsageParameterIdsRequest = {
    /**
     * List of parameter IDs
     */
    ids?: Array<string> | null;
};

/**
 * UsageParameterResponse
 *
 * Row of the ``usage_parameter`` table.
 */
export type UsageParameterResponse = {
    /**
     * Parameter ID
     */
    id: string;
    /**
     * Parameter name
     */
    name: string;
    /**
     * Parameter description
     */
    desc?: string | null;
    /**
     * Whether this is a custom parameter
     */
    custom: boolean;
    /**
     * Default value for the parameter.
     */
    default?: number | null;
    /**
     * Parameter formula
     */
    formula?: string | null;
    /**
     * Item type this parameter applies to
     */
    item_type: string;
    /**
     * Parameter units
     */
    units?: string | null;
};

/**
 * UsageParameterUpdateRequest
 *
 * Request body for updating a usage parameter.
 */
export type UsageParameterUpdateRequest = {
    /**
     * Parameter ID
     */
    id: string;
    /**
     * Parameter name
     */
    name?: string | null;
    /**
     * Parameter description
     */
    desc?: string | null;
    /**
     * Whether this is a custom parameter
     */
    custom: boolean;
    /**
     * Parameter formula
     */
    formula?: string | null;
    /**
     * Item type this parameter applies to
     */
    item_type?: string | null;
    /**
     * Parameter units
     */
    units?: string | null;
};

/**
 * UsageResetDatesRequest
 *
 * Request body for adding reset dates.
 */
export type UsageResetDatesRequest = {
    /**
     * List of dates in MM/DD/YYYY format
     */
    date_list: Array<string>;
};

/**
 * UsageRetentionConfig
 *
 * Tiered retention policy for ``usage_consumption``.
 *
 * Daily rows are kept untouched for ``daily_months`` months. Rows
 * aged between ``daily_months`` and ``weekly_months`` collapse into
 * weekly buckets (Monday-of-week, summed ``inc``, last ``abs`` of
 * the bucket). Older rows collapse into monthly buckets (first-of-
 * month, same aggregation). When ``total_months`` is set, anything
 * older is deleted; when unset, monthly history is retained
 * indefinitely.
 *
 * Per-field bounds are enforced by Pydantic. The cross-field
 * invariants ``weekly_months > daily_months`` and (when set)
 * ``total_months >= weekly_months`` are enforced at the route /
 * service boundary via :meth:`assert_tier_ordering`. They are NOT
 * a ``model_validator`` because raising a ``ValueError`` from one
 * propagates a ``RequestValidationError`` whose ``ctx['error']``
 * holds the raw ``ValueError``, and the project's global handler
 * serialises ``exc.errors()`` to JSON — non-serialisable types in
 * ``ctx`` would crash the handler with a 500.
 */
export type UsageRetentionConfig = {
    /**
     * Months of recent daily granularity to keep untouched.
     */
    daily_months?: number;
    /**
     * Months from now until rows collapse from weekly to monthly.
     */
    weekly_months?: number;
    /**
     * Hard cap. Rows older than this are deleted. ``None`` keeps all aggregated history.
     */
    total_months?: number | null;
};

/**
 * UsageStartEndItemResponse
 *
 * Row of the start/end consumption comparison list.
 */
export type UsageStartEndItemResponse = {
    /**
     * Item ID
     */
    item_id: string;
    /**
     * Item name
     */
    item_name: string;
    /**
     * Item description
     */
    item_description: string;
    /**
     * Consumer type
     */
    item_consumer?: string | null;
    /**
     * Consumption snapshot at the window start.
     */
    start: UsageItemConsumption;
    /**
     * Consumption snapshot at the window end.
     */
    end: UsageItemConsumption;
    /**
     * Whether this item id appears more than once in the result.
     */
    duplicated_item_id: boolean;
};

/**
 * UsageStartEndRequest
 *
 * Request body for start/end consumption.
 */
export type UsageStartEndRequest = {
    /**
     * Start date in YYYY-MM-DD format
     */
    start_date?: string | null;
    /**
     * End date in YYYY-MM-DD format
     */
    end_date?: string | null;
    /**
     * List of item IDs to filter
     */
    items_ids?: Array<string> | null;
    /**
     * List of item IDs for ownership checks
     */
    item_ids?: Array<string> | null;
    /**
     * Type of item
     */
    item_type?: string | null;
    /**
     * Consumer type (e.g. category, hypervisor)
     */
    item_consumer?: string | null;
    /**
     * Grouping parameters
     */
    grouping?: Array<string> | null;
};

/**
 * UsageUnifyItemNameResponse
 *
 * Response for ``/admin/item/usage/unify/{item_id}/item_name``.
 */
export type UsageUnifyItemNameResponse = {
    /**
     * Resolved canonical item name.
     */
    name: string;
};

/**
 * UserAPIKeyResponse
 */
export type UserApiKeyResponse = {
    exists: boolean;
    expires: number;
};

/**
 * UserAllowedHardwareResponse
 */
export type UserAllowedHardwareResponse = {
    /**
     * Whether nested virtualization is enabled
     */
    virtualization_nested?: boolean | null;
    /**
     * List of available network interfaces
     */
    interfaces: Array<HardwareItem>;
    /**
     * List of available graphics configurations
     */
    graphics: Array<HardwareItem>;
    /**
     * List of available video cards
     */
    videos: Array<HardwareItem>;
    /**
     * List of available boot order options
     */
    boot_order: Array<HardwareItem>;
    /**
     * List of available Quality of Service profiles
     */
    qos_id: Array<HardwareItem>;
    /**
     * List of available ISO images
     */
    isos: Array<MediaHardwareItem>;
    /**
     * List of available floppy disk images
     */
    floppies: Array<MediaHardwareItem>;
    /**
     * Reservable resources like vGPUs
     */
    reservables: ApiSchemasUsersReservables;
    /**
     * List of available disk bus types
     */
    disk_bus: Array<HardwareItem>;
    /**
     * List of forced hypervisors
     */
    forced_hyp: Array<string>;
    /**
     * List of favorite hypervisors
     */
    favourite_hyp: Array<string>;
    /**
     * User quota information or boolean if no quota applies
     */
    quota: boolean | {
        [key: string]: unknown;
    };
    /**
     * Type of quota restriction that was applied
     */
    restriction_applied: 'user_quota' | 'role_quota' | 'group_quota' | 'category_quota';
};

/**
 * UserAllowedMediaPaginationResponse
 */
export type UserAllowedMediaPaginationResponse = {
    /**
     * List of allowed media for the current page
     */
    rows: Array<UserSharedMedia>;
    /**
     * Total number of items across all pages
     */
    total: number;
};

/**
 * UserAllowedMediaSearchFields
 */
export const UserAllowedMediaSearchFields = {
    NAME: 'name',
    DESCRIPTION: 'description',
    GROUP_NAME: 'group_name',
    CATEGORY_NAME: 'category_name'
} as const;

/**
 * UserAllowedMediaSearchFields
 */
export type UserAllowedMediaSearchFields = typeof UserAllowedMediaSearchFields[keyof typeof UserAllowedMediaSearchFields];

/**
 * UserAllowedTemplateFlatItem
 *
 * Single row in the flat allowed-template list returned by
 * ``GET /items/templates/allowed/{kind}`` (replaces v3
 * ``GET /user/templates/allowed/{kind}``).
 *
 * Mirrors the v3 ``allowed.get_items_allowed`` pluck for the template
 * kind: ``id``, ``name``, ``kind``, ``category``, ``group``, ``icon``,
 * ``image``, ``user``, ``description``, ``status``, ``enabled``, plus
 * the merge fields ``category_name`` / ``group_name`` / ``user_name``
 * that the helper injects automatically.
 */
export type UserAllowedTemplateFlatItem = {
    id: string;
    name: string;
    kind?: string;
    category?: string | null;
    category_name?: string | null;
    group?: string | null;
    group_name?: string | null;
    icon?: string | null;
    image?: DomainImageOutput | null;
    user?: unknown | null;
    user_name?: string | null;
    description?: string | null;
    status?: string | null;
    enabled?: boolean | null;
    allowed?: unknown | null;
    reservables?: ApiSchemasDomainsHardwareReservables | null;
    [key: string]: unknown | string | string | null | string | null | string | null | string | null | string | null | DomainImageOutput | null | unknown | null | string | null | string | null | string | null | boolean | null | unknown | null | ApiSchemasDomainsHardwareReservables | null | undefined;
};

/**
 * UserAllowedTemplateSearchFields
 */
export const UserAllowedTemplateSearchFields = {
    NAME: 'name',
    DESCRIPTION: 'description',
    GROUP_NAME: 'group_name',
    CATEGORY_NAME: 'category_name'
} as const;

/**
 * UserAllowedTemplateSearchFields
 */
export type UserAllowedTemplateSearchFields = typeof UserAllowedTemplateSearchFields[keyof typeof UserAllowedTemplateSearchFields];

/**
 * UserAllowedTemplatesPaginationResponse
 */
export type UserAllowedTemplatesPaginationResponse = {
    /**
     * List of allowed templates for the current page
     */
    rows: Array<UserSharedTemplate>;
    /**
     * Total number of items across all pages
     */
    total: number;
};

/**
 * UserAppliedQuotaResponse
 *
 * Response for user applied quota.
 */
export type UserAppliedQuotaResponse = {
    /**
     * The applied quota or False if no quota applies.
     */
    quota: {
        [key: string]: unknown;
    } | false;
    /**
     * The type of quota restriction applied to the user.
     */
    restriction_applied: string;
};

/**
 * UserBastionSshKeyResponse
 *
 * The user's single bastion SSH public key.
 */
export type UserBastionSshKeyResponse = {
    /**
     * The user's bastion SSH public key, or null if none is set.
     */
    ssh_key?: string | null;
};

/**
 * UserBookingPlan
 */
export type UserBookingPlan = {
    /**
     * The ID of the booking plan.
     */
    plan_id: string;
    /**
     * The priority level of the booking plan.
     */
    priority: number;
    /**
     * The ID of the item associated with the booking plan.
     */
    item_id: string;
    /**
     * The ID of the subitem associated with the booking plan.
     */
    subitem_id: string;
    /**
     * The number of units booked under this plan.
     */
    units_booked: number;
};

/**
 * UserBookingResponse
 *
 * Response model for user bookings
 */
export type UserBookingResponse = {
    /**
     * The ID of the booking event.
     */
    id: string;
    /**
     * The ID of the item using the booking.
     */
    item_id: string;
    /**
     * The type of the item using the booking.
     */
    item_type: 'desktop' | 'deployment';
    /**
     * The number of units booked.
     */
    units: number;
    /**
     * The reservables associated with the booking.
     */
    reservables: {
        [key: string]: Array<string>;
    };
    /**
     * The start time of the booking.
     */
    start: string;
    /**
     * The end time of the booking.
     */
    end: string;
    /**
     * The title of the booking.
     */
    title: string;
    /**
     * The ID of the user who made the booking.
     */
    user_id: string;
    /**
     * Whether the booking is editable by the user.
     */
    editable: boolean;
    /**
     * Whether it's an availability event or a user booking event.
     */
    event_type: 'event' | 'available' | 'unavailable' | 'overridable';
    /**
     * List of booking plans.
     */
    plans: Array<UserBookingPlan>;
};

/**
 * UserConfigResponse
 *
 * User configuration response model
 */
export type UserConfigResponse = {
    show_bookings_button: boolean;
    show_gpu_plannings?: boolean;
    documentation_url: string;
    viewers_documentation_url: string;
    show_change_email_button: boolean;
    show_temporal_tab: boolean;
    http_port: string;
    https_port: string;
    bastion_domain: string | null;
    bastion_ssh_port: string | null;
    can_use_bastion: boolean;
    can_use_bastion_individual_domains: boolean;
    session: {
        [key: string]: unknown;
    };
    /**
     * The user's category `custom_url_name`. Lets the frontend redirect to /login/<provider>/<category> on logout.
     */
    category_custom_url?: string | null;
    /**
     * Which frontend to serve: 'deprecated' (Vue 2 only), 'actual' (Vue 3 only), 'all' (both, with toggler), or 'hidden' (both reachable by path, no toggler shown).
     */
    frontend_mode?: 'deprecated' | 'actual' | 'all' | 'hidden';
    /**
     * Whether a user may hold several temporal desktops from the same template. Only true on frontend_mode 'actual': the Vue 2 frontend maps each template card to a single desktop.
     */
    multiple_temporal_desktops?: boolean;
    /**
     * Faro telemetry configuration for the browser SDK.
     */
    faro: FaroConfig;
};

/**
 * UserDeploymentDesktop
 *
 * User deployment desktop model.
 *
 * The shape mirrors what ``DesktopsProcessed._parse_desktop`` (and the
 * deployment-desktop wrapper ``DeploymentDesktopsProcessed.
 * _parse_deployment_desktop``) returns: ``status`` is the canonical
 * field, ``state`` is the apiv3 alias the old Vue 2 frontend reads,
 * and ``viewers`` are emitted in the hyphenated form both frontends
 * consume (``browser-vnc``), declared as plain strings like the
 * sibling ``UserDesktop.viewers``.
 */
export type UserDeploymentDesktop = {
    /**
     * ID of the desktop
     */
    id: string;
    /**
     * Image associated with the desktop
     */
    image: Image;
    /**
     * Name of the desktop
     */
    name: string;
    /**
     * Status of the desktop
     */
    status: DesktopStatusEnum;
    /**
     * Vue 2 alias for ``status``; same value.
     */
    state?: DesktopStatusEnum | null;
    type?: string | null;
    template?: string | null;
    /**
     * List of viewers that will be available to access the desktop, hyphenated (``browser-vnc``).
     */
    viewers?: Array<string>;
    /**
     * IP of the desktop. Only set when the desktop is started and has an IP assigned.
     */
    ip?: string | null;
    viewer?: {
        [key: string]: unknown;
    } | boolean | null;
    icon?: string | null;
    description?: string | null;
    progress?: {
        [key: string]: unknown;
    } | null;
    editable?: boolean | null;
    scheduled?: {
        [key: string]: unknown;
    } | null;
    server?: boolean | null;
    accessed?: number | null;
    tag?: string | null;
    visible?: boolean | null;
    user?: string | null;
    user_name?: string | null;
    user_photo?: string | null;
    group?: string | null;
    group_name?: string | null;
    category?: string | null;
    category_name?: string | null;
    reservables?: {
        [key: string]: unknown;
    } | null;
    interfaces?: Array<{
        [key: string]: unknown;
    }> | null;
    current_action?: string | boolean | null;
    storage?: Array<string | null> | null;
    permissions?: Array<string>;
    bastion_target?: {
        [key: string]: unknown;
    } | null;
};

/**
 * UserDeploymentInfo
 *
 * Deployment user info model
 */
export type UserDeploymentInfo = {
    /**
     * ID of the deployment
     */
    id: string;
    /**
     * Name of the deployment
     */
    name: string;
    /**
     * Description of the deployment
     */
    description?: string | null;
    /**
     * Indicates if the deployment is visible
     */
    tag_visible: boolean;
    /**
     * Information about the user associated with the deployment
     */
    user: UserDeploymentUserInfo;
    /**
     * Information about the owner of the deployment
     */
    owner: UserDeploymentUserInfo;
    /**
     * Resources associated with the deployment
     */
    resources?: UserDeploymentResource | null;
    /**
     * List of permissions the user has over the deployment desktops
     */
    user_permissions?: Array<DeploymentPermissions>;
};

/**
 * UserDeploymentResource
 *
 * User deployment resource model
 */
export type UserDeploymentResource = {
    /**
     * Instructions to use the resource
     */
    instructions?: string | null;
    /**
     * Files associated with the resource
     */
    files?: Array<string> | null;
    /**
     * Annotations for the resource
     */
    annotations?: string | null;
};

/**
 * UserDeploymentResponse
 *
 * List of user deployment desktops response model
 */
export type UserDeploymentResponse = {
    /**
     * Information about deployment and user
     */
    info: UserDeploymentInfo;
    /**
     * List of desktops associated with the user in the deployment
     */
    desktops: Array<UserDeploymentDesktop>;
};

/**
 * UserDeploymentUserInfo
 *
 * Deployment user info model
 */
export type UserDeploymentUserInfo = {
    /**
     * ID of the user
     */
    id: string;
    /**
     * Name of the user
     */
    name: string;
    /**
     * Username of the user
     */
    username: string;
    /**
     * Photo of the user
     */
    photo?: string | null;
};

/**
 * UserDesktopBastionTarget
 */
export type UserDesktopBastionTarget = {
    http: BastionHttpConfig;
    id: string;
    ssh: BastionSshConfig;
    domains?: Array<string>;
};

/**
 * UserDesktopInterface
 */
export type UserDesktopInterface = {
    id: string;
    mac: string;
};

/**
 * UserDesktopProgress
 */
export type UserDesktopProgress = {
    /**
     * Progress percentage
     */
    percentage?: number | null;
    /**
     * Average throughput
     */
    throughput_average?: string | null;
    /**
     * Estimated time remaining
     */
    time_left?: string | null;
    /**
     * Size of the operation
     */
    size?: string | null;
};

/**
 * UserDesktopScheduled
 */
export type UserDesktopScheduled = {
    /**
     * Scheduled shutdown time for the desktop. If False, no shutdown is scheduled. If None, the desktop is not scheduled.
     */
    shutdown?: string | false | null;
};

/**
 * UserDesktopsResponse
 */
export type UserDesktopsResponse = {
    /**
     * List of user desktops.
     */
    desktops: Array<ApiSchemasDomainsDesktopsUserDesktop>;
};

/**
 * UserDetailsResponse
 *
 * User response model
 */
export type UserDetailsResponse = {
    /**
     * The ID of the user.
     */
    id: string;
    /**
     * The username of the user.
     */
    username: string;
    /**
     * The URL of the user's photo.
     */
    photo?: string;
    /**
     * The full name of the user.
     */
    name: string;
    /**
     * The email address of the user.
     */
    email: string;
    /**
     * Indicates if the user's email is verified.
     */
    email_verified?: boolean | number | null;
    /**
     * The preferred language of the user.
     */
    lang?: string | null;
    /**
     * The authentication provider for the user.
     */
    provider: string;
    /**
     * The category ID of the user.
     */
    category: string;
    /**
     * The name of the category of the user.
     */
    category_name: string;
    /**
     * The primary group ID of the user.
     */
    group: string;
    /**
     * The name of the primary group of the user.
     */
    group_name: string;
    /**
     * The role ID of the user.
     */
    role: string;
    /**
     * The name of the role of the user.
     */
    role_name: string;
    /**
     * List of secondary groups IDs and names the user belongs to.
     */
    secondary_groups_data: Array<SecondaryGroupsData>;
    /**
     * User storage configuration model
     */
    user_storage?: UserStorageModel | null;
};

/**
 * UserGroupingLogRow
 *
 * Row in the ``user_grouping`` view of ``query_paginated``.
 */
export type UserGroupingLogRow = {
    count: number;
    owner_user_name?: string | null;
    owner_user_id?: string | null;
    owner_group_name?: string | null;
    owner_group_id?: string | null;
    owner_category_name?: string | null;
    owner_category_id?: string | null;
    started_time?: number | string | null;
};

/**
 * UserHardwareKindAllowedResponse
 *
 * Response for ``/item/user/hardware/{kind}/allowed``.
 *
 * ``Quotas.get_hardware_kind_allowed`` returns a partial subset of
 * the full ``UserAllowedHardwareResponse`` shape: only the requested
 * ``kind`` key is populated (``isos`` populates both ``isos`` and
 * ``floppies``; ``quota`` populates ``quota`` and
 * ``restriction_applied`` from the applied quota lookup). Every
 * field is therefore declared ``Optional`` so a single strict model
 * covers all kinds without drift from the canonical
 * ``UserAllowedHardwareResponse``.
 */
export type UserHardwareKindAllowedResponse = {
    interfaces?: Array<HardwareItem> | null;
    graphics?: Array<HardwareItem> | null;
    videos?: Array<HardwareItem> | null;
    boot_order?: Array<HardwareItem> | null;
    qos_id?: Array<HardwareItem> | null;
    isos?: Array<MediaHardwareItem> | null;
    floppies?: Array<MediaHardwareItem> | null;
    reservables?: ApiSchemasUsersReservables | null;
    disk_bus?: Array<HardwareItem> | null;
    forced_hyp?: Array<string> | null;
    favourite_hyp?: Array<string> | null;
    quota?: boolean | {
        [key: string]: unknown;
    } | null;
    restriction_applied?: 'user_quota' | 'role_quota' | 'group_quota' | 'category_quota' | null;
};

/**
 * UserListItem
 *
 * One row of the ``/items/users`` listing.
 *
 * Mirrors the columns plucked by
 * ``CommonUsers.get_with_category`` (id / name / category /
 * category_name / photo / accessed). This is the shape the
 * deployments / lab forms read to populate the user multi-select.
 */
export type UserListItem = {
    /**
     * The ID of the user.
     */
    id: string;
    /**
     * The full name of the user.
     */
    name: string;
    /**
     * The category ID of the user.
     */
    category: string;
    /**
     * The name of the user's category.
     */
    category_name: string;
    /**
     * The URL of the user's photo.
     */
    photo?: string;
    /**
     * Timestamp of the user's last access.
     */
    accessed?: number | null;
};

/**
 * UserLogRow
 *
 * Raw row from ``r.table('logs_users')`` (writer:
 * ``isardvdi_common.helpers.api_logs_users``).
 */
export type UserLogRow = {
    id: string;
    started_time?: number | string | null;
    stopped_time?: number | string | boolean | null;
    owner_user_id?: string | null;
    owner_user_name?: string | null;
    owner_role_id?: string | null;
    owner_category_id?: string | null;
    owner_category_name?: string | null;
    owner_group_id?: string | null;
    owner_group_name?: string | null;
};

/**
 * UserMediaResponse
 *
 * List of media items response model
 */
export type UserMediaResponse = {
    /**
     * List of user media
     */
    media: Array<MediaItemResponse>;
};

/**
 * UserOwnsDesktopRequest
 *
 * Request body for the three ``owns_desktop`` variants.
 *
 * Exactly one of the three (mutually-exclusive) shapes below must be
 * set by the caller. The route dispatches based on the JWT payload
 * and the fields populated:
 *
 * 1. ``ip`` alone → look up the running desktop by its viewer
 * ``guest_ip``. If the JWT is a direct-viewer token that
 * already carries ``desktop_id``, the ``ip`` is instead
 * interpreted as the ``connection_ip`` to match the desktop's
 * ``viewer.guest_ip``.
 * 2. ``proxy_video`` + ``proxy_hyper_host`` + ``port`` → look up
 * the desktop by the composite ``proxies`` index.
 *
 * Used by rdpgw, websockify and guac as a service-side preflight
 * before proxying a direct-viewer connection.
 */
export type UserOwnsDesktopRequest = {
    /**
     * Either the caller's connection IP (when the direct-viewer token carries a ``desktop_id``) or the desktop's viewer ``guest_ip`` to look up (otherwise).
     */
    ip?: string | null;
    /**
     * The proxy video host, optionally with port (``host`` or ``host:port``). Used together with ``proxy_hyper_host`` and ``port``.
     */
    proxy_video?: string | null;
    /**
     * The hypervisor host behind the proxy.
     */
    proxy_hyper_host?: string | null;
    /**
     * The viewer port to match.
     */
    port?: number | null;
};

/**
 * UserPasswordPolicyResponse
 *
 * User password policy response model
 */
export type UserPasswordPolicyResponse = {
    /**
     * Minimum number of digits required in the password
     */
    digits: number;
    /**
     * Password expiration time in days
     */
    expiration: number;
    /**
     * Minimum length of the password
     */
    length: number;
    /**
     * Minimum number of lowercase letters required in the password
     */
    lowercase: number;
    /**
     * Whether the password should be able to include the username
     */
    not_username: boolean;
    /**
     * Number of old passwords to remember and not allow reuse
     */
    old_passwords: number;
    /**
     * Minimum number of special characters required in the password
     */
    special_characters: number;
    /**
     * Minimum number of uppercase letters required in the password
     */
    uppercase: number;
};

/**
 * UserQuota
 */
export type UserQuota = {
    desktops: number;
    volatile: number;
    templates: number;
    isos: number;
    total_size: number;
    deployments_total: number;
    deployment_desktops: number;
    deployment_users: number;
    started_deployment_desktops: number;
    running: number;
    memory: number;
    vcpus: number;
    desktops_disk_size: number;
    total_soft_size: number;
};

/**
 * UserQuotaResponse
 *
 * User quota response model
 */
export type UserQuotaResponse = {
    /**
     * The type of quota restriction applied to the user.
     */
    restriction_applied: string;
    /**
     * The quota limits for the user or False if no quota applies.
     */
    quota: UserQuota | false;
    /**
     * Current resource usage of the user.
     */
    used: UserQuotaUsed;
};

/**
 * UserQuotaUsed
 */
export type UserQuotaUsed = {
    desktops: number;
    volatile: number;
    templates: number;
    isos: number;
    total_size: number;
    media_size: number;
    storage_size: number;
    deployments_total: number;
    deployment_desktops: number;
    deployment_users: number;
    started_deployment_desktops: number;
    running: number;
    memory: number;
    vcpus: number;
};

/**
 * UserResponse
 */
export type UserResponse = {
    name: string;
    email: string;
    role: string;
    role_name: string;
    /**
     * The URL of the user's photo.
     */
    photo?: string;
    items_in_bin: number;
};

/**
 * UserSetBastionSshKeyPutData
 *
 * Request body for setting the user's bastion SSH public key.
 */
export type UserSetBastionSshKeyPutData = {
    /**
     * A single SSH public key (e.g. 'ssh-ed25519 AAAA... comment').
     */
    ssh_key: string;
};

/**
 * UserSetEmailPutData
 */
export type UserSetEmailPutData = {
    email: string;
};

/**
 * UserSetLangPutData
 */
export type UserSetLangPutData = {
    lang: string;
};

/**
 * UserSetPasswordPutData
 */
export type UserSetPasswordPutData = {
    current_password: string;
    password: string;
};

/**
 * UserSharedMedia
 */
export type UserSharedMedia = {
    /**
     * ID of the allowed media
     */
    id: string;
    /**
     * Name of the allowed media
     */
    name: string;
    /**
     * Description of the allowed media
     */
    description?: string | null;
    /**
     * Name of the user that allowed the media
     */
    user?: MediaUser | null;
    /**
     * Name of the group that allowed the template
     */
    group_name?: string | null;
    /**
     * Name of the category of the allowed template
     */
    category_name?: string | null;
    /**
     * Timestamp of the last access to the media.
     */
    accessed: number;
    /**
     * Kind of the media
     */
    kind: MediaKindEnum;
    /**
     * Status of the media
     */
    status: MediaStatusEnum;
    /**
     * Download progress of the media
     */
    progress?: MediaProgress;
};

/**
 * UserSharedMediaResponse
 */
export type UserSharedMediaResponse = {
    media: Array<UserSharedMedia>;
};

/**
 * UserSharedTemplate
 */
export type UserSharedTemplate = {
    /**
     * ID of the allowed template
     */
    id: string;
    /**
     * Name of the allowed template
     */
    name: string;
    /**
     * Description of the allowed template
     */
    description?: string | null;
    /**
     * Image of the allowed template. May be None for templates without a custom image assigned.
     */
    image?: DomainImageOutput | null;
    /**
     * Owner of the template (user object or user_id string)
     */
    user?: SharedTemplateUserInfo | string | null;
    /**
     * Name of the group that allowed the template
     */
    group_name?: string | null;
    /**
     * Name of the category of the allowed template
     */
    category_name?: string | null;
    /**
     * Domain status of the template row (``CreatingTemplate``, ``Stopped``, or ``Failed``). Surfaced so the frontend can flag failed shared templates as non-usable.
     */
    status?: TemplateStatusEnum | null;
    /**
     * Timestamp of the last access to the desktop.
     */
    accessed: number;
};

/**
 * UserSharedTemplatesResponse
 */
export type UserSharedTemplatesResponse = {
    /**
     * List of shared templates for the user
     */
    templates: Array<UserSharedTemplate>;
};

/**
 * UserStorageAddRequest
 *
 * Request for adding a user storage provider
 */
export type UserStorageAddRequest = {
    provider: string;
    name: string;
    description: string;
    url: string;
    urlprefix: string;
    access: string;
    quota: unknown;
    verify_cert: boolean;
};

/**
 * UserStorageAutoRegisterRequest
 *
 * Request for auto-registering a user storage provider
 */
export type UserStorageAutoRegisterRequest = {
    domain: string;
    user: string;
    password: string;
    intra_docker: boolean;
    verify_cert: boolean;
};

/**
 * UserStorageConnTestRequest
 *
 * Request for testing a user storage connection
 */
export type UserStorageConnTestRequest = {
    provider: string;
    url: string;
    urlprefix: string;
    user: string;
    password: string;
    verify_cert: boolean;
};

/**
 * UserStorageIdResponse
 *
 * Response for the auto-register / add provider endpoints — both
 * services return the new provider's id only.
 */
export type UserStorageIdResponse = {
    id: string;
};

/**
 * UserStorageLoginAuthResponse
 *
 * Response for ``GET /admin/user_storage/{provider_id}/login_auth``.
 */
export type UserStorageLoginAuthResponse = {
    login_url?: string | null;
};

/**
 * UserStorageModel
 *
 * User storage configuration model
 */
export type UserStorageModel = {
    user_id?: string | null;
    provider_id?: string | null;
    password?: string | null;
    email?: string | null;
    displayname?: string | null;
    quota?: number | null;
    web?: string | null;
    dav?: string | null;
    tls?: boolean | null;
    verify_cert?: boolean | null;
    token?: string | null;
    token_web?: string | null;
    token_davs?: string | null;
    provider_quota?: ProviderQuotaModel | null;
};

/**
 * UserStorageProvider
 *
 * One row of ``GET /admin/user_storage`` and the per-id read.
 *
 * Permissive (``ConfigDict(extra="allow")``) — the provider doc
 * carries auth_basic / oauth2 fields that differ per protocol.
 */
export type UserStorageProvider = {
    id?: string | null;
    provider?: string | null;
    name?: string | null;
    [key: string]: unknown | string | null | string | null | string | null | undefined;
};

/**
 * UserStorageUser
 *
 * One row of ``GET /admin/user_storage/users``.
 */
export type UserStorageUser = {
    id?: string | null;
    user_id?: string | null;
    user_name?: string | null;
    provider_id?: string | null;
    [key: string]: unknown | string | null | string | null | string | null | string | null | undefined;
};

/**
 * UserTemplate
 */
export type UserTemplate = {
    /**
     * ID of the template
     */
    id: string;
    /**
     * Name of the template
     */
    name: string;
    /**
     * Description of the template
     */
    description?: string | null;
    /**
     * Image of the template. May be None for templates without a custom image assigned.
     */
    image?: DomainImageOutput | null;
    /**
     * Whether the template is visible to other users. Defaults to True so legacy template rows (and seeded fixtures) without the field don't fail Pydantic validation when listed via ``GET /items/templates``. Templates created via the apiv4 endpoints always set this explicitly. Matches the default used by the sibling ``enabled`` fields on ``Template`` / ``AdminTemplate`` schemas in this file.
     */
    enabled?: boolean;
    /**
     * Domain status of the template row. ``CreatingTemplate`` while the apiv4 + isard-storage task chain is rewriting the source desktop's qcow2 into the template; ``Stopped`` once the chain finishes; ``Failed`` on chain failure.
     */
    status?: TemplateStatusEnum | null;
    /**
     * Progress dict written by the storage worker's ``move()`` task while the rsync branch of the template-creation chain runs. Carries ``total_percent`` and ``received_percent`` (both 0-100). Absent for templates not currently being created.
     */
    progress?: {
        [key: string]: unknown;
    } | null;
};

/**
 * UserTemplateSearchFields
 */
export const UserTemplateSearchFields = { NAME: 'name', DESCRIPTION: 'description' } as const;

/**
 * UserTemplateSearchFields
 */
export type UserTemplateSearchFields = typeof UserTemplateSearchFields[keyof typeof UserTemplateSearchFields];

/**
 * UserTemplatesPaginationResponse
 */
export type UserTemplatesPaginationResponse = {
    /**
     * List of allowed templates for the current page
     */
    rows: Array<UserTemplate>;
    /**
     * Total number of items across all pages
     */
    total: number;
};

/**
 * UserTemplatesResponse
 */
export type UserTemplatesResponse = {
    /**
     * List of user templates
     */
    templates: Array<UserTemplate>;
};

/**
 * UserVpnData
 */
export type UserVpnData = {
    kind?: string | null;
    content?: string | null;
    name?: string | null;
    ext?: string | null;
    mime?: string | null;
};

/**
 * ValidationError
 */
export type ValidationError = {
    loc: Array<string | number>;
    msg: string;
    type: string;
    input?: unknown;
    ctx?: {
        [key: string]: unknown;
    };
};

/**
 * ViewerConfigItem
 *
 * Single viewer configuration row.
 *
 * Fields match the per-viewer dict stored under ``config.viewers`` in
 * RethinkDB and consumed by the webapp DataTables admin view.
 */
export type ViewerConfigItem = {
    key?: string | null;
    viewer?: string | null;
    custom?: string | null;
    default?: string | null;
    fixed?: string | null;
};

/**
 * ViewerConfigUpdateRequest
 *
 * Request to update a viewer custom configuration
 */
export type ViewerConfigUpdateRequest = {
    custom?: string | null;
};

/**
 * ViewerKind
 */
export const ViewerKind = { FILE: 'file', BROWSER: 'browser' } as const;

/**
 * ViewerKind
 */
export type ViewerKind = typeof ViewerKind[keyof typeof ViewerKind];

/**
 * ViewerProtocol
 */
export const ViewerProtocol = {
    SPICE: 'spice',
    VNC: 'vnc',
    RDP: 'rdp',
    RDPGW: 'rdpgw'
} as const;

/**
 * ViewerProtocol
 */
export type ViewerProtocol = typeof ViewerProtocol[keyof typeof ViewerProtocol];

/**
 * ViewersDocsResponse
 */
export type ViewersDocsResponse = {
    /**
     * URL to the documentation of the possible viewers.
     */
    viewers_documentation_url: string;
};

/**
 * ViewersModel
 */
export type ViewersModel = {
    'file-spice'?: FileSpiceViewer | null;
    'browser-vnc'?: BrowserVncViewer | null;
    'browser-rdp'?: BrowserRdpViewer | null;
    'file-rdpgw'?: FileRdpgwViewer | null;
};

/**
 * VirtInstallItem
 *
 * OS template (virt_install XML) item
 */
export type VirtInstallItem = {
    /**
     * ID of the virt_install template
     */
    id: string;
    /**
     * Name of the OS template
     */
    name: string;
    /**
     * Description of the OS template
     */
    description?: string | null;
    /**
     * Version of the OS template
     */
    vers?: string | null;
};

/**
 * VirtInstallListResponse
 *
 * Response containing list of available OS templates for media installs
 */
export type VirtInstallListResponse = {
    /**
     * List of available OS templates
     */
    installs: Array<VirtInstallItem>;
};

/**
 * VpnConnectionRequest
 *
 * Request for VPN connection update
 */
export type VpnConnectionRequest = {
    remote_ip: string;
    remote_port: number;
};

/**
 * VpnDisconnectListItem
 *
 * Single VPN disconnect item.
 *
 * Matches the peer dict built by ``docker/vpn/src/isardvdi_vpn/wg_monitor.py``
 * (around line 129): ``{"kind": <device>, "client_ip": <allowed_ip>}``.
 */
export type VpnDisconnectListItem = {
    kind: string;
    client_ip: string;
};

/**
 * WebappDomainItem
 *
 * Common shape returned by the legacy ``/item/user/webapp-desktops``
 * and ``/item/user/webapp-templates`` endpoints.
 *
 * Both endpoints return raw rows from the ``domains`` table
 * (``DomainsProcessed.list_webapp_desktops_for_user`` /
 * ``list_webapp_templates_for_user``) with a few heavy fields
 * stripped (``xml``, ``history_domain``, ``allowed`` for desktops;
 * plus ``viewer`` for templates). Every field is ``Optional`` —
 * ``DomainModel`` itself only requires ``category``, ``group``,
 * ``kind``, ``name``, ``persistent``, ``status`` and ``id``, but
 * real rows from older schemas may have any of these missing.
 */
export type WebappDomainItem = {
    /**
     * ID of the domain row.
     */
    id: string;
    name?: string | null;
    /**
     * ``desktop`` or ``template``.
     */
    kind?: string | null;
    status?: string | null;
    description?: string | null;
    detail?: string | null;
    persistent?: boolean | null;
    user?: string | null;
    username?: string | null;
    category?: string | null;
    group?: string | null;
    accessed?: number | null;
    icon?: string | null;
    image?: DomainImageOutput | null;
    os?: string | null;
    parents?: Array<string> | null;
    create_dict?: {
        [key: string]: unknown;
    } | null;
    guest_properties?: {
        [key: string]: unknown;
    } | null;
    hardware?: {
        [key: string]: unknown;
    } | null;
    hardware_from_xml?: {
        [key: string]: unknown;
    } | null;
    options?: {
        [key: string]: unknown;
    } | null;
    booking_id?: string | boolean | null;
    server?: boolean | string | null;
    tag?: string | boolean | null;
    tag_desktop_id?: string | boolean | null;
    tag_visible?: boolean | null;
    progress?: {
        [key: string]: unknown;
    } | null;
    /**
     * Full viewer subdocument. Present on desktops; stripped on the templates listing.
     */
    viewer?: {
        [key: string]: unknown;
    } | null;
    forced_hyp?: boolean | Array<string> | null;
    favourite_hyp?: boolean | Array<string> | null;
    hyp_started?: boolean | string | null;
    hypervisors_pools?: Array<string> | null;
    disks_info?: Array<{
        [key: string]: unknown;
    }> | null;
    hw_stats?: {
        [key: string]: unknown;
    } | null;
    from_template?: string | null;
    current_action?: string | boolean | null;
    scheduled?: {
        [key: string]: unknown;
    } | null;
    enabled?: boolean | null;
};

/**
 * WorkerHealthRow
 *
 * Per-worker heartbeat + governor-published row.
 *
 * ``up``/``hash_present``/``heartbeat_age_seconds`` come from the bounded
 * ``rq:workers`` SET and the ``rq:worker:<name>`` hash. PSI / ``deferring`` /
 * ``served_lanes`` / ``multitenancy`` / ``last_job_*`` come from the
 * ``governor:worker:<name>`` hash, which is NOT published yet (a later step) —
 * until then those degrade to ``null`` and ``served_known=false``. Deliberately
 * has NO ``current_job_runtime_seconds`` (catalog #2b is backed instead by the
 * problem-tasks endpoint + the per-pool ``started_over_timeout`` gauge).
 */
export type WorkerHealthRow = {
    name: string;
    pool?: string | null;
    kind?: string | null;
    state?: string | null;
    up?: boolean;
    hash_present?: boolean;
    last_heartbeat?: number | null;
    heartbeat_age_seconds?: number | null;
    current_lane?: string | null;
    served_lanes?: Array<string>;
    served_known?: boolean;
    psi_available?: boolean | null;
    psi_cpu?: number | null;
    psi_io?: number | null;
    psi_mem?: number | null;
    deferring?: boolean | null;
    at_cap?: boolean | null;
    floor?: boolean | null;
    multitenancy?: boolean | null;
    last_job_id?: string | null;
    last_job_action?: string | null;
};

export type GuestPropertiesCredentialsInput = {
    /**
     * Username for accessing the desktop using RDP.
     */
    username?: string | null;
    /**
     * Password for accessing the desktop using RDP.
     */
    password?: string | null;
};

export type GuestPropertiesViewersInput = {
    /**
     * Configuration for browser-based RDP access.
     */
    browser_rdp?: ViewerConfig | null;
    /**
     * Configuration for browser-based VNC access.
     */
    browser_vnc?: ViewerConfig | null;
    /**
     * Configuration for file-based RDP gateway access.
     */
    file_rdpgw?: ViewerConfig | null;
    /**
     * Configuration for file-based RDP VPN access.
     */
    file_rdpvpn?: ViewerConfig | null;
    /**
     * Configuration for file-based SPICE access.
     */
    file_spice?: ViewerConfig | null;
};

/**
 * _ImageFile
 */
export type ImageFile = {
    data: string;
    filename: string;
};

/**
 * _ViewerConfig
 */
export type ViewerConfig = {
    /**
     * Configuration options for the viewer. Can be null if no specific options are set.
     */
    options?: {
        [key: string]: unknown;
    } | null;
};

/**
 * Response shape for ``GET /item/disclaimer``.
 *
 * The service returns the current user's disclaimer template as a
 * dict ``{title, body}``; both are markdown text.
 */
export type ApiSchemasAdminAuthenticationDisclaimerResponse = {
    title?: string | null;
    body?: string | null;
    [key: string]: unknown | string | null | string | null | undefined;
};

export type ApiSchemasAllowedAllowedOutput = {
    /**
     * List of allowed groups. If False, no groups are allowed.
     */
    groups?: boolean | Array<unknown>;
    /**
     * List of allowed users. If False, no users are allowed.
     */
    users?: boolean | Array<unknown>;
    /**
     * List of allowed categories. If False, no categories are allowed.
     */
    categories?: boolean | Array<unknown>;
    /**
     * List of allowed roles. If False, no roles are allowed.
     */
    roles?: boolean | Array<unknown>;
};

export type ApiSchemasAllowedAvailableGroup = {
    /**
     * Group ID
     */
    id: string;
    /**
     * Group name
     */
    name: string;
    /**
     * Group description
     */
    description?: string | null;
    /**
     * Number of users whose primary group is this one
     */
    users_count?: number;
};

/**
 * Request model for verifying a single bastion domain's DNS
 */
export type ApiSchemasBastionBastionDomainVerifyRequest = {
    /**
     * Domain name to verify DNS for.
     */
    domain: string;
};

/**
 * Response model for bastion domain verification
 */
export type ApiSchemasBastionBastionDomainVerifyResponse = {
    /**
     * Whether the domain's DNS is correctly configured.
     */
    verified: boolean;
};

export type ApiSchemasCategoriesAvailableGroup = {
    /**
     * Group ID
     */
    id: string;
    /**
     * Group name
     */
    name: string;
    /**
     * Group description
     */
    description?: string | null;
    /**
     * Number of users whose primary group is this one
     */
    users_count?: number;
};

/**
 * Ad-hoc DNS verification of a single bastion domain without
 * saving it.
 */
export type ApiSchemasDomainsDesktopsBastionDomainVerifyRequest = {
    /**
     * The candidate bastion domain to DNS-verify.
     */
    domain: string;
};

export type ApiSchemasDomainsDesktopsBastionDomainVerifyResponse = {
    /**
     * True if the DNS record for ``domain`` resolves to the desktop's expected CNAME target.
     */
    verified: boolean;
};

export type ApiSchemasDomainsDesktopsUserDesktop = {
    /**
     * ID of the desktop.
     */
    id: string;
    /**
     * Name of the desktop.
     */
    name: string;
    /**
     * Status of the desktop.
     */
    status: DesktopStatusEnum;
    /**
     * Human-readable reason for the current status, set by the engine (e.g. why a start ended Failed: no hypervisor online / no GPU capacity). None when there is no reason.
     */
    status_reason?: string | null;
    /**
     * Type of the desktop.
     */
    type: DesktopTypeEnum;
    /**
     * ID of the template used to create the desktop. If None, the desktop is not based on a template (mainly desktops created through media).
     */
    template?: string | null;
    /**
     * List of available viewers for the desktop.
     */
    viewers: Array<string>;
    /**
     * Icon of the desktop, if available. If None, no icon is set.
     */
    icon?: string | null;
    /**
     * Image of the desktop. If None, no image is set.
     */
    image?: DomainImageOutput | null;
    /**
     * Description of the desktop.
     */
    description?: string | null;
    /**
     * IP address of the desktop. If None, the desktop is not running or does not have an IP assigned.
     */
    ip?: string | null;
    /**
     * Progress of the desktop, if applicable. Can be a string or an integer.
     */
    progress?: UserDesktopProgress | null;
    /**
     * If true, the desktop can be edited. If false, the desktop cannot be edited.
     */
    editable?: boolean | null;
    /**
     * Scheduled actions for the desktop, such as shutdown. If None, no scheduled actions are set.
     */
    scheduled?: UserDesktopScheduled | null;
    /**
     * If true, the desktop is a server. If false, the desktop is not a server.
     */
    server?: boolean | null;
    /**
     * If true, the server desktop is autostarted. Only meaningful when server is true.
     */
    server_autostart?: boolean | null;
    /**
     * Timestamp of the last access to the desktop. If None, the desktop has not been accessed yet.
     */
    accessed?: number | null;
    /**
     * Tag associated with the desktop. If False, no tag is set. If a string, it represents the tag ID.
     */
    tag?: string | boolean | null;
    /**
     * If true, the desktop is visible to the user. If false, the desktop is not visible.
     */
    visible?: boolean | null;
    /**
     * ID of the user who owns the desktop.
     */
    user: string;
    /**
     * ID of the group that owns the desktop.
     */
    group: string;
    /**
     * ID of the category that owns the desktop.
     */
    category: string;
    /**
     * The domain bookable resources associated with the desktop.
     */
    reservables?: ApiSchemasDomainsHardwareReservables;
    /**
     * List of network interfaces associated with the desktop.
     */
    interfaces: Array<UserDesktopInterface>;
    /**
     * Current action being performed on the desktop, if any. If None, no action is being performed.
     */
    current_action?: string | boolean | null;
    /**
     * List of storage IDs associated with the desktop.
     */
    storage: Array<string> | Array<null> | null;
    /**
     * List of permissions the user has on the desktop, such as 'recreate'
     */
    permissions?: Array<string>;
    /**
     * If true, the desktop needs to be booked before use. If false, no booking is required.
     */
    needs_booking?: boolean;
    /**
     * Start time of the next booking for the desktop. If None, no booking is scheduled.
     */
    next_booking_start?: string | null;
    /**
     * End time of the next booking for the desktop. If None, no booking is scheduled.
     */
    next_booking_end?: string | null;
    /**
     * ID of the booking associated with the desktop. If False, no booking is required. If None, the desktop is not booked.
     */
    booking_id?: string | boolean | null;
    /**
     * Bastion target configuration for the desktop. If None, no bastion is configured. If a dict, it contains the bastion configuration.
     */
    bastion_target?: UserDesktopBastionTarget | null;
};

export type ApiSchemasDomainsHardwareDomainGuestPropertiesGuestPropertiesCredentials = {
    /**
     * Username for accessing the desktop using RDP.
     */
    username?: string | null;
    /**
     * Password for accessing the desktop using RDP.
     */
    password?: string | null;
};

export type ApiSchemasDomainsHardwareDomainGuestPropertiesGuestPropertiesViewers = {
    /**
     * Configuration for browser-based RDP access.
     */
    browser_rdp?: ViewerConfig | null;
    /**
     * Configuration for browser-based VNC access.
     */
    browser_vnc?: ViewerConfig | null;
    /**
     * Configuration for file-based RDP gateway access.
     */
    file_rdpgw?: ViewerConfig | null;
    /**
     * Configuration for file-based RDP VPN access.
     */
    file_rdpvpn?: ViewerConfig | null;
    /**
     * Configuration for file-based SPICE access.
     */
    file_spice?: ViewerConfig | null;
};

export type ApiSchemasDomainsHardwareReservables = {
    /**
     * List of IDs of vGPUs that can be reserved for the desktop. If None, no vGPUs can be reserved nor used. Each ID must be a valid vGPU ID.
     */
    vgpus?: Array<string> | null;
};

/**
 * Disclaimer response model
 */
export type ApiSchemasLoginDisclaimerResponse = {
    title: string;
    body: string;
    footer: string;
};

export type ApiSchemasUsersReservables = {
    /**
     * List of available virtual GPUs
     */
    vgpus: Array<ReservableVgpuItem>;
};

export type ApiSchemasUsersUserDesktop = {
    id: string;
    name: string;
    status: string;
    kind: string;
    image?: DomainImageOutput | null;
    description?: string | null;
    tag_visible?: boolean | null;
    accessed?: number | null;
    user?: string | null;
    ip?: string | null;
};

export type IsardvdiCommonSchemasSharedAllowedAllowed = {
    categories?: boolean | Array<unknown>;
    groups?: boolean | Array<unknown>;
    roles?: boolean | Array<unknown>;
    users?: boolean | Array<unknown>;
};

export type IsardvdiCommonSchemasSharedHardwareGuestPropertiesGuestPropertiesCredentials = {
    username?: string;
    password?: string;
};

export type IsardvdiCommonSchemasSharedHardwareGuestPropertiesGuestPropertiesViewers = {
    browser_rdp?: {
        [key: string]: unknown;
    } | null;
    browser_vnc?: {
        [key: string]: unknown;
    } | null;
    file_rdpgw?: {
        [key: string]: unknown;
    } | null;
    file_rdpvpn?: {
        [key: string]: unknown;
    } | null;
    file_spice?: {
        [key: string]: unknown;
    } | null;
};

export type ApiV4CategoriesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/categories';
};

export type ApiV4CategoriesErrors = {
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type ApiV4CategoriesError = ApiV4CategoriesErrors[keyof ApiV4CategoriesErrors];

export type ApiV4CategoriesResponses = {
    /**
     * Successful Response
     */
    200: CategoryResponseList;
};

export type ApiV4CategoriesResponse = ApiV4CategoriesResponses[keyof ApiV4CategoriesResponses];

export type ApiV4CategoryData = {
    body?: never;
    path: {
        custom_url: string;
    };
    query?: never;
    url: '/api/v4/item/category/{custom_url}';
};

export type ApiV4CategoryErrors = {
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type ApiV4CategoryError = ApiV4CategoryErrors[keyof ApiV4CategoryErrors];

export type ApiV4CategoryResponses = {
    /**
     * Successful Response
     */
    200: CategoryResponse;
};

export type ApiV4CategoryResponse = ApiV4CategoryResponses[keyof ApiV4CategoryResponses];

export type ApiV4LoginConfigData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/login-config';
};

export type ApiV4LoginConfigErrors = {
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type ApiV4LoginConfigError = ApiV4LoginConfigErrors[keyof ApiV4LoginConfigErrors];

export type ApiV4LoginConfigResponses = {
    /**
     * Successful Response
     */
    200: LoginConfigResponse;
};

export type ApiV4LoginConfigResponse = ApiV4LoginConfigResponses[keyof ApiV4LoginConfigResponses];

export type GetLoginConfigByCategoryData = {
    body?: never;
    path: {
        category_id: string;
    };
    query?: never;
    url: '/api/v4/item/login-config/{category_id}';
};

export type GetLoginConfigByCategoryErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetLoginConfigByCategoryError = GetLoginConfigByCategoryErrors[keyof GetLoginConfigByCategoryErrors];

export type GetLoginConfigByCategoryResponses = {
    /**
     * Successful Response
     */
    200: LoginConfigResponse;
};

export type GetLoginConfigByCategoryResponse = GetLoginConfigByCategoryResponses[keyof GetLoginConfigByCategoryResponses];

export type MaintenanceStatusData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/maintenance/status';
};

export type MaintenanceStatusErrors = {
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type MaintenanceStatusError = MaintenanceStatusErrors[keyof MaintenanceStatusErrors];

export type MaintenanceStatusResponses = {
    /**
     * Successful Response
     */
    200: MaintenanceStatusResponse;
};

export type MaintenanceStatusResponse2 = MaintenanceStatusResponses[keyof MaintenanceStatusResponses];

export type GetMaintenanceTextFrontendData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/maintenance/text/frontend';
};

export type GetMaintenanceTextFrontendErrors = {
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetMaintenanceTextFrontendError = GetMaintenanceTextFrontendErrors[keyof GetMaintenanceTextFrontendErrors];

export type GetMaintenanceTextFrontendResponses = {
    /**
     * Successful Response
     */
    200: MaintenanceTextResponse;
    /**
     * No Content
     */
    204: void;
};

export type GetMaintenanceTextFrontendResponse = GetMaintenanceTextFrontendResponses[keyof GetMaintenanceTextFrontendResponses];

export type ApiVersionData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/';
};

export type ApiVersionResponses = {
    /**
     * Successful Response
     */
    200: ApiVersion;
};

export type ApiVersionResponse = ApiVersionResponses[keyof ApiVersionResponses];

export type ApiV4CategoryCustomUrlData = {
    body?: never;
    path: {
        category_id: string;
    };
    query?: never;
    url: '/api/v4/item/category/{category_id}/custom_url';
};

export type ApiV4CategoryCustomUrlErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type ApiV4CategoryCustomUrlError = ApiV4CategoryCustomUrlErrors[keyof ApiV4CategoryCustomUrlErrors];

export type ApiV4CategoryCustomUrlResponses = {
    /**
     * Successful Response
     */
    200: string;
};

export type ApiV4CategoryCustomUrlResponse = ApiV4CategoryCustomUrlResponses[keyof ApiV4CategoryCustomUrlResponses];

export type GetLogoData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/logo';
};

export type GetLogoErrors = {
    /**
     * Not Found
     */
    404: ErrorResponse;
};

export type GetLogoError = GetLogoErrors[keyof GetLogoErrors];

export type GetLogoResponses = {
    /**
     * Logo image file
     */
    200: unknown;
};

export type GetCategoryLogoData = {
    body?: never;
    path: {
        category_id: string;
    };
    query?: never;
    url: '/api/v4/logo/category/{category_id}';
};

export type GetCategoryLogoErrors = {
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCategoryLogoError = GetCategoryLogoErrors[keyof GetCategoryLogoErrors];

export type GetCategoryLogoResponses = {
    /**
     * Logo image file
     */
    200: unknown;
};

export type GetLogoCollapsedData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/logo-collapsed';
};

export type GetLogoCollapsedErrors = {
    /**
     * Not Found
     */
    404: ErrorResponse;
};

export type GetLogoCollapsedError = GetLogoCollapsedErrors[keyof GetLogoCollapsedErrors];

export type GetLogoCollapsedResponses = {
    /**
     * Collapsed logo image file
     */
    200: unknown;
};

export type GetCategoryLogoCollapsedData = {
    body?: never;
    path: {
        category_id: string;
    };
    query?: never;
    url: '/api/v4/logo-collapsed/category/{category_id}';
};

export type GetCategoryLogoCollapsedErrors = {
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetCategoryLogoCollapsedError = GetCategoryLogoCollapsedErrors[keyof GetCategoryLogoCollapsedErrors];

export type GetCategoryLogoCollapsedResponses = {
    /**
     * Collapsed logo image file
     */
    200: unknown;
};

export type GetDesktopViewerByTokenData = {
    body?: never;
    path: {
        /**
         * Code provided for the desktop viewer. Mainly defined when generating the share link.
         */
        token: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/token/{token}/get-viewer';
};

export type GetDesktopViewerByTokenErrors = {
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: DesktopNotBookedErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDesktopViewerByTokenError = GetDesktopViewerByTokenErrors[keyof GetDesktopViewerByTokenErrors];

export type GetDesktopViewerByTokenResponses = {
    /**
     * Successful Response
     */
    200: DesktopViewerResponse;
};

export type GetDesktopViewerByTokenResponse = GetDesktopViewerByTokenResponses[keyof GetDesktopViewerByTokenResponses];

export type RenewDesktopViewerByTokenData = {
    body?: never;
    path: {
        /**
         * Code provided for the desktop viewer. Mainly defined when generating the share link.
         */
        token: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/token/{token}/renew-viewer';
};

export type RenewDesktopViewerByTokenErrors = {
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: DesktopNotBookedErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type RenewDesktopViewerByTokenError = RenewDesktopViewerByTokenErrors[keyof RenewDesktopViewerByTokenErrors];

export type RenewDesktopViewerByTokenResponses = {
    /**
     * Successful Response
     */
    200: DesktopViewerResponse;
};

export type RenewDesktopViewerByTokenResponse = RenewDesktopViewerByTokenResponses[keyof RenewDesktopViewerByTokenResponses];

export type GetViewerDocsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/desktop/get-viewers-docs';
};

export type GetViewerDocsErrors = {
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetViewerDocsError = GetViewerDocsErrors[keyof GetViewerDocsErrors];

export type GetViewerDocsResponses = {
    /**
     * Successful Response
     */
    200: ViewersDocsResponse;
};

export type GetViewerDocsResponse = GetViewerDocsResponses[keyof GetViewerDocsResponses];

export type LogViewerClickData = {
    body?: never;
    path: {
        /**
         * Direct viewer share token
         */
        token: string;
        /**
         * Viewer protocol used
         */
        protocol: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/token/{token}/viewer/{protocol}';
};

export type LogViewerClickErrors = {
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: DesktopNotBookedErrorResponse;
};

export type LogViewerClickError = LogViewerClickErrors[keyof LogViewerClickErrors];

export type LogViewerClickResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type LogViewerClickResponse = LogViewerClickResponses[keyof LogViewerClickResponses];

export type GetBastionTargetsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/bastions';
};

export type GetBastionTargetsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
};

export type GetBastionTargetsError = GetBastionTargetsErrors[keyof GetBastionTargetsErrors];

export type GetBastionTargetsResponses = {
    /**
     * Successful Response
     */
    200: Array<BastionResponse>;
};

export type GetBastionTargetsResponse = GetBastionTargetsResponses[keyof GetBastionTargetsResponses];

export type GetDesktopBastionData = {
    body?: never;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}/bastion';
};

export type GetDesktopBastionErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDesktopBastionError = GetDesktopBastionErrors[keyof GetDesktopBastionErrors];

export type GetDesktopBastionResponses = {
    /**
     * Successful Response
     */
    200: BastionResponse;
};

export type GetDesktopBastionResponse = GetDesktopBastionResponses[keyof GetDesktopBastionResponses];

export type UpdateDesktopBastionData = {
    body: BastionRequest;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}/bastion';
};

export type UpdateDesktopBastionErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type UpdateDesktopBastionError = UpdateDesktopBastionErrors[keyof UpdateDesktopBastionErrors];

export type UpdateDesktopBastionResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type UpdateDesktopBastionResponse = UpdateDesktopBastionResponses[keyof UpdateDesktopBastionResponses];

export type GetDesktopBastionActiveData = {
    body?: never;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}/bastion/active';
};

export type GetDesktopBastionActiveErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDesktopBastionActiveError = GetDesktopBastionActiveErrors[keyof GetDesktopBastionActiveErrors];

export type GetDesktopBastionActiveResponses = {
    /**
     * Successful Response
     */
    200: BastionActiveResponse;
};

export type GetDesktopBastionActiveResponse = GetDesktopBastionActiveResponses[keyof GetDesktopBastionActiveResponses];

export type UpdateBastionAuthorizedKeysData = {
    body: BastionAuthorizedKeysRequest;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}/bastion/authorized-keys';
};

export type UpdateBastionAuthorizedKeysErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type UpdateBastionAuthorizedKeysError = UpdateBastionAuthorizedKeysErrors[keyof UpdateBastionAuthorizedKeysErrors];

export type UpdateBastionAuthorizedKeysResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type UpdateBastionAuthorizedKeysResponse = UpdateBastionAuthorizedKeysResponses[keyof UpdateBastionAuthorizedKeysResponses];

export type UpdateBastionDomainsData = {
    body: BastionDomainsRequest;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}/bastion/domains';
};

export type UpdateBastionDomainsErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Precondition Failed
     */
    412: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type UpdateBastionDomainsError = UpdateBastionDomainsErrors[keyof UpdateBastionDomainsErrors];

export type UpdateBastionDomainsResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type UpdateBastionDomainsResponse = UpdateBastionDomainsResponses[keyof UpdateBastionDomainsResponses];

export type VerifyBastionDomainData = {
    body: ApiSchemasBastionBastionDomainVerifyRequest;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}/bastion/domain/verify';
};

export type VerifyBastionDomainErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Precondition Failed
     */
    412: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type VerifyBastionDomainError = VerifyBastionDomainErrors[keyof VerifyBastionDomainErrors];

export type VerifyBastionDomainResponses = {
    /**
     * Successful Response
     */
    200: ApiSchemasBastionBastionDomainVerifyResponse;
};

export type VerifyBastionDomainResponse = VerifyBastionDomainResponses[keyof VerifyBastionDomainResponses];

export type GetDesktopCardImagesData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Desktop ID to filter user cards
         */
        desktop_id?: string | null;
    };
    url: '/api/v4/items/images/desktops';
};

export type GetDesktopCardImagesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDesktopCardImagesError = GetDesktopCardImagesErrors[keyof GetDesktopCardImagesErrors];

export type GetDesktopCardImagesResponses = {
    /**
     * Successful Response
     */
    200: Array<CardResponse>;
};

export type GetDesktopCardImagesResponse = GetDesktopCardImagesResponses[keyof GetDesktopCardImagesResponses];

export type GetDesktopImagesByTypeData = {
    body?: never;
    path: {
        kind: 'stock' | 'user';
    };
    query?: {
        /**
         * Desktop ID to filter user cards
         */
        desktop_id?: string | null;
    };
    url: '/api/v4/images/desktops/{kind}';
};

export type GetDesktopImagesByTypeErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDesktopImagesByTypeError = GetDesktopImagesByTypeErrors[keyof GetDesktopImagesByTypeErrors];

export type GetDesktopImagesByTypeResponses = {
    /**
     * Successful Response
     */
    200: Array<CardResponse>;
};

export type GetDesktopImagesByTypeResponse = GetDesktopImagesByTypeResponses[keyof GetDesktopImagesByTypeResponses];

export type GetStockDefaultCardData = {
    body?: never;
    path: {
        domain_id: string;
    };
    query?: never;
    url: '/api/v4/images/desktops/stock/default/{domain_id}';
};

export type GetStockDefaultCardErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetStockDefaultCardError = GetStockDefaultCardErrors[keyof GetStockDefaultCardErrors];

export type GetStockDefaultCardResponses = {
    /**
     * Successful Response
     */
    200: CardResponse;
};

export type GetStockDefaultCardResponse = GetStockDefaultCardResponses[keyof GetStockDefaultCardResponses];

export type GetUserDefaultCardData = {
    body?: never;
    path: {
        domain_id: string;
    };
    query?: never;
    url: '/api/v4/images/desktops/user/default/{domain_id}';
};

export type GetUserDefaultCardErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetUserDefaultCardError = GetUserDefaultCardErrors[keyof GetUserDefaultCardErrors];

export type GetUserDefaultCardResponses = {
    /**
     * Successful Response
     */
    200: CardResponse;
};

export type GetUserDefaultCardResponse = GetUserDefaultCardResponses[keyof GetUserDefaultCardResponses];

export type StopUserDesktopsInDeploymentData = {
    body?: DeploymentStopRequest;
    path: {
        deployment_id: string;
        user_id: string;
    };
    query?: never;
    url: '/api/v4/item/deployment/{deployment_id}/user/{user_id}/stop';
};

export type StopUserDesktopsInDeploymentErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * User not allowed or not a member of the deployment
     */
    403: ErrorResponse;
    /**
     * No dekstops found for user
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type StopUserDesktopsInDeploymentError = StopUserDesktopsInDeploymentErrors[keyof StopUserDesktopsInDeploymentErrors];

export type StopUserDesktopsInDeploymentResponses = {
    /**
     * Desktops stopped successfully
     */
    204: void;
};

export type StopUserDesktopsInDeploymentResponse = StopUserDesktopsInDeploymentResponses[keyof StopUserDesktopsInDeploymentResponses];

export type GetAllSharedDeploymentsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/deployments/get-shared';
};

export type GetAllSharedDeploymentsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetAllSharedDeploymentsError = GetAllSharedDeploymentsErrors[keyof GetAllSharedDeploymentsErrors];

export type GetAllSharedDeploymentsResponses = {
    /**
     * Successful Response
     */
    200: SharedDeploymentsResponse;
};

export type GetAllSharedDeploymentsResponse = GetAllSharedDeploymentsResponses[keyof GetAllSharedDeploymentsResponses];

export type GetDeploymentUserDesktopsData = {
    body?: never;
    path: {
        deployment_id: string;
        user_id: string;
    };
    query?: never;
    url: '/api/v4/item/deployment/{deployment_id}/desktops/user/{user_id}';
};

export type GetDeploymentUserDesktopsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDeploymentUserDesktopsError = GetDeploymentUserDesktopsErrors[keyof GetDeploymentUserDesktopsErrors];

export type GetDeploymentUserDesktopsResponses = {
    /**
     * Successful Response
     */
    200: UserDeploymentResponse;
};

export type GetDeploymentUserDesktopsResponse = GetDeploymentUserDesktopsResponses[keyof GetDeploymentUserDesktopsResponses];

export type GetDeploymentUserDesktopsDetailData = {
    body?: never;
    path: {
        deployment_id: string;
        user_id: string;
    };
    query?: never;
    url: '/api/v4/item/deployment/{deployment_id}/desktops/user/{user_id}/detail';
};

export type GetDeploymentUserDesktopsDetailErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDeploymentUserDesktopsDetailError = GetDeploymentUserDesktopsDetailErrors[keyof GetDeploymentUserDesktopsDetailErrors];

export type GetDeploymentUserDesktopsDetailResponses = {
    /**
     * Successful Response
     */
    200: Array<DesktopDetailsResponse>;
};

export type GetDeploymentUserDesktopsDetailResponse = GetDeploymentUserDesktopsDetailResponses[keyof GetDeploymentUserDesktopsDetailResponses];

export type DeleteMediaData = {
    body?: never;
    path: {
        media_id: string;
    };
    query?: never;
    url: '/api/v4/item/media/{media_id}';
};

export type DeleteMediaErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type DeleteMediaError = DeleteMediaErrors[keyof DeleteMediaErrors];

export type DeleteMediaResponses = {
    /**
     * Successful Response
     */
    200: DeleteResponse;
    /**
     * Accepted
     */
    202: DeleteResponse;
};

export type DeleteMediaResponse = DeleteMediaResponses[keyof DeleteMediaResponses];

export type GetMediaData = {
    body?: never;
    path: {
        media_id: string;
    };
    query?: never;
    url: '/api/v4/item/media/{media_id}';
};

export type GetMediaErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetMediaError = GetMediaErrors[keyof GetMediaErrors];

export type GetMediaResponses = {
    /**
     * Successful Response
     */
    200: MediaResponse;
};

export type GetMediaResponse = GetMediaResponses[keyof GetMediaResponses];

export type GetMediaTasksData = {
    body?: never;
    path: {
        media_id: string;
    };
    query?: never;
    url: '/api/v4/item/media/{media_id}/tasks';
};

export type GetMediaTasksErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetMediaTasksError = GetMediaTasksErrors[keyof GetMediaTasksErrors];

export type GetMediaTasksResponses = {
    /**
     * Successful Response
     */
    200: Array<OwnerTaskItem>;
};

export type GetMediaTasksResponse = GetMediaTasksResponses[keyof GetMediaTasksResponses];

export type GetUserMediaData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/media';
};

export type GetUserMediaErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetUserMediaError = GetUserMediaErrors[keyof GetUserMediaErrors];

export type GetUserMediaResponses = {
    /**
     * Successful Response
     */
    200: UserMediaResponse;
};

export type GetUserMediaResponse = GetUserMediaResponses[keyof GetUserMediaResponses];

export type GetUserSharedMediaData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/media/get-shared';
};

export type GetUserSharedMediaErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetUserSharedMediaError = GetUserSharedMediaErrors[keyof GetUserSharedMediaErrors];

export type GetUserSharedMediaResponses = {
    /**
     * Successful Response
     */
    200: UserSharedMediaResponse;
};

export type GetUserSharedMediaResponse = GetUserSharedMediaResponses[keyof GetUserSharedMediaResponses];

export type GetUserAllowedMediaData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Start the retrieval after the given accessed. If not provided, starts from the beginning.
         */
        start_after?: number;
        /**
         * Number of media to return. Default is 10. The given value will be multiplied by 5 in order to preload more media for the user.
         */
        page_size?: number;
        /**
         * Field to sort the media by. Default is 'accessed'.
         */
        sort_field?: 'accessed';
        /**
         * Order to sort the media by. Default is 'desc'. Can be 'asc' or 'desc'.
         */
        sort_order?: 'desc' | 'asc';
        /**
         * Search term to filter media by name or description. If provided, search_field must also be provided.
         */
        search?: string;
        /**
         * Field to search in. If not provided, no search is performed.
         */
        search_field?: UserAllowedMediaSearchFields | null;
    };
    url: '/api/v4/items/media/get-allowed';
};

export type GetUserAllowedMediaErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetUserAllowedMediaError = GetUserAllowedMediaErrors[keyof GetUserAllowedMediaErrors];

export type GetUserAllowedMediaResponses = {
    /**
     * Successful Response
     */
    200: UserAllowedMediaPaginationResponse;
};

export type GetUserAllowedMediaResponse = GetUserAllowedMediaResponses[keyof GetUserAllowedMediaResponses];

export type GetMediaDesktopsData = {
    body?: never;
    path: {
        media_id: string;
    };
    query?: never;
    url: '/api/v4/item/media/{media_id}/get-desktops';
};

export type GetMediaDesktopsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetMediaDesktopsError = GetMediaDesktopsErrors[keyof GetMediaDesktopsErrors];

export type GetMediaDesktopsResponses = {
    /**
     * Successful Response
     */
    200: Array<MediaDesktopResponse>;
};

export type GetMediaDesktopsResponse = GetMediaDesktopsResponses[keyof GetMediaDesktopsResponses];

export type MigrationImportUserData = {
    body: ImportUserRequest;
    path?: never;
    query?: never;
    url: '/api/v4/item/user-migration/import-user';
};

export type MigrationImportUserErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type MigrationImportUserError = MigrationImportUserErrors[keyof MigrationImportUserErrors];

export type MigrationImportUserResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type MigrationImportUserResponse = MigrationImportUserResponses[keyof MigrationImportUserResponses];

export type MigrationListItemsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/user-migration/list-items';
};

export type MigrationListItemsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type MigrationListItemsError = MigrationListItemsErrors[keyof MigrationListItemsErrors];

export type MigrationListItemsResponses = {
    /**
     * Successful Response
     */
    200: MigrationListItemsResponse;
};

export type MigrationListItemsResponse2 = MigrationListItemsResponses[keyof MigrationListItemsResponses];

export type MigrationMigrateUserData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/user-migration/migrate-user';
};

export type MigrationMigrateUserErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type MigrationMigrateUserError = MigrationMigrateUserErrors[keyof MigrationMigrateUserErrors];

export type MigrationMigrateUserResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type MigrationMigrateUserResponse = MigrationMigrateUserResponses[keyof MigrationMigrateUserResponses];

export type GetStatusBarNotificationsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/notifications/status-bar';
};

export type GetStatusBarNotificationsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetStatusBarNotificationsError = GetStatusBarNotificationsErrors[keyof GetStatusBarNotificationsErrors];

export type GetStatusBarNotificationsResponses = {
    /**
     * Successful Response
     */
    200: StatusBarNotificationResponse | null;
};

export type GetStatusBarNotificationsResponse = GetStatusBarNotificationsResponses[keyof GetStatusBarNotificationsResponses];

export type GetUserNotificationDisplaysData = {
    body?: never;
    path: {
        trigger: string;
    };
    query?: never;
    url: '/api/v4/items/notifications/user/displays/{trigger}';
};

export type GetUserNotificationDisplaysErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetUserNotificationDisplaysError = GetUserNotificationDisplaysErrors[keyof GetUserNotificationDisplaysErrors];

export type GetUserNotificationDisplaysResponses = {
    /**
     * Successful Response
     */
    200: NotificationsUserDisplaysTriggerResponse;
};

export type GetUserNotificationDisplaysResponse = GetUserNotificationDisplaysResponses[keyof GetUserNotificationDisplaysResponses];

export type GetUserNotificationTriggerDisplayData = {
    body?: never;
    path: {
        trigger: string;
        display: NotificationDisplayEnum;
    };
    query?: never;
    url: '/api/v4/items/notifications/user/{trigger}/{display}';
};

export type GetUserNotificationTriggerDisplayErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetUserNotificationTriggerDisplayError = GetUserNotificationTriggerDisplayErrors[keyof GetUserNotificationTriggerDisplayErrors];

export type GetUserNotificationTriggerDisplayResponses = {
    /**
     * Successful Response
     */
    200: NotificationsUserTriggerDisplayFlatResponse;
};

export type GetUserNotificationTriggerDisplayResponse = GetUserNotificationTriggerDisplayResponses[keyof GetUserNotificationTriggerDisplayResponses];

export type GetUserNotificationTriggerDisplayNestedData = {
    body?: never;
    path: {
        trigger: string;
        display: NotificationDisplayEnum;
    };
    query?: never;
    url: '/api/v4/notification/user/{trigger}/{display}';
};

export type GetUserNotificationTriggerDisplayNestedErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetUserNotificationTriggerDisplayNestedError = GetUserNotificationTriggerDisplayNestedErrors[keyof GetUserNotificationTriggerDisplayNestedErrors];

export type GetUserNotificationTriggerDisplayNestedResponses = {
    /**
     * Successful Response
     */
    200: NotificationsUserTriggerDisplayResponse;
};

export type GetUserNotificationTriggerDisplayNestedResponse = GetUserNotificationTriggerDisplayNestedResponses[keyof GetUserNotificationTriggerDisplayNestedResponses];

export type CheckQuotaNewDesktopData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/quota/desktop/new';
};

export type CheckQuotaNewDesktopErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Cannot create a new desktop, as it would exceed quota
     */
    428: ErrorResponse;
};

export type CheckQuotaNewDesktopError = CheckQuotaNewDesktopErrors[keyof CheckQuotaNewDesktopErrors];

export type CheckQuotaNewDesktopResponses = {
    /**
     * Can create a new desktop
     */
    204: void;
};

export type CheckQuotaNewDesktopResponse = CheckQuotaNewDesktopResponses[keyof CheckQuotaNewDesktopResponses];

export type CheckQuotaNewVolatileDesktopData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/quota/desktop/new-volatile';
};

export type CheckQuotaNewVolatileDesktopErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Cannot create a new temporal desktop, as it would exceed quota
     */
    428: ErrorResponse;
};

export type CheckQuotaNewVolatileDesktopError = CheckQuotaNewVolatileDesktopErrors[keyof CheckQuotaNewVolatileDesktopErrors];

export type CheckQuotaNewVolatileDesktopResponses = {
    /**
     * Can create a new temporal desktop
     */
    204: void;
};

export type CheckQuotaNewVolatileDesktopResponse = CheckQuotaNewVolatileDesktopResponses[keyof CheckQuotaNewVolatileDesktopResponses];

export type GetRecycleBinDefaultDeleteConfigData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/recycle-bin/get-default-delete-config';
};

export type GetRecycleBinDefaultDeleteConfigErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
};

export type GetRecycleBinDefaultDeleteConfigError = GetRecycleBinDefaultDeleteConfigErrors[keyof GetRecycleBinDefaultDeleteConfigErrors];

export type GetRecycleBinDefaultDeleteConfigResponses = {
    /**
     * Successful Response
     */
    200: boolean;
};

export type GetRecycleBinDefaultDeleteConfigResponse = GetRecycleBinDefaultDeleteConfigResponses[keyof GetRecycleBinDefaultDeleteConfigResponses];

export type GetRecycleBinCutoffTimeData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/recycle-bin/get-user-cutoff-time';
};

export type GetRecycleBinCutoffTimeErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Failed to retrieve cutoff time
     */
    500: unknown;
};

export type GetRecycleBinCutoffTimeError = GetRecycleBinCutoffTimeErrors[keyof GetRecycleBinCutoffTimeErrors];

export type GetRecycleBinCutoffTimeResponses = {
    /**
     * Successful Response
     */
    200: RecycleBinCutoffTimeResponse;
};

export type GetRecycleBinCutoffTimeResponse = GetRecycleBinCutoffTimeResponses[keyof GetRecycleBinCutoffTimeResponses];

export type GetRecycleBinCountData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/recycle-bin/count';
};

export type GetRecycleBinCountErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
};

export type GetRecycleBinCountError = GetRecycleBinCountErrors[keyof GetRecycleBinCountErrors];

export type GetRecycleBinCountResponses = {
    /**
     * Successful Response
     */
    200: number;
};

export type GetRecycleBinCountResponse = GetRecycleBinCountResponses[keyof GetRecycleBinCountResponses];

export type EmptyRecycleBinData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/recycle-bin/empty';
};

export type EmptyRecycleBinErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type EmptyRecycleBinError = EmptyRecycleBinErrors[keyof EmptyRecycleBinErrors];

export type EmptyRecycleBinResponses = {
    /**
     * Successful Response
     */
    202: DeleteResponse;
};

export type EmptyRecycleBinResponse = EmptyRecycleBinResponses[keyof EmptyRecycleBinResponses];

export type GetRecycleBinItemCountUserData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/recycle-bin';
};

export type GetRecycleBinItemCountUserErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
};

export type GetRecycleBinItemCountUserError = GetRecycleBinItemCountUserErrors[keyof GetRecycleBinItemCountUserErrors];

export type GetRecycleBinItemCountUserResponses = {
    /**
     * Successful Response
     */
    200: RecycleBinEntriesResponse;
};

export type GetRecycleBinItemCountUserResponse = GetRecycleBinItemCountUserResponses[keyof GetRecycleBinItemCountUserResponses];

export type DeleteRecycleBinEntryData = {
    body?: never;
    path: {
        recycle_bin_id: string;
    };
    query?: never;
    url: '/api/v4/item/recycle-bin/{recycle_bin_id}';
};

export type DeleteRecycleBinEntryErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type DeleteRecycleBinEntryError = DeleteRecycleBinEntryErrors[keyof DeleteRecycleBinEntryErrors];

export type DeleteRecycleBinEntryResponses = {
    /**
     * Successful Response
     */
    202: DeleteResponse;
};

export type DeleteRecycleBinEntryResponse = DeleteRecycleBinEntryResponses[keyof DeleteRecycleBinEntryResponses];

export type GetRecycleBinData = {
    body?: never;
    path: {
        recycle_bin_id: string;
    };
    query?: never;
    url: '/api/v4/item/recycle-bin/{recycle_bin_id}';
};

export type GetRecycleBinErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetRecycleBinError = GetRecycleBinErrors[keyof GetRecycleBinErrors];

export type GetRecycleBinResponses = {
    /**
     * Successful Response
     */
    200: RecycleBinResponse;
};

export type GetRecycleBinResponse = GetRecycleBinResponses[keyof GetRecycleBinResponses];

export type RestoreRecycleBinData = {
    body?: never;
    path: {
        recycle_bin_id: string;
    };
    query?: never;
    url: '/api/v4/item/recycle-bin/{recycle_bin_id}/restore';
};

export type RestoreRecycleBinErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: RecycleBinRestoreErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type RestoreRecycleBinError = RestoreRecycleBinErrors[keyof RestoreRecycleBinErrors];

export type RestoreRecycleBinResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type RestoreRecycleBinResponse = RestoreRecycleBinResponses[keyof RestoreRecycleBinResponses];

export type BulkRestoreRecycleBinData = {
    body: RecycleBinBulkRequest;
    path?: never;
    query?: never;
    url: '/api/v4/items/recycle-bin/restore';
};

export type BulkRestoreRecycleBinErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type BulkRestoreRecycleBinError = BulkRestoreRecycleBinErrors[keyof BulkRestoreRecycleBinErrors];

export type BulkRestoreRecycleBinResponses = {
    /**
     * Successful Response
     */
    200: RecycleBinBulkResponse;
};

export type BulkRestoreRecycleBinResponse = BulkRestoreRecycleBinResponses[keyof BulkRestoreRecycleBinResponses];

export type BulkDeleteRecycleBinData = {
    body: RecycleBinBulkRequest;
    path?: never;
    query?: never;
    url: '/api/v4/items/recycle-bin/delete';
};

export type BulkDeleteRecycleBinErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type BulkDeleteRecycleBinError = BulkDeleteRecycleBinErrors[keyof BulkDeleteRecycleBinErrors];

export type BulkDeleteRecycleBinResponses = {
    /**
     * Successful Response
     */
    200: RecycleBinBulkResponse;
};

export type BulkDeleteRecycleBinResponse = BulkDeleteRecycleBinResponses[keyof BulkDeleteRecycleBinResponses];

export type DeleteStorageData = {
    body?: never;
    path: {
        storage_id: string;
    };
    query?: never;
    url: '/api/v4/item/storage/{storage_id}';
};

export type DeleteStorageErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type DeleteStorageError = DeleteStorageErrors[keyof DeleteStorageErrors];

export type DeleteStorageResponses = {
    /**
     * Successful Response
     */
    202: DeleteResponse;
};

export type DeleteStorageResponse = DeleteStorageResponses[keyof DeleteStorageResponses];

export type GetStorageData = {
    body?: never;
    path: {
        storage_id: string;
    };
    query?: never;
    url: '/api/v4/item/storage/{storage_id}';
};

export type GetStorageErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetStorageError = GetStorageErrors[keyof GetStorageErrors];

export type GetStorageResponses = {
    /**
     * Successful Response
     */
    200: StorageDetailResponse;
};

export type GetStorageResponse = GetStorageResponses[keyof GetStorageResponses];

export type GetStorageTasksData = {
    body?: never;
    path: {
        storage_id: string;
    };
    query?: never;
    url: '/api/v4/item/storage/{storage_id}/tasks';
};

export type GetStorageTasksErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetStorageTasksError = GetStorageTasksErrors[keyof GetStorageTasksErrors];

export type GetStorageTasksResponses = {
    /**
     * Successful Response
     */
    200: Array<OwnerTaskItem>;
};

export type GetStorageTasksResponse = GetStorageTasksResponses[keyof GetStorageTasksResponses];

export type GetUserReadyStoragesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/storage/ready';
};

export type GetUserReadyStoragesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetUserReadyStoragesError = GetUserReadyStoragesErrors[keyof GetUserReadyStoragesErrors];

export type GetUserReadyStoragesResponses = {
    /**
     * Successful Response
     */
    200: Array<StorageItem>;
};

export type GetUserReadyStoragesResponse = GetUserReadyStoragesResponses[keyof GetUserReadyStoragesResponses];

export type AbortStorageOperationsData = {
    body?: never;
    path: {
        storage_id: string;
    };
    query?: never;
    url: '/api/v4/item/storage/{storage_id}/abort-operations';
};

export type AbortStorageOperationsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AbortStorageOperationsError = AbortStorageOperationsErrors[keyof AbortStorageOperationsErrors];

export type AbortStorageOperationsResponses = {
    /**
     * Successful Response
     */
    200: TaskIdResponse;
};

export type AbortStorageOperationsResponse = AbortStorageOperationsResponses[keyof AbortStorageOperationsResponses];

export type GetStorageReadyData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/storage/get-ready';
};

export type GetStorageReadyErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetStorageReadyError = GetStorageReadyErrors[keyof GetStorageReadyErrors];

export type GetStorageReadyResponses = {
    /**
     * Successful Response
     */
    200: StorageReadyResponse;
};

export type GetStorageReadyResponse = GetStorageReadyResponses[keyof GetStorageReadyResponses];

export type CheckStoragePoolCreationAvailabilityData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/storage-pools/check-create-availability';
};

export type CheckStoragePoolCreationAvailabilityErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
};

export type CheckStoragePoolCreationAvailabilityError = CheckStoragePoolCreationAvailabilityErrors[keyof CheckStoragePoolCreationAvailabilityErrors];

export type CheckStoragePoolCreationAvailabilityResponses = {
    /**
     * Can create a new storage pool
     */
    204: void;
};

export type CheckStoragePoolCreationAvailabilityResponse = CheckStoragePoolCreationAvailabilityResponses[keyof CheckStoragePoolCreationAvailabilityResponses];

export type CheckStoragePoolAvailabilityCompatData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/storage-pool/availability';
};

export type CheckStoragePoolAvailabilityCompatErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type CheckStoragePoolAvailabilityCompatError = CheckStoragePoolAvailabilityCompatErrors[keyof CheckStoragePoolAvailabilityCompatErrors];

export type CheckStoragePoolAvailabilityCompatResponses = {
    /**
     * Successful Response
     */
    200: CheckCategoryAvailabilityResponse;
    /**
     * Available
     */
    204: void;
};

export type CheckStoragePoolAvailabilityCompatResponse = CheckStoragePoolAvailabilityCompatResponses[keyof CheckStoragePoolAvailabilityCompatResponses];

export type GetQueuesHealthData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/queues/health';
};

export type GetQueuesHealthErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
};

export type GetQueuesHealthError = GetQueuesHealthErrors[keyof GetQueuesHealthErrors];

export type GetQueuesHealthResponses = {
    /**
     * Successful Response
     */
    200: QueuesHealthResponse;
};

export type GetQueuesHealthResponse = GetQueuesHealthResponses[keyof GetQueuesHealthResponses];

export type CancelTaskData = {
    body?: never;
    path: {
        task_id: string;
    };
    query?: never;
    url: '/api/v4/task/{task_id}';
};

export type CancelTaskErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type CancelTaskError = CancelTaskErrors[keyof CancelTaskErrors];

export type CancelTaskResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type CancelTaskResponse = CancelTaskResponses[keyof CancelTaskResponses];

export type GetTaskData = {
    body?: never;
    path: {
        task_id: string;
    };
    query?: never;
    url: '/api/v4/task/{task_id}';
};

export type GetTaskErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetTaskError = GetTaskErrors[keyof GetTaskErrors];

export type GetTaskResponses = {
    /**
     * Successful Response
     */
    200: TaskResponse;
};

export type GetTaskResponse = GetTaskResponses[keyof GetTaskResponses];

export type GetUserTasksData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/tasks';
};

export type GetUserTasksErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetUserTasksError = GetUserTasksErrors[keyof GetUserTasksErrors];

export type GetUserTasksResponses = {
    /**
     * Successful Response
     */
    200: Array<TaskResponse>;
};

export type GetUserTasksResponse = GetUserTasksResponses[keyof GetUserTasksResponses];

export type GetUserVpnData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/user/get-vpn';
};

export type GetUserVpnErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Failed to retrieve the user VPN
     */
    500: unknown;
};

export type GetUserVpnError = GetUserVpnErrors[keyof GetUserVpnErrors];

export type GetUserVpnResponses = {
    /**
     * VPN configuration file (text/plain or download)
     */
    200: unknown;
};

export type UserResetVpnData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/user/reset-vpn';
};

export type UserResetVpnErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Failed to reset the user VPN
     */
    500: unknown;
};

export type UserResetVpnError = UserResetVpnErrors[keyof UserResetVpnErrors];

export type UserResetVpnResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type UserResetVpnResponse = UserResetVpnResponses[keyof UserResetVpnResponses];

export type GetAllowedHardwareData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/user/get-allowed-hardware';
};

export type GetAllowedHardwareErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Failed to retrieve allowed hardware
     */
    500: unknown;
};

export type GetAllowedHardwareError = GetAllowedHardwareErrors[keyof GetAllowedHardwareErrors];

export type GetAllowedHardwareResponses = {
    /**
     * Successful Response
     */
    200: UserAllowedHardwareResponse;
};

export type GetAllowedHardwareResponse = GetAllowedHardwareResponses[keyof GetAllowedHardwareResponses];

export type GetAllowedHardwareForDomainData = {
    body?: never;
    path: {
        domain_id: string;
    };
    query?: never;
    url: '/api/v4/item/user/get-allowed-hardware/{domain_id}';
};

export type GetAllowedHardwareForDomainErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Failed to retrieve allowed hardware
     */
    500: unknown;
};

export type GetAllowedHardwareForDomainError = GetAllowedHardwareForDomainErrors[keyof GetAllowedHardwareForDomainErrors];

export type GetAllowedHardwareForDomainResponses = {
    /**
     * Successful Response
     */
    200: UserAllowedHardwareResponse;
};

export type GetAllowedHardwareForDomainResponse = GetAllowedHardwareForDomainResponses[keyof GetAllowedHardwareForDomainResponses];

export type DeleteUserData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/user';
};

export type DeleteUserErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * User not found
     */
    404: unknown;
    /**
     * Failed to delete user
     */
    500: unknown;
};

export type DeleteUserError = DeleteUserErrors[keyof DeleteUserErrors];

export type DeleteUserResponses = {
    /**
     * User deleted successfully
     */
    204: void;
};

export type DeleteUserResponse = DeleteUserResponses[keyof DeleteUserResponses];

export type GetUserData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/user';
};

export type GetUserErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
};

export type GetUserError = GetUserErrors[keyof GetUserErrors];

export type GetUserResponses = {
    /**
     * Successful Response
     */
    200: UserResponse;
};

export type GetUserResponse = GetUserResponses[keyof GetUserResponses];

export type GetUserDetailsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/user/get-details';
};

export type GetUserDetailsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
};

export type GetUserDetailsError = GetUserDetailsErrors[keyof GetUserDetailsErrors];

export type GetUserDetailsResponses = {
    /**
     * Successful Response
     */
    200: UserDetailsResponse;
};

export type GetUserDetailsResponse = GetUserDetailsResponses[keyof GetUserDetailsResponses];

export type GetUserQuotasData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/user/get-quotas';
};

export type GetUserQuotasErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
};

export type GetUserQuotasError = GetUserQuotasErrors[keyof GetUserQuotasErrors];

export type GetUserQuotasResponses = {
    /**
     * Successful Response
     */
    200: UserQuotaResponse;
};

export type GetUserQuotasResponse = GetUserQuotasResponses[keyof GetUserQuotasResponses];

export type GetUserConfigData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/user/get-config';
};

export type GetUserConfigErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
};

export type GetUserConfigError = GetUserConfigErrors[keyof GetUserConfigErrors];

export type GetUserConfigResponses = {
    /**
     * Successful Response
     */
    200: UserConfigResponse;
};

export type GetUserConfigResponse = GetUserConfigResponses[keyof GetUserConfigResponses];

export type SetUserLanguageData = {
    body: UserSetLangPutData;
    path?: never;
    query?: never;
    url: '/api/v4/item/user/set-lang';
};

export type SetUserLanguageErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * User not found
     */
    404: unknown;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Failed to set language
     */
    500: unknown;
};

export type SetUserLanguageError = SetUserLanguageErrors[keyof SetUserLanguageErrors];

export type SetUserLanguageResponses = {
    /**
     * Language set successfully
     */
    200: SimpleResponse;
};

export type SetUserLanguageResponse = SetUserLanguageResponses[keyof SetUserLanguageResponses];

export type DeleteUserBastionSshKeyData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/user/bastion-ssh-key';
};

export type DeleteUserBastionSshKeyErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type DeleteUserBastionSshKeyError = DeleteUserBastionSshKeyErrors[keyof DeleteUserBastionSshKeyErrors];

export type DeleteUserBastionSshKeyResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteUserBastionSshKeyResponse = DeleteUserBastionSshKeyResponses[keyof DeleteUserBastionSshKeyResponses];

export type GetUserBastionSshKeyData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/user/bastion-ssh-key';
};

export type GetUserBastionSshKeyErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
};

export type GetUserBastionSshKeyError = GetUserBastionSshKeyErrors[keyof GetUserBastionSshKeyErrors];

export type GetUserBastionSshKeyResponses = {
    /**
     * Successful Response
     */
    200: UserBastionSshKeyResponse;
};

export type GetUserBastionSshKeyResponse = GetUserBastionSshKeyResponses[keyof GetUserBastionSshKeyResponses];

export type SetUserBastionSshKeyData = {
    body: UserSetBastionSshKeyPutData;
    path?: never;
    query?: never;
    url: '/api/v4/item/user/bastion-ssh-key';
};

export type SetUserBastionSshKeyErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type SetUserBastionSshKeyError = SetUserBastionSshKeyErrors[keyof SetUserBastionSshKeyErrors];

export type SetUserBastionSshKeyResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type SetUserBastionSshKeyResponse = SetUserBastionSshKeyResponses[keyof SetUserBastionSshKeyResponses];

export type SetUserPasswordData = {
    body: UserSetPasswordPutData;
    path?: never;
    query?: never;
    url: '/api/v4/item/user/set-password';
};

export type SetUserPasswordErrors = {
    /**
     * Bad Request
     */
    400: PasswordPolicyErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * User not found
     */
    404: unknown;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Failed to set password
     */
    500: unknown;
};

export type SetUserPasswordError = SetUserPasswordErrors[keyof SetUserPasswordErrors];

export type SetUserPasswordResponses = {
    /**
     * Password set successfully
     */
    200: SimpleResponse;
};

export type SetUserPasswordResponse = SetUserPasswordResponses[keyof SetUserPasswordResponses];

export type GetUserDesktopsLegacyData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/user/desktops';
};

export type GetUserDesktopsLegacyErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * User not found
     */
    404: unknown;
    /**
     * Failed to retrieve desktops
     */
    500: unknown;
};

export type GetUserDesktopsLegacyError = GetUserDesktopsLegacyErrors[keyof GetUserDesktopsLegacyErrors];

export type GetUserDesktopsLegacyResponses = {
    /**
     * Desktops retrieved successfully
     */
    200: Array<ApiSchemasDomainsDesktopsUserDesktop>;
};

export type GetUserDesktopsLegacyResponse = GetUserDesktopsLegacyResponses[keyof GetUserDesktopsLegacyResponses];

export type GetUserDesktopData = {
    body?: never;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/user/desktop/{desktop_id}';
};

export type GetUserDesktopErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Desktop not found
     */
    404: unknown;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Failed to retrieve desktop
     */
    500: unknown;
};

export type GetUserDesktopError = GetUserDesktopErrors[keyof GetUserDesktopErrors];

export type GetUserDesktopResponses = {
    /**
     * Desktop retrieved successfully
     */
    200: ApiSchemasUsersUserDesktop;
};

export type GetUserDesktopResponse = GetUserDesktopResponses[keyof GetUserDesktopResponses];

export type GetUserVpnWithOsData = {
    body?: never;
    path: {
        kind: 'config' | 'install';
        os: string;
    };
    query?: never;
    url: '/api/v4/item/user/vpn/{kind}/{os}';
};

export type GetUserVpnWithOsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Failed to retrieve VPN data
     */
    500: unknown;
};

export type GetUserVpnWithOsError = GetUserVpnWithOsErrors[keyof GetUserVpnWithOsErrors];

export type GetUserVpnWithOsResponses = {
    /**
     * VPN data retrieved successfully
     */
    200: UserVpnData;
};

export type GetUserVpnWithOsResponse = GetUserVpnWithOsResponses[keyof GetUserVpnWithOsResponses];

export type GetUserVpnByKindData = {
    body?: never;
    path: {
        kind: 'config';
    };
    query?: never;
    url: '/api/v4/item/user/vpn/{kind}';
};

export type GetUserVpnByKindErrors = {
    /**
     * Invalid VPN request
     */
    400: unknown;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Failed to retrieve VPN data
     */
    500: unknown;
};

export type GetUserVpnByKindError = GetUserVpnByKindErrors[keyof GetUserVpnByKindErrors];

export type GetUserVpnByKindResponses = {
    /**
     * VPN data retrieved successfully
     */
    200: UserVpnData;
};

export type GetUserVpnByKindResponse = GetUserVpnByKindResponses[keyof GetUserVpnByKindResponses];

export type GetWebappDesktopsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/user/webapp-desktops';
};

export type GetWebappDesktopsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Failed to retrieve webapp desktops
     */
    500: unknown;
};

export type GetWebappDesktopsError = GetWebappDesktopsErrors[keyof GetWebappDesktopsErrors];

export type GetWebappDesktopsResponses = {
    /**
     * Webapp desktops retrieved successfully
     */
    200: Array<WebappDomainItem>;
};

export type GetWebappDesktopsResponse = GetWebappDesktopsResponses[keyof GetWebappDesktopsResponses];

export type GetWebappTemplatesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/user/webapp-templates';
};

export type GetWebappTemplatesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Failed to retrieve webapp templates
     */
    500: unknown;
};

export type GetWebappTemplatesError = GetWebappTemplatesErrors[keyof GetWebappTemplatesErrors];

export type GetWebappTemplatesResponses = {
    /**
     * Webapp templates retrieved successfully
     */
    200: Array<WebappDomainItem>;
};

export type GetWebappTemplatesResponse = GetWebappTemplatesResponses[keyof GetWebappTemplatesResponses];

export type GetHardwareKindAllowedData = {
    body?: never;
    path: {
        kind: 'interfaces' | 'graphics' | 'videos' | 'boot_order' | 'qos_id' | 'reservables' | 'forced_hyp' | 'favourite_hyp' | 'quota' | 'isos' | 'floppies' | 'disk_bus';
    };
    query?: never;
    url: '/api/v4/item/user/hardware/{kind}/allowed';
};

export type GetHardwareKindAllowedErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * User not found
     */
    404: unknown;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Failed to retrieve hardware kind allowed
     */
    500: unknown;
};

export type GetHardwareKindAllowedError = GetHardwareKindAllowedErrors[keyof GetHardwareKindAllowedErrors];

export type GetHardwareKindAllowedResponses = {
    /**
     * Hardware kind allowed retrieved successfully
     */
    200: UserHardwareKindAllowedResponse;
};

export type GetHardwareKindAllowedResponse = GetHardwareKindAllowedResponses[keyof GetHardwareKindAllowedResponses];

export type GetUserAppliedQuotaData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/user/applied-quota';
};

export type GetUserAppliedQuotaErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Failed to retrieve applied quota
     */
    500: unknown;
};

export type GetUserAppliedQuotaError = GetUserAppliedQuotaErrors[keyof GetUserAppliedQuotaErrors];

export type GetUserAppliedQuotaResponses = {
    /**
     * Applied quota retrieved successfully
     */
    200: UserAppliedQuotaResponse;
};

export type GetUserAppliedQuotaResponse = GetUserAppliedQuotaResponses[keyof GetUserAppliedQuotaResponses];

export type GetBastionAllowedData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/user/bastion-allowed';
};

export type GetBastionAllowedErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Failed to check bastion access
     */
    500: unknown;
};

export type GetBastionAllowedError = GetBastionAllowedErrors[keyof GetBastionAllowedErrors];

export type GetBastionAllowedResponses = {
    /**
     * Bastion access check completed
     */
    200: {
        [key: string]: unknown;
    };
};

export type GetBastionAllowedResponse = GetBastionAllowedResponses[keyof GetBastionAllowedResponses];

export type AllowedsTableTermData = {
    body: AllowedTermRequest;
    path: {
        /**
         * Table to search in
         */
        table: 'domains' | 'roles' | 'categories' | 'groups' | 'users' | 'media' | 'deployments';
    };
    query?: never;
    url: '/api/v4/items/alloweds/term/{table}';
};

export type AllowedsTableTermErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AllowedsTableTermError = AllowedsTableTermErrors[keyof AllowedsTableTermErrors];

export type AllowedsTableTermResponses = {
    /**
     * Successful Response
     */
    200: Array<AllowedTermItem>;
};

export type AllowedsTableTermResponse = AllowedsTableTermResponses[keyof AllowedsTableTermResponses];

export type AdminAllowedUpdateData = {
    body: AllowedUpdateRequest;
    path: {
        /**
         * Table containing the item
         */
        table: 'bastion' | 'bastion_domains' | 'bookings_priority' | 'boots' | 'desktops_priority' | 'domains' | 'interfaces' | 'media' | 'notifications' | 'qos_disk' | 'remotevpn' | 'reservables_vgpus' | 'storage_pool' | 'unused_item_timeout' | 'videos';
    };
    query?: never;
    url: '/api/v4/item/allowed/update/{table}';
};

export type AdminAllowedUpdateErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminAllowedUpdateError = AdminAllowedUpdateErrors[keyof AdminAllowedUpdateErrors];

export type AdminAllowedUpdateResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminAllowedUpdateResponse = AdminAllowedUpdateResponses[keyof AdminAllowedUpdateResponses];

export type AllowedTableData = {
    body: AllowedGetRequest;
    path: {
        /**
         * Table containing the item
         */
        table: string;
    };
    query?: never;
    url: '/api/v4/item/allowed/table/{table}';
};

export type AllowedTableErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AllowedTableError = AllowedTableErrors[keyof AllowedTableErrors];

export type AllowedTableResponses = {
    /**
     * Successful Response
     */
    200: AllowedTableResponse;
};

export type AllowedTableResponse2 = AllowedTableResponses[keyof AllowedTableResponses];

export type GetUserBookingsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Start date in ISO 8601 format. Defaults to now.
         */
        startDate?: string | null;
        /**
         * End date in ISO 8601 format. Defaults to 30 days from start.
         */
        endDate?: string | null;
    };
    url: '/api/v4/items/bookings';
};

export type GetUserBookingsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetUserBookingsError = GetUserBookingsErrors[keyof GetUserBookingsErrors];

export type GetUserBookingsResponses = {
    /**
     * Successful Response
     */
    200: Array<UserBookingResponse>;
};

export type GetUserBookingsResponse = GetUserBookingsResponses[keyof GetUserBookingsResponses];

export type GetBookingDesktopData = {
    body?: never;
    path: {
        item_id: string;
    };
    query: {
        /**
         * Start date in ISO 8601 format
         */
        startDate: string;
        /**
         * End date in ISO 8601 format
         */
        endDate: string;
        /**
         * Type of bookings to return: 'all' for all bookings, 'event' for booking events only, 'availability' for availability slots only.
         */
        returnType?: 'all' | 'event' | 'availability';
    };
    url: '/api/v4/item/booking/get-desktop/{item_id}';
};

export type GetBookingDesktopErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetBookingDesktopError = GetBookingDesktopErrors[keyof GetBookingDesktopErrors];

export type GetBookingDesktopResponses = {
    /**
     * Successful Response
     */
    200: ItemBookingsResponse;
};

export type GetBookingDesktopResponse = GetBookingDesktopResponses[keyof GetBookingDesktopResponses];

export type GetBookingPriorityDesktopData = {
    body?: never;
    path: {
        item_id: string;
    };
    query?: never;
    url: '/api/v4/items/bookings/get-priority-desktop/{item_id}';
};

export type GetBookingPriorityDesktopErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetBookingPriorityDesktopError = GetBookingPriorityDesktopErrors[keyof GetBookingPriorityDesktopErrors];

export type GetBookingPriorityDesktopResponses = {
    /**
     * Successful Response
     */
    200: BookingPriorityDesktopResponse;
};

export type GetBookingPriorityDesktopResponse = GetBookingPriorityDesktopResponses[keyof GetBookingPriorityDesktopResponses];

export type GetBookingPriorityDeploymentData = {
    body?: never;
    path: {
        item_id: string;
    };
    query?: never;
    url: '/api/v4/items/bookings/get-priority-deployment/{item_id}';
};

export type GetBookingPriorityDeploymentErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetBookingPriorityDeploymentError = GetBookingPriorityDeploymentErrors[keyof GetBookingPriorityDeploymentErrors];

export type GetBookingPriorityDeploymentResponses = {
    /**
     * Successful Response
     */
    200: BookingPriorityDesktopResponse;
};

export type GetBookingPriorityDeploymentResponse = GetBookingPriorityDeploymentResponses[keyof GetBookingPriorityDeploymentResponses];

export type DeleteBookingEventData = {
    body?: never;
    path: {
        booking_id: string;
    };
    query?: never;
    url: '/api/v4/item/booking/event/{booking_id}';
};

export type DeleteBookingEventErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type DeleteBookingEventError = DeleteBookingEventErrors[keyof DeleteBookingEventErrors];

export type DeleteBookingEventResponses = {
    /**
     * Successful Response
     */
    200: EmptyResponse;
};

export type DeleteBookingEventResponse = DeleteBookingEventResponses[keyof DeleteBookingEventResponses];

export type UpdateBookingEventData = {
    body: UpdateBookingEventRequest;
    path: {
        booking_id: string;
    };
    query?: never;
    url: '/api/v4/item/booking/event/{booking_id}/edit';
};

export type UpdateBookingEventErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Conflict
     */
    409: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type UpdateBookingEventError = UpdateBookingEventErrors[keyof UpdateBookingEventErrors];

export type UpdateBookingEventResponses = {
    /**
     * Successful Response
     */
    200: EmptyResponse;
};

export type UpdateBookingEventResponse = UpdateBookingEventResponses[keyof UpdateBookingEventResponses];

export type CreateBookingEventData = {
    body: CreateBookingEventRequest;
    path?: never;
    query?: never;
    url: '/api/v4/item/booking/event';
};

export type CreateBookingEventErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
};

export type CreateBookingEventError = CreateBookingEventErrors[keyof CreateBookingEventErrors];

export type CreateBookingEventResponses = {
    /**
     * Successful Response
     */
    201: BookingEventResponse;
};

export type CreateBookingEventResponse = CreateBookingEventResponses[keyof CreateBookingEventResponses];

export type GetMaxBookingDateData = {
    body?: never;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/booking/max-booking-date/{desktop_id}';
};

export type GetMaxBookingDateErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetMaxBookingDateError = GetMaxBookingDateErrors[keyof GetMaxBookingDateErrors];

export type GetMaxBookingDateResponses = {
    /**
     * Successful Response
     */
    200: MaxBookingDateResponse;
};

export type GetMaxBookingDateResponse = GetMaxBookingDateResponses[keyof GetMaxBookingDateResponses];

export type GetItemAvailabilityData = {
    body?: never;
    path: {
        item_type: 'desktop' | 'deployment';
        item_id: string;
    };
    query?: never;
    url: '/api/v4/item/booking/availability/{item_type}/{item_id}';
};

export type GetItemAvailabilityErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetItemAvailabilityError = GetItemAvailabilityErrors[keyof GetItemAvailabilityErrors];

export type GetItemAvailabilityResponses = {
    /**
     * Successful Response
     */
    200: Array<AvailabilityResponse>;
};

export type GetItemAvailabilityResponse = GetItemAvailabilityResponses[keyof GetItemAvailabilityResponses];

export type GetBookingReservablesAvailableData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/booking/reservables-available';
};

export type GetBookingReservablesAvailableErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetBookingReservablesAvailableError = GetBookingReservablesAvailableErrors[keyof GetBookingReservablesAvailableErrors];

export type GetBookingReservablesAvailableResponses = {
    /**
     * Successful Response
     */
    200: AvailableReservablesResponse;
};

export type GetBookingReservablesAvailableResponse = GetBookingReservablesAvailableResponses[keyof GetBookingReservablesAvailableResponses];

export type GetReservablesAvailableData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/reservables/get-available';
};

export type GetReservablesAvailableErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
};

export type GetReservablesAvailableError = GetReservablesAvailableErrors[keyof GetReservablesAvailableErrors];

export type GetReservablesAvailableResponses = {
    /**
     * Successful Response
     */
    200: AvailableReservablesResponse;
};

export type GetReservablesAvailableResponse = GetReservablesAvailableResponses[keyof GetReservablesAvailableResponses];

export type BookingProvisioningData = {
    body: BookingProvisioningRequest;
    path?: never;
    query?: never;
    url: '/api/v4/item/reservables-planner/booking-provisioning';
};

export type BookingProvisioningErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type BookingProvisioningError = BookingProvisioningErrors[keyof BookingProvisioningErrors];

export type BookingProvisioningResponses = {
    /**
     * Successful Response
     */
    200: {
        [key: string]: unknown;
    };
};

export type BookingProvisioningResponse = BookingProvisioningResponses[keyof BookingProvisioningResponses];

export type GetShareLinkData = {
    body?: never;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}/get-share-link';
};

export type GetShareLinkErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetShareLinkError = GetShareLinkErrors[keyof GetShareLinkErrors];

export type GetShareLinkResponses = {
    /**
     * Successful Response
     */
    200: DesktopShareLinkResponse;
};

export type GetShareLinkResponse = GetShareLinkResponses[keyof GetShareLinkResponses];

export type UpdateShareLinkData = {
    body: DesktopUpdateShareLinkRequest;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}/update-share-link';
};

export type UpdateShareLinkErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type UpdateShareLinkError = UpdateShareLinkErrors[keyof UpdateShareLinkErrors];

export type UpdateShareLinkResponses = {
    /**
     * Successful Response
     */
    200: DesktopShareLinkResponse;
};

export type UpdateShareLinkResponse = UpdateShareLinkResponses[keyof UpdateShareLinkResponses];

export type CreateNonpersistentDesktopData = {
    body: NewNonpersistentDesktopRequest;
    path?: never;
    query?: never;
    url: '/api/v4/item/desktop/new-nonpersistent';
};

export type CreateNonpersistentDesktopErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Conflict
     */
    409: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
    /**
     * Service Unavailable
     */
    503: ErrorResponse;
};

export type CreateNonpersistentDesktopError = CreateNonpersistentDesktopErrors[keyof CreateNonpersistentDesktopErrors];

export type CreateNonpersistentDesktopResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type CreateNonpersistentDesktopResponse = CreateNonpersistentDesktopResponses[keyof CreateNonpersistentDesktopResponses];

export type CreateDesktopData = {
    body: CreateDesktopRequest;
    path?: never;
    query?: never;
    url: '/api/v4/item/desktop';
};

export type CreateDesktopErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Conflict
     */
    409: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
    /**
     * Service Unavailable
     */
    503: ErrorResponse;
};

export type CreateDesktopError = CreateDesktopErrors[keyof CreateDesktopErrors];

export type CreateDesktopResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type CreateDesktopResponse = CreateDesktopResponses[keyof CreateDesktopResponses];

export type DeleteDesktopData = {
    body?: never;
    path: {
        desktop_id: string;
    };
    query?: {
        /**
         * Whether to permanently delete the desktop
         */
        permanent?: boolean;
    };
    url: '/api/v4/item/desktop/{desktop_id}';
};

export type DeleteDesktopErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type DeleteDesktopError = DeleteDesktopErrors[keyof DeleteDesktopErrors];

export type DeleteDesktopResponses = {
    /**
     * Successful Response
     */
    200: DeleteResponse;
    /**
     * Accepted
     */
    202: DeleteResponse;
    /**
     * No Content
     */
    204: void;
};

export type DeleteDesktopResponse = DeleteDesktopResponses[keyof DeleteDesktopResponses];

export type GetDesktopData = {
    body?: never;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}';
};

export type GetDesktopErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDesktopError = GetDesktopErrors[keyof GetDesktopErrors];

export type GetDesktopResponses = {
    /**
     * Successful Response
     */
    200: Desktop;
};

export type GetDesktopResponse = GetDesktopResponses[keyof GetDesktopResponses];

export type GetDesktopNetworksData = {
    body?: never;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}/get-networks';
};

export type GetDesktopNetworksErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDesktopNetworksError = GetDesktopNetworksErrors[keyof GetDesktopNetworksErrors];

export type GetDesktopNetworksResponses = {
    /**
     * Successful Response
     */
    200: DesktopNetworksResponse;
};

export type GetDesktopNetworksResponse = GetDesktopNetworksResponses[keyof GetDesktopNetworksResponses];

export type GetDesktopDetailsData = {
    body?: never;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}/get-details';
};

export type GetDesktopDetailsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDesktopDetailsError = GetDesktopDetailsErrors[keyof GetDesktopDetailsErrors];

export type GetDesktopDetailsResponses = {
    /**
     * Successful Response
     */
    200: DesktopDetailsResponse;
};

export type GetDesktopDetailsResponse = GetDesktopDetailsResponses[keyof GetDesktopDetailsResponses];

export type GetDesktopBastionLegacyData = {
    body?: never;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}/get-bastion';
};

export type GetDesktopBastionLegacyErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDesktopBastionLegacyError = GetDesktopBastionLegacyErrors[keyof GetDesktopBastionLegacyErrors];

export type GetDesktopBastionLegacyResponses = {
    /**
     * Successful Response
     */
    200: DesktopBastionResponse;
};

export type GetDesktopBastionLegacyResponse = GetDesktopBastionLegacyResponses[keyof GetDesktopBastionLegacyResponses];

export type UpdateDesktopBastionAuthorizedKeysData = {
    body: BastionAuthorizedKeysUpdateRequest;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}/update-bastion-authorized-keys';
};

export type UpdateDesktopBastionAuthorizedKeysErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type UpdateDesktopBastionAuthorizedKeysError = UpdateDesktopBastionAuthorizedKeysErrors[keyof UpdateDesktopBastionAuthorizedKeysErrors];

export type UpdateDesktopBastionAuthorizedKeysResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type UpdateDesktopBastionAuthorizedKeysResponse = UpdateDesktopBastionAuthorizedKeysResponses[keyof UpdateDesktopBastionAuthorizedKeysResponses];

export type ExtendDesktopTimeoutData = {
    body?: never;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}/extend-timeout';
};

export type ExtendDesktopTimeoutErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type ExtendDesktopTimeoutError = ExtendDesktopTimeoutErrors[keyof ExtendDesktopTimeoutErrors];

export type ExtendDesktopTimeoutResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type ExtendDesktopTimeoutResponse = ExtendDesktopTimeoutResponses[keyof ExtendDesktopTimeoutResponses];

export type StopDesktopData = {
    body?: never;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}/stop';
};

export type StopDesktopErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Too Many Requests
     */
    429: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type StopDesktopError = StopDesktopErrors[keyof StopDesktopErrors];

export type StopDesktopResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type StopDesktopResponse = StopDesktopResponses[keyof StopDesktopResponses];

export type StopDesktopsData = {
    body: DesktopsStopRequest;
    path?: never;
    query?: never;
    url: '/api/v4/items/desktops/stop';
};

export type StopDesktopsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type StopDesktopsError = StopDesktopsErrors[keyof StopDesktopsErrors];

export type StopDesktopsResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type StopDesktopsResponse = StopDesktopsResponses[keyof StopDesktopsResponses];

export type StartDesktopData = {
    body?: never;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}/start';
};

export type StartDesktopErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Too Many Requests
     */
    429: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
    /**
     * Service Unavailable
     */
    503: ErrorResponse;
};

export type StartDesktopError = StartDesktopErrors[keyof StartDesktopErrors];

export type StartDesktopResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type StartDesktopResponse = StartDesktopResponses[keyof StartDesktopResponses];

export type UpdateStatusDesktopData = {
    body?: never;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}/update-status';
};

export type UpdateStatusDesktopErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type UpdateStatusDesktopError = UpdateStatusDesktopErrors[keyof UpdateStatusDesktopErrors];

export type UpdateStatusDesktopResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type UpdateStatusDesktopResponse = UpdateStatusDesktopResponses[keyof UpdateStatusDesktopResponses];

export type RetryFailedDesktopData = {
    body?: never;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}/retry';
};

export type RetryFailedDesktopErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Conflict
     */
    409: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Too Many Requests
     */
    429: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
    /**
     * Service Unavailable
     */
    503: ErrorResponse;
};

export type RetryFailedDesktopError = RetryFailedDesktopErrors[keyof RetryFailedDesktopErrors];

export type RetryFailedDesktopResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type RetryFailedDesktopResponse = RetryFailedDesktopResponses[keyof RetryFailedDesktopResponses];

export type ForceStopDesktopData = {
    body?: never;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}/force-stop';
};

export type ForceStopDesktopErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type ForceStopDesktopError = ForceStopDesktopErrors[keyof ForceStopDesktopErrors];

export type ForceStopDesktopResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type ForceStopDesktopResponse = ForceStopDesktopResponses[keyof ForceStopDesktopResponses];

export type GetDesktopImagesData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Image type filter: 'stock', 'user', or omit for both.
         */
        image_type?: DesktopImageType | null;
        /**
         * Domain ID for user cards
         */
        domain_id?: string;
    };
    url: '/api/v4/items/desktops/get-images';
};

export type GetDesktopImagesErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDesktopImagesError = GetDesktopImagesErrors[keyof GetDesktopImagesErrors];

export type GetDesktopImagesResponses = {
    /**
     * Successful Response
     */
    200: DesktopImagesResponse;
};

export type GetDesktopImagesResponse = GetDesktopImagesResponses[keyof GetDesktopImagesResponses];

export type DeleteUserDeploymentDesktopsData = {
    body?: never;
    path: {
        /**
         * The ID of the user
         */
        user_id: string;
        deployment_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{deployment_id}/{user_id}';
};

export type DeleteUserDeploymentDesktopsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type DeleteUserDeploymentDesktopsError = DeleteUserDeploymentDesktopsErrors[keyof DeleteUserDeploymentDesktopsErrors];

export type DeleteUserDeploymentDesktopsResponses = {
    /**
     * Successful Response
     */
    202: DeleteResponse;
};

export type DeleteUserDeploymentDesktopsResponse = DeleteUserDeploymentDesktopsResponses[keyof DeleteUserDeploymentDesktopsResponses];

export type GetUserDesktopsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/desktops';
};

export type GetUserDesktopsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetUserDesktopsError = GetUserDesktopsErrors[keyof GetUserDesktopsErrors];

export type GetUserDesktopsResponses = {
    /**
     * Successful Response
     */
    200: UserDesktopsResponse;
};

export type GetUserDesktopsResponse = GetUserDesktopsResponses[keyof GetUserDesktopsResponses];

export type GetUserDesktopsPaginatedData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Start the retrieval after the given accessed. If not provided, starts from the beginning.
         */
        start_after?: number;
        /**
         * Number of desktops to return. Default is 10. The given value will be multiplied by 5 in order to preload more desktops for the user.
         */
        page_size?: number;
        /**
         * Field to sort the desktops by. Only 'accessed' is supported.
         */
        sort_field?: 'accessed';
        /**
         * Order to sort the desktops by. Default is 'desc'. Can be 'asc' or 'desc'.
         */
        sort_order?: 'desc' | 'asc';
        /**
         * Search term to filter desktops by name or description. If provided, search_field must also be provided.
         */
        search?: string;
        /**
         * Field to search in. If not provided, no search is performed.
         */
        search_field?: DesktopSearchFields | null;
        tag?: boolean | null;
        persistent?: boolean | null;
    };
    url: '/api/v4/items/paginated/desktops';
};

export type GetUserDesktopsPaginatedErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetUserDesktopsPaginatedError = GetUserDesktopsPaginatedErrors[keyof GetUserDesktopsPaginatedErrors];

export type GetUserDesktopsPaginatedResponses = {
    /**
     * Successful Response
     */
    200: DesktopsPaginationResponse;
};

export type GetUserDesktopsPaginatedResponse = GetUserDesktopsPaginatedResponses[keyof GetUserDesktopsPaginatedResponses];

export type GetDomainsAllowedReservablesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/domains/get-allowed-reservables';
};

export type GetDomainsAllowedReservablesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
};

export type GetDomainsAllowedReservablesError = GetDomainsAllowedReservablesErrors[keyof GetDomainsAllowedReservablesErrors];

export type GetDomainsAllowedReservablesResponses = {
    /**
     * Successful Response
     */
    200: AllowedReservablesResponse;
};

export type GetDomainsAllowedReservablesResponse = GetDomainsAllowedReservablesResponses[keyof GetDomainsAllowedReservablesResponses];

export type CreateDesktopFromMediaData = {
    body: CreateDesktopFromMedia;
    path?: never;
    query?: never;
    url: '/api/v4/item/desktop/from-media';
};

export type CreateDesktopFromMediaErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type CreateDesktopFromMediaError = CreateDesktopFromMediaErrors[keyof CreateDesktopFromMediaErrors];

export type CreateDesktopFromMediaResponses = {
    /**
     * Successful Response
     */
    201: SimpleResponse;
};

export type CreateDesktopFromMediaResponse = CreateDesktopFromMediaResponses[keyof CreateDesktopFromMediaResponses];

export type GetDesktopInfoData = {
    body?: never;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}/get-info';
};

export type GetDesktopInfoErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetDesktopInfoError = GetDesktopInfoErrors[keyof GetDesktopInfoErrors];

export type GetDesktopInfoResponses = {
    /**
     * Successful Response
     */
    200: DomainInfoResponse;
};

export type GetDesktopInfoResponse = GetDesktopInfoResponses[keyof GetDesktopInfoResponses];

export type EditDesktopData = {
    body: DesktopEditRequest;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}/edit';
};

export type EditDesktopErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type EditDesktopError = EditDesktopErrors[keyof EditDesktopErrors];

export type EditDesktopResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type EditDesktopResponse = EditDesktopResponses[keyof EditDesktopResponses];

export type GetDesktopViewerByTypeData = {
    body?: never;
    path: {
        desktop_id: string;
        /**
         * The type of viewer to get the connection string for
         */
        viewer_type: 'browser-vnc' | 'file-spice' | 'file-rdpgw' | 'file-rdpvpn' | 'browser-rdp';
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}/get-viewer/{viewer_type}';
};

export type GetDesktopViewerByTypeErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDesktopViewerByTypeError = GetDesktopViewerByTypeErrors[keyof GetDesktopViewerByTypeErrors];

export type GetDesktopViewerByTypeResponses = {
    /**
     * Successful Response
     */
    200: DesktopGetViewerResponse;
};

export type GetDesktopViewerByTypeResponse = GetDesktopViewerByTypeResponses[keyof GetDesktopViewerByTypeResponses];

export type RecreateDesktopData = {
    body?: never;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}/recreate';
};

export type RecreateDesktopErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type RecreateDesktopError = RecreateDesktopErrors[keyof RecreateDesktopErrors];

export type RecreateDesktopResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type RecreateDesktopResponse = RecreateDesktopResponses[keyof RecreateDesktopResponses];

export type UpdateDesktopBastionDomainData = {
    body: BastionDomainUpdateRequest;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}/update-bastion-domain';
};

export type UpdateDesktopBastionDomainErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type UpdateDesktopBastionDomainError = UpdateDesktopBastionDomainErrors[keyof UpdateDesktopBastionDomainErrors];

export type UpdateDesktopBastionDomainResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type UpdateDesktopBastionDomainResponse = UpdateDesktopBastionDomainResponses[keyof UpdateDesktopBastionDomainResponses];

export type UpdateDesktopBastionDomainsData = {
    body: BastionDomainsUpdateRequest;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}/update-bastion-domains';
};

export type UpdateDesktopBastionDomainsErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Conflict
     */
    409: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type UpdateDesktopBastionDomainsError = UpdateDesktopBastionDomainsErrors[keyof UpdateDesktopBastionDomainsErrors];

export type UpdateDesktopBastionDomainsResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type UpdateDesktopBastionDomainsResponse = UpdateDesktopBastionDomainsResponses[keyof UpdateDesktopBastionDomainsResponses];

export type VerifyDesktopBastionDomainData = {
    body: ApiSchemasDomainsDesktopsBastionDomainVerifyRequest;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}/verify-bastion-domain';
};

export type VerifyDesktopBastionDomainErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Conflict
     */
    409: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type VerifyDesktopBastionDomainError = VerifyDesktopBastionDomainErrors[keyof VerifyDesktopBastionDomainErrors];

export type VerifyDesktopBastionDomainResponses = {
    /**
     * Successful Response
     */
    200: ApiSchemasDomainsDesktopsBastionDomainVerifyResponse;
};

export type VerifyDesktopBastionDomainResponse = VerifyDesktopBastionDomainResponses[keyof VerifyDesktopBastionDomainResponses];

export type GetTemplateInfoData = {
    body?: never;
    path: {
        template_id: string;
    };
    query?: never;
    url: '/api/v4/item/template/{template_id}/get-info';
};

export type GetTemplateInfoErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTemplateInfoError = GetTemplateInfoErrors[keyof GetTemplateInfoErrors];

export type GetTemplateInfoResponses = {
    /**
     * Successful Response
     */
    200: DomainInfoResponse;
};

export type GetTemplateInfoResponse = GetTemplateInfoResponses[keyof GetTemplateInfoResponses];

export type GetTemplateDetailsData = {
    body?: never;
    path: {
        template_id: string;
    };
    query?: never;
    url: '/api/v4/item/template/{template_id}/get-details';
};

export type GetTemplateDetailsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetTemplateDetailsError = GetTemplateDetailsErrors[keyof GetTemplateDetailsErrors];

export type GetTemplateDetailsResponses = {
    /**
     * Successful Response
     */
    200: TemplateDetailsResponse;
};

export type GetTemplateDetailsResponse = GetTemplateDetailsResponses[keyof GetTemplateDetailsResponses];

export type GetTemplateNonpersistentDesktopData = {
    body?: never;
    path: {
        template_id: string;
    };
    query?: never;
    url: '/api/v4/item/template/{template_id}/get-nonpersistent-desktop';
};

export type GetTemplateNonpersistentDesktopErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetTemplateNonpersistentDesktopError = GetTemplateNonpersistentDesktopErrors[keyof GetTemplateNonpersistentDesktopErrors];

export type GetTemplateNonpersistentDesktopResponses = {
    /**
     * Successful Response
     */
    200: TemplateNonpersistentDesktopResponse;
};

export type GetTemplateNonpersistentDesktopResponse = GetTemplateNonpersistentDesktopResponses[keyof GetTemplateNonpersistentDesktopResponses];

export type GetUserAllowedTemplatesFlatData = {
    body?: never;
    path: {
        kind: 'all' | 'shared';
    };
    query?: never;
    url: '/api/v4/items/templates/allowed/{kind}';
};

export type GetUserAllowedTemplatesFlatErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetUserAllowedTemplatesFlatError = GetUserAllowedTemplatesFlatErrors[keyof GetUserAllowedTemplatesFlatErrors];

export type GetUserAllowedTemplatesFlatResponses = {
    /**
     * Successful Response
     */
    200: Array<UserAllowedTemplateFlatItem>;
};

export type GetUserAllowedTemplatesFlatResponse = GetUserAllowedTemplatesFlatResponses[keyof GetUserAllowedTemplatesFlatResponses];

export type GetUserSharedTemplatesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/templates/get-shared';
};

export type GetUserSharedTemplatesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetUserSharedTemplatesError = GetUserSharedTemplatesErrors[keyof GetUserSharedTemplatesErrors];

export type GetUserSharedTemplatesResponses = {
    /**
     * Successful Response
     */
    200: UserSharedTemplatesResponse;
};

export type GetUserSharedTemplatesResponse = GetUserSharedTemplatesResponses[keyof GetUserSharedTemplatesResponses];

export type GetUserAllowedTemplatesData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Start the retrieval after the given accessed. If not provided, starts from the beginning.
         */
        start_after?: number;
        /**
         * Number of templates to return. Default is 10. The given value will be multiplied by 5 in order to preload more templates for the user.
         */
        page_size?: number;
        /**
         * Field to sort the templates by. Default is 'accessed'.
         */
        sort_field?: 'accessed';
        /**
         * Order to sort the templates by. Default is 'desc'. Can be 'asc' or 'desc'.
         */
        sort_order?: 'desc' | 'asc';
        /**
         * Search term to filter templates by name or description. If provided, search_field must also be provided.
         */
        search?: string;
        /**
         * Field to search in. If not provided, no search is performed.
         */
        search_field?: UserAllowedTemplateSearchFields | null;
    };
    url: '/api/v4/items/templates/get-allowed';
};

export type GetUserAllowedTemplatesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetUserAllowedTemplatesError = GetUserAllowedTemplatesErrors[keyof GetUserAllowedTemplatesErrors];

export type GetUserAllowedTemplatesResponses = {
    /**
     * Successful Response
     */
    200: UserAllowedTemplatesPaginationResponse;
};

export type GetUserAllowedTemplatesResponse = GetUserAllowedTemplatesResponses[keyof GetUserAllowedTemplatesResponses];

export type SearchUsersInCategoryData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * String to search for in the users' name, username or group name
         */
        search?: string;
        /**
         * Maximum number of users to return.
         */
        limit?: number;
        /**
         * Restrict results to these roles
         */
        roles?: Array<string> | null;
        /**
         * Restrict results to users whose primary or secondary group is one of these
         */
        groups?: Array<string> | null;
    };
    url: '/api/v4/item/category/users/search';
};

export type SearchUsersInCategoryErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type SearchUsersInCategoryError = SearchUsersInCategoryErrors[keyof SearchUsersInCategoryErrors];

export type SearchUsersInCategoryResponses = {
    /**
     * Successful Response
     */
    200: CategoriesUsersSearchResponse;
};

export type SearchUsersInCategoryResponse = SearchUsersInCategoryResponses[keyof SearchUsersInCategoryResponses];

export type GetAvailableGroupsForCategoryData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/category/allowed/get-available-groups';
};

export type GetAvailableGroupsForCategoryErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetAvailableGroupsForCategoryError = GetAvailableGroupsForCategoryErrors[keyof GetAvailableGroupsForCategoryErrors];

export type GetAvailableGroupsForCategoryResponses = {
    /**
     * Successful Response
     */
    200: GroupsInCategoryResponse;
};

export type GetAvailableGroupsForCategoryResponse = GetAvailableGroupsForCategoryResponses[keyof GetAvailableGroupsForCategoryResponses];

export type BulkDeleteDeploymentsData = {
    body: BulkDeleteDeploymentsRequest;
    path?: never;
    query?: never;
    url: '/api/v4/items/deployments';
};

export type BulkDeleteDeploymentsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: BulkDeleteDeploymentsErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type BulkDeleteDeploymentsError = BulkDeleteDeploymentsErrors[keyof BulkDeleteDeploymentsErrors];

export type BulkDeleteDeploymentsResponses = {
    /**
     * Deployments queued for deletion
     */
    204: void;
};

export type BulkDeleteDeploymentsResponse = BulkDeleteDeploymentsResponses[keyof BulkDeleteDeploymentsResponses];

export type GetAllDeploymentsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/deployments';
};

export type GetAllDeploymentsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetAllDeploymentsError = GetAllDeploymentsErrors[keyof GetAllDeploymentsErrors];

export type GetAllDeploymentsResponses = {
    /**
     * Successful Response
     */
    200: OwnedDeploymentsResponse;
};

export type GetAllDeploymentsResponse = GetAllDeploymentsResponses[keyof GetAllDeploymentsResponses];

export type CheckDeploymentQuotaGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/deployment/check-quota';
};

export type CheckDeploymentQuotaGetErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type CheckDeploymentQuotaGetError = CheckDeploymentQuotaGetErrors[keyof CheckDeploymentQuotaGetErrors];

export type CheckDeploymentQuotaGetResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type CheckDeploymentQuotaGetResponse = CheckDeploymentQuotaGetResponses[keyof CheckDeploymentQuotaGetResponses];

export type CheckDeploymentQuotaPostData = {
    body: CheckQuotaRequest;
    path?: never;
    query?: never;
    url: '/api/v4/item/deployment/check-quota';
};

export type CheckDeploymentQuotaPostErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type CheckDeploymentQuotaPostError = CheckDeploymentQuotaPostErrors[keyof CheckDeploymentQuotaPostErrors];

export type CheckDeploymentQuotaPostResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type CheckDeploymentQuotaPostResponse = CheckDeploymentQuotaPostResponses[keyof CheckDeploymentQuotaPostResponses];

export type DeleteDeploymentData = {
    body?: never;
    path: {
        deployment_id: string;
    };
    query?: {
        /**
         * Whether to permanently delete the desktop
         */
        permanent?: boolean;
    };
    url: '/api/v4/item/deployment/{deployment_id}';
};

export type DeleteDeploymentErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type DeleteDeploymentError = DeleteDeploymentErrors[keyof DeleteDeploymentErrors];

export type DeleteDeploymentResponses = {
    /**
     * Successful Response
     */
    200: DeleteResponse;
    /**
     * Accepted
     */
    202: DeleteResponse;
};

export type DeleteDeploymentResponse = DeleteDeploymentResponses[keyof DeleteDeploymentResponses];

export type GetDeploymentData = {
    body?: never;
    path: {
        deployment_id: string;
    };
    query?: never;
    url: '/api/v4/item/deployment/{deployment_id}';
};

export type GetDeploymentErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDeploymentError = GetDeploymentErrors[keyof GetDeploymentErrors];

export type GetDeploymentResponses = {
    /**
     * Successful Response
     */
    200: DeploymentResponse;
};

export type GetDeploymentResponse = GetDeploymentResponses[keyof GetDeploymentResponses];

export type CreateDeploymentData = {
    body: CreateDeploymentRequest;
    path?: never;
    query?: never;
    url: '/api/v4/item/deployment';
};

export type CreateDeploymentErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Conflict
     */
    409: DesktopNameExistsErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type CreateDeploymentError = CreateDeploymentErrors[keyof CreateDeploymentErrors];

export type CreateDeploymentResponses = {
    /**
     * Deployment created successfully
     */
    201: SimpleResponse;
};

export type CreateDeploymentResponse = CreateDeploymentResponses[keyof CreateDeploymentResponses];

export type StopAllDesktopsInDeploymentData = {
    body?: DeploymentStopRequest;
    path: {
        deployment_id: string;
    };
    query?: never;
    url: '/api/v4/item/deployment/{deployment_id}/stop';
};

export type StopAllDesktopsInDeploymentErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * No dekstops found in deployment
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type StopAllDesktopsInDeploymentError = StopAllDesktopsInDeploymentErrors[keyof StopAllDesktopsInDeploymentErrors];

export type StopAllDesktopsInDeploymentResponses = {
    /**
     * Desktops stopped successfully
     */
    204: void;
};

export type StopAllDesktopsInDeploymentResponse = StopAllDesktopsInDeploymentResponses[keyof StopAllDesktopsInDeploymentResponses];

export type ToggleDeploymentVisibilityData = {
    body?: ToggleVisibilityRequest | null;
    path: {
        deployment_id: string;
    };
    query?: never;
    url: '/api/v4/item/deployment/{deployment_id}/toggle-visibility';
};

export type ToggleDeploymentVisibilityErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type ToggleDeploymentVisibilityError = ToggleDeploymentVisibilityErrors[keyof ToggleDeploymentVisibilityErrors];

export type ToggleDeploymentVisibilityResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type ToggleDeploymentVisibilityResponse = ToggleDeploymentVisibilityResponses[keyof ToggleDeploymentVisibilityResponses];

export type SetDeploymentVisibilityData = {
    body: SetDeploymentVisibilityRequest;
    path: {
        deployment_id: string;
    };
    query?: never;
    url: '/api/v4/item/deployment/{deployment_id}/visibility';
};

export type SetDeploymentVisibilityErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type SetDeploymentVisibilityError = SetDeploymentVisibilityErrors[keyof SetDeploymentVisibilityErrors];

export type SetDeploymentVisibilityResponses = {
    /**
     * Successful Response
     */
    200: DeploymentVisibilityResponse;
};

export type SetDeploymentVisibilityResponse = SetDeploymentVisibilityResponses[keyof SetDeploymentVisibilityResponses];

export type EditDeploymentData = {
    body: DeploymentEditRequest;
    path: {
        deployment_id: string;
    };
    query?: never;
    url: '/api/v4/item/deployment/{deployment_id}/edit';
};

export type EditDeploymentErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type EditDeploymentError = EditDeploymentErrors[keyof EditDeploymentErrors];

export type EditDeploymentResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type EditDeploymentResponse = EditDeploymentResponses[keyof EditDeploymentResponses];

export type GetDeploymentCsvData = {
    body?: never;
    path: {
        deployment_id: string;
    };
    query?: {
        /**
         * Regenerate all direct viewer links before exporting
         */
        regenerate?: boolean;
    };
    url: '/api/v4/item/deployment/{deployment_id}/download-csv';
};

export type GetDeploymentCsvErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDeploymentCsvError = GetDeploymentCsvErrors[keyof GetDeploymentCsvErrors];

export type GetDeploymentCsvResponses = {
    /**
     * CSV file with direct-viewer URLs
     */
    200: unknown;
};

export type CountRecreateDeploymentDesktopsData = {
    body?: never;
    path: {
        deployment_id: string;
    };
    query?: never;
    url: '/api/v4/item/deployment/{deployment_id}/recreate/count';
};

export type CountRecreateDeploymentDesktopsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type CountRecreateDeploymentDesktopsError = CountRecreateDeploymentDesktopsErrors[keyof CountRecreateDeploymentDesktopsErrors];

export type CountRecreateDeploymentDesktopsResponses = {
    /**
     * Successful Response
     */
    200: DeploymentRecreateCountResponse;
};

export type CountRecreateDeploymentDesktopsResponse = CountRecreateDeploymentDesktopsResponses[keyof CountRecreateDeploymentDesktopsResponses];

export type RecreateDeploymentData = {
    body?: never;
    path: {
        deployment_id: string;
    };
    query?: never;
    url: '/api/v4/item/deployment/{deployment_id}/recreate';
};

export type RecreateDeploymentErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type RecreateDeploymentError = RecreateDeploymentErrors[keyof RecreateDeploymentErrors];

export type RecreateDeploymentResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type RecreateDeploymentResponse = RecreateDeploymentResponses[keyof RecreateDeploymentResponses];

export type GetDeploymentBastionData = {
    body?: never;
    path: {
        deployment_id: string;
    };
    query?: never;
    url: '/api/v4/item/deployment/{deployment_id}/bastion';
};

export type GetDeploymentBastionErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDeploymentBastionError = GetDeploymentBastionErrors[keyof GetDeploymentBastionErrors];

export type GetDeploymentBastionResponses = {
    /**
     * Successful Response
     */
    200: DeploymentBastionConfig;
};

export type GetDeploymentBastionResponse = GetDeploymentBastionResponses[keyof GetDeploymentBastionResponses];

export type SetDeploymentBastionData = {
    body: DeploymentBastionConfig;
    path: {
        deployment_id: string;
    };
    query?: never;
    url: '/api/v4/item/deployment/{deployment_id}/bastion';
};

export type SetDeploymentBastionErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type SetDeploymentBastionError = SetDeploymentBastionErrors[keyof SetDeploymentBastionErrors];

export type SetDeploymentBastionResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type SetDeploymentBastionResponse = SetDeploymentBastionResponses[keyof SetDeploymentBastionResponses];

export type GetDeploymentBastionCsvData = {
    body?: never;
    path: {
        deployment_id: string;
    };
    query?: never;
    url: '/api/v4/item/deployment/{deployment_id}/bastion/csv';
};

export type GetDeploymentBastionCsvErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDeploymentBastionCsvError = GetDeploymentBastionCsvErrors[keyof GetDeploymentBastionCsvErrors];

export type GetDeploymentBastionCsvResponses = {
    /**
     * CSV file with bastion data
     */
    200: unknown;
};

export type GetDeploymentDesktopBastionData = {
    body?: never;
    path: {
        deployment_id: string;
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/deployment/{deployment_id}/desktop/{desktop_id}/bastion';
};

export type GetDeploymentDesktopBastionErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDeploymentDesktopBastionError = GetDeploymentDesktopBastionErrors[keyof GetDeploymentDesktopBastionErrors];

export type GetDeploymentDesktopBastionResponses = {
    /**
     * Successful Response
     */
    200: BastionActiveResponse;
};

export type GetDeploymentDesktopBastionResponse = GetDeploymentDesktopBastionResponses[keyof GetDeploymentDesktopBastionResponses];

export type GetDeploymentVideowallData = {
    body?: never;
    path: {
        deployment_id: string;
    };
    query?: never;
    url: '/api/v4/item/deployment/{deployment_id}/videowall';
};

export type GetDeploymentVideowallErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDeploymentVideowallError = GetDeploymentVideowallErrors[keyof GetDeploymentVideowallErrors];

export type GetDeploymentVideowallResponses = {
    /**
     * Successful Response
     */
    200: DeploymentVideowallResponse;
};

export type GetDeploymentVideowallResponse = GetDeploymentVideowallResponses[keyof GetDeploymentVideowallResponses];

export type StartAllDesktopsInDeploymentData = {
    body?: never;
    path: {
        deployment_id: string;
    };
    query?: never;
    url: '/api/v4/item/deployment/{deployment_id}/start';
};

export type StartAllDesktopsInDeploymentErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
    /**
     * Service Unavailable
     */
    503: ErrorResponse;
};

export type StartAllDesktopsInDeploymentError = StartAllDesktopsInDeploymentErrors[keyof StartAllDesktopsInDeploymentErrors];

export type StartAllDesktopsInDeploymentResponses = {
    /**
     * Desktops started successfully
     */
    204: void;
};

export type StartAllDesktopsInDeploymentResponse = StartAllDesktopsInDeploymentResponses[keyof StartAllDesktopsInDeploymentResponses];

export type ToggleDomainVisibilityData = {
    body?: never;
    path: {
        deployment_id: string;
        domain_id: string;
    };
    query?: never;
    url: '/api/v4/item/deployment/{deployment_id}/domain/{domain_id}/toggle-visibility';
};

export type ToggleDomainVisibilityErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type ToggleDomainVisibilityError = ToggleDomainVisibilityErrors[keyof ToggleDomainVisibilityErrors];

export type ToggleDomainVisibilityResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type ToggleDomainVisibilityResponse = ToggleDomainVisibilityResponses[keyof ToggleDomainVisibilityResponses];

export type ToggleDesktopDeploymentVisibilityData = {
    body?: never;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}/toggle-deployment-visibility';
};

export type ToggleDesktopDeploymentVisibilityErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type ToggleDesktopDeploymentVisibilityError = ToggleDesktopDeploymentVisibilityErrors[keyof ToggleDesktopDeploymentVisibilityErrors];

export type ToggleDesktopDeploymentVisibilityResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type ToggleDesktopDeploymentVisibilityResponse = ToggleDesktopDeploymentVisibilityResponses[keyof ToggleDesktopDeploymentVisibilityResponses];

export type GetDeploymentHardwareData = {
    body?: never;
    path: {
        deployment_id: string;
    };
    query?: never;
    url: '/api/v4/item/deployment/{deployment_id}/hardware';
};

export type GetDeploymentHardwareErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDeploymentHardwareError = GetDeploymentHardwareErrors[keyof GetDeploymentHardwareErrors];

export type GetDeploymentHardwareResponses = {
    /**
     * Successful Response
     */
    200: DeploymentHardwareResponse;
};

export type GetDeploymentHardwareResponse = GetDeploymentHardwareResponses[keyof GetDeploymentHardwareResponses];

export type GetDeploymentInfoData = {
    body?: never;
    path: {
        deployment_id: string;
    };
    query?: never;
    url: '/api/v4/item/deployment/{deployment_id}/info';
};

export type GetDeploymentInfoErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDeploymentInfoError = GetDeploymentInfoErrors[keyof GetDeploymentInfoErrors];

export type GetDeploymentInfoResponses = {
    /**
     * Successful Response
     */
    200: DeploymentInfoResponse;
};

export type GetDeploymentInfoResponse = GetDeploymentInfoResponses[keyof GetDeploymentInfoResponses];

export type GetDeploymentCoOwnersData = {
    body?: never;
    path: {
        deployment_id: string;
    };
    query?: never;
    url: '/api/v4/item/deployment/{deployment_id}/co-owners';
};

export type GetDeploymentCoOwnersErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDeploymentCoOwnersError = GetDeploymentCoOwnersErrors[keyof GetDeploymentCoOwnersErrors];

export type GetDeploymentCoOwnersResponses = {
    /**
     * Successful Response
     */
    200: CoOwnersResponse;
};

export type GetDeploymentCoOwnersResponse = GetDeploymentCoOwnersResponses[keyof GetDeploymentCoOwnersResponses];

export type UpdateDeploymentCoOwnersData = {
    body: CoOwnersRequest;
    path: {
        deployment_id: string;
    };
    query?: never;
    url: '/api/v4/item/deployment/{deployment_id}/co-owners';
};

export type UpdateDeploymentCoOwnersErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type UpdateDeploymentCoOwnersError = UpdateDeploymentCoOwnersErrors[keyof UpdateDeploymentCoOwnersErrors];

export type UpdateDeploymentCoOwnersResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type UpdateDeploymentCoOwnersResponse = UpdateDeploymentCoOwnersResponses[keyof UpdateDeploymentCoOwnersResponses];

export type GetDeploymentAllowedData = {
    body?: never;
    path: {
        deployment_id: string;
    };
    query?: never;
    url: '/api/v4/item/deployment/{deployment_id}/get-allowed';
};

export type GetDeploymentAllowedErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDeploymentAllowedError = GetDeploymentAllowedErrors[keyof GetDeploymentAllowedErrors];

export type GetDeploymentAllowedResponses = {
    /**
     * Successful Response
     */
    200: AllowedResponse;
};

export type GetDeploymentAllowedResponse = GetDeploymentAllowedResponses[keyof GetDeploymentAllowedResponses];

export type EditDeploymentUsersData = {
    body: DeploymentEditUsersRequest;
    path: {
        deployment_id: string;
    };
    query?: never;
    url: '/api/v4/item/deployment/{deployment_id}/edit-users';
};

export type EditDeploymentUsersErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type EditDeploymentUsersError = EditDeploymentUsersErrors[keyof EditDeploymentUsersErrors];

export type EditDeploymentUsersResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type EditDeploymentUsersResponse = EditDeploymentUsersResponses[keyof EditDeploymentUsersResponses];

export type GetDeploymentPermissionsData = {
    body?: never;
    path: {
        deployment_id: string;
    };
    query?: never;
    url: '/api/v4/item/deployment/{deployment_id}/permissions';
};

export type GetDeploymentPermissionsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDeploymentPermissionsError = GetDeploymentPermissionsErrors[keyof GetDeploymentPermissionsErrors];

export type GetDeploymentPermissionsResponses = {
    /**
     * Successful Response
     */
    200: Array<DeploymentPermissions>;
};

export type GetDeploymentPermissionsResponse = GetDeploymentPermissionsResponses[keyof GetDeploymentPermissionsResponses];

export type GetUsersInGroupData = {
    body?: never;
    path: {
        /**
         * The ID of the group
         */
        group_id: string;
    };
    query?: {
        /**
         * Restrict results to these roles
         */
        roles?: Array<string> | null;
        /**
         * Also return users that have this group as a secondary group
         */
        include_secondary?: boolean;
    };
    url: '/api/v4/item/group/{group_id}/get-users';
};

export type GetUsersInGroupErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetUsersInGroupError = GetUsersInGroupErrors[keyof GetUsersInGroupErrors];

export type GetUsersInGroupResponses = {
    /**
     * Successful Response
     */
    200: GroupUsersResponse;
};

export type GetUsersInGroupResponse = GetUsersInGroupResponses[keyof GetUsersInGroupResponses];

export type GetMediaInstallsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/media/installs';
};

export type GetMediaInstallsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetMediaInstallsError = GetMediaInstallsErrors[keyof GetMediaInstallsErrors];

export type GetMediaInstallsResponses = {
    /**
     * Successful Response
     */
    200: VirtInstallListResponse;
};

export type GetMediaInstallsResponse = GetMediaInstallsResponses[keyof GetMediaInstallsResponses];

export type GetMediaAllowedTableData = {
    body?: never;
    path: {
        media_id: string;
    };
    query?: never;
    url: '/api/v4/item/media/{media_id}/get-allowed';
};

export type GetMediaAllowedTableErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetMediaAllowedTableError = GetMediaAllowedTableErrors[keyof GetMediaAllowedTableErrors];

export type GetMediaAllowedTableResponses = {
    /**
     * Successful Response
     */
    200: AllowedResponse;
};

export type GetMediaAllowedTableResponse = GetMediaAllowedTableResponses[keyof GetMediaAllowedTableResponses];

export type UpdateMediaAllowedData = {
    body: AllowedUpdate;
    path: {
        media_id: string;
    };
    query?: never;
    url: '/api/v4/item/media/{media_id}/update-allowed';
};

export type UpdateMediaAllowedErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type UpdateMediaAllowedError = UpdateMediaAllowedErrors[keyof UpdateMediaAllowedErrors];

export type UpdateMediaAllowedResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type UpdateMediaAllowedResponse = UpdateMediaAllowedResponses[keyof UpdateMediaAllowedResponses];

export type CreateMediaData = {
    body: CreateMediaRequest;
    path?: never;
    query?: never;
    url: '/api/v4/item/media';
};

export type CreateMediaErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Conflict
     */
    409: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type CreateMediaError = CreateMediaErrors[keyof CreateMediaErrors];

export type CreateMediaResponses = {
    /**
     * Media created successfully
     */
    201: SimpleResponse;
};

export type CreateMediaResponse = CreateMediaResponses[keyof CreateMediaResponses];

export type AbortMediaDownloadData = {
    body?: never;
    path: {
        media_id: string;
    };
    query?: never;
    url: '/api/v4/item/media/{media_id}/abort';
};

export type AbortMediaDownloadErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type AbortMediaDownloadError = AbortMediaDownloadErrors[keyof AbortMediaDownloadErrors];

export type AbortMediaDownloadResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type AbortMediaDownloadResponse = AbortMediaDownloadResponses[keyof AbortMediaDownloadResponses];

export type StartMediaDownloadData = {
    body?: never;
    path: {
        media_id: string;
    };
    query?: never;
    url: '/api/v4/item/media/{media_id}/download';
};

export type StartMediaDownloadErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type StartMediaDownloadError = StartMediaDownloadErrors[keyof StartMediaDownloadErrors];

export type StartMediaDownloadResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type StartMediaDownloadResponse = StartMediaDownloadResponses[keyof StartMediaDownloadResponses];

export type CheckQuotaNewMediaData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/quota/media/new';
};

export type CheckQuotaNewMediaErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Cannot create a new media, as it would exceed quota
     */
    428: ErrorResponse;
};

export type CheckQuotaNewMediaError = CheckQuotaNewMediaErrors[keyof CheckQuotaNewMediaErrors];

export type CheckQuotaNewMediaResponses = {
    /**
     * Can create a new media
     */
    204: void;
};

export type CheckQuotaNewMediaResponse = CheckQuotaNewMediaResponses[keyof CheckQuotaNewMediaResponses];

export type CheckQuotaNewTemplateData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/quota/template/new';
};

export type CheckQuotaNewTemplateErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Cannot create a new template, as it would exceed quota
     */
    428: ErrorResponse;
};

export type CheckQuotaNewTemplateError = CheckQuotaNewTemplateErrors[keyof CheckQuotaNewTemplateErrors];

export type CheckQuotaNewTemplateResponses = {
    /**
     * Can create a new template
     */
    204: void;
};

export type CheckQuotaNewTemplateResponse = CheckQuotaNewTemplateResponses[keyof CheckQuotaNewTemplateResponses];

export type CheckQuotaNewDeploymentData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/quota/deployment/new';
};

export type CheckQuotaNewDeploymentErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Cannot create a new deployment, as it would exceed quota
     */
    428: ErrorResponse;
};

export type CheckQuotaNewDeploymentError = CheckQuotaNewDeploymentErrors[keyof CheckQuotaNewDeploymentErrors];

export type CheckQuotaNewDeploymentResponses = {
    /**
     * Can create a new deployment
     */
    204: void;
};

export type CheckQuotaNewDeploymentResponse = CheckQuotaNewDeploymentResponses[keyof CheckQuotaNewDeploymentResponses];

export type IncreaseStorageSizeData = {
    body?: never;
    path: {
        storage_id: string;
        priority: string;
        increment: number;
    };
    query?: never;
    url: '/api/v4/item/storage/{storage_id}/priority/{priority}/increase/{increment}';
};

export type IncreaseStorageSizeErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type IncreaseStorageSizeError = IncreaseStorageSizeErrors[keyof IncreaseStorageSizeErrors];

export type IncreaseStorageSizeResponses = {
    /**
     * Successful Response
     */
    200: TaskIdResponse;
};

export type IncreaseStorageSizeResponse = IncreaseStorageSizeResponses[keyof IncreaseStorageSizeResponses];

export type GetAllUsersData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/users';
};

export type GetAllUsersErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Failed to retrieve users
     */
    500: unknown;
};

export type GetAllUsersError = GetAllUsersErrors[keyof GetAllUsersErrors];

export type GetAllUsersResponses = {
    /**
     * Users retrieved successfully
     */
    200: Array<UserListItem>;
};

export type GetAllUsersResponse = GetAllUsersResponses[keyof GetAllUsersResponses];

export type GetAllGroupsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/groups';
};

export type GetAllGroupsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Failed to retrieve groups
     */
    500: unknown;
};

export type GetAllGroupsError = GetAllGroupsErrors[keyof GetAllGroupsErrors];

export type GetAllGroupsResponses = {
    /**
     * Groups retrieved successfully
     */
    200: Array<DeploymentGroup>;
};

export type GetAllGroupsResponse = GetAllGroupsResponses[keyof GetAllGroupsResponses];

export type GetUserApiKeyData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/user/get-api-key';
};

export type GetUserApiKeyErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * User not found
     */
    404: unknown;
    /**
     * Failed to retrieve API key
     */
    500: unknown;
};

export type GetUserApiKeyError = GetUserApiKeyErrors[keyof GetUserApiKeyErrors];

export type GetUserApiKeyResponses = {
    /**
     * API key retrieved successfully
     */
    200: UserApiKeyResponse;
};

export type GetUserApiKeyResponse = GetUserApiKeyResponses[keyof GetUserApiKeyResponses];

export type ExpireUserApiKeyData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/user/expire-api-key';
};

export type ExpireUserApiKeyErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * User not found
     */
    404: unknown;
    /**
     * Failed to expire API key
     */
    500: unknown;
};

export type ExpireUserApiKeyError = ExpireUserApiKeyErrors[keyof ExpireUserApiKeyErrors];

export type ExpireUserApiKeyResponses = {
    /**
     * API key expired successfully
     */
    204: void;
};

export type ExpireUserApiKeyResponse = ExpireUserApiKeyResponses[keyof ExpireUserApiKeyResponses];

export type GroupsUsersCountData = {
    body: GroupsUsersCountPutData;
    path?: never;
    query?: never;
    url: '/api/v4/items/groups-users/count';
};

export type GroupsUsersCountErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Failed to count users in groups
     */
    500: unknown;
};

export type GroupsUsersCountError = GroupsUsersCountErrors[keyof GroupsUsersCountErrors];

export type GroupsUsersCountResponses = {
    /**
     * Count retrieved successfully
     */
    200: GroupsUsersCountResponse;
};

export type GroupsUsersCountResponse2 = GroupsUsersCountResponses[keyof GroupsUsersCountResponses];

export type GetBookingDeploymentData = {
    body?: never;
    path: {
        item_id: string;
    };
    query: {
        /**
         * Start date in ISO 8601 format
         */
        startDate: string;
        /**
         * End date in ISO 8601 format
         */
        endDate: string;
        /**
         * Type of bookings to return: 'all' for all bookings, 'event' for booking events only, 'availability' for availability slots only.
         */
        returnType?: 'all' | 'event' | 'availability';
    };
    url: '/api/v4/item/booking/get-deployment/{item_id}';
};

export type GetBookingDeploymentErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetBookingDeploymentError = GetBookingDeploymentErrors[keyof GetBookingDeploymentErrors];

export type GetBookingDeploymentResponses = {
    /**
     * Successful Response
     */
    200: ItemBookingsResponse;
};

export type GetBookingDeploymentResponse = GetBookingDeploymentResponses[keyof GetBookingDeploymentResponses];

export type BulkEditDesktopsData = {
    body: BulkEditDesktopsRequest;
    path?: never;
    query?: never;
    url: '/api/v4/items/desktops/bulk-edit';
};

export type BulkEditDesktopsErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type BulkEditDesktopsError = BulkEditDesktopsErrors[keyof BulkEditDesktopsErrors];

export type BulkEditDesktopsResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponsePlural;
};

export type BulkEditDesktopsResponse = BulkEditDesktopsResponses[keyof BulkEditDesktopsResponses];

export type BulkCreatePersistentDesktopsData = {
    body: BulkCreatePersistentDesktopsRequest;
    path?: never;
    query?: never;
    url: '/api/v4/items/desktops/bulk-create';
};

export type BulkCreatePersistentDesktopsErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type BulkCreatePersistentDesktopsError = BulkCreatePersistentDesktopsErrors[keyof BulkCreatePersistentDesktopsErrors];

export type BulkCreatePersistentDesktopsResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponsePlural;
};

export type BulkCreatePersistentDesktopsResponse = BulkCreatePersistentDesktopsResponses[keyof BulkCreatePersistentDesktopsResponses];

export type GetUserTemplatesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/templates';
};

export type GetUserTemplatesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetUserTemplatesError = GetUserTemplatesErrors[keyof GetUserTemplatesErrors];

export type GetUserTemplatesResponses = {
    /**
     * Successful Response
     */
    200: UserTemplatesResponse;
};

export type GetUserTemplatesResponse = GetUserTemplatesResponses[keyof GetUserTemplatesResponses];

export type GetUserTemplatesPaginatedData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Start the retrieval after the given accessed. If not provided, starts from the beginning.
         */
        start_after?: number;
        /**
         * Number of templates to return. Default is 10. The given value will be multiplied by 5 in order to preload more templates for the user.
         */
        page_size?: number;
        /**
         * Field to sort the templates by. Default is 'accessed'.
         */
        sort_field?: string;
        /**
         * Order to sort the templates by. Default is 'desc'. Can be 'asc' or 'desc'.
         */
        sort_order?: 'desc' | 'asc';
        /**
         * Search term to filter templates by name or description. If provided, search_field must also be provided.
         */
        search?: string;
        /**
         * Field to search in. If not provided, no search is performed.
         */
        search_field?: UserTemplateSearchFields | null;
        enabled?: boolean | null;
    };
    url: '/api/v4/items/paginated/templates';
};

export type GetUserTemplatesPaginatedErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetUserTemplatesPaginatedError = GetUserTemplatesPaginatedErrors[keyof GetUserTemplatesPaginatedErrors];

export type GetUserTemplatesPaginatedResponses = {
    /**
     * Successful Response
     */
    200: UserTemplatesPaginationResponse;
};

export type GetUserTemplatesPaginatedResponse = GetUserTemplatesPaginatedResponses[keyof GetUserTemplatesPaginatedResponses];

export type GetTemplateAllowedData = {
    body?: never;
    path: {
        template_id: string;
    };
    query?: never;
    url: '/api/v4/item/template/{template_id}/get-allowed';
};

export type GetTemplateAllowedErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetTemplateAllowedError = GetTemplateAllowedErrors[keyof GetTemplateAllowedErrors];

export type GetTemplateAllowedResponses = {
    /**
     * Successful Response
     */
    200: AllowedResponse;
};

export type GetTemplateAllowedResponse = GetTemplateAllowedResponses[keyof GetTemplateAllowedResponses];

export type UpdateTemplateAllowedData = {
    body: AllowedUpdate;
    path: {
        template_id: string;
    };
    query?: never;
    url: '/api/v4/item/template/{template_id}/update-allowed';
};

export type UpdateTemplateAllowedErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type UpdateTemplateAllowedError = UpdateTemplateAllowedErrors[keyof UpdateTemplateAllowedErrors];

export type UpdateTemplateAllowedResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type UpdateTemplateAllowedResponse = UpdateTemplateAllowedResponses[keyof UpdateTemplateAllowedResponses];

export type UpdateTemplateData = {
    body: TemplateEditRequest;
    path: {
        template_id: string;
    };
    query?: never;
    url: '/api/v4/item/template/{template_id}/edit';
};

export type UpdateTemplateErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type UpdateTemplateError = UpdateTemplateErrors[keyof UpdateTemplateErrors];

export type UpdateTemplateResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type UpdateTemplateResponse = UpdateTemplateResponses[keyof UpdateTemplateResponses];

export type SetTemplateEnabledData = {
    body: TemplateSetEnabledRequest;
    path: {
        template_id: string;
    };
    query?: never;
    url: '/api/v4/item/template/{template_id}/set-enabled';
};

export type SetTemplateEnabledErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type SetTemplateEnabledError = SetTemplateEnabledErrors[keyof SetTemplateEnabledErrors];

export type SetTemplateEnabledResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type SetTemplateEnabledResponse = SetTemplateEnabledResponses[keyof SetTemplateEnabledResponses];

export type GetTemplateTreeData = {
    body?: never;
    path: {
        template_id: string;
    };
    query?: never;
    url: '/api/v4/item/template/{template_id}/get-tree';
};

export type GetTemplateTreeErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetTemplateTreeError = GetTemplateTreeErrors[keyof GetTemplateTreeErrors];

export type GetTemplateTreeResponses = {
    /**
     * Successful Response
     */
    200: TemplateTreeResponse;
};

export type GetTemplateTreeResponse = GetTemplateTreeResponses[keyof GetTemplateTreeResponses];

export type CreateTemplateData = {
    body: NewTemplateRequest;
    path?: never;
    query?: never;
    url: '/api/v4/item/template';
};

export type CreateTemplateErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type CreateTemplateError = CreateTemplateErrors[keyof CreateTemplateErrors];

export type CreateTemplateResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type CreateTemplateResponse = CreateTemplateResponses[keyof CreateTemplateResponses];

export type DuplicateTemplateData = {
    body: DuplicateTemplateRequest;
    path: {
        template_id: string;
    };
    query?: never;
    url: '/api/v4/item/template/{template_id}/duplicate';
};

export type DuplicateTemplateErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DuplicateTemplateError = DuplicateTemplateErrors[keyof DuplicateTemplateErrors];

export type DuplicateTemplateResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type DuplicateTemplateResponse = DuplicateTemplateResponses[keyof DuplicateTemplateResponses];

export type DeleteTemplateData = {
    body?: never;
    path: {
        template_id: string;
    };
    query?: never;
    url: '/api/v4/item/template/{template_id}';
};

export type DeleteTemplateErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type DeleteTemplateError = DeleteTemplateErrors[keyof DeleteTemplateErrors];

export type DeleteTemplateResponses = {
    /**
     * Successful Response
     */
    200: DeleteResponse;
    /**
     * Accepted
     */
    202: DeleteResponse;
};

export type DeleteTemplateResponse = DeleteTemplateResponses[keyof DeleteTemplateResponses];

export type ConvertTemplateToDesktopData = {
    body: TemplateToDesktopRequest;
    path: {
        template_id: string;
    };
    query?: never;
    url: '/api/v4/item/template/{template_id}/convert-to-desktop';
};

export type ConvertTemplateToDesktopErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ConvertTemplateToDesktopError = ConvertTemplateToDesktopErrors[keyof ConvertTemplateToDesktopErrors];

export type ConvertTemplateToDesktopResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type ConvertTemplateToDesktopResponse = ConvertTemplateToDesktopResponses[keyof ConvertTemplateToDesktopResponses];

export type ToggleTemplateEnabledData = {
    body?: never;
    path: {
        template_id: string;
    };
    query?: never;
    url: '/api/v4/item/template/{template_id}/toggle-enabled';
};

export type ToggleTemplateEnabledErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ToggleTemplateEnabledError = ToggleTemplateEnabledErrors[keyof ToggleTemplateEnabledErrors];

export type ToggleTemplateEnabledResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type ToggleTemplateEnabledResponse = ToggleTemplateEnabledResponses[keyof ToggleTemplateEnabledResponses];

export type GetAdminBastionConfigData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/config/bastion';
};

export type GetAdminBastionConfigErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetAdminBastionConfigError = GetAdminBastionConfigErrors[keyof GetAdminBastionConfigErrors];

export type GetAdminBastionConfigResponses = {
    /**
     * Successful Response
     */
    200: AdminBastionConfigResponse;
};

export type GetAdminBastionConfigResponse = GetAdminBastionConfigResponses[keyof GetAdminBastionConfigResponses];

export type UpdateBastionConfigData = {
    body: AdminBastionConfigUpdateRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/config/bastion';
};

export type UpdateBastionConfigErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type UpdateBastionConfigError = UpdateBastionConfigErrors[keyof UpdateBastionConfigErrors];

export type UpdateBastionConfigResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type UpdateBastionConfigResponse = UpdateBastionConfigResponses[keyof UpdateBastionConfigResponses];

export type GetBastionDomainVerificationConfigData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/bastion/config';
};

export type GetBastionDomainVerificationConfigErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetBastionDomainVerificationConfigError = GetBastionDomainVerificationConfigErrors[keyof GetBastionDomainVerificationConfigErrors];

export type GetBastionDomainVerificationConfigResponses = {
    /**
     * Successful Response
     */
    200: BastionDomainVerificationConfigResponse;
};

export type GetBastionDomainVerificationConfigResponse = GetBastionDomainVerificationConfigResponses[keyof GetBastionDomainVerificationConfigResponses];

export type ChangeDeploymentOwnerData = {
    body?: never;
    path: {
        deployment_id: string;
        user_id: string;
    };
    query?: never;
    url: '/api/v4/item/deployment/{deployment_id}/change-owner/{user_id}';
};

export type ChangeDeploymentOwnerErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type ChangeDeploymentOwnerError = ChangeDeploymentOwnerErrors[keyof ChangeDeploymentOwnerErrors];

export type ChangeDeploymentOwnerResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type ChangeDeploymentOwnerResponse = ChangeDeploymentOwnerResponses[keyof ChangeDeploymentOwnerResponses];

export type AdminGetQuotaByKindData = {
    body?: never;
    path: {
        kind: 'user' | 'category' | 'group';
    };
    query?: never;
    url: '/api/v4/admin/quota/{kind}';
};

export type AdminGetQuotaByKindErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetQuotaByKindError = AdminGetQuotaByKindErrors[keyof AdminGetQuotaByKindErrors];

export type AdminGetQuotaByKindResponses = {
    /**
     * Successful Response
     */
    200: AdminQuotaResponse;
};

export type AdminGetQuotaByKindResponse = AdminGetQuotaByKindResponses[keyof AdminGetQuotaByKindResponses];

export type AdminGetQuotaByKindItemData = {
    body?: never;
    path: {
        kind: 'user' | 'category' | 'group';
        item_id: string;
    };
    query?: never;
    url: '/api/v4/admin/quota/{kind}/{item_id}';
};

export type AdminGetQuotaByKindItemErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetQuotaByKindItemError = AdminGetQuotaByKindItemErrors[keyof AdminGetQuotaByKindItemErrors];

export type AdminGetQuotaByKindItemResponses = {
    /**
     * Successful Response
     */
    200: AdminQuotaResponse;
};

export type AdminGetQuotaByKindItemResponse = AdminGetQuotaByKindItemResponses[keyof AdminGetQuotaByKindItemResponses];

export type GetSystemCutoffTimeData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/recycle-bin/system/cutoff-time';
};

export type GetSystemCutoffTimeErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
};

export type GetSystemCutoffTimeError = GetSystemCutoffTimeErrors[keyof GetSystemCutoffTimeErrors];

export type GetSystemCutoffTimeResponses = {
    /**
     * Successful Response
     */
    200: RecycleBinSystemCutoffTimeResponse;
};

export type GetSystemCutoffTimeResponse = GetSystemCutoffTimeResponses[keyof GetSystemCutoffTimeResponses];

export type UpdateSystemCutoffTimeData = {
    body: RecycleBinUpdateCutoffTimeRequest;
    path?: never;
    query?: never;
    url: '/api/v4/item/recycle-bin/system/cutoff-time';
};

export type UpdateSystemCutoffTimeErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type UpdateSystemCutoffTimeError = UpdateSystemCutoffTimeErrors[keyof UpdateSystemCutoffTimeErrors];

export type UpdateSystemCutoffTimeResponses = {
    /**
     * Successful Response
     */
    200: RecycleBinSystemCutoffTimeResponse;
};

export type UpdateSystemCutoffTimeResponse = UpdateSystemCutoffTimeResponses[keyof UpdateSystemCutoffTimeResponses];

export type GetRecycleBinStatusData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/recycle-bin/status';
};

export type GetRecycleBinStatusErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
};

export type GetRecycleBinStatusError = GetRecycleBinStatusErrors[keyof GetRecycleBinStatusErrors];

export type GetRecycleBinStatusResponses = {
    /**
     * Successful Response
     */
    200: RecycleBinStatusResponse;
};

export type GetRecycleBinStatusResponse = GetRecycleBinStatusResponses[keyof GetRecycleBinStatusResponses];

export type GetRecycleBinAdminEntriesData = {
    body?: never;
    path?: never;
    query?: {
        status?: string | null;
    };
    url: '/api/v4/items/recycle-bin/admin-entries';
};

export type GetRecycleBinAdminEntriesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetRecycleBinAdminEntriesError = GetRecycleBinAdminEntriesErrors[keyof GetRecycleBinAdminEntriesErrors];

export type GetRecycleBinAdminEntriesResponses = {
    /**
     * Successful Response
     */
    200: Array<RecycleBinEntry>;
};

export type GetRecycleBinAdminEntriesResponse = GetRecycleBinAdminEntriesResponses[keyof GetRecycleBinAdminEntriesResponses];

export type GetQcow2GeometryData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/storage/qcow2-geometry';
};

export type GetQcow2GeometryErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetQcow2GeometryError = GetQcow2GeometryErrors[keyof GetQcow2GeometryErrors];

export type GetQcow2GeometryResponses = {
    /**
     * Successful Response
     */
    200: Qcow2GeometryResponse;
};

export type GetQcow2GeometryResponse = GetQcow2GeometryResponses[keyof GetQcow2GeometryResponses];

export type GetStorageParentsData = {
    body?: never;
    path: {
        storage_id: string;
    };
    query?: never;
    url: '/api/v4/item/storage/{storage_id}/parents';
};

export type GetStorageParentsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetStorageParentsError = GetStorageParentsErrors[keyof GetStorageParentsErrors];

export type GetStorageParentsResponses = {
    /**
     * Successful Response
     */
    200: Array<{
        [key: string]: unknown;
    }>;
};

export type GetStorageParentsResponse = GetStorageParentsResponses[keyof GetStorageParentsResponses];

export type GetStorageTaskData = {
    body?: never;
    path: {
        storage_id: string;
    };
    query?: never;
    url: '/api/v4/item/storage/{storage_id}/task';
};

export type GetStorageTaskErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetStorageTaskError = GetStorageTaskErrors[keyof GetStorageTaskErrors];

export type GetStorageTaskResponses = {
    /**
     * Successful Response
     */
    200: {
        [key: string]: unknown;
    };
};

export type GetStorageTaskResponse = GetStorageTaskResponses[keyof GetStorageTaskResponses];

export type GetStorageHasDerivativesData = {
    body?: never;
    path: {
        storage_id: string;
    };
    query?: never;
    url: '/api/v4/item/storage/{storage_id}/has-derivatives';
};

export type GetStorageHasDerivativesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetStorageHasDerivativesError = GetStorageHasDerivativesErrors[keyof GetStorageHasDerivativesErrors];

export type GetStorageHasDerivativesResponses = {
    /**
     * Successful Response
     */
    200: StorageDerivativesResponse;
};

export type GetStorageHasDerivativesResponse = GetStorageHasDerivativesResponses[keyof GetStorageHasDerivativesResponses];

export type CheckStorageBackingChainData = {
    body?: never;
    path: {
        storage_id: string;
    };
    query?: never;
    url: '/api/v4/item/storage/{storage_id}/check-backing-chain';
};

export type CheckStorageBackingChainErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type CheckStorageBackingChainError = CheckStorageBackingChainErrors[keyof CheckStorageBackingChainErrors];

export type CheckStorageBackingChainResponses = {
    /**
     * Successful Response
     */
    200: {
        [key: string]: unknown;
    };
};

export type CheckStorageBackingChainResponse = CheckStorageBackingChainResponses[keyof CheckStorageBackingChainResponses];

export type RecreateStorageData = {
    body: StorageRecreateRequest;
    path: {
        storage_id: string;
    };
    query?: never;
    url: '/api/v4/item/storage/{storage_id}/recreate';
};

export type RecreateStorageErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type RecreateStorageError = RecreateStorageErrors[keyof RecreateStorageErrors];

export type RecreateStorageResponses = {
    /**
     * Successful Response
     */
    200: TaskIdResponse;
};

export type RecreateStorageResponse = RecreateStorageResponses[keyof RecreateStorageResponses];

export type StopStorageDesktopsData = {
    body?: never;
    path: {
        storage_id: string;
    };
    query?: never;
    url: '/api/v4/item/storage/{storage_id}/stop';
};

export type StopStorageDesktopsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type StopStorageDesktopsError = StopStorageDesktopsErrors[keyof StopStorageDesktopsErrors];

export type StopStorageDesktopsResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type StopStorageDesktopsResponse = StopStorageDesktopsResponses[keyof StopStorageDesktopsResponses];

export type GetStorageStoragesWithUuidData = {
    body?: never;
    path: {
        storage_id: string;
    };
    query?: never;
    url: '/api/v4/item/storage/{storage_id}/storages_with_uuid';
};

export type GetStorageStoragesWithUuidErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetStorageStoragesWithUuidError = GetStorageStoragesWithUuidErrors[keyof GetStorageStoragesWithUuidErrors];

export type GetStorageStoragesWithUuidResponses = {
    /**
     * Successful Response
     */
    200: Array<StoragesWithUuidEntry>;
};

export type GetStorageStoragesWithUuidResponse = GetStorageStoragesWithUuidResponses[keyof GetStorageStoragesWithUuidResponses];

export type ListAllStoragesWithUuidData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/storage/storages_with_uuid';
};

export type ListAllStoragesWithUuidErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type ListAllStoragesWithUuidError = ListAllStoragesWithUuidErrors[keyof ListAllStoragesWithUuidErrors];

export type ListAllStoragesWithUuidResponses = {
    /**
     * Successful Response
     */
    200: Array<StoragesWithUuidEntry>;
};

export type ListAllStoragesWithUuidResponse = ListAllStoragesWithUuidResponses[keyof ListAllStoragesWithUuidResponses];

export type ListAllStoragesWithUuidStatusData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/storage/storages_with_uuid/status';
};

export type ListAllStoragesWithUuidStatusErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type ListAllStoragesWithUuidStatusError = ListAllStoragesWithUuidStatusErrors[keyof ListAllStoragesWithUuidStatusErrors];

export type ListAllStoragesWithUuidStatusResponses = {
    /**
     * Successful Response
     */
    200: Array<StoragesWithUuidEntry>;
};

export type ListAllStoragesWithUuidStatusResponse = ListAllStoragesWithUuidStatusResponses[keyof ListAllStoragesWithUuidStatusResponses];

export type ListAllStoragesWithUuidFilteredData = {
    body?: never;
    path: {
        status: string;
    };
    query?: never;
    url: '/api/v4/items/storage/storages_with_uuid/{status}';
};

export type ListAllStoragesWithUuidFilteredErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type ListAllStoragesWithUuidFilteredError = ListAllStoragesWithUuidFilteredErrors[keyof ListAllStoragesWithUuidFilteredErrors];

export type ListAllStoragesWithUuidFilteredResponses = {
    /**
     * Successful Response
     */
    200: Array<StoragesWithUuidEntry>;
};

export type ListAllStoragesWithUuidFilteredResponse = ListAllStoragesWithUuidFilteredResponses[keyof ListAllStoragesWithUuidFilteredResponses];

export type GetDefaultStoragePoolData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/storage-pool/default';
};

export type GetDefaultStoragePoolErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDefaultStoragePoolError = GetDefaultStoragePoolErrors[keyof GetDefaultStoragePoolErrors];

export type GetDefaultStoragePoolResponses = {
    /**
     * Successful Response
     */
    200: StoragePoolResponse;
};

export type GetDefaultStoragePoolResponse = GetDefaultStoragePoolResponses[keyof GetDefaultStoragePoolResponses];

export type GetStoragePoolByPathData = {
    body: StoragePoolByPathRequest;
    path?: never;
    query?: never;
    url: '/api/v4/storage-pool/by-path';
};

export type GetStoragePoolByPathErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetStoragePoolByPathError = GetStoragePoolByPathErrors[keyof GetStoragePoolByPathErrors];

export type GetStoragePoolByPathResponses = {
    /**
     * Successful Response
     */
    200: StoragePoolResponse;
};

export type GetStoragePoolByPathResponse = GetStoragePoolByPathResponses[keyof GetStoragePoolByPathResponses];

export type GetAdminTasksData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page size (max 10000 to bound payload).
         */
        limit?: number;
        /**
         * Skip the first ``offset`` matching tasks.
         */
        offset?: number;
    };
    url: '/api/v4/admin/tasks';
};

export type GetAdminTasksErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetAdminTasksError = GetAdminTasksErrors[keyof GetAdminTasksErrors];

export type GetAdminTasksResponses = {
    /**
     * Successful Response
     */
    200: Array<TaskResponse>;
};

export type GetAdminTasksResponse = GetAdminTasksResponses[keyof GetAdminTasksResponses];

export type AnalyticsStorageData = {
    body: AnalyticsCategoriesRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/analytics/storage';
};

export type AnalyticsStorageErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AnalyticsStorageError = AnalyticsStorageErrors[keyof AnalyticsStorageErrors];

export type AnalyticsStorageResponses = {
    /**
     * Successful Response
     */
    200: StorageUsageResponse;
};

export type AnalyticsStorageResponse = AnalyticsStorageResponses[keyof AnalyticsStorageResponses];

export type AnalyticsResourceCountData = {
    body: AnalyticsCategoriesRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/analytics/resources/count';
};

export type AnalyticsResourceCountErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AnalyticsResourceCountError = AnalyticsResourceCountErrors[keyof AnalyticsResourceCountErrors];

export type AnalyticsResourceCountResponses = {
    /**
     * Successful Response
     */
    200: ResourceCountResponse;
};

export type AnalyticsResourceCountResponse = AnalyticsResourceCountResponses[keyof AnalyticsResourceCountResponses];

export type AnalyticsSuggestedRemovalsData = {
    body: AnalyticsSuggestedRemovalsRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/analytics/suggested_removals';
};

export type AnalyticsSuggestedRemovalsErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AnalyticsSuggestedRemovalsError = AnalyticsSuggestedRemovalsErrors[keyof AnalyticsSuggestedRemovalsErrors];

export type AnalyticsSuggestedRemovalsResponses = {
    /**
     * Successful Response
     */
    200: SuggestedRemovalsResponse;
};

export type AnalyticsSuggestedRemovalsResponse = AnalyticsSuggestedRemovalsResponses[keyof AnalyticsSuggestedRemovalsResponses];

export type AnalyticsGraphsConfListData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/analytics/graph';
};

export type AnalyticsGraphsConfListErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AnalyticsGraphsConfListError = AnalyticsGraphsConfListErrors[keyof AnalyticsGraphsConfListErrors];

export type AnalyticsGraphsConfListResponses = {
    /**
     * Successful Response
     */
    200: Array<AnalyticsGraphConfigResponse>;
};

export type AnalyticsGraphsConfListResponse = AnalyticsGraphsConfListResponses[keyof AnalyticsGraphsConfListResponses];

export type AdminAuthenticationProvidersData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/authentication/providers';
};

export type AdminAuthenticationProvidersErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminAuthenticationProvidersError = AdminAuthenticationProvidersErrors[keyof AdminAuthenticationProvidersErrors];

export type AdminAuthenticationProvidersResponses = {
    /**
     * Successful Response
     */
    200: ProvidersResponse;
};

export type AdminAuthenticationProvidersResponse = AdminAuthenticationProvidersResponses[keyof AdminAuthenticationProvidersResponses];

export type GetCategoryBrandingData = {
    body?: never;
    path: {
        category_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/category/{category_id}/branding';
};

export type GetCategoryBrandingErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetCategoryBrandingError = GetCategoryBrandingErrors[keyof GetCategoryBrandingErrors];

export type GetCategoryBrandingResponses = {
    /**
     * Branding configuration
     */
    200: BrandingResponse;
};

export type GetCategoryBrandingResponse = GetCategoryBrandingResponses[keyof GetCategoryBrandingResponses];

export type UpdateCategoryBrandingData = {
    body: BrandingUpdateData;
    path: {
        category_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/category/{category_id}/branding';
};

export type UpdateCategoryBrandingErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type UpdateCategoryBrandingError = UpdateCategoryBrandingErrors[keyof UpdateCategoryBrandingErrors];

export type UpdateCategoryBrandingResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type UpdateCategoryBrandingResponse = UpdateCategoryBrandingResponses[keyof UpdateCategoryBrandingResponses];

export type GetCategoryAuthenticationData = {
    body?: never;
    path: {
        category_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/category/{category_id}/authentication';
};

export type GetCategoryAuthenticationErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetCategoryAuthenticationError = GetCategoryAuthenticationErrors[keyof GetCategoryAuthenticationErrors];

export type GetCategoryAuthenticationResponses = {
    /**
     * Authentication configuration
     */
    200: CategoryAuthenticationResponse;
};

export type GetCategoryAuthenticationResponse = GetCategoryAuthenticationResponses[keyof GetCategoryAuthenticationResponses];

export type UpdateCategoryAuthenticationData = {
    body: AdminCategoryAuthenticationData;
    path: {
        category_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/category/{category_id}/authentication';
};

export type UpdateCategoryAuthenticationErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type UpdateCategoryAuthenticationError = UpdateCategoryAuthenticationErrors[keyof UpdateCategoryAuthenticationErrors];

export type UpdateCategoryAuthenticationResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type UpdateCategoryAuthenticationResponse = UpdateCategoryAuthenticationResponses[keyof UpdateCategoryAuthenticationResponses];

export type UpdateCategoryLoginNotificationData = {
    body: CategoryLoginNotificationData;
    path: {
        category_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/category/{category_id}/login_notification';
};

export type UpdateCategoryLoginNotificationErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type UpdateCategoryLoginNotificationError = UpdateCategoryLoginNotificationErrors[keyof UpdateCategoryLoginNotificationErrors];

export type UpdateCategoryLoginNotificationResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type UpdateCategoryLoginNotificationResponse = UpdateCategoryLoginNotificationResponses[keyof UpdateCategoryLoginNotificationResponses];

export type EnableCategoryLoginNotificationData = {
    body: CategoryLoginNotificationEnableData;
    path: {
        category_id: string;
        notification_type: 'cover' | 'form';
    };
    query?: never;
    url: '/api/v4/admin/item/category/{category_id}/login_notification/{notification_type}/enable';
};

export type EnableCategoryLoginNotificationErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type EnableCategoryLoginNotificationError = EnableCategoryLoginNotificationErrors[keyof EnableCategoryLoginNotificationErrors];

export type EnableCategoryLoginNotificationResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type EnableCategoryLoginNotificationResponse = EnableCategoryLoginNotificationResponses[keyof EnableCategoryLoginNotificationResponses];

export type AdminListDomainsData2 = {
    body: AdminListDomainsData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/domains';
};

export type AdminListDomainsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminListDomainsError = AdminListDomainsErrors[keyof AdminListDomainsErrors];

export type AdminListDomainsResponses = {
    /**
     * Domains list retrieved
     */
    200: Array<AdminDomainListItem>;
};

export type AdminListDomainsResponse = AdminListDomainsResponses[keyof AdminListDomainsResponses];

export type AdminDomainDetailsData = {
    body?: never;
    path: {
        domain_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/domain/{domain_id}/details';
};

export type AdminDomainDetailsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDomainDetailsError = AdminDomainDetailsErrors[keyof AdminDomainDetailsErrors];

export type AdminDomainDetailsResponses = {
    /**
     * Domain details retrieved
     */
    200: AdminDomainDetailsResponse;
};

export type AdminDomainDetailsResponse2 = AdminDomainDetailsResponses[keyof AdminDomainDetailsResponses];

export type AdminDomainViewerDataData = {
    body?: never;
    path: {
        domain_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/domain/{domain_id}/viewer_data';
};

export type AdminDomainViewerDataErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDomainViewerDataError = AdminDomainViewerDataErrors[keyof AdminDomainViewerDataErrors];

export type AdminDomainViewerDataResponses = {
    /**
     * Domain viewer data retrieved
     */
    200: AdminDomainViewerDataResponse;
};

export type AdminDomainViewerDataResponse2 = AdminDomainViewerDataResponses[keyof AdminDomainViewerDataResponses];

export type AdminDomainsStatusData = {
    body?: never;
    path: {
        status: string;
    };
    query?: never;
    url: '/api/v4/admin/item/domains_status/{status}';
};

export type AdminDomainsStatusErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDomainsStatusError = AdminDomainsStatusErrors[keyof AdminDomainsStatusErrors];

export type AdminDomainsStatusResponses = {
    /**
     * Domains by status retrieved
     */
    200: Array<AdminDomainStatusItem>;
};

export type AdminDomainsStatusResponse = AdminDomainsStatusResponses[keyof AdminDomainsStatusResponses];

export type AdminFindStoragesByDomainStatusData = {
    body?: never;
    path: {
        status: string;
    };
    query?: never;
    url: '/api/v4/admin/items/domains/status/{status}/find_storages';
};

export type AdminFindStoragesByDomainStatusErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminFindStoragesByDomainStatusError = AdminFindStoragesByDomainStatusErrors[keyof AdminFindStoragesByDomainStatusErrors];

export type AdminFindStoragesByDomainStatusResponses = {
    /**
     * Successful Response
     */
    200: AdminFindStoragesByStatusResponse;
};

export type AdminFindStoragesByDomainStatusResponse = AdminFindStoragesByDomainStatusResponses[keyof AdminFindStoragesByDomainStatusResponses];

export type AdminDomainStorageData = {
    body?: never;
    path: {
        domain_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/domain/storage/{domain_id}';
};

export type AdminDomainStorageErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDomainStorageError = AdminDomainStorageErrors[keyof AdminDomainStorageErrors];

export type AdminDomainStorageResponses = {
    /**
     * Domain storage info retrieved
     */
    200: Array<AdminDomainStorageItem>;
};

export type AdminDomainStorageResponse = AdminDomainStorageResponses[keyof AdminDomainStorageResponses];

export type AdminDesktopsTreeListData = {
    body?: never;
    path: {
        template_id: string;
    };
    query?: never;
    url: '/api/v4/admin/items/desktops/tree_list/{template_id}';
};

export type AdminDesktopsTreeListErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDesktopsTreeListError = AdminDesktopsTreeListErrors[keyof AdminDesktopsTreeListErrors];

export type AdminDesktopsTreeListResponses = {
    /**
     * Template tree list retrieved
     */
    200: Array<AdminTemplateTreeNode>;
};

export type AdminDesktopsTreeListResponse = AdminDesktopsTreeListResponses[keyof AdminDesktopsTreeListResponses];

export type AdminDomainTemplateTreeData = {
    body?: never;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/domain/template_tree/{desktop_id}';
};

export type AdminDomainTemplateTreeErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDomainTemplateTreeError = AdminDomainTemplateTreeErrors[keyof AdminDomainTemplateTreeErrors];

export type AdminDomainTemplateTreeResponses = {
    /**
     * Domain template tree retrieved
     */
    200: Array<AdminDomainTemplateTreeItem>;
};

export type AdminDomainTemplateTreeResponse = AdminDomainTemplateTreeResponses[keyof AdminDomainTemplateTreeResponses];

export type AdminMultipleActionsData2 = {
    body: AdminMultipleActionsData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/multiple_actions';
};

export type AdminMultipleActionsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminMultipleActionsError = AdminMultipleActionsErrors[keyof AdminMultipleActionsErrors];

export type AdminMultipleActionsResponses = {
    /**
     * Actions performed
     */
    204: void;
};

export type AdminMultipleActionsResponse = AdminMultipleActionsResponses[keyof AdminMultipleActionsResponses];

export type AdminTemplateDeleteData = {
    body?: never;
    path: {
        template_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/templates/delete/{template_id}';
};

export type AdminTemplateDeleteErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminTemplateDeleteError = AdminTemplateDeleteErrors[keyof AdminTemplateDeleteErrors];

export type AdminTemplateDeleteResponses = {
    /**
     * Template deleted
     */
    204: void;
};

export type AdminTemplateDeleteResponse = AdminTemplateDeleteResponses[keyof AdminTemplateDeleteResponses];

export type AdminDomainsNameSearchData = {
    body?: never;
    path: {
        kind: DomainKindEnum;
    };
    query?: {
        q?: string;
    };
    url: '/api/v4/admin/items/domains/name/{kind}/search';
};

export type AdminDomainsNameSearchErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDomainsNameSearchError = AdminDomainsNameSearchErrors[keyof AdminDomainsNameSearchErrors];

export type AdminDomainsNameSearchResponses = {
    /**
     * Matching domain names retrieved
     */
    200: AdminDomainsFieldResponse;
};

export type AdminDomainsNameSearchResponse = AdminDomainsNameSearchResponses[keyof AdminDomainsNameSearchResponses];

export type AdminDomainsFieldData = {
    body?: never;
    path: {
        field: DomainFilterFieldEnum;
        kind: DomainKindEnum;
    };
    query?: never;
    url: '/api/v4/admin/items/domains/{field}/{kind}';
};

export type AdminDomainsFieldErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDomainsFieldError = AdminDomainsFieldErrors[keyof AdminDomainsFieldErrors];

export type AdminDomainsFieldResponses = {
    /**
     * Domain field values retrieved
     */
    200: AdminDomainsFieldResponse;
};

export type AdminDomainsFieldResponse2 = AdminDomainsFieldResponses[keyof AdminDomainsFieldResponses];

export type AdminDomainHardwareData = {
    body?: never;
    path: {
        domain_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/domain/hardware/{domain_id}';
};

export type AdminDomainHardwareErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDomainHardwareError = AdminDomainHardwareErrors[keyof AdminDomainHardwareErrors];

export type AdminDomainHardwareResponses = {
    /**
     * Domain hardware retrieved
     */
    200: AdminDomainHardwareResponse;
};

export type AdminDomainHardwareResponse2 = AdminDomainHardwareResponses[keyof AdminDomainHardwareResponses];

export type AdminDomainSearchInfoData = {
    body?: never;
    path: {
        domain_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/domain/search-info/{domain_id}';
};

export type AdminDomainSearchInfoErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDomainSearchInfoError = AdminDomainSearchInfoErrors[keyof AdminDomainSearchInfoErrors];

export type AdminDomainSearchInfoResponses = {
    /**
     * Domain search info retrieved
     */
    200: AdminDomainSearchInfoResponse;
};

export type AdminDomainSearchInfoResponse2 = AdminDomainSearchInfoResponses[keyof AdminDomainSearchInfoResponses];

export type AdminLogsDesktopsRawData = {
    body: LogsDataTablesRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/logs_desktops';
};

export type AdminLogsDesktopsRawErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminLogsDesktopsRawError = AdminLogsDesktopsRawErrors[keyof AdminLogsDesktopsRawErrors];

export type AdminLogsDesktopsRawResponses = {
    /**
     * Desktop logs retrieved
     */
    200: LogsDataTablesResponseDesktopLogRow;
};

export type AdminLogsDesktopsRawResponse = AdminLogsDesktopsRawResponses[keyof AdminLogsDesktopsRawResponses];

export type AdminLogsDesktopsViewData = {
    body: LogsDataTablesRequest;
    path: {
        view: string;
    };
    query?: never;
    url: '/api/v4/admin/items/logs_desktops/{view}';
};

export type AdminLogsDesktopsViewErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminLogsDesktopsViewError = AdminLogsDesktopsViewErrors[keyof AdminLogsDesktopsViewErrors];

export type AdminLogsDesktopsViewResponses = {
    /**
     * Desktop logs retrieved
     */
    200: LogsDataTablesResponseUnionDesktopLogRowDesktopGroupingLogRowCategoryGroupingLogRow;
};

export type AdminLogsDesktopsViewResponse = AdminLogsDesktopsViewResponses[keyof AdminLogsDesktopsViewResponses];

export type AdminLogsUsersRawData = {
    body: LogsDataTablesRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/logs_users';
};

export type AdminLogsUsersRawErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminLogsUsersRawError = AdminLogsUsersRawErrors[keyof AdminLogsUsersRawErrors];

export type AdminLogsUsersRawResponses = {
    /**
     * User logs retrieved
     */
    200: LogsDataTablesResponseUserLogRow;
};

export type AdminLogsUsersRawResponse = AdminLogsUsersRawResponses[keyof AdminLogsUsersRawResponses];

export type AdminLogsUsersViewData = {
    body: LogsDataTablesRequest;
    path: {
        view: string;
    };
    query?: never;
    url: '/api/v4/admin/items/logs_users/{view}';
};

export type AdminLogsUsersViewErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminLogsUsersViewError = AdminLogsUsersViewErrors[keyof AdminLogsUsersViewErrors];

export type AdminLogsUsersViewResponses = {
    /**
     * User logs retrieved
     */
    200: LogsDataTablesResponseUnionUserLogRowUserGroupingLogRowCategoryGroupingLogRow;
};

export type AdminLogsUsersViewResponse = AdminLogsUsersViewResponses[keyof AdminLogsUsersViewResponses];

export type AdminLogsDesktopsListData = {
    body?: never;
    path?: never;
    query?: {
        start_date?: string;
        end_date?: string;
        limit?: number;
        offset?: number;
        desktop_id?: string;
        user_id?: string;
    };
    url: '/api/v4/admin/items/logs_desktops/list';
};

export type AdminLogsDesktopsListErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminLogsDesktopsListError = AdminLogsDesktopsListErrors[keyof AdminLogsDesktopsListErrors];

export type AdminLogsDesktopsListResponses = {
    /**
     * Successful Response
     */
    200: Array<DesktopLogRow>;
};

export type AdminLogsDesktopsListResponse = AdminLogsDesktopsListResponses[keyof AdminLogsDesktopsListResponses];

export type AdminLogsUsersListData = {
    body?: never;
    path?: never;
    query?: {
        start_date?: string;
        end_date?: string;
        limit?: number;
        offset?: number;
        user_id?: string;
        group_id?: string;
    };
    url: '/api/v4/admin/items/logs_users/list';
};

export type AdminLogsUsersListErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminLogsUsersListError = AdminLogsUsersListErrors[keyof AdminLogsUsersListErrors];

export type AdminLogsUsersListResponses = {
    /**
     * Successful Response
     */
    200: Array<UserLogRow>;
};

export type AdminLogsUsersListResponse = AdminLogsUsersListResponses[keyof AdminLogsUsersListResponses];

export type AdminLoginConfigGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/login-config';
};

export type AdminLoginConfigGetErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminLoginConfigGetError = AdminLoginConfigGetErrors[keyof AdminLoginConfigGetErrors];

export type AdminLoginConfigGetResponses = {
    /**
     * Successful Response
     */
    200: AdminLoginConfigResponse;
};

export type AdminLoginConfigGetResponse = AdminLoginConfigGetResponses[keyof AdminLoginConfigGetResponses];

export type AdminLoginConfigByCategoryData = {
    body?: never;
    path: {
        /**
         * Category ID
         */
        category_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/login-config/{category_id}';
};

export type AdminLoginConfigByCategoryErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminLoginConfigByCategoryError = AdminLoginConfigByCategoryErrors[keyof AdminLoginConfigByCategoryErrors];

export type AdminLoginConfigByCategoryResponses = {
    /**
     * Successful Response
     */
    200: AdminLoginConfigResponse;
};

export type AdminLoginConfigByCategoryResponse = AdminLoginConfigByCategoryResponses[keyof AdminLoginConfigByCategoryResponses];

export type AdminMediaStatusData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/media/status';
};

export type AdminMediaStatusErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminMediaStatusError = AdminMediaStatusErrors[keyof AdminMediaStatusErrors];

export type AdminMediaStatusResponses = {
    /**
     * Successful Response
     */
    200: Array<AdminMediaStatusCount>;
};

export type AdminMediaStatusResponse = AdminMediaStatusResponses[keyof AdminMediaStatusResponses];

export type AdminMediaListData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/media';
};

export type AdminMediaListErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminMediaListError = AdminMediaListErrors[keyof AdminMediaListErrors];

export type AdminMediaListResponses = {
    /**
     * Successful Response
     */
    200: Array<AdminMediaItem>;
};

export type AdminMediaListResponse = AdminMediaListResponses[keyof AdminMediaListResponses];

export type AdminMediaByStatusData = {
    body?: never;
    path: {
        /**
         * Media status to filter by
         */
        status: string;
    };
    query?: never;
    url: '/api/v4/admin/items/media/{status}';
};

export type AdminMediaByStatusErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminMediaByStatusError = AdminMediaByStatusErrors[keyof AdminMediaByStatusErrors];

export type AdminMediaByStatusResponses = {
    /**
     * Successful Response
     */
    200: Array<AdminMediaItem>;
};

export type AdminMediaByStatusResponse = AdminMediaByStatusResponses[keyof AdminMediaByStatusResponses];

export type ListDesktopAttachedMediaData = {
    body?: never;
    path: {
        desktop_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/desktop/{desktop_id}/media-list';
};

export type ListDesktopAttachedMediaErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ListDesktopAttachedMediaError = ListDesktopAttachedMediaErrors[keyof ListDesktopAttachedMediaErrors];

export type ListDesktopAttachedMediaResponses = {
    /**
     * Successful Response
     */
    200: Array<DesktopAttachedMediaItem>;
};

export type ListDesktopAttachedMediaResponse = ListDesktopAttachedMediaResponses[keyof ListDesktopAttachedMediaResponses];

export type ChangeMediaOwnerData = {
    body?: never;
    path: {
        /**
         * The ID of the media
         */
        media_id: string;
        /**
         * The ID of the new owner
         */
        user_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/media/{media_id}/change-owner/{user_id}';
};

export type ChangeMediaOwnerErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type ChangeMediaOwnerError = ChangeMediaOwnerErrors[keyof ChangeMediaOwnerErrors];

export type ChangeMediaOwnerResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type ChangeMediaOwnerResponse = ChangeMediaOwnerResponses[keyof ChangeMediaOwnerResponses];

export type CheckMediaData = {
    body?: never;
    path: {
        media_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/media/{media_id}/check';
};

export type CheckMediaErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type CheckMediaError = CheckMediaErrors[keyof CheckMediaErrors];

export type CheckMediaResponses = {
    /**
     * Successful Response
     */
    200: MediaCheckResponse;
};

export type CheckMediaResponse = CheckMediaResponses[keyof CheckMediaResponses];

export type AdminStorageStatusData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/storage/status';
};

export type AdminStorageStatusErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminStorageStatusError = AdminStorageStatusErrors[keyof AdminStorageStatusErrors];

export type AdminStorageStatusResponses = {
    /**
     * Successful Response
     */
    200: Array<AdminStorageStatusCount>;
};

export type AdminStorageStatusResponse = AdminStorageStatusResponses[keyof AdminStorageStatusResponses];

export type AdminStorageListData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/storage';
};

export type AdminStorageListErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminStorageListError = AdminStorageListErrors[keyof AdminStorageListErrors];

export type AdminStorageListResponses = {
    /**
     * Successful Response
     */
    200: Array<AdminStorageItem>;
};

export type AdminStorageListResponse = AdminStorageListResponses[keyof AdminStorageListResponses];

export type AdminStorageListFilteredData = {
    body: AdminStorageFilterRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/storage';
};

export type AdminStorageListFilteredErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminStorageListFilteredError = AdminStorageListFilteredErrors[keyof AdminStorageListFilteredErrors];

export type AdminStorageListFilteredResponses = {
    /**
     * Successful Response
     */
    200: Array<AdminStorageItem>;
};

export type AdminStorageListFilteredResponse = AdminStorageListFilteredResponses[keyof AdminStorageListFilteredResponses];

export type AdminStorageByStatusData = {
    body?: never;
    path: {
        /**
         * Storage status to filter by
         */
        status: string;
    };
    query?: never;
    url: '/api/v4/admin/items/storage/by-status/{status}';
};

export type AdminStorageByStatusErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminStorageByStatusError = AdminStorageByStatusErrors[keyof AdminStorageByStatusErrors];

export type AdminStorageByStatusResponses = {
    /**
     * Successful Response
     */
    200: Array<AdminStorageItem>;
};

export type AdminStorageByStatusResponse = AdminStorageByStatusResponses[keyof AdminStorageByStatusResponses];

export type AdminStorageByStatusFilteredData = {
    body: AdminStorageFilterRequest;
    path: {
        /**
         * Storage status to filter by
         */
        status: string;
    };
    query?: never;
    url: '/api/v4/admin/items/storage/by-status/{status}';
};

export type AdminStorageByStatusFilteredErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminStorageByStatusFilteredError = AdminStorageByStatusFilteredErrors[keyof AdminStorageByStatusFilteredErrors];

export type AdminStorageByStatusFilteredResponses = {
    /**
     * Successful Response
     */
    200: Array<AdminStorageItem>;
};

export type AdminStorageByStatusFilteredResponse = AdminStorageByStatusFilteredResponses[keyof AdminStorageByStatusFilteredResponses];

export type AdminStorageByPendingActionData = {
    body?: never;
    path: {
        /**
         * Pending action to filter by
         */
        action: string;
    };
    query?: {
        limit?: number | null;
    };
    url: '/api/v4/admin/items/storage/pending/{action}';
};

export type AdminStorageByPendingActionErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminStorageByPendingActionError = AdminStorageByPendingActionErrors[keyof AdminStorageByPendingActionErrors];

export type AdminStorageByPendingActionResponses = {
    /**
     * Successful Response
     */
    200: Array<AdminStorageItem>;
};

export type AdminStorageByPendingActionResponse = AdminStorageByPendingActionResponses[keyof AdminStorageByPendingActionResponses];

export type AdminStorageDomainsData = {
    body?: never;
    path: {
        /**
         * Storage ID
         */
        storage_id: string;
    };
    query?: never;
    url: '/api/v4/admin/items/storage/domains/{storage_id}';
};

export type AdminStorageDomainsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminStorageDomainsError = AdminStorageDomainsErrors[keyof AdminStorageDomainsErrors];

export type AdminStorageDomainsResponses = {
    /**
     * Successful Response
     */
    200: Array<AdminStorageDomain>;
};

export type AdminStorageDomainsResponse = AdminStorageDomainsResponses[keyof AdminStorageDomainsResponses];

export type AdminMediaDomainsData = {
    body?: never;
    path: {
        /**
         * Media ID
         */
        storage_id: string;
    };
    query?: never;
    url: '/api/v4/admin/items/media/domains/{storage_id}';
};

export type AdminMediaDomainsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminMediaDomainsError = AdminMediaDomainsErrors[keyof AdminMediaDomainsErrors];

export type AdminMediaDomainsResponses = {
    /**
     * Successful Response
     */
    200: Array<AdminStorageDomain>;
};

export type AdminMediaDomainsResponse = AdminMediaDomainsResponses[keyof AdminMediaDomainsResponses];

export type AdminStorageDeleteData = {
    body?: never;
    path: {
        /**
         * Storage ID to delete
         */
        storage_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/storage/{storage_id}';
};

export type AdminStorageDeleteErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminStorageDeleteError = AdminStorageDeleteErrors[keyof AdminStorageDeleteErrors];

export type AdminStorageDeleteResponses = {
    /**
     * Successful Response
     */
    202: DeleteResponse;
};

export type AdminStorageDeleteResponse = AdminStorageDeleteResponses[keyof AdminStorageDeleteResponses];

export type AdminStorageInfoData = {
    body?: never;
    path: {
        /**
         * Storage ID
         */
        storage_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/storage/info/{storage_id}';
};

export type AdminStorageInfoErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminStorageInfoError = AdminStorageInfoErrors[keyof AdminStorageInfoErrors];

export type AdminStorageInfoResponses = {
    /**
     * Successful Response
     */
    200: AdminStorageInfo;
};

export type AdminStorageInfoResponse = AdminStorageInfoResponses[keyof AdminStorageInfoResponses];

export type AdminStorageSearchInfoData = {
    body?: never;
    path: {
        /**
         * Storage ID
         */
        storage_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/storage/search-info/{storage_id}';
};

export type AdminStorageSearchInfoErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminStorageSearchInfoError = AdminStorageSearchInfoErrors[keyof AdminStorageSearchInfoErrors];

export type AdminStorageSearchInfoResponses = {
    /**
     * Successful Response
     */
    200: AdminStorageInfo;
};

export type AdminStorageSearchInfoResponse = AdminStorageSearchInfoResponses[keyof AdminStorageSearchInfoResponses];

export type AdminTableGetData = {
    body?: never;
    path: {
        /**
         * Database table name
         */
        table: string;
    };
    query?: never;
    url: '/api/v4/admin/items/table/{table}';
};

export type AdminTableGetErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminTableGetError = AdminTableGetErrors[keyof AdminTableGetErrors];

export type AdminTableGetResponses = {
    /**
     * Successful Response
     */
    200: TableItem | Array<TableItem>;
};

export type AdminTableGetResponse = AdminTableGetResponses[keyof AdminTableGetResponses];

export type AdminTableListData = {
    body: TableListRequest;
    path: {
        /**
         * Database table name
         */
        table: string;
    };
    query?: never;
    url: '/api/v4/admin/items/table/{table}';
};

export type AdminTableListErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminTableListError = AdminTableListErrors[keyof AdminTableListErrors];

export type AdminTableListResponses = {
    /**
     * Successful Response
     */
    200: TableItem | Array<TableItem>;
};

export type AdminTableListResponse = AdminTableListResponses[keyof AdminTableListResponses];

export type AdminUsageConsumptionData = {
    body: UsageConsumptionRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/usage';
};

export type AdminUsageConsumptionErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageConsumptionError = AdminUsageConsumptionErrors[keyof AdminUsageConsumptionErrors];

export type AdminUsageConsumptionResponses = {
    /**
     * Successful Response
     */
    200: Array<UsageItemConsumption>;
};

export type AdminUsageConsumptionResponse = AdminUsageConsumptionResponses[keyof AdminUsageConsumptionResponses];

export type AdminUsageStartEndData = {
    body: UsageStartEndRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/usage/start_end';
};

export type AdminUsageStartEndErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageStartEndError = AdminUsageStartEndErrors[keyof AdminUsageStartEndErrors];

export type AdminUsageStartEndResponses = {
    /**
     * Successful Response
     */
    200: Array<UsageStartEndItemResponse>;
};

export type AdminUsageStartEndResponse = AdminUsageStartEndResponses[keyof AdminUsageStartEndResponses];

export type AdminUsageConsumersData = {
    body?: never;
    path: {
        item_type: string;
    };
    query?: never;
    url: '/api/v4/admin/items/usage/consumers/{item_type}';
};

export type AdminUsageConsumersErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageConsumersError = AdminUsageConsumersErrors[keyof AdminUsageConsumersErrors];

export type AdminUsageConsumersResponses = {
    /**
     * Successful Response
     */
    200: Array<string>;
};

export type AdminUsageConsumersResponse = AdminUsageConsumersResponses[keyof AdminUsageConsumersResponses];

export type AdminUsageDistinctItemsData = {
    body?: never;
    path: {
        item_consumer: string;
        start: string;
        end: string;
    };
    query?: never;
    url: '/api/v4/admin/items/usage/distinct_items/{item_consumer}/{start}/{end}';
};

export type AdminUsageDistinctItemsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageDistinctItemsError = AdminUsageDistinctItemsErrors[keyof AdminUsageDistinctItemsErrors];

export type AdminUsageDistinctItemsResponses = {
    /**
     * Successful Response
     */
    200: Array<UsageDistinctItemResponse>;
};

export type AdminUsageDistinctItemsResponse = AdminUsageDistinctItemsResponses[keyof AdminUsageDistinctItemsResponses];

export type AdminUsageListParametersData = {
    body: UsageParameterIdsRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/usage/list_parameters';
};

export type AdminUsageListParametersErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageListParametersError = AdminUsageListParametersErrors[keyof AdminUsageListParametersErrors];

export type AdminUsageListParametersResponses = {
    /**
     * Successful Response
     */
    200: Array<UsageParameterResponse> | {
        [key: string]: unknown;
    };
};

export type AdminUsageListParametersResponse = AdminUsageListParametersResponses[keyof AdminUsageListParametersResponses];

export type AdminUsageGroupingsDropdownData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/usage/groupings_dropdown';
};

export type AdminUsageGroupingsDropdownErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageGroupingsDropdownError = AdminUsageGroupingsDropdownErrors[keyof AdminUsageGroupingsDropdownErrors];

export type AdminUsageGroupingsDropdownResponses = {
    /**
     * Successful Response
     */
    200: UsageGroupingsDropdownResponse;
};

export type AdminUsageGroupingsDropdownResponse = AdminUsageGroupingsDropdownResponses[keyof AdminUsageGroupingsDropdownResponses];

export type AdminUsageGroupingGetData = {
    body?: never;
    path: {
        grouping_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/usage/grouping/{grouping_id}';
};

export type AdminUsageGroupingGetErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageGroupingGetError = AdminUsageGroupingGetErrors[keyof AdminUsageGroupingGetErrors];

export type AdminUsageGroupingGetResponses = {
    /**
     * Successful Response
     */
    200: UsageGroupingResponse;
};

export type AdminUsageGroupingGetResponse = AdminUsageGroupingGetResponses[keyof AdminUsageGroupingGetResponses];

export type AdminUsageCreditsData = {
    body?: never;
    path: {
        consumer: string;
        item_type: string;
        item_id: string;
        grouping_id: string;
        start_date: string;
        end_date: string;
    };
    query?: never;
    url: '/api/v4/admin/items/usage/credits/{consumer}/{item_type}/{item_id}/{grouping_id}/{start_date}/{end_date}';
};

export type AdminUsageCreditsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageCreditsError = AdminUsageCreditsErrors[keyof AdminUsageCreditsErrors];

export type AdminUsageCreditsResponses = {
    /**
     * Successful Response
     */
    200: Array<UsageCreditIntervalResponse>;
};

export type AdminUsageCreditsResponse = AdminUsageCreditsResponses[keyof AdminUsageCreditsResponses];

export type AdminUsageResetDatesAllData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/usage/reset_date';
};

export type AdminUsageResetDatesAllErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageResetDatesAllError = AdminUsageResetDatesAllErrors[keyof AdminUsageResetDatesAllErrors];

export type AdminUsageResetDatesAllResponses = {
    /**
     * Successful Response
     */
    200: Array<string>;
};

export type AdminUsageResetDatesAllResponse = AdminUsageResetDatesAllResponses[keyof AdminUsageResetDatesAllResponses];

export type AdminUsageResetDatesRangeData = {
    body?: never;
    path: {
        start_date: string;
        end_date: string;
    };
    query?: never;
    url: '/api/v4/admin/items/usage/reset_date/{start_date}/{end_date}';
};

export type AdminUsageResetDatesRangeErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageResetDatesRangeError = AdminUsageResetDatesRangeErrors[keyof AdminUsageResetDatesRangeErrors];

export type AdminUsageResetDatesRangeResponses = {
    /**
     * Successful Response
     */
    200: Array<string>;
};

export type AdminUsageResetDatesRangeResponse = AdminUsageResetDatesRangeResponses[keyof AdminUsageResetDatesRangeResponses];

export type AdminGetJwtData = {
    body?: never;
    path: {
        user_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/jwt/{user_id}';
};

export type AdminGetJwtErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetJwtError = AdminGetJwtErrors[keyof AdminGetJwtErrors];

export type AdminGetJwtResponses = {
    /**
     * Successful Response
     */
    200: AdminUserImpersonateJwtResponse;
};

export type AdminGetJwtResponse = AdminGetJwtResponses[keyof AdminGetJwtResponses];

export type AdminUserExistsData = {
    body?: never;
    path: {
        user_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/user/{user_id}/exists';
};

export type AdminUserExistsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUserExistsError = AdminUserExistsErrors[keyof AdminUserExistsErrors];

export type AdminUserExistsResponses = {
    /**
     * User existence check result
     */
    200: boolean;
};

export type AdminUserExistsResponse = AdminUserExistsResponses[keyof AdminUserExistsResponses];

export type AdminGetUserData = {
    body?: never;
    path: {
        user_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/user/{user_id}';
};

export type AdminGetUserErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetUserError = AdminGetUserErrors[keyof AdminGetUserErrors];

export type AdminGetUserResponses = {
    /**
     * User data retrieved
     */
    200: AdminUserFullDataResponse;
};

export type AdminGetUserResponse = AdminGetUserResponses[keyof AdminGetUserResponses];

export type AdminUpdateUserData = {
    body: AdminUserUpdateData;
    path: {
        user_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/user/{user_id}';
};

export type AdminUpdateUserErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUpdateUserError = AdminUpdateUserErrors[keyof AdminUpdateUserErrors];

export type AdminUpdateUserResponses = {
    /**
     * User updated
     */
    204: void;
};

export type AdminUpdateUserResponse = AdminUpdateUserResponses[keyof AdminUpdateUserResponses];

export type AdminGetUserRawData = {
    body?: never;
    path: {
        user_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/user/{user_id}/raw';
};

export type AdminGetUserRawErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetUserRawError = AdminGetUserRawErrors[keyof AdminGetUserRawErrors];

export type AdminGetUserRawResponses = {
    /**
     * Raw user data retrieved
     */
    200: AdminUserFullDataResponse;
};

export type AdminGetUserRawResponse = AdminGetUserRawResponses[keyof AdminGetUserRawResponses];

export type AdminDeleteUsersData = {
    body: AdminUserDeleteData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/users';
};

export type AdminDeleteUsersErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Some users could not be deleted
     */
    428: unknown;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDeleteUsersError = AdminDeleteUsersErrors[keyof AdminDeleteUsersErrors];

export type AdminDeleteUsersResponses = {
    /**
     * Users deleted
     */
    200: AdminUserDeleteResponse;
};

export type AdminDeleteUsersResponse = AdminDeleteUsersResponses[keyof AdminDeleteUsersResponses];

export type AdminListUsersData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/users';
};

export type AdminListUsersErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminListUsersError = AdminListUsersErrors[keyof AdminListUsersErrors];

export type AdminListUsersResponses = {
    /**
     * Users list retrieved
     */
    200: Array<AdminUser>;
};

export type AdminListUsersResponse = AdminListUsersResponses[keyof AdminListUsersResponses];

export type AdminListUsersNavData = {
    body?: never;
    path: {
        nav: 'management' | 'quotas_limits';
    };
    query?: never;
    url: '/api/v4/admin/items/users/{nav}/users';
};

export type AdminListUsersNavErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminListUsersNavError = AdminListUsersNavErrors[keyof AdminListUsersNavErrors];

export type AdminListUsersNavResponses = {
    /**
     * Users list retrieved
     */
    200: Array<AdminUserNavItem>;
};

export type AdminListUsersNavResponse = AdminListUsersNavResponses[keyof AdminListUsersNavResponses];

export type AdminCreateUserData = {
    body: AdminUserCreateData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/user';
};

export type AdminCreateUserErrors = {
    /**
     * Bad Request
     */
    400: PasswordPolicyErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminCreateUserError = AdminCreateUserErrors[keyof AdminCreateUserErrors];

export type AdminCreateUserResponses = {
    /**
     * User created
     */
    200: AdminUser;
};

export type AdminCreateUserResponse = AdminCreateUserResponses[keyof AdminCreateUserResponses];

export type AdminUpdateUsersBulkData = {
    body: AdminUserUpdateData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/users/bulk';
};

export type AdminUpdateUsersBulkErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUpdateUsersBulkError = AdminUpdateUsersBulkErrors[keyof AdminUpdateUsersBulkErrors];

export type AdminUpdateUsersBulkResponses = {
    /**
     * Users updated
     */
    204: void;
};

export type AdminUpdateUsersBulkResponse = AdminUpdateUsersBulkResponses[keyof AdminUpdateUsersBulkResponses];

export type AdminUserLogoutData = {
    body?: never;
    path: {
        user_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/user/{user_id}/logout';
};

export type AdminUserLogoutErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUserLogoutError = AdminUserLogoutErrors[keyof AdminUserLogoutErrors];

export type AdminUserLogoutResponses = {
    /**
     * User logged out
     */
    204: void;
};

export type AdminUserLogoutResponse = AdminUserLogoutResponses[keyof AdminUserLogoutResponses];

export type AdminSearchUsersData = {
    body: AdminUserSearchData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/users/search';
};

export type AdminSearchUsersErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminSearchUsersError = AdminSearchUsersErrors[keyof AdminSearchUsersErrors];

export type AdminSearchUsersResponses = {
    /**
     * Search results
     */
    200: Array<AdminUserSearchItem>;
};

export type AdminSearchUsersResponse = AdminSearchUsersResponses[keyof AdminSearchUsersResponses];

export type AdminValidateCsvUsersData = {
    body: Array<{
        [key: string]: unknown;
    }>;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/users/csv/validate';
};

export type AdminValidateCsvUsersErrors = {
    /**
     * Bad Request
     */
    400: PasswordPolicyErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminValidateCsvUsersError = AdminValidateCsvUsersErrors[keyof AdminValidateCsvUsersErrors];

export type AdminValidateCsvUsersResponses = {
    /**
     * Validation results
     */
    200: AdminCsvValidateCreateResponse;
};

export type AdminValidateCsvUsersResponse = AdminValidateCsvUsersResponses[keyof AdminValidateCsvUsersResponses];

export type AdminValidateCsvUsersEditData = {
    body: Array<{
        [key: string]: unknown;
    }>;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/users/csv/validate';
};

export type AdminValidateCsvUsersEditErrors = {
    /**
     * Bad Request
     */
    400: PasswordPolicyErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminValidateCsvUsersEditError = AdminValidateCsvUsersEditErrors[keyof AdminValidateCsvUsersEditErrors];

export type AdminValidateCsvUsersEditResponses = {
    /**
     * Validation results
     */
    200: Array<AdminCsvUserEditRow>;
};

export type AdminValidateCsvUsersEditResponse = AdminValidateCsvUsersEditResponses[keyof AdminValidateCsvUsersEditResponses];

export type AdminImportCsvUsersData = {
    body: AdminCsvUserImportData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/users/csv';
};

export type AdminImportCsvUsersErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminImportCsvUsersError = AdminImportCsvUsersErrors[keyof AdminImportCsvUsersErrors];

export type AdminImportCsvUsersResponses = {
    /**
     * Import started
     */
    200: AdminCsvImportResponse;
};

export type AdminImportCsvUsersResponse = AdminImportCsvUsersResponses[keyof AdminImportCsvUsersResponses];

export type AdminEditCsvUsersData = {
    body: AdminCsvUserEditData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/users/csv';
};

export type AdminEditCsvUsersErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminEditCsvUsersError = AdminEditCsvUsersErrors[keyof AdminEditCsvUsersErrors];

export type AdminEditCsvUsersResponses = {
    /**
     * Users updated
     */
    204: void;
};

export type AdminEditCsvUsersResponse = AdminEditCsvUsersResponses[keyof AdminEditCsvUsersResponses];

export type AdminBulkCreateUsersData = {
    body: AdminBulkUserCreateData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/bulk/user';
};

export type AdminBulkCreateUsersErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminBulkCreateUsersError = AdminBulkCreateUsersErrors[keyof AdminBulkCreateUsersErrors];

export type AdminBulkCreateUsersResponses = {
    /**
     * Bulk creation started
     */
    200: AdminCsvImportResponse;
};

export type AdminBulkCreateUsersResponse = AdminBulkCreateUsersResponses[keyof AdminBulkCreateUsersResponses];

export type AdminSecondaryGroupsAddData = {
    body: AdminSecondaryGroupsData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/user/secondary-groups/add';
};

export type AdminSecondaryGroupsAddErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminSecondaryGroupsAddError = AdminSecondaryGroupsAddErrors[keyof AdminSecondaryGroupsAddErrors];

export type AdminSecondaryGroupsAddResponses = {
    /**
     * Secondary groups added
     */
    204: void;
};

export type AdminSecondaryGroupsAddResponse = AdminSecondaryGroupsAddResponses[keyof AdminSecondaryGroupsAddResponses];

export type AdminSecondaryGroupsOverwriteData = {
    body: AdminSecondaryGroupsData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/user/secondary-groups/overwrite';
};

export type AdminSecondaryGroupsOverwriteErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminSecondaryGroupsOverwriteError = AdminSecondaryGroupsOverwriteErrors[keyof AdminSecondaryGroupsOverwriteErrors];

export type AdminSecondaryGroupsOverwriteResponses = {
    /**
     * Secondary groups overwritten
     */
    204: void;
};

export type AdminSecondaryGroupsOverwriteResponse = AdminSecondaryGroupsOverwriteResponses[keyof AdminSecondaryGroupsOverwriteResponses];

export type AdminSecondaryGroupsDeleteData = {
    body: AdminSecondaryGroupsData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/user/secondary-groups/delete';
};

export type AdminSecondaryGroupsDeleteErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminSecondaryGroupsDeleteError = AdminSecondaryGroupsDeleteErrors[keyof AdminSecondaryGroupsDeleteErrors];

export type AdminSecondaryGroupsDeleteResponses = {
    /**
     * Secondary groups removed
     */
    204: void;
};

export type AdminSecondaryGroupsDeleteResponse = AdminSecondaryGroupsDeleteResponses[keyof AdminSecondaryGroupsDeleteResponses];

export type AdminGetPasswordPolicyData = {
    body?: never;
    path: {
        user_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/user/password-policy/{user_id}';
};

export type AdminGetPasswordPolicyErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetPasswordPolicyError = AdminGetPasswordPolicyErrors[keyof AdminGetPasswordPolicyErrors];

export type AdminGetPasswordPolicyResponses = {
    /**
     * Password policy retrieved
     */
    200: AdminPasswordPolicyResponse;
};

export type AdminGetPasswordPolicyResponse = AdminGetPasswordPolicyResponses[keyof AdminGetPasswordPolicyResponses];

export type AdminResetVpnData = {
    body?: never;
    path: {
        user_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/user/reset-vpn/{user_id}';
};

export type AdminResetVpnErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminResetVpnError = AdminResetVpnErrors[keyof AdminResetVpnErrors];

export type AdminResetVpnResponses = {
    /**
     * VPN reset
     */
    204: void;
};

export type AdminResetVpnResponse = AdminResetVpnResponses[keyof AdminResetVpnResponses];

export type AdminListGroupsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/groups';
};

export type AdminListGroupsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminListGroupsError = AdminListGroupsErrors[keyof AdminListGroupsErrors];

export type AdminListGroupsResponses = {
    /**
     * Groups list retrieved
     */
    200: Array<AdminGroupListItem>;
};

export type AdminListGroupsResponse = AdminListGroupsResponses[keyof AdminListGroupsResponses];

export type AdminListGroupsNavData = {
    body?: never;
    path: {
        nav: 'management' | 'quotas_limits';
    };
    query?: never;
    url: '/api/v4/admin/items/users/{nav}/groups';
};

export type AdminListGroupsNavErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminListGroupsNavError = AdminListGroupsNavErrors[keyof AdminListGroupsNavErrors];

export type AdminListGroupsNavResponses = {
    /**
     * Groups list retrieved
     */
    200: Array<AdminGroupNavItem>;
};

export type AdminListGroupsNavResponse = AdminListGroupsNavResponses[keyof AdminListGroupsNavResponses];

export type AdminDeleteGroupData = {
    body?: never;
    path: {
        group_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/group/{group_id}';
};

export type AdminDeleteGroupErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDeleteGroupError = AdminDeleteGroupErrors[keyof AdminDeleteGroupErrors];

export type AdminDeleteGroupResponses = {
    /**
     * Group deleted
     */
    204: void;
};

export type AdminDeleteGroupResponse = AdminDeleteGroupResponses[keyof AdminDeleteGroupResponses];

export type AdminGetGroupData = {
    body?: never;
    path: {
        group_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/group/{group_id}';
};

export type AdminGetGroupErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetGroupError = AdminGetGroupErrors[keyof AdminGetGroupErrors];

export type AdminGetGroupResponses = {
    /**
     * Group data retrieved
     */
    200: AdminGroupFullDataResponse;
};

export type AdminGetGroupResponse = AdminGetGroupResponses[keyof AdminGetGroupResponses];

export type AdminUpdateGroupData = {
    body: AdminGroupUpdateData;
    path: {
        group_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/group/{group_id}';
};

export type AdminUpdateGroupErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUpdateGroupError = AdminUpdateGroupErrors[keyof AdminUpdateGroupErrors];

export type AdminUpdateGroupResponses = {
    /**
     * Group updated
     */
    204: void;
};

export type AdminUpdateGroupResponse = AdminUpdateGroupResponses[keyof AdminUpdateGroupResponses];

export type AdminCreateGroupData = {
    body: AdminGroupCreateData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/group';
};

export type AdminCreateGroupErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Conflict
     */
    409: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminCreateGroupError = AdminCreateGroupErrors[keyof AdminCreateGroupErrors];

export type AdminCreateGroupResponses = {
    /**
     * Group created
     */
    200: AdminGroup;
};

export type AdminCreateGroupResponse = AdminCreateGroupResponses[keyof AdminCreateGroupResponses];

export type AdminGetGroupUsersData = {
    body?: never;
    path: {
        group_id: string;
    };
    query?: never;
    url: '/api/v4/admin/items/group/{group_id}/users';
};

export type AdminGetGroupUsersErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetGroupUsersError = AdminGetGroupUsersErrors[keyof AdminGetGroupUsersErrors];

export type AdminGetGroupUsersResponses = {
    /**
     * Group users retrieved
     */
    200: Array<AdminGroupUserItem>;
};

export type AdminGetGroupUsersResponse = AdminGetGroupUsersResponses[keyof AdminGetGroupUsersResponses];

export type AdminGroupEnrollmentData2 = {
    body: AdminGroupEnrollmentData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/group/enrollment';
};

export type AdminGroupEnrollmentErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGroupEnrollmentError = AdminGroupEnrollmentErrors[keyof AdminGroupEnrollmentErrors];

export type AdminGroupEnrollmentResponses = {
    /**
     * Enrollment updated
     */
    200: AdminGroupEnrollmentResponse;
};

export type AdminGroupEnrollmentResponse2 = AdminGroupEnrollmentResponses[keyof AdminGroupEnrollmentResponses];

export type AdminListCategoriesNavData = {
    body?: never;
    path: {
        nav: 'management' | 'quotas_limits';
    };
    query?: never;
    url: '/api/v4/admin/items/users/{nav}/categories';
};

export type AdminListCategoriesNavErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminListCategoriesNavError = AdminListCategoriesNavErrors[keyof AdminListCategoriesNavErrors];

export type AdminListCategoriesNavResponses = {
    /**
     * Categories list retrieved
     */
    200: Array<AdminCategoryNavItem>;
};

export type AdminListCategoriesNavResponse = AdminListCategoriesNavResponses[keyof AdminListCategoriesNavResponses];

export type AdminGetCategoryUsersData = {
    body?: never;
    path: {
        category_id: string;
    };
    query?: never;
    url: '/api/v4/admin/items/category/{category_id}/users';
};

export type AdminGetCategoryUsersErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetCategoryUsersError = AdminGetCategoryUsersErrors[keyof AdminGetCategoryUsersErrors];

export type AdminGetCategoryUsersResponses = {
    /**
     * Category users retrieved
     */
    200: Array<AdminCategoryUserItem>;
};

export type AdminGetCategoryUsersResponse = AdminGetCategoryUsersResponses[keyof AdminGetCategoryUsersResponses];

export type AdminGetCategoryByNameData = {
    body?: never;
    path: {
        category_name: string;
    };
    query?: never;
    url: '/api/v4/admin/item/category/get/{category_name}';
};

export type AdminGetCategoryByNameErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetCategoryByNameError = AdminGetCategoryByNameErrors[keyof AdminGetCategoryByNameErrors];

export type AdminGetCategoryByNameResponses = {
    /**
     * Category ID retrieved
     */
    200: string;
};

export type AdminGetCategoryByNameResponse = AdminGetCategoryByNameResponses[keyof AdminGetCategoryByNameResponses];

export type AdminGetGroupByNameCategoryData = {
    body?: never;
    path: {
        category_name: string;
        group_name: string;
    };
    query?: never;
    url: '/api/v4/admin/item/group/get/{category_name}/{group_name}';
};

export type AdminGetGroupByNameCategoryErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetGroupByNameCategoryError = AdminGetGroupByNameCategoryErrors[keyof AdminGetGroupByNameCategoryErrors];

export type AdminGetGroupByNameCategoryResponses = {
    /**
     * Group ID retrieved
     */
    200: string;
};

export type AdminGetGroupByNameCategoryResponse = AdminGetGroupByNameCategoryResponses[keyof AdminGetGroupByNameCategoryResponses];

export type AdminUpdateGroupQuotaData = {
    body: AdminQuotaUpdateData;
    path: {
        group_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/quota/group/{group_id}';
};

export type AdminUpdateGroupQuotaErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUpdateGroupQuotaError = AdminUpdateGroupQuotaErrors[keyof AdminUpdateGroupQuotaErrors];

export type AdminUpdateGroupQuotaResponses = {
    /**
     * Group quota updated
     */
    204: void;
};

export type AdminUpdateGroupQuotaResponse = AdminUpdateGroupQuotaResponses[keyof AdminUpdateGroupQuotaResponses];

export type AdminUpdateGroupLimitsData = {
    body: AdminLimitsUpdateData;
    path: {
        group_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/limits/group/{group_id}';
};

export type AdminUpdateGroupLimitsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUpdateGroupLimitsError = AdminUpdateGroupLimitsErrors[keyof AdminUpdateGroupLimitsErrors];

export type AdminUpdateGroupLimitsResponses = {
    /**
     * Group limits updated
     */
    204: void;
};

export type AdminUpdateGroupLimitsResponse = AdminUpdateGroupLimitsResponses[keyof AdminUpdateGroupLimitsResponses];

export type AdminUserDeleteCheckData = {
    body: AdminDeleteChecksData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/user/delete/check';
};

export type AdminUserDeleteCheckErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUserDeleteCheckError = AdminUserDeleteCheckErrors[keyof AdminUserDeleteCheckErrors];

export type AdminUserDeleteCheckResponses = {
    /**
     * Deletion check results
     */
    200: AdminDeleteChecksResponse;
};

export type AdminUserDeleteCheckResponse = AdminUserDeleteCheckResponses[keyof AdminUserDeleteCheckResponses];

export type AdminGroupDeleteCheckData = {
    body: AdminDeleteChecksData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/group/delete/check';
};

export type AdminGroupDeleteCheckErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGroupDeleteCheckError = AdminGroupDeleteCheckErrors[keyof AdminGroupDeleteCheckErrors];

export type AdminGroupDeleteCheckResponses = {
    /**
     * Deletion check results
     */
    200: AdminDeleteChecksResponse;
};

export type AdminGroupDeleteCheckResponse = AdminGroupDeleteCheckResponses[keyof AdminGroupDeleteCheckResponses];

export type AdminCategoryDeleteCheckData = {
    body: AdminDeleteChecksData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/category/delete/check';
};

export type AdminCategoryDeleteCheckErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminCategoryDeleteCheckError = AdminCategoryDeleteCheckErrors[keyof AdminCategoryDeleteCheckErrors];

export type AdminCategoryDeleteCheckResponses = {
    /**
     * Deletion check results
     */
    200: AdminDeleteChecksResponse;
};

export type AdminCategoryDeleteCheckResponse = AdminCategoryDeleteCheckResponses[keyof AdminCategoryDeleteCheckResponses];

export type AdminCheckGroupCategoryData2 = {
    body: AdminCheckGroupCategoryData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/check/group/category';
};

export type AdminCheckGroupCategoryErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminCheckGroupCategoryError = AdminCheckGroupCategoryErrors[keyof AdminCheckGroupCategoryErrors];

export type AdminCheckGroupCategoryResponses = {
    /**
     * Check results
     */
    200: Array<{
        [key: string]: unknown;
    }>;
};

export type AdminCheckGroupCategoryResponse = AdminCheckGroupCategoryResponses[keyof AdminCheckGroupCategoryResponses];

export type AdminGetTemplatesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/templates';
};

export type AdminGetTemplatesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetTemplatesError = AdminGetTemplatesErrors[keyof AdminGetTemplatesErrors];

export type AdminGetTemplatesResponses = {
    /**
     * Templates retrieved
     */
    200: Array<AdminTemplateItem>;
};

export type AdminGetTemplatesResponse = AdminGetTemplatesResponses[keyof AdminGetTemplatesResponses];

export type AdminGetUserTemplatesData = {
    body?: never;
    path: {
        user_id: string;
    };
    query?: never;
    url: '/api/v4/admin/items/user/{user_id}/templates';
};

export type AdminGetUserTemplatesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetUserTemplatesError = AdminGetUserTemplatesErrors[keyof AdminGetUserTemplatesErrors];

export type AdminGetUserTemplatesResponses = {
    /**
     * User templates retrieved
     */
    200: Array<AdminUserTemplateItem>;
};

export type AdminGetUserTemplatesResponse = AdminGetUserTemplatesResponses[keyof AdminGetUserTemplatesResponses];

export type AdminGetUserDesktopsData = {
    body?: never;
    path: {
        user_id: string;
    };
    query?: never;
    url: '/api/v4/admin/items/user/{user_id}/desktops';
};

export type AdminGetUserDesktopsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetUserDesktopsError = AdminGetUserDesktopsErrors[keyof AdminGetUserDesktopsErrors];

export type AdminGetUserDesktopsResponses = {
    /**
     * User desktops retrieved
     */
    200: Array<AdminUserDesktopItem>;
};

export type AdminGetUserDesktopsResponse = AdminGetUserDesktopsResponses[keyof AdminGetUserDesktopsResponses];

export type AdminGetRolesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/roles';
};

export type AdminGetRolesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetRolesError = AdminGetRolesErrors[keyof AdminGetRolesErrors];

export type AdminGetRolesResponses = {
    /**
     * Roles retrieved
     */
    200: Array<AdminRoleItem>;
};

export type AdminGetRolesResponse = AdminGetRolesResponses[keyof AdminGetRolesResponses];

export type AdminGetUserVpnWithOsData = {
    body?: never;
    path: {
        user_id: string;
        kind: 'config' | 'install';
        os: string;
    };
    query?: never;
    url: '/api/v4/admin/item/user/{user_id}/vpn/{kind}/{os}';
};

export type AdminGetUserVpnWithOsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetUserVpnWithOsError = AdminGetUserVpnWithOsErrors[keyof AdminGetUserVpnWithOsErrors];

export type AdminGetUserVpnWithOsResponses = {
    /**
     * VPN data retrieved
     */
    200: AdminUserVpnFileResponse;
};

export type AdminGetUserVpnWithOsResponse = AdminGetUserVpnWithOsResponses[keyof AdminGetUserVpnWithOsResponses];

export type AdminGetUserVpnData = {
    body?: never;
    path: {
        user_id: string;
        kind: 'config' | 'install';
    };
    query?: never;
    url: '/api/v4/admin/item/user/{user_id}/vpn/{kind}';
};

export type AdminGetUserVpnErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetUserVpnError = AdminGetUserVpnErrors[keyof AdminGetUserVpnErrors];

export type AdminGetUserVpnResponses = {
    /**
     * VPN data retrieved
     */
    200: AdminUserVpnFileResponse;
};

export type AdminGetUserVpnResponse = AdminGetUserVpnResponses[keyof AdminGetUserVpnResponses];

export type AdminGetUserSchemaData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/userschema';
};

export type AdminGetUserSchemaErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetUserSchemaError = AdminGetUserSchemaErrors[keyof AdminGetUserSchemaErrors];

export type AdminGetUserSchemaResponses = {
    /**
     * User schema retrieved
     */
    200: AdminUserSchemaResponse;
};

export type AdminGetUserSchemaResponse = AdminGetUserSchemaResponses[keyof AdminGetUserSchemaResponses];

export type AdminGetQuotasData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/quotas';
};

export type AdminGetQuotasErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetQuotasError = AdminGetQuotasErrors[keyof AdminGetQuotasErrors];

export type AdminGetQuotasResponses = {
    /**
     * Quotas retrieved
     */
    200: AdminQuotasResponse;
};

export type AdminGetQuotasResponse = AdminGetQuotasResponses[keyof AdminGetQuotasResponses];

export type AdminGetUserAppliedQuotaData = {
    body?: never;
    path: {
        user_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/user/appliedquota/{user_id}';
};

export type AdminGetUserAppliedQuotaErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetUserAppliedQuotaError = AdminGetUserAppliedQuotaErrors[keyof AdminGetUserAppliedQuotaErrors];

export type AdminGetUserAppliedQuotaResponses = {
    /**
     * Applied quota retrieved
     */
    200: AdminAppliedQuotaResponse;
};

export type AdminGetUserAppliedQuotaResponse = AdminGetUserAppliedQuotaResponses[keyof AdminGetUserAppliedQuotaResponses];

export type AdminMigrateUserData = {
    body?: never;
    path: {
        user_id: string;
        target_user_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/user/migrate/{user_id}/{target_user_id}';
};

export type AdminMigrateUserErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Migration validation errors
     */
    428: unknown;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminMigrateUserError = AdminMigrateUserErrors[keyof AdminMigrateUserErrors];

export type AdminMigrateUserResponses = {
    /**
     * Migration started
     */
    200: AdminMigrationStartedResponse;
};

export type AdminMigrateUserResponse = AdminMigrateUserResponses[keyof AdminMigrateUserResponses];

export type AdminCheckMigrationData = {
    body?: never;
    path: {
        user_id: string;
        target_user_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/user/migrate/check/{user_id}/{target_user_id}';
};

export type AdminCheckMigrationErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminCheckMigrationError = AdminCheckMigrationErrors[keyof AdminCheckMigrationErrors];

export type AdminCheckMigrationResponses = {
    /**
     * Migration check results
     */
    200: AdminMigrationErrorsResponse;
};

export type AdminCheckMigrationResponse = AdminCheckMigrationResponses[keyof AdminCheckMigrationResponses];

export type AdminMigrateUserDesktopsData = {
    body?: never;
    path: {
        user_id: string;
        target_user_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/user/migrate/resource/desktop/{user_id}/{target_user_id}';
};

export type AdminMigrateUserDesktopsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminMigrateUserDesktopsError = AdminMigrateUserDesktopsErrors[keyof AdminMigrateUserDesktopsErrors];

export type AdminMigrateUserDesktopsResponses = {
    /**
     * Desktops migrated
     */
    204: void;
};

export type AdminMigrateUserDesktopsResponse = AdminMigrateUserDesktopsResponses[keyof AdminMigrateUserDesktopsResponses];

export type AdminMigrateUserTemplatesData = {
    body?: never;
    path: {
        user_id: string;
        target_user_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/user/migrate/resource/template/{user_id}/{target_user_id}';
};

export type AdminMigrateUserTemplatesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminMigrateUserTemplatesError = AdminMigrateUserTemplatesErrors[keyof AdminMigrateUserTemplatesErrors];

export type AdminMigrateUserTemplatesResponses = {
    /**
     * Templates migrated
     */
    204: void;
};

export type AdminMigrateUserTemplatesResponse = AdminMigrateUserTemplatesResponses[keyof AdminMigrateUserTemplatesResponses];

export type AdminMigrateUserMediaData = {
    body?: never;
    path: {
        user_id: string;
        target_user_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/user/migrate/resource/media/{user_id}/{target_user_id}';
};

export type AdminMigrateUserMediaErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminMigrateUserMediaError = AdminMigrateUserMediaErrors[keyof AdminMigrateUserMediaErrors];

export type AdminMigrateUserMediaResponses = {
    /**
     * Media migrated
     */
    204: void;
};

export type AdminMigrateUserMediaResponse = AdminMigrateUserMediaResponses[keyof AdminMigrateUserMediaResponses];

export type AdminMigrateUserDeploymentsData = {
    body?: never;
    path: {
        user_id: string;
        target_user_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/user/migrate/resource/deployments/{user_id}/{target_user_id}';
};

export type AdminMigrateUserDeploymentsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminMigrateUserDeploymentsError = AdminMigrateUserDeploymentsErrors[keyof AdminMigrateUserDeploymentsErrors];

export type AdminMigrateUserDeploymentsResponses = {
    /**
     * Deployments migrated
     */
    204: void;
};

export type AdminMigrateUserDeploymentsResponse = AdminMigrateUserDeploymentsResponses[keyof AdminMigrateUserDeploymentsResponses];

export type AdminCheckMigratedData2 = {
    body: AdminCheckMigratedData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/user/check/migrated';
};

export type AdminCheckMigratedErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminCheckMigratedError = AdminCheckMigratedErrors[keyof AdminCheckMigratedErrors];

export type AdminCheckMigratedResponses = {
    /**
     * Migration check results
     */
    200: AdminCheckMigratedResponse;
};

export type AdminCheckMigratedResponse2 = AdminCheckMigratedResponses[keyof AdminCheckMigratedResponses];

export type AdminGetBastionDomainData = {
    body?: never;
    path: {
        category_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/category/{category_id}/bastion_domain';
};

export type AdminGetBastionDomainErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetBastionDomainError = AdminGetBastionDomainErrors[keyof AdminGetBastionDomainErrors];

export type AdminGetBastionDomainResponses = {
    /**
     * Bastion domain retrieved
     */
    200: AdminBastionDomainResponse;
};

export type AdminGetBastionDomainResponse = AdminGetBastionDomainResponses[keyof AdminGetBastionDomainResponses];

export type AdminUpdateBastionDomainData = {
    body: AdminBastionDomainData;
    path: {
        category_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/category/{category_id}/bastion_domain';
};

export type AdminUpdateBastionDomainErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUpdateBastionDomainError = AdminUpdateBastionDomainErrors[keyof AdminUpdateBastionDomainErrors];

export type AdminUpdateBastionDomainResponses = {
    /**
     * Bastion domain updated
     */
    200: AdminBastionDomainResponse;
};

export type AdminUpdateBastionDomainResponse = AdminUpdateBastionDomainResponses[keyof AdminUpdateBastionDomainResponses];

export type GetItemPlanningsData = {
    body?: never;
    path: {
        reservable_item_id: string;
    };
    query?: {
        /**
         * Start date filter (ISO format, e.g. 2023-10-01T00:00:00+00:00)
         */
        start?: string | null;
        /**
         * End date filter (ISO format, e.g. 2023-10-01T00:00:00+00:00)
         */
        end?: string | null;
    };
    url: '/api/v4/items/planning/{reservable_item_id}';
};

export type GetItemPlanningsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetItemPlanningsError = GetItemPlanningsErrors[keyof GetItemPlanningsErrors];

export type GetItemPlanningsResponses = {
    /**
     * Successful Response
     */
    200: PlanningListResponse;
};

export type GetItemPlanningsResponse = GetItemPlanningsResponses[keyof GetItemPlanningsResponses];

export type DeletePlanningData = {
    body?: never;
    path: {
        /**
         * The planning ID to delete
         */
        plan_id: string;
    };
    query?: never;
    url: '/api/v4/item/planning/{plan_id}';
};

export type DeletePlanningErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type DeletePlanningError = DeletePlanningErrors[keyof DeletePlanningErrors];

export type DeletePlanningResponses = {
    /**
     * Successful Response
     */
    200: DeleteResponse;
};

export type DeletePlanningResponse = DeletePlanningResponses[keyof DeletePlanningResponses];

export type CreatePlanningData = {
    body: CreatePlanningRequest;
    path?: never;
    query?: never;
    url: '/api/v4/item/planning';
};

export type CreatePlanningErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type CreatePlanningError = CreatePlanningErrors[keyof CreatePlanningErrors];

export type CreatePlanningResponses = {
    /**
     * Successful Response
     */
    201: SimpleResponse;
};

export type CreatePlanningResponse = CreatePlanningResponses[keyof CreatePlanningResponses];

export type GetReservablesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/reservables';
};

export type GetReservablesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetReservablesError = GetReservablesErrors[keyof GetReservablesErrors];

export type GetReservablesResponses = {
    /**
     * Successful Response
     */
    200: ReservablesListResponse;
};

export type GetReservablesResponse = GetReservablesResponses[keyof GetReservablesResponses];

export type GetReservableItemsData = {
    body?: never;
    path: {
        /**
         * The reservable type (e.g., 'gpus', 'usbs')
         */
        reservable_type: string;
    };
    query?: never;
    url: '/api/v4/items/reservables/{reservable_type}';
};

export type GetReservableItemsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetReservableItemsError = GetReservableItemsErrors[keyof GetReservableItemsErrors];

export type GetReservableItemsResponses = {
    /**
     * Successful Response
     */
    200: ReservableDetailResponse;
};

export type GetReservableItemsResponse = GetReservableItemsResponses[keyof GetReservableItemsResponses];

export type ListEnabledSubitemsData = {
    body?: never;
    path: {
        /**
         * The reservable type
         */
        reservable_type: 'gpus' | 'usbs';
        /**
         * The item ID
         */
        item_id: string;
    };
    query?: never;
    url: '/api/v4/item/reservable/enabled/{reservable_type}/{item_id}';
};

export type ListEnabledSubitemsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type ListEnabledSubitemsError = ListEnabledSubitemsErrors[keyof ListEnabledSubitemsErrors];

export type ListEnabledSubitemsResponses = {
    /**
     * Successful Response
     */
    200: Array<{
        [key: string]: unknown;
    }>;
};

export type ListEnabledSubitemsResponse = ListEnabledSubitemsResponses[keyof ListEnabledSubitemsResponses];

export type ListAllPlansData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/reservables-planner';
};

export type ListAllPlansErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type ListAllPlansError = ListAllPlansErrors[keyof ListAllPlansErrors];

export type ListAllPlansResponses = {
    /**
     * Successful Response
     */
    200: Array<{
        [key: string]: unknown;
    }>;
};

export type ListAllPlansResponse = ListAllPlansResponses[keyof ListAllPlansResponses];

export type GetItemPlansData = {
    body?: never;
    path: {
        /**
         * The item ID
         */
        item_id: string;
    };
    query?: {
        /**
         * Lower bound of the time window (ISO 8601, UTC)
         */
        start?: string | null;
        /**
         * Upper bound of the time window (ISO 8601, UTC)
         */
        end?: string | null;
    };
    url: '/api/v4/item/reservables-planner/by-item/{item_id}';
};

export type GetItemPlansErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetItemPlansError = GetItemPlansErrors[keyof GetItemPlansErrors];

export type GetItemPlansResponses = {
    /**
     * Successful Response
     */
    200: Array<{
        [key: string]: unknown;
    }>;
};

export type GetItemPlansResponse = GetItemPlansResponses[keyof GetItemPlansResponses];

export type CreatePlanData = {
    body: CreatePlanRequest;
    path?: never;
    query?: never;
    url: '/api/v4/item/reservables-planner';
};

export type CreatePlanErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type CreatePlanError = CreatePlanErrors[keyof CreatePlanErrors];

export type CreatePlanResponses = {
    /**
     * Successful Response
     */
    200: {
        [key: string]: unknown;
    };
};

export type CreatePlanResponse = CreatePlanResponses[keyof CreatePlanResponses];

export type DeletePlanData = {
    body?: never;
    path: {
        /**
         * The plan ID
         */
        plan_id: string;
    };
    query?: never;
    url: '/api/v4/item/reservables-planner/{plan_id}';
};

export type DeletePlanErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type DeletePlanError = DeletePlanErrors[keyof DeletePlanErrors];

export type DeletePlanResponses = {
    /**
     * Successful Response
     */
    200: EmptyResponse;
};

export type DeletePlanResponse = DeletePlanResponses[keyof DeletePlanResponses];

export type UpdatePlanData = {
    body?: never;
    path: {
        /**
         * The plan ID
         */
        plan_id: string;
        /**
         * New start datetime (ISO 8601)
         */
        start: string;
        /**
         * New end datetime (ISO 8601)
         */
        end: string;
    };
    query?: never;
    url: '/api/v4/item/reservables-planner/{plan_id}/{start}/{end}';
};

export type UpdatePlanErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type UpdatePlanError = UpdatePlanErrors[keyof UpdatePlanErrors];

export type UpdatePlanResponses = {
    /**
     * Successful Response
     */
    200: EmptyResponse;
};

export type UpdatePlanResponse = UpdatePlanResponses[keyof UpdatePlanResponses];

export type ChangeDesktopOwnerData = {
    body?: never;
    path: {
        /**
         * The ID of the desktop
         */
        desktop_id: string;
        /**
         * The ID of the new owner
         */
        user_id: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/{desktop_id}/change-owner/{user_id}';
};

export type ChangeDesktopOwnerErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type ChangeDesktopOwnerError = ChangeDesktopOwnerErrors[keyof ChangeDesktopOwnerErrors];

export type ChangeDesktopOwnerResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type ChangeDesktopOwnerResponse = ChangeDesktopOwnerResponses[keyof ChangeDesktopOwnerResponses];

export type ChangeTemplateOwnerData = {
    body?: never;
    path: {
        /**
         * The ID of the template
         */
        template_id: string;
        /**
         * The ID of the new owner
         */
        user_id: string;
    };
    query?: never;
    url: '/api/v4/item/template/{template_id}/change-owner/{user_id}';
};

export type ChangeTemplateOwnerErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type ChangeTemplateOwnerError = ChangeTemplateOwnerErrors[keyof ChangeTemplateOwnerErrors];

export type ChangeTemplateOwnerResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type ChangeTemplateOwnerResponse = ChangeTemplateOwnerResponses[keyof ChangeTemplateOwnerResponses];

export type RemoveDisallowedBastionTargetsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/bastion/disallowed';
};

export type RemoveDisallowedBastionTargetsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type RemoveDisallowedBastionTargetsError = RemoveDisallowedBastionTargetsErrors[keyof RemoveDisallowedBastionTargetsErrors];

export type RemoveDisallowedBastionTargetsResponses = {
    /**
     * Successful Response
     */
    200: {
        [key: string]: unknown;
    };
};

export type RemoveDisallowedBastionTargetsResponse = RemoveDisallowedBastionTargetsResponses[keyof RemoveDisallowedBastionTargetsResponses];

export type GenerateDefaultCardData = {
    body: GenerateCardRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/images/desktops/generate';
};

export type GenerateDefaultCardErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GenerateDefaultCardError = GenerateDefaultCardErrors[keyof GenerateDefaultCardErrors];

export type GenerateDefaultCardResponses = {
    /**
     * Successful Response
     */
    200: CardResponse;
};

export type GenerateDefaultCardResponse = GenerateDefaultCardResponses[keyof GenerateDefaultCardResponses];

export type GetMaintenanceTextData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/maintenance/text';
};

export type GetMaintenanceTextErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetMaintenanceTextError = GetMaintenanceTextErrors[keyof GetMaintenanceTextErrors];

export type GetMaintenanceTextResponses = {
    /**
     * Successful Response
     */
    200: MaintenanceTextGetResponse;
};

export type GetMaintenanceTextResponse = GetMaintenanceTextResponses[keyof GetMaintenanceTextResponses];

export type UpdateMaintenanceTextData = {
    body: MaintenanceTextUpdate;
    path?: never;
    query?: never;
    url: '/api/v4/maintenance/text';
};

export type UpdateMaintenanceTextErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
    /**
     * Service Unavailable
     */
    503: ErrorResponse;
};

export type UpdateMaintenanceTextError = UpdateMaintenanceTextErrors[keyof UpdateMaintenanceTextErrors];

export type UpdateMaintenanceTextResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type UpdateMaintenanceTextResponse = UpdateMaintenanceTextResponses[keyof UpdateMaintenanceTextResponses];

export type GetCategoryMaintenanceData = {
    body?: never;
    path: {
        category_id: string;
    };
    query?: never;
    url: '/api/v4/maintenance/{category_id}';
};

export type GetCategoryMaintenanceErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetCategoryMaintenanceError = GetCategoryMaintenanceErrors[keyof GetCategoryMaintenanceErrors];

export type GetCategoryMaintenanceResponses = {
    /**
     * Successful Response
     */
    200: MaintenanceStatusResponse;
};

export type GetCategoryMaintenanceResponse = GetCategoryMaintenanceResponses[keyof GetCategoryMaintenanceResponses];

export type GetMaintenanceData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/maintenance';
};

export type GetMaintenanceErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetMaintenanceError = GetMaintenanceErrors[keyof GetMaintenanceErrors];

export type GetMaintenanceResponses = {
    /**
     * Successful Response
     */
    200: MaintenanceStatusResponse;
};

export type GetMaintenanceResponse = GetMaintenanceResponses[keyof GetMaintenanceResponses];

export type UpdateMaintenanceData = {
    body: MaintenanceStatusUpdate;
    path?: never;
    query?: never;
    url: '/api/v4/maintenance';
};

export type UpdateMaintenanceErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type UpdateMaintenanceError = UpdateMaintenanceErrors[keyof UpdateMaintenanceErrors];

export type UpdateMaintenanceResponses = {
    /**
     * Successful Response
     */
    200: MaintenanceStatusResponse;
};

export type UpdateMaintenanceResponse = UpdateMaintenanceResponses[keyof UpdateMaintenanceResponses];

export type GetMigrationConfigData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/config/user-migration';
};

export type GetMigrationConfigErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
};

export type GetMigrationConfigError = GetMigrationConfigErrors[keyof GetMigrationConfigErrors];

export type GetMigrationConfigResponses = {
    /**
     * Successful Response
     */
    200: MigrationConfigResponse;
};

export type GetMigrationConfigResponse = GetMigrationConfigResponses[keyof GetMigrationConfigResponses];

export type UpdateMigrationConfigData = {
    body: MigrationConfigUpdateRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/config/user-migration';
};

export type UpdateMigrationConfigErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type UpdateMigrationConfigError = UpdateMigrationConfigErrors[keyof UpdateMigrationConfigErrors];

export type UpdateMigrationConfigResponses = {
    /**
     * Successful Response
     */
    200: MigrationConfigResponse;
};

export type UpdateMigrationConfigResponse = UpdateMigrationConfigResponses[keyof UpdateMigrationConfigResponses];

export type GetAllMigrationsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/user-migrations';
};

export type GetAllMigrationsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
};

export type GetAllMigrationsError = GetAllMigrationsErrors[keyof GetAllMigrationsErrors];

export type GetAllMigrationsResponses = {
    /**
     * Successful Response
     */
    200: AdminMigrationsResponse;
};

export type GetAllMigrationsResponse = GetAllMigrationsResponses[keyof GetAllMigrationsResponses];

export type RevokeMigrationData = {
    body?: never;
    path: {
        migration_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/user-migration/{migration_id}/revoke';
};

export type RevokeMigrationErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type RevokeMigrationError = RevokeMigrationErrors[keyof RevokeMigrationErrors];

export type RevokeMigrationResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type RevokeMigrationResponse = RevokeMigrationResponses[keyof RevokeMigrationResponses];

export type DeleteMigrationData = {
    body?: never;
    path: {
        migration_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/user-migration/{migration_id}';
};

export type DeleteMigrationErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteMigrationError = DeleteMigrationErrors[keyof DeleteMigrationErrors];

export type DeleteMigrationResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteMigrationResponse = DeleteMigrationResponses[keyof DeleteMigrationResponses];

export type DeleteCutoffTimeSurpassedData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/recycle-bin/cutoff-time-surpassed';
};

export type DeleteCutoffTimeSurpassedErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
};

export type DeleteCutoffTimeSurpassedError = DeleteCutoffTimeSurpassedErrors[keyof DeleteCutoffTimeSurpassedErrors];

export type DeleteCutoffTimeSurpassedResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteCutoffTimeSurpassedResponse = DeleteCutoffTimeSurpassedResponses[keyof DeleteCutoffTimeSurpassedResponses];

export type ListStuckRecycleBinEntriesData = {
    body?: never;
    path?: never;
    query?: {
        older_than_minutes?: number;
    };
    url: '/api/v4/items/recycle-bin/stuck';
};

export type ListStuckRecycleBinEntriesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type ListStuckRecycleBinEntriesError = ListStuckRecycleBinEntriesErrors[keyof ListStuckRecycleBinEntriesErrors];

export type ListStuckRecycleBinEntriesResponses = {
    /**
     * Successful Response
     */
    200: Array<StuckRecycleBinEntry>;
};

export type ListStuckRecycleBinEntriesResponse = ListStuckRecycleBinEntriesResponses[keyof ListStuckRecycleBinEntriesResponses];

export type RecoverStuckRecycleBinEntriesData = {
    body: RecoverStuckRequest;
    path?: never;
    query?: never;
    url: '/api/v4/items/recycle-bin/recover-stuck';
};

export type RecoverStuckRecycleBinEntriesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type RecoverStuckRecycleBinEntriesError = RecoverStuckRecycleBinEntriesErrors[keyof RecoverStuckRecycleBinEntriesErrors];

export type RecoverStuckRecycleBinEntriesResponses = {
    /**
     * Successful Response
     */
    200: RecoverStuckResponse;
};

export type RecoverStuckRecycleBinEntriesResponse = RecoverStuckRecycleBinEntriesResponses[keyof RecoverStuckRecycleBinEntriesResponses];

export type SetOldEntriesMaxTimeData = {
    body?: never;
    path: {
        max_time: number;
    };
    query?: never;
    url: '/api/v4/item/recycle-bin/old-entries/max-time/{max_time}';
};

export type SetOldEntriesMaxTimeErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type SetOldEntriesMaxTimeError = SetOldEntriesMaxTimeErrors[keyof SetOldEntriesMaxTimeErrors];

export type SetOldEntriesMaxTimeResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type SetOldEntriesMaxTimeResponse = SetOldEntriesMaxTimeResponses[keyof SetOldEntriesMaxTimeResponses];

export type SetOldEntriesActionData = {
    body?: never;
    path: {
        action: OldEntriesActionEnum;
    };
    query?: never;
    url: '/api/v4/item/recycle-bin/old-entries/action/{action}';
};

export type SetOldEntriesActionErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type SetOldEntriesActionError = SetOldEntriesActionErrors[keyof SetOldEntriesActionErrors];

export type SetOldEntriesActionResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type SetOldEntriesActionResponse = SetOldEntriesActionResponses[keyof SetOldEntriesActionResponses];

export type GetOldEntriesConfigData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/recycle-bin/old-entries/config';
};

export type GetOldEntriesConfigErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
};

export type GetOldEntriesConfigError = GetOldEntriesConfigErrors[keyof GetOldEntriesConfigErrors];

export type GetOldEntriesConfigResponses = {
    /**
     * Successful Response
     */
    200: RecycleBinOldEntriesConfig;
};

export type GetOldEntriesConfigResponse = GetOldEntriesConfigResponses[keyof GetOldEntriesConfigResponses];

export type DeleteOldEntriesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/recycle-bin/old-entries/delete';
};

export type DeleteOldEntriesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
};

export type DeleteOldEntriesError = DeleteOldEntriesErrors[keyof DeleteOldEntriesErrors];

export type DeleteOldEntriesResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteOldEntriesResponse = DeleteOldEntriesResponses[keyof DeleteOldEntriesResponses];

export type SetDefaultDeleteData = {
    body: RecycleBinSetDefaultDeleteRequest;
    path?: never;
    query?: never;
    url: '/api/v4/item/recycle-bin/config/default-delete';
};

export type SetDefaultDeleteErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type SetDefaultDeleteError = SetDefaultDeleteErrors[keyof SetDefaultDeleteErrors];

export type SetDefaultDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type SetDefaultDeleteResponse = SetDefaultDeleteResponses[keyof SetDefaultDeleteResponses];

export type GetDeleteActionData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/recycle-bin/config/delete-action';
};

export type GetDeleteActionErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
};

export type GetDeleteActionError = GetDeleteActionErrors[keyof GetDeleteActionErrors];

export type GetDeleteActionResponses = {
    /**
     * Successful Response
     */
    200: string;
};

export type GetDeleteActionResponse = GetDeleteActionResponses[keyof GetDeleteActionResponses];

export type SetDeleteActionData = {
    body?: never;
    path: {
        action: DeleteActionEnum;
    };
    query?: never;
    url: '/api/v4/item/recycle-bin/config/delete-action/{action}';
};

export type SetDeleteActionErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type SetDeleteActionError = SetDeleteActionErrors[keyof SetDeleteActionErrors];

export type SetDeleteActionResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type SetDeleteActionResponse = SetDeleteActionResponses[keyof SetDeleteActionResponses];

export type GetAllUnusedItemTimeoutRulesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/recycle-bin/unused-item-timeout-rules';
};

export type GetAllUnusedItemTimeoutRulesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
};

export type GetAllUnusedItemTimeoutRulesError = GetAllUnusedItemTimeoutRulesErrors[keyof GetAllUnusedItemTimeoutRulesErrors];

export type GetAllUnusedItemTimeoutRulesResponses = {
    /**
     * Successful Response
     */
    200: UnusedItemTimeoutRulesResponse;
};

export type GetAllUnusedItemTimeoutRulesResponse = GetAllUnusedItemTimeoutRulesResponses[keyof GetAllUnusedItemTimeoutRulesResponses];

export type CreateUnusedItemTimeoutRuleData = {
    body: UnusedItemTimeoutRuleCreateRequest;
    path?: never;
    query?: never;
    url: '/api/v4/items/recycle-bin/unused-item-timeout-rules';
};

export type CreateUnusedItemTimeoutRuleErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type CreateUnusedItemTimeoutRuleError = CreateUnusedItemTimeoutRuleErrors[keyof CreateUnusedItemTimeoutRuleErrors];

export type CreateUnusedItemTimeoutRuleResponses = {
    /**
     * Successful Response
     */
    201: SimpleResponse;
};

export type CreateUnusedItemTimeoutRuleResponse = CreateUnusedItemTimeoutRuleResponses[keyof CreateUnusedItemTimeoutRuleResponses];

export type DeleteUnusedItemTimeoutRuleData = {
    body?: never;
    path: {
        rule_id: string;
    };
    query?: never;
    url: '/api/v4/item/recycle-bin/unused-item-timeout-rule/{rule_id}';
};

export type DeleteUnusedItemTimeoutRuleErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DeleteUnusedItemTimeoutRuleError = DeleteUnusedItemTimeoutRuleErrors[keyof DeleteUnusedItemTimeoutRuleErrors];

export type DeleteUnusedItemTimeoutRuleResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteUnusedItemTimeoutRuleResponse = DeleteUnusedItemTimeoutRuleResponses[keyof DeleteUnusedItemTimeoutRuleResponses];

export type GetUnusedItemTimeoutRuleData = {
    body?: never;
    path: {
        rule_id: string;
    };
    query?: never;
    url: '/api/v4/item/recycle-bin/unused-item-timeout-rule/{rule_id}';
};

export type GetUnusedItemTimeoutRuleErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GetUnusedItemTimeoutRuleError = GetUnusedItemTimeoutRuleErrors[keyof GetUnusedItemTimeoutRuleErrors];

export type GetUnusedItemTimeoutRuleResponses = {
    /**
     * Successful Response
     */
    200: UnusedItemTimeoutRule;
};

export type GetUnusedItemTimeoutRuleResponse = GetUnusedItemTimeoutRuleResponses[keyof GetUnusedItemTimeoutRuleResponses];

export type UpdateUnusedItemTimeoutRuleData = {
    body: UnusedItemTimeoutRuleUpdateRequest;
    path: {
        rule_id: string;
    };
    query?: never;
    url: '/api/v4/item/recycle-bin/unused-item-timeout-rule/{rule_id}';
};

export type UpdateUnusedItemTimeoutRuleErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type UpdateUnusedItemTimeoutRuleError = UpdateUnusedItemTimeoutRuleErrors[keyof UpdateUnusedItemTimeoutRuleErrors];

export type UpdateUnusedItemTimeoutRuleResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type UpdateUnusedItemTimeoutRuleResponse = UpdateUnusedItemTimeoutRuleResponses[keyof UpdateUnusedItemTimeoutRuleResponses];

export type RecycleBinAddUnusedItemsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/recycle-bin/unused-items';
};

export type RecycleBinAddUnusedItemsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type RecycleBinAddUnusedItemsError = RecycleBinAddUnusedItemsErrors[keyof RecycleBinAddUnusedItemsErrors];

export type RecycleBinAddUnusedItemsResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type RecycleBinAddUnusedItemsResponse = RecycleBinAddUnusedItemsResponses[keyof RecycleBinAddUnusedItemsResponses];

export type SetStorageMaintenanceData = {
    body: StorageMaintenanceRequest;
    path: {
        storage_id: string;
    };
    query?: never;
    url: '/api/v4/item/storage/{storage_id}/status/maintenance';
};

export type SetStorageMaintenanceErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type SetStorageMaintenanceError = SetStorageMaintenanceErrors[keyof SetStorageMaintenanceErrors];

export type SetStorageMaintenanceResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type SetStorageMaintenanceResponse = SetStorageMaintenanceResponses[keyof SetStorageMaintenanceResponses];

export type SetStorageReadyData = {
    body?: never;
    path: {
        storage_id: string;
    };
    query?: never;
    url: '/api/v4/item/storage/{storage_id}/status/ready';
};

export type SetStorageReadyErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type SetStorageReadyError = SetStorageReadyErrors[keyof SetStorageReadyErrors];

export type SetStorageReadyResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type SetStorageReadyResponse = SetStorageReadyResponses[keyof SetStorageReadyResponses];

export type BatchCheckBackingChainData = {
    body: StorageBatchIdsRequest;
    path?: never;
    query?: never;
    url: '/api/v4/items/storage/status';
};

export type BatchCheckBackingChainErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type BatchCheckBackingChainError = BatchCheckBackingChainErrors[keyof BatchCheckBackingChainErrors];

export type BatchCheckBackingChainResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type BatchCheckBackingChainResponse = BatchCheckBackingChainResponses[keyof BatchCheckBackingChainResponses];

export type BatchCheckBackingChainByStatusData = {
    body?: never;
    path: {
        status: string;
    };
    query?: never;
    url: '/api/v4/items/storage/status/{status}';
};

export type BatchCheckBackingChainByStatusErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type BatchCheckBackingChainByStatusError = BatchCheckBackingChainByStatusErrors[keyof BatchCheckBackingChainByStatusErrors];

export type BatchCheckBackingChainByStatusResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type BatchCheckBackingChainByStatusResponse = BatchCheckBackingChainByStatusResponses[keyof BatchCheckBackingChainByStatusResponses];

export type CreateStorageData = {
    body: StorageCreateRequest;
    path: {
        priority: string;
    };
    query?: never;
    url: '/api/v4/item/storage/priority/{priority}';
};

export type CreateStorageErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type CreateStorageError = CreateStorageErrors[keyof CreateStorageErrors];

export type CreateStorageResponses = {
    /**
     * Successful Response
     */
    200: StorageCreateResponse;
};

export type CreateStorageResponse = CreateStorageResponses[keyof CreateStorageResponses];

export type GetStorageStatusesData = {
    body?: never;
    path: {
        storage_id: string;
    };
    query?: never;
    url: '/api/v4/item/storage/{storage_id}/statuses';
};

export type GetStorageStatusesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetStorageStatusesError = GetStorageStatusesErrors[keyof GetStorageStatusesErrors];

export type GetStorageStatusesResponses = {
    /**
     * Successful Response
     */
    200: StorageStatusesResponse;
};

export type GetStorageStatusesResponse = GetStorageStatusesResponses[keyof GetStorageStatusesResponses];

export type SparsifyStorageData = {
    body?: never;
    path: {
        storage_id: string;
        priority: string;
    };
    query?: never;
    url: '/api/v4/item/storage/{storage_id}/sparsify/priority/{priority}';
};

export type SparsifyStorageErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type SparsifyStorageError = SparsifyStorageErrors[keyof SparsifyStorageErrors];

export type SparsifyStorageResponses = {
    /**
     * Successful Response
     */
    200: TaskIdResponse;
};

export type SparsifyStorageResponse = SparsifyStorageResponses[keyof SparsifyStorageResponses];

export type BatchSparsifyStoragesData = {
    body: StorageBatchIdsRequest;
    path?: never;
    query?: never;
    url: '/api/v4/items/storage/sparsify';
};

export type BatchSparsifyStoragesErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type BatchSparsifyStoragesError = BatchSparsifyStoragesErrors[keyof BatchSparsifyStoragesErrors];

export type BatchSparsifyStoragesResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type BatchSparsifyStoragesResponse = BatchSparsifyStoragesResponses[keyof BatchSparsifyStoragesResponses];

export type BatchSparsifyStoragesByStatusData = {
    body?: never;
    path: {
        status: string;
    };
    query?: never;
    url: '/api/v4/items/storage/sparsify/{status}';
};

export type BatchSparsifyStoragesByStatusErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type BatchSparsifyStoragesByStatusError = BatchSparsifyStoragesByStatusErrors[keyof BatchSparsifyStoragesByStatusErrors];

export type BatchSparsifyStoragesByStatusResponses = {
    /**
     * Storages queued for sparsify
     */
    204: void;
};

export type BatchSparsifyStoragesByStatusResponse = BatchSparsifyStoragesByStatusResponses[keyof BatchSparsifyStoragesByStatusResponses];

export type RepairStorageData = {
    body?: never;
    path: {
        storage_id: string;
        what: string;
    };
    query?: never;
    url: '/api/v4/item/storage/{storage_id}/repair/{what}';
};

export type RepairStorageErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type RepairStorageError = RepairStorageErrors[keyof RepairStorageErrors];

export type RepairStorageResponses = {
    /**
     * Successful Response
     */
    200: TaskIdResponse;
};

export type RepairStorageResponse = RepairStorageResponses[keyof RepairStorageResponses];

export type BatchRepairPendingLeaksData = {
    body?: never;
    path?: never;
    query?: {
        limit?: number | null;
    };
    url: '/api/v4/items/storage/repair/leaks';
};

export type BatchRepairPendingLeaksErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type BatchRepairPendingLeaksError = BatchRepairPendingLeaksErrors[keyof BatchRepairPendingLeaksErrors];

export type BatchRepairPendingLeaksResponses = {
    /**
     * Successful Response
     */
    200: StorageRepairBatchResponse;
};

export type BatchRepairPendingLeaksResponse = BatchRepairPendingLeaksResponses[keyof BatchRepairPendingLeaksResponses];

export type CheckStorageData = {
    body?: never;
    path: {
        storage_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/storage/{storage_id}/check';
};

export type CheckStorageErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type CheckStorageError = CheckStorageErrors[keyof CheckStorageErrors];

export type CheckStorageResponses = {
    /**
     * Successful Response
     */
    200: TaskIdResponse;
};

export type CheckStorageResponse = CheckStorageResponses[keyof CheckStorageResponses];

export type BatchCheckStorageByStatusData = {
    body?: never;
    path: {
        status: string;
    };
    query?: never;
    url: '/api/v4/admin/items/storage/check/{status}';
};

export type BatchCheckStorageByStatusErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type BatchCheckStorageByStatusError = BatchCheckStorageByStatusErrors[keyof BatchCheckStorageByStatusErrors];

export type BatchCheckStorageByStatusResponses = {
    /**
     * Successful Response
     */
    200: StorageRepairBatchResponse;
};

export type BatchCheckStorageByStatusResponse = BatchCheckStorageByStatusResponses[keyof BatchCheckStorageByStatusResponses];

export type GetStorageSweepConfigData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/storage/sweep/config';
};

export type GetStorageSweepConfigErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetStorageSweepConfigError = GetStorageSweepConfigErrors[keyof GetStorageSweepConfigErrors];

export type GetStorageSweepConfigResponses = {
    /**
     * Successful Response
     */
    200: StorageSweepConfigResponse;
};

export type GetStorageSweepConfigResponse = GetStorageSweepConfigResponses[keyof GetStorageSweepConfigResponses];

export type UpdateStorageSweepConfigData = {
    body: StorageSweepConfigRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/storage/sweep/config';
};

export type UpdateStorageSweepConfigErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type UpdateStorageSweepConfigError = UpdateStorageSweepConfigErrors[keyof UpdateStorageSweepConfigErrors];

export type UpdateStorageSweepConfigResponses = {
    /**
     * Successful Response
     */
    200: StorageSweepConfigResponse;
};

export type UpdateStorageSweepConfigResponse = UpdateStorageSweepConfigResponses[keyof UpdateStorageSweepConfigResponses];

export type RunStorageSweepData = {
    body: StorageSweepRunRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/storage/sweep/run';
};

export type RunStorageSweepErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type RunStorageSweepError = RunStorageSweepErrors[keyof RunStorageSweepErrors];

export type RunStorageSweepResponses = {
    /**
     * Successful Response
     */
    200: StorageSweepRunResponse;
};

export type RunStorageSweepResponse = RunStorageSweepResponses[keyof RunStorageSweepResponses];

export type DisconnectStorageData = {
    body?: never;
    path: {
        storage_id: string;
        priority: string;
    };
    query?: never;
    url: '/api/v4/item/storage/{storage_id}/disconnect/priority/{priority}';
};

export type DisconnectStorageErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type DisconnectStorageError = DisconnectStorageErrors[keyof DisconnectStorageErrors];

export type DisconnectStorageResponses = {
    /**
     * Successful Response
     */
    200: TaskIdResponse;
};

export type DisconnectStorageResponse = DisconnectStorageResponses[keyof DisconnectStorageResponses];

export type ConvertStorageData = {
    body: StorageConvertRequest;
    path: {
        storage_id: string;
    };
    query?: never;
    url: '/api/v4/item/storage/{storage_id}/convert';
};

export type ConvertStorageErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type ConvertStorageError = ConvertStorageErrors[keyof ConvertStorageErrors];

export type ConvertStorageResponses = {
    /**
     * Successful Response
     */
    200: StorageConvertResponse;
};

export type ConvertStorageResponse = ConvertStorageResponses[keyof ConvertStorageResponses];

export type VirtWinRegStorageData = {
    body: StorageVirtWinRegRequest;
    path: {
        storage_id: string;
        priority: string;
    };
    query?: never;
    url: '/api/v4/item/storage/{storage_id}/virt-win-reg/priority/{priority}';
};

export type VirtWinRegStorageErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type VirtWinRegStorageError = VirtWinRegStorageErrors[keyof VirtWinRegStorageErrors];

export type VirtWinRegStorageResponses = {
    /**
     * Successful Response
     */
    200: TaskIdResponse;
};

export type VirtWinRegStorageResponse = VirtWinRegStorageResponses[keyof VirtWinRegStorageResponses];

export type MoveStorageByPathData = {
    body: StorageMoveByPathRequest;
    path: {
        storage_id: string;
    };
    query?: never;
    url: '/api/v4/item/storage/{storage_id}/move/by-path';
};

export type MoveStorageByPathErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type MoveStorageByPathError = MoveStorageByPathErrors[keyof MoveStorageByPathErrors];

export type MoveStorageByPathResponses = {
    /**
     * Successful Response
     */
    200: TaskIdResponse;
};

export type MoveStorageByPathResponse = MoveStorageByPathResponses[keyof MoveStorageByPathResponses];

export type RsyncStorageToPathData = {
    body: StorageRsyncToPathRequest;
    path: {
        storage_id: string;
    };
    query?: never;
    url: '/api/v4/item/storage/{storage_id}/rsync/to-path';
};

export type RsyncStorageToPathErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type RsyncStorageToPathError = RsyncStorageToPathErrors[keyof RsyncStorageToPathErrors];

export type RsyncStorageToPathResponses = {
    /**
     * Successful Response
     */
    200: TaskIdResponse;
};

export type RsyncStorageToPathResponse = RsyncStorageToPathResponses[keyof RsyncStorageToPathResponses];

export type RsyncStorageToStoragePoolData = {
    body: StorageRsyncToStoragePoolRequest;
    path: {
        storage_id: string;
    };
    query?: never;
    url: '/api/v4/item/storage/{storage_id}/rsync/to-storage-pool';
};

export type RsyncStorageToStoragePoolErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type RsyncStorageToStoragePoolError = RsyncStorageToStoragePoolErrors[keyof RsyncStorageToStoragePoolErrors];

export type RsyncStorageToStoragePoolResponses = {
    /**
     * Successful Response
     */
    200: TaskIdResponse;
};

export type RsyncStorageToStoragePoolResponse = RsyncStorageToStoragePoolResponses[keyof RsyncStorageToStoragePoolResponses];

export type DeleteStoragePathData = {
    body: StoragePathRequest;
    path: {
        storage_id: string;
    };
    query?: never;
    url: '/api/v4/item/storage/{storage_id}/path';
};

export type DeleteStoragePathErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type DeleteStoragePathError = DeleteStoragePathErrors[keyof DeleteStoragePathErrors];

export type DeleteStoragePathResponses = {
    /**
     * Successful Response
     */
    200: TaskIdResponse;
};

export type DeleteStoragePathResponse = DeleteStoragePathResponses[keyof DeleteStoragePathResponses];

export type SetStoragePathData = {
    body: StoragePathRequest;
    path: {
        storage_id: string;
    };
    query?: never;
    url: '/api/v4/item/storage/{storage_id}/path';
};

export type SetStoragePathErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type SetStoragePathError = SetStoragePathErrors[keyof SetStoragePathErrors];

export type SetStoragePathResponses = {
    /**
     * Successful Response
     */
    200: TaskIdResponse;
};

export type SetStoragePathResponse = SetStoragePathResponses[keyof SetStoragePathResponses];

export type FindStorageData = {
    body?: never;
    path: {
        storage_id: string;
    };
    query?: never;
    url: '/api/v4/item/storage/{storage_id}/find';
};

export type FindStorageErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type FindStorageError = FindStorageErrors[keyof FindStorageErrors];

export type FindStorageResponses = {
    /**
     * Successful Response
     */
    200: TaskIdResponse;
};

export type FindStorageResponse = FindStorageResponses[keyof FindStorageResponses];

export type BatchFindStoragesData = {
    body: StorageBatchIdsRequest;
    path?: never;
    query?: never;
    url: '/api/v4/items/storage/find';
};

export type BatchFindStoragesErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type BatchFindStoragesError = BatchFindStoragesErrors[keyof BatchFindStoragesErrors];

export type BatchFindStoragesResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type BatchFindStoragesResponse = BatchFindStoragesResponses[keyof BatchFindStoragesResponses];

export type BatchFindStoragesByStatusData = {
    body?: never;
    path: {
        status: string;
    };
    query?: never;
    url: '/api/v4/items/storage/find/{status}';
};

export type BatchFindStoragesByStatusErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type BatchFindStoragesByStatusError = BatchFindStoragesByStatusErrors[keyof BatchFindStoragesByStatusErrors];

export type BatchFindStoragesByStatusResponses = {
    /**
     * Storages queued to be found on disk
     */
    204: void;
};

export type BatchFindStoragesByStatusResponse = BatchFindStoragesByStatusResponses[keyof BatchFindStoragesByStatusResponses];

export type CreateStoragePoolData = {
    body: StoragePoolCreateRequest;
    path?: never;
    query?: never;
    url: '/api/v4/storage-pool';
};

export type CreateStoragePoolErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type CreateStoragePoolError = CreateStoragePoolErrors[keyof CreateStoragePoolErrors];

export type CreateStoragePoolResponses = {
    /**
     * Successful Response
     */
    201: StoragePoolMutationResponse;
};

export type CreateStoragePoolResponse = CreateStoragePoolResponses[keyof CreateStoragePoolResponses];

export type ListStoragePoolsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/storage-pools';
};

export type ListStoragePoolsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type ListStoragePoolsError = ListStoragePoolsErrors[keyof ListStoragePoolsErrors];

export type ListStoragePoolsResponses = {
    /**
     * Successful Response
     */
    200: StoragePoolListResponse;
};

export type ListStoragePoolsResponse = ListStoragePoolsResponses[keyof ListStoragePoolsResponses];

export type DeleteStoragePoolData = {
    body?: never;
    path: {
        storage_pool_id: string;
    };
    query?: never;
    url: '/api/v4/storage-pool/{storage_pool_id}';
};

export type DeleteStoragePoolErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type DeleteStoragePoolError = DeleteStoragePoolErrors[keyof DeleteStoragePoolErrors];

export type DeleteStoragePoolResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type DeleteStoragePoolResponse = DeleteStoragePoolResponses[keyof DeleteStoragePoolResponses];

export type GetStoragePoolData = {
    body?: never;
    path: {
        storage_pool_id: string;
    };
    query?: never;
    url: '/api/v4/storage-pool/{storage_pool_id}';
};

export type GetStoragePoolErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetStoragePoolError = GetStoragePoolErrors[keyof GetStoragePoolErrors];

export type GetStoragePoolResponses = {
    /**
     * Successful Response
     */
    200: StoragePoolResponse;
};

export type GetStoragePoolResponse = GetStoragePoolResponses[keyof GetStoragePoolResponses];

export type UpdateStoragePoolData = {
    body: StoragePoolUpdateRequest;
    path: {
        storage_pool_id: string;
    };
    query?: never;
    url: '/api/v4/storage-pool/{storage_pool_id}';
};

export type UpdateStoragePoolErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type UpdateStoragePoolError = UpdateStoragePoolErrors[keyof UpdateStoragePoolErrors];

export type UpdateStoragePoolResponses = {
    /**
     * Successful Response
     */
    200: StoragePoolMutationResponse;
};

export type UpdateStoragePoolResponse = UpdateStoragePoolResponses[keyof UpdateStoragePoolResponses];

export type GetStoragePoolPendingData = {
    body?: never;
    path: {
        storage_pool_id: string;
    };
    query?: never;
    url: '/api/v4/storage-pool/{storage_pool_id}/pending';
};

export type GetStoragePoolPendingErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetStoragePoolPendingError = GetStoragePoolPendingErrors[keyof GetStoragePoolPendingErrors];

export type GetStoragePoolPendingResponses = {
    /**
     * Successful Response
     */
    200: StoragePoolPendingResponse;
};

export type GetStoragePoolPendingResponse = GetStoragePoolPendingResponses[keyof GetStoragePoolPendingResponses];

export type CheckCategoryStoragePoolAvailabilityData = {
    body: CheckCategoryAvailabilityRequest;
    path?: never;
    query?: never;
    url: '/api/v4/storage-pool/check-category-availability';
};

export type CheckCategoryStoragePoolAvailabilityErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type CheckCategoryStoragePoolAvailabilityError = CheckCategoryStoragePoolAvailabilityErrors[keyof CheckCategoryStoragePoolAvailabilityErrors];

export type CheckCategoryStoragePoolAvailabilityResponses = {
    /**
     * Successful Response
     */
    200: CheckCategoryAvailabilityResponse;
};

export type CheckCategoryStoragePoolAvailabilityResponse = CheckCategoryStoragePoolAvailabilityResponses[keyof CheckCategoryStoragePoolAvailabilityResponses];

export type AdminCancelTaskData = {
    body?: never;
    path: {
        task_id: string;
    };
    query?: never;
    url: '/api/v4/admin/task/{task_id}';
};

export type AdminCancelTaskErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminCancelTaskError = AdminCancelTaskErrors[keyof AdminCancelTaskErrors];

export type AdminCancelTaskResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminCancelTaskResponse = AdminCancelTaskResponses[keyof AdminCancelTaskResponses];

export type RetryTaskData = {
    body?: never;
    path: {
        task_id: string;
    };
    query?: never;
    url: '/api/v4/admin/task/{task_id}/retry';
};

export type RetryTaskErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
    /**
     * Too Many Requests
     */
    429: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type RetryTaskError = RetryTaskErrors[keyof RetryTaskErrors];

export type RetryTaskResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type RetryTaskResponse = RetryTaskResponses[keyof RetryTaskResponses];

export type RetryAllFailedTasksData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/tasks/retry';
};

export type RetryAllFailedTasksErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type RetryAllFailedTasksError = RetryAllFailedTasksErrors[keyof RetryAllFailedTasksErrors];

export type RetryAllFailedTasksResponses = {
    /**
     * Successful Response
     */
    200: RetryAllTasksResponse;
};

export type RetryAllFailedTasksResponse = RetryAllFailedTasksResponses[keyof RetryAllFailedTasksResponses];

export type VpnConnectionDisconnectData = {
    body?: never;
    path: {
        kind: 'users' | 'hypers';
        client_ip: string;
    };
    query?: never;
    url: '/api/v4/admin/vpn_connection/{kind}/{client_ip}';
};

export type VpnConnectionDisconnectErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type VpnConnectionDisconnectError = VpnConnectionDisconnectErrors[keyof VpnConnectionDisconnectErrors];

export type VpnConnectionDisconnectResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type VpnConnectionDisconnectResponse = VpnConnectionDisconnectResponses[keyof VpnConnectionDisconnectResponses];

export type VpnConnectionConnectData = {
    body: VpnConnectionRequest;
    path: {
        kind: 'users' | 'hypers';
        client_ip: string;
    };
    query?: never;
    url: '/api/v4/admin/vpn_connection/{kind}/{client_ip}';
};

export type VpnConnectionConnectErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type VpnConnectionConnectError = VpnConnectionConnectErrors[keyof VpnConnectionConnectErrors];

export type VpnConnectionConnectResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type VpnConnectionConnectResponse = VpnConnectionConnectResponses[keyof VpnConnectionConnectResponses];

export type VpnConnectionRoamData = {
    body: VpnConnectionRequest;
    path: {
        kind: 'users' | 'hypers';
        client_ip: string;
    };
    query?: never;
    url: '/api/v4/admin/vpn_connection/{kind}/{client_ip}';
};

export type VpnConnectionRoamErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type VpnConnectionRoamError = VpnConnectionRoamErrors[keyof VpnConnectionRoamErrors];

export type VpnConnectionRoamResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type VpnConnectionRoamResponse = VpnConnectionRoamResponses[keyof VpnConnectionRoamResponses];

export type VpnConnectionResetData = {
    body?: never;
    path: {
        kind: 'all';
    };
    query?: never;
    url: '/api/v4/admin/vpn_connection/{kind}';
};

export type VpnConnectionResetErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type VpnConnectionResetError = VpnConnectionResetErrors[keyof VpnConnectionResetErrors];

export type VpnConnectionResetResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type VpnConnectionResetResponse = VpnConnectionResetResponses[keyof VpnConnectionResetResponses];

export type VpnConnectionsDisconnectData = {
    body: AdminVpnConnectionsDisconnectRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/vpn_connections';
};

export type VpnConnectionsDisconnectErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type VpnConnectionsDisconnectError = VpnConnectionsDisconnectErrors[keyof VpnConnectionsDisconnectErrors];

export type VpnConnectionsDisconnectResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type VpnConnectionsDisconnectResponse = VpnConnectionsDisconnectResponses[keyof VpnConnectionsDisconnectResponses];

export type AnalyticsGraphConfDeleteData = {
    body?: never;
    path: {
        conf_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/analytics/graph/{conf_id}';
};

export type AnalyticsGraphConfDeleteErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AnalyticsGraphConfDeleteError = AnalyticsGraphConfDeleteErrors[keyof AnalyticsGraphConfDeleteErrors];

export type AnalyticsGraphConfDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AnalyticsGraphConfDeleteResponse = AnalyticsGraphConfDeleteResponses[keyof AnalyticsGraphConfDeleteResponses];

export type AnalyticsGraphConfGetData = {
    body?: never;
    path: {
        conf_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/analytics/graph/{conf_id}';
};

export type AnalyticsGraphConfGetErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AnalyticsGraphConfGetError = AnalyticsGraphConfGetErrors[keyof AnalyticsGraphConfGetErrors];

export type AnalyticsGraphConfGetResponses = {
    /**
     * Successful Response
     */
    200: AnalyticsGraphConfigResponse;
};

export type AnalyticsGraphConfGetResponse = AnalyticsGraphConfGetResponses[keyof AnalyticsGraphConfGetResponses];

export type AnalyticsGraphConfUpdateData = {
    body: AnalyticsGraphUpdateRequest;
    path: {
        conf_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/analytics/graph/{conf_id}';
};

export type AnalyticsGraphConfUpdateErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AnalyticsGraphConfUpdateError = AnalyticsGraphConfUpdateErrors[keyof AnalyticsGraphConfUpdateErrors];

export type AnalyticsGraphConfUpdateResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AnalyticsGraphConfUpdateResponse = AnalyticsGraphConfUpdateResponses[keyof AnalyticsGraphConfUpdateResponses];

export type AnalyticsGraphConfAddData = {
    body: AnalyticsGraphCreateRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/analytics/graph';
};

export type AnalyticsGraphConfAddErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AnalyticsGraphConfAddError = AnalyticsGraphConfAddErrors[keyof AnalyticsGraphConfAddErrors];

export type AnalyticsGraphConfAddResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AnalyticsGraphConfAddResponse = AnalyticsGraphConfAddResponses[keyof AnalyticsGraphConfAddResponses];

export type AnalyticsDesktopsLessUsedData = {
    body: DesktopAnalyticsRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/analytics/desktops/less_used';
};

export type AnalyticsDesktopsLessUsedErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AnalyticsDesktopsLessUsedError = AnalyticsDesktopsLessUsedErrors[keyof AnalyticsDesktopsLessUsedErrors];

export type AnalyticsDesktopsLessUsedResponses = {
    /**
     * Successful Response
     */
    200: Array<DesktopAnalyticsRow>;
};

export type AnalyticsDesktopsLessUsedResponse = AnalyticsDesktopsLessUsedResponses[keyof AnalyticsDesktopsLessUsedResponses];

export type AnalyticsDesktopsRecentlyUsedData = {
    body: DesktopAnalyticsRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/analytics/desktops/recently_used';
};

export type AnalyticsDesktopsRecentlyUsedErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AnalyticsDesktopsRecentlyUsedError = AnalyticsDesktopsRecentlyUsedErrors[keyof AnalyticsDesktopsRecentlyUsedErrors];

export type AnalyticsDesktopsRecentlyUsedResponses = {
    /**
     * Successful Response
     */
    200: Array<DesktopAnalyticsRow>;
};

export type AnalyticsDesktopsRecentlyUsedResponse = AnalyticsDesktopsRecentlyUsedResponses[keyof AnalyticsDesktopsRecentlyUsedResponses];

export type AnalyticsDesktopsMostUsedData = {
    body: DesktopAnalyticsRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/analytics/desktops/most_used';
};

export type AnalyticsDesktopsMostUsedErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AnalyticsDesktopsMostUsedError = AnalyticsDesktopsMostUsedErrors[keyof AnalyticsDesktopsMostUsedErrors];

export type AnalyticsDesktopsMostUsedResponses = {
    /**
     * Successful Response
     */
    200: Array<DesktopAnalyticsRow>;
};

export type AnalyticsDesktopsMostUsedResponse = AnalyticsDesktopsMostUsedResponses[keyof AnalyticsDesktopsMostUsedResponses];

export type AdminEchartDailyItemsData = {
    body: EchartRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/echart/daily_items';
};

export type AdminEchartDailyItemsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminEchartDailyItemsError = AdminEchartDailyItemsErrors[keyof AdminEchartDailyItemsErrors];

export type AdminEchartDailyItemsResponses = {
    /**
     * Successful Response
     */
    200: EchartDailyItemsResponse;
};

export type AdminEchartDailyItemsResponse = AdminEchartDailyItemsResponses[keyof AdminEchartDailyItemsResponses];

export type AdminEchartData = {
    body: EchartRequest;
    path: {
        view: 'grouped_items' | 'grouped_unique_items' | 'nested_array_grouped_items';
    };
    query?: never;
    url: '/api/v4/admin/item/echart/{view}';
};

export type AdminEchartErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminEchartError = AdminEchartErrors[keyof AdminEchartErrors];

export type AdminEchartResponses = {
    /**
     * Successful Response
     */
    200: Array<EchartGroupedItem>;
};

export type AdminEchartResponse = AdminEchartResponses[keyof AdminEchartResponses];

export type AdminAuthenticationPolicyAddData = {
    body: PolicyCreateRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/authentication/policy';
};

export type AdminAuthenticationPolicyAddErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Conflict
     */
    409: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminAuthenticationPolicyAddError = AdminAuthenticationPolicyAddErrors[keyof AdminAuthenticationPolicyAddErrors];

export type AdminAuthenticationPolicyAddResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminAuthenticationPolicyAddResponse = AdminAuthenticationPolicyAddResponses[keyof AdminAuthenticationPolicyAddResponses];

export type AdminAuthenticationPoliciesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/authentication/policies';
};

export type AdminAuthenticationPoliciesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminAuthenticationPoliciesError = AdminAuthenticationPoliciesErrors[keyof AdminAuthenticationPoliciesErrors];

export type AdminAuthenticationPoliciesResponses = {
    /**
     * Successful Response
     */
    200: Array<PolicyResponse>;
};

export type AdminAuthenticationPoliciesResponse = AdminAuthenticationPoliciesResponses[keyof AdminAuthenticationPoliciesResponses];

export type AdminAuthenticationPolicyDeleteData = {
    body?: never;
    path: {
        policy_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/authentication/policy/{policy_id}';
};

export type AdminAuthenticationPolicyDeleteErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminAuthenticationPolicyDeleteError = AdminAuthenticationPolicyDeleteErrors[keyof AdminAuthenticationPolicyDeleteErrors];

export type AdminAuthenticationPolicyDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminAuthenticationPolicyDeleteResponse = AdminAuthenticationPolicyDeleteResponses[keyof AdminAuthenticationPolicyDeleteResponses];

export type AdminAuthenticationPolicyData = {
    body?: never;
    path: {
        policy_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/authentication/policy/{policy_id}';
};

export type AdminAuthenticationPolicyErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminAuthenticationPolicyError = AdminAuthenticationPolicyErrors[keyof AdminAuthenticationPolicyErrors];

export type AdminAuthenticationPolicyResponses = {
    /**
     * Successful Response
     */
    200: PolicyResponse;
};

export type AdminAuthenticationPolicyResponse = AdminAuthenticationPolicyResponses[keyof AdminAuthenticationPolicyResponses];

export type AdminAuthenticationPolicyEditData = {
    body: PolicyEditRequest;
    path: {
        policy_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/authentication/policy/{policy_id}';
};

export type AdminAuthenticationPolicyEditErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminAuthenticationPolicyEditError = AdminAuthenticationPolicyEditErrors[keyof AdminAuthenticationPolicyEditErrors];

export type AdminAuthenticationPolicyEditResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminAuthenticationPolicyEditResponse = AdminAuthenticationPolicyEditResponses[keyof AdminAuthenticationPolicyEditResponses];

export type AdminForceEmailData = {
    body?: never;
    path: {
        policy_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/authentication/force_validate/email/{policy_id}';
};

export type AdminForceEmailErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminForceEmailError = AdminForceEmailErrors[keyof AdminForceEmailErrors];

export type AdminForceEmailResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminForceEmailResponse = AdminForceEmailResponses[keyof AdminForceEmailResponses];

export type AdminForceDisclaimerData = {
    body?: never;
    path: {
        policy_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/authentication/force_validate/disclaimer/{policy_id}';
};

export type AdminForceDisclaimerErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminForceDisclaimerError = AdminForceDisclaimerErrors[keyof AdminForceDisclaimerErrors];

export type AdminForceDisclaimerResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminForceDisclaimerResponse = AdminForceDisclaimerResponses[keyof AdminForceDisclaimerResponses];

export type AdminForcePasswordData = {
    body?: never;
    path: {
        policy_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/authentication/force_validate/password/{policy_id}';
};

export type AdminForcePasswordErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminForcePasswordError = AdminForcePasswordErrors[keyof AdminForcePasswordErrors];

export type AdminForcePasswordResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminForcePasswordResponse = AdminForcePasswordResponses[keyof AdminForcePasswordResponses];

export type GetProviderConfigRouteData = {
    body?: never;
    path: {
        provider: 'local' | 'google' | 'saml' | 'ldap';
    };
    query?: never;
    url: '/api/v4/admin/item/authentication/provider/{provider}';
};

export type GetProviderConfigRouteErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetProviderConfigRouteError = GetProviderConfigRouteErrors[keyof GetProviderConfigRouteErrors];

export type GetProviderConfigRouteResponses = {
    /**
     * Successful Response
     */
    200: ProviderConfigResponse;
};

export type GetProviderConfigRouteResponse = GetProviderConfigRouteResponses[keyof GetProviderConfigRouteResponses];

export type EditProviderConfigRouteData = {
    body: ProviderConfigUpdateRequest;
    path: {
        provider: 'local' | 'google' | 'saml' | 'ldap';
    };
    query?: never;
    url: '/api/v4/admin/item/authentication/provider/{provider}';
};

export type EditProviderConfigRouteErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type EditProviderConfigRouteError = EditProviderConfigRouteErrors[keyof EditProviderConfigRouteErrors];

export type EditProviderConfigRouteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type EditProviderConfigRouteResponse = EditProviderConfigRouteResponses[keyof EditProviderConfigRouteResponses];

export type AdminGetMigrationExceptionsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/authentication/migrations/exceptions';
};

export type AdminGetMigrationExceptionsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetMigrationExceptionsError = AdminGetMigrationExceptionsErrors[keyof AdminGetMigrationExceptionsErrors];

export type AdminGetMigrationExceptionsResponses = {
    /**
     * Successful Response
     */
    200: Array<MigrationException>;
};

export type AdminGetMigrationExceptionsResponse = AdminGetMigrationExceptionsResponses[keyof AdminGetMigrationExceptionsResponses];

export type AdminAddMigrationExceptionData = {
    body: MigrationExceptionCreateRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/authentication/migrations/exceptions';
};

export type AdminAddMigrationExceptionErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminAddMigrationExceptionError = AdminAddMigrationExceptionErrors[keyof AdminAddMigrationExceptionErrors];

export type AdminAddMigrationExceptionResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminAddMigrationExceptionResponse = AdminAddMigrationExceptionResponses[keyof AdminAddMigrationExceptionResponses];

export type AdminDeleteMigrationExceptionData = {
    body?: never;
    path: {
        exception_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/authentication/migrations/exceptions/{exception_id}';
};

export type AdminDeleteMigrationExceptionErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDeleteMigrationExceptionError = AdminDeleteMigrationExceptionErrors[keyof AdminDeleteMigrationExceptionErrors];

export type AdminDeleteMigrationExceptionResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminDeleteMigrationExceptionResponse = AdminDeleteMigrationExceptionResponses[keyof AdminDeleteMigrationExceptionResponses];

export type AdminBackupsListData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/backups';
};

export type AdminBackupsListErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminBackupsListError = AdminBackupsListErrors[keyof AdminBackupsListErrors];

export type AdminBackupsListResponses = {
    /**
     * Successful Response
     */
    200: BackupItem | Array<BackupItem>;
};

export type AdminBackupsListResponse = AdminBackupsListResponses[keyof AdminBackupsListResponses];

export type AdminBackupIntegrityGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/backups/integrity';
};

export type AdminBackupIntegrityGetErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminBackupIntegrityGetError = AdminBackupIntegrityGetErrors[keyof AdminBackupIntegrityGetErrors];

export type AdminBackupIntegrityGetResponses = {
    /**
     * Successful Response
     */
    200: BackupIntegrityResponse;
};

export type AdminBackupIntegrityGetResponse = AdminBackupIntegrityGetResponses[keyof AdminBackupIntegrityGetResponses];

export type AdminBackupIntegritySetData = {
    body: BackupIntegritySetRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/backups/integrity';
};

export type AdminBackupIntegritySetErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminBackupIntegritySetError = AdminBackupIntegritySetErrors[keyof AdminBackupIntegritySetErrors];

export type AdminBackupIntegritySetResponses = {
    /**
     * Successful Response
     */
    200: BackupIntegrityResponse;
};

export type AdminBackupIntegritySetResponse = AdminBackupIntegritySetResponses[keyof AdminBackupIntegritySetResponses];

export type AdminBackupConfigData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/backups/config';
};

export type AdminBackupConfigErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminBackupConfigError = AdminBackupConfigErrors[keyof AdminBackupConfigErrors];

export type AdminBackupConfigResponses = {
    /**
     * Successful Response
     */
    200: BackupConfigResponse;
};

export type AdminBackupConfigResponse = AdminBackupConfigResponses[keyof AdminBackupConfigResponses];

export type AdminBackupGetData = {
    body?: never;
    path: {
        /**
         * Backup ID
         */
        backup_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/backups/{backup_id}';
};

export type AdminBackupGetErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminBackupGetError = AdminBackupGetErrors[keyof AdminBackupGetErrors];

export type AdminBackupGetResponses = {
    /**
     * Successful Response
     */
    200: BackupItem;
};

export type AdminBackupGetResponse = AdminBackupGetResponses[keyof AdminBackupGetResponses];

export type AdminBackupReportData = {
    body: BackupReportRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/backups';
};

export type AdminBackupReportErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminBackupReportError = AdminBackupReportErrors[keyof AdminBackupReportErrors];

export type AdminBackupReportResponses = {
    /**
     * Successful Response
     */
    200: BackupReportInsertResponse;
};

export type AdminBackupReportResponse = AdminBackupReportResponses[keyof AdminBackupReportResponses];

export type AdminDomainXmlGetData = {
    body?: never;
    path: {
        domain_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/domain/{domain_id}/xml';
};

export type AdminDomainXmlGetErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDomainXmlGetError = AdminDomainXmlGetErrors[keyof AdminDomainXmlGetErrors];

export type AdminDomainXmlGetResponses = {
    /**
     * Domain XML retrieved
     */
    200: AdminDomainXmlResponse;
};

export type AdminDomainXmlGetResponse = AdminDomainXmlGetResponses[keyof AdminDomainXmlGetResponses];

export type AdminDomainXmlUpdateData = {
    body: AdminDomainXmlData;
    path: {
        domain_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/domain/{domain_id}/xml';
};

export type AdminDomainXmlUpdateErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDomainXmlUpdateError = AdminDomainXmlUpdateErrors[keyof AdminDomainXmlUpdateErrors];

export type AdminDomainXmlUpdateResponses = {
    /**
     * Domain XML updated
     */
    200: AdminDomainXmlResponse;
};

export type AdminDomainXmlUpdateResponse = AdminDomainXmlUpdateResponses[keyof AdminDomainXmlUpdateResponses];

export type AdminDomainLiveXmlData = {
    body?: never;
    path: {
        domain_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/domain/{domain_id}/live_xml';
};

export type AdminDomainLiveXmlErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Conflict
     */
    409: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
    /**
     * Gateway Timeout
     */
    504: ErrorResponse;
};

export type AdminDomainLiveXmlError = AdminDomainLiveXmlErrors[keyof AdminDomainLiveXmlErrors];

export type AdminDomainLiveXmlResponses = {
    /**
     * Successful Response
     */
    200: AdminDomainLiveXmlResponse;
};

export type AdminDomainLiveXmlResponse2 = AdminDomainLiveXmlResponses[keyof AdminDomainLiveXmlResponses];

export type AdminDesktopsStatusData = {
    body?: never;
    path: {
        current_status: string;
        target_status: string;
    };
    query?: never;
    url: '/api/v4/admin/items/desktops/{current_status}/{target_status}';
};

export type AdminDesktopsStatusErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDesktopsStatusError = AdminDesktopsStatusErrors[keyof AdminDesktopsStatusErrors];

export type AdminDesktopsStatusResponses = {
    /**
     * Status changed
     */
    204: void;
};

export type AdminDesktopsStatusResponse = AdminDesktopsStatusResponses[keyof AdminDesktopsStatusResponses];

export type AdminDesktopsStatusCategoryData = {
    body?: never;
    path: {
        category: string;
        current_status: string;
        target_status: string;
    };
    query?: never;
    url: '/api/v4/admin/items/desktops/category/{category}/status/{current_status}/{target_status}';
};

export type AdminDesktopsStatusCategoryErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDesktopsStatusCategoryError = AdminDesktopsStatusCategoryErrors[keyof AdminDesktopsStatusCategoryErrors];

export type AdminDesktopsStatusCategoryResponses = {
    /**
     * Status changed
     */
    204: void;
};

export type AdminDesktopsStatusCategoryResponse = AdminDesktopsStatusCategoryResponses[keyof AdminDesktopsStatusCategoryResponses];

export type AdminDomainStoragePathData2 = {
    body: AdminDomainStoragePathData;
    path: {
        domain_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/domain/{domain_id}/storage_path';
};

export type AdminDomainStoragePathErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDomainStoragePathError = AdminDomainStoragePathErrors[keyof AdminDomainStoragePathErrors];

export type AdminDomainStoragePathResponses = {
    /**
     * Storage path updated
     */
    200: AdminDomainStoragePathResponse;
};

export type AdminDomainStoragePathResponse2 = AdminDomainStoragePathResponses[keyof AdminDomainStoragePathResponses];

export type AdminLogsDesktopsConfigData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/logs_desktops/config/old_entries';
};

export type AdminLogsDesktopsConfigErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminLogsDesktopsConfigError = AdminLogsDesktopsConfigErrors[keyof AdminLogsDesktopsConfigErrors];

export type AdminLogsDesktopsConfigResponses = {
    /**
     * Config retrieved
     */
    200: LogsRetentionConfigResponse;
};

export type AdminLogsDesktopsConfigResponse = AdminLogsDesktopsConfigResponses[keyof AdminLogsDesktopsConfigResponses];

export type AdminLogsDesktopsMaxTimeData = {
    body?: never;
    path: {
        max_time: number;
    };
    query?: never;
    url: '/api/v4/admin/item/logs_desktops/config/old_entries/max_time/{max_time}';
};

export type AdminLogsDesktopsMaxTimeErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminLogsDesktopsMaxTimeError = AdminLogsDesktopsMaxTimeErrors[keyof AdminLogsDesktopsMaxTimeErrors];

export type AdminLogsDesktopsMaxTimeResponses = {
    /**
     * Max time updated
     */
    200: LogsRetentionConfigResponse;
};

export type AdminLogsDesktopsMaxTimeResponse = AdminLogsDesktopsMaxTimeResponses[keyof AdminLogsDesktopsMaxTimeResponses];

export type AdminLogsDesktopsActionData = {
    body?: never;
    path: {
        action: 'delete' | 'none';
    };
    query?: never;
    url: '/api/v4/admin/item/logs_desktops/config/old_entries/action/{action}';
};

export type AdminLogsDesktopsActionErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminLogsDesktopsActionError = AdminLogsDesktopsActionErrors[keyof AdminLogsDesktopsActionErrors];

export type AdminLogsDesktopsActionResponses = {
    /**
     * Action updated
     */
    200: LogsRetentionConfigResponse;
};

export type AdminLogsDesktopsActionResponse = AdminLogsDesktopsActionResponses[keyof AdminLogsDesktopsActionResponses];

export type AdminLogsDesktopsDeleteData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/logs_desktops/old_entries/delete';
};

export type AdminLogsDesktopsDeleteErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminLogsDesktopsDeleteError = AdminLogsDesktopsDeleteErrors[keyof AdminLogsDesktopsDeleteErrors];

export type AdminLogsDesktopsDeleteResponses = {
    /**
     * Delete initiated, returns count
     */
    200: number;
};

export type AdminLogsDesktopsDeleteResponse = AdminLogsDesktopsDeleteResponses[keyof AdminLogsDesktopsDeleteResponses];

export type AdminLogsDesktopsDeleteAllData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/logs_desktops/old_entries/delete/all';
};

export type AdminLogsDesktopsDeleteAllErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminLogsDesktopsDeleteAllError = AdminLogsDesktopsDeleteAllErrors[keyof AdminLogsDesktopsDeleteAllErrors];

export type AdminLogsDesktopsDeleteAllResponses = {
    /**
     * Delete initiated, returns count
     */
    200: number;
};

export type AdminLogsDesktopsDeleteAllResponse = AdminLogsDesktopsDeleteAllResponses[keyof AdminLogsDesktopsDeleteAllResponses];

export type AdminLogsUsersConfigData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/logs_users/config/old_entries';
};

export type AdminLogsUsersConfigErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminLogsUsersConfigError = AdminLogsUsersConfigErrors[keyof AdminLogsUsersConfigErrors];

export type AdminLogsUsersConfigResponses = {
    /**
     * Config retrieved
     */
    200: LogsRetentionConfigResponse;
};

export type AdminLogsUsersConfigResponse = AdminLogsUsersConfigResponses[keyof AdminLogsUsersConfigResponses];

export type AdminLogsUsersMaxTimeData = {
    body?: never;
    path: {
        max_time: number;
    };
    query?: never;
    url: '/api/v4/admin/item/logs_users/config/old_entries/max_time/{max_time}';
};

export type AdminLogsUsersMaxTimeErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminLogsUsersMaxTimeError = AdminLogsUsersMaxTimeErrors[keyof AdminLogsUsersMaxTimeErrors];

export type AdminLogsUsersMaxTimeResponses = {
    /**
     * Max time updated
     */
    200: LogsRetentionConfigResponse;
};

export type AdminLogsUsersMaxTimeResponse = AdminLogsUsersMaxTimeResponses[keyof AdminLogsUsersMaxTimeResponses];

export type AdminLogsUsersActionData = {
    body?: never;
    path: {
        action: 'delete' | 'none';
    };
    query?: never;
    url: '/api/v4/admin/item/logs_users/config/old_entries/action/{action}';
};

export type AdminLogsUsersActionErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminLogsUsersActionError = AdminLogsUsersActionErrors[keyof AdminLogsUsersActionErrors];

export type AdminLogsUsersActionResponses = {
    /**
     * Action updated
     */
    200: LogsRetentionConfigResponse;
};

export type AdminLogsUsersActionResponse = AdminLogsUsersActionResponses[keyof AdminLogsUsersActionResponses];

export type AdminLogsUsersDeleteData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/logs_users/old_entries/delete';
};

export type AdminLogsUsersDeleteErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminLogsUsersDeleteError = AdminLogsUsersDeleteErrors[keyof AdminLogsUsersDeleteErrors];

export type AdminLogsUsersDeleteResponses = {
    /**
     * Delete initiated, returns count
     */
    200: number;
};

export type AdminLogsUsersDeleteResponse = AdminLogsUsersDeleteResponses[keyof AdminLogsUsersDeleteResponses];

export type AdminLogsUsersDeleteAllData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/logs_users/old_entries/delete/all';
};

export type AdminLogsUsersDeleteAllErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminLogsUsersDeleteAllError = AdminLogsUsersDeleteAllErrors[keyof AdminLogsUsersDeleteAllErrors];

export type AdminLogsUsersDeleteAllResponses = {
    /**
     * Delete initiated, returns count
     */
    200: number;
};

export type AdminLogsUsersDeleteAllResponse = AdminLogsUsersDeleteAllResponses[keyof AdminLogsUsersDeleteAllResponses];

export type AdminDomainXmlCapabilitiesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/domains/xml_capabilities';
};

export type AdminDomainXmlCapabilitiesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDomainXmlCapabilitiesError = AdminDomainXmlCapabilitiesErrors[keyof AdminDomainXmlCapabilitiesErrors];

export type AdminDomainXmlCapabilitiesResponses = {
    /**
     * Successful Response
     */
    200: AdminDomainXmlCapabilitiesResponse;
};

export type AdminDomainXmlCapabilitiesResponse2 = AdminDomainXmlCapabilitiesResponses[keyof AdminDomainXmlCapabilitiesResponses];

export type AdminDomainXmlSectionsParseData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/domains/xml_sections/parse';
};

export type AdminDomainXmlSectionsParseErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDomainXmlSectionsParseError = AdminDomainXmlSectionsParseErrors[keyof AdminDomainXmlSectionsParseErrors];

export type AdminDomainXmlSectionsParseResponses = {
    /**
     * Successful Response
     */
    200: AdminDomainXmlSectionsParseResponse;
};

export type AdminDomainXmlSectionsParseResponse2 = AdminDomainXmlSectionsParseResponses[keyof AdminDomainXmlSectionsParseResponses];

export type AdminDomainXmlSectionsGetData = {
    body?: never;
    path: {
        domain_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/domains/xml_sections/{domain_id}';
};

export type AdminDomainXmlSectionsGetErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDomainXmlSectionsGetError = AdminDomainXmlSectionsGetErrors[keyof AdminDomainXmlSectionsGetErrors];

export type AdminDomainXmlSectionsGetResponses = {
    /**
     * Successful Response
     */
    200: AdminDomainXmlSectionsGetResponse;
};

export type AdminDomainXmlSectionsGetResponse2 = AdminDomainXmlSectionsGetResponses[keyof AdminDomainXmlSectionsGetResponses];

export type AdminDomainXmlSectionsSaveData2 = {
    body: AdminDomainXmlSectionsSaveData;
    path: {
        domain_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/domains/xml_sections/{domain_id}';
};

export type AdminDomainXmlSectionsSaveErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDomainXmlSectionsSaveError = AdminDomainXmlSectionsSaveErrors[keyof AdminDomainXmlSectionsSaveErrors];

export type AdminDomainXmlSectionsSaveResponses = {
    /**
     * Successful Response
     */
    200: AdminDomainXmlSectionsSaveResponse;
};

export type AdminDomainXmlSectionsSaveResponse2 = AdminDomainXmlSectionsSaveResponses[keyof AdminDomainXmlSectionsSaveResponses];

export type AdminDomainXmlSectionsSaveAsVirtInstallData = {
    body?: never;
    path: {
        domain_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/domains/xml_sections/{domain_id}/save_virt_install';
};

export type AdminDomainXmlSectionsSaveAsVirtInstallErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Conflict
     */
    409: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDomainXmlSectionsSaveAsVirtInstallError = AdminDomainXmlSectionsSaveAsVirtInstallErrors[keyof AdminDomainXmlSectionsSaveAsVirtInstallErrors];

export type AdminDomainXmlSectionsSaveAsVirtInstallResponses = {
    /**
     * Successful Response
     */
    200: AdminVirtInstallSaveResponse;
};

export type AdminDomainXmlSectionsSaveAsVirtInstallResponse = AdminDomainXmlSectionsSaveAsVirtInstallResponses[keyof AdminDomainXmlSectionsSaveAsVirtInstallResponses];

export type AdminVirtInstallXmlSectionsGetData = {
    body?: never;
    path: {
        virt_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/virt_install/xml_sections/{virt_id}';
};

export type AdminVirtInstallXmlSectionsGetErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminVirtInstallXmlSectionsGetError = AdminVirtInstallXmlSectionsGetErrors[keyof AdminVirtInstallXmlSectionsGetErrors];

export type AdminVirtInstallXmlSectionsGetResponses = {
    /**
     * Successful Response
     */
    200: AdminDomainXmlSectionsGetResponse;
};

export type AdminVirtInstallXmlSectionsGetResponse = AdminVirtInstallXmlSectionsGetResponses[keyof AdminVirtInstallXmlSectionsGetResponses];

export type AdminVirtInstallXmlSectionsSaveData = {
    body?: never;
    path: {
        virt_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/virt_install/xml_sections/{virt_id}';
};

export type AdminVirtInstallXmlSectionsSaveErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminVirtInstallXmlSectionsSaveError = AdminVirtInstallXmlSectionsSaveErrors[keyof AdminVirtInstallXmlSectionsSaveErrors];

export type AdminVirtInstallXmlSectionsSaveResponses = {
    /**
     * Successful Response
     */
    200: AdminVirtInstallXmlSectionsSaveResponse;
};

export type AdminVirtInstallXmlSectionsSaveResponse2 = AdminVirtInstallXmlSectionsSaveResponses[keyof AdminVirtInstallXmlSectionsSaveResponses];

export type AdminDownloadsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/downloads';
};

export type AdminDownloadsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
    /**
     * Gateway Timeout
     */
    504: ErrorResponse;
};

export type AdminDownloadsError = AdminDownloadsErrors[keyof AdminDownloadsErrors];

export type AdminDownloadsResponses = {
    /**
     * Successful Response
     */
    200: DownloadsOverviewResponse;
};

export type AdminDownloadsResponse = AdminDownloadsResponses[keyof AdminDownloadsResponses];

export type AdminDownloadsKindData = {
    body?: never;
    path: {
        /**
         * Download kind: domains, media, virt_install, videos, viewers
         */
        kind: 'domains' | 'media' | 'virt_install' | 'videos' | 'viewers';
    };
    query?: never;
    url: '/api/v4/admin/items/downloads/{kind}';
};

export type AdminDownloadsKindErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
    /**
     * Gateway Timeout
     */
    504: ErrorResponse;
};

export type AdminDownloadsKindError = AdminDownloadsKindErrors[keyof AdminDownloadsKindErrors];

export type AdminDownloadsKindResponses = {
    /**
     * Successful Response
     */
    200: Array<DownloadItem>;
};

export type AdminDownloadsKindResponse = AdminDownloadsKindResponses[keyof AdminDownloadsKindResponses];

export type AdminDownloadsRegisterData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/downloads/register';
};

export type AdminDownloadsRegisterErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDownloadsRegisterError = AdminDownloadsRegisterErrors[keyof AdminDownloadsRegisterErrors];

export type AdminDownloadsRegisterResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminDownloadsRegisterResponse = AdminDownloadsRegisterResponses[keyof AdminDownloadsRegisterResponses];

export type AdminDownloadsActionData = {
    body?: never;
    path: {
        /**
         * Action: download, abort, delete
         */
        action: string;
        /**
         * Download kind
         */
        kind: string;
    };
    query?: never;
    url: '/api/v4/admin/item/downloads/{action}/{kind}';
};

export type AdminDownloadsActionErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
    /**
     * Gateway Timeout
     */
    504: ErrorResponse;
};

export type AdminDownloadsActionError = AdminDownloadsActionErrors[keyof AdminDownloadsActionErrors];

export type AdminDownloadsActionResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminDownloadsActionResponse = AdminDownloadsActionResponses[keyof AdminDownloadsActionResponses];

export type AdminDownloadsActionIdData = {
    body?: DownloadItem | null;
    path: {
        /**
         * Action: download, abort, delete
         */
        action: string;
        /**
         * Download kind
         */
        kind: string;
        /**
         * Item ID
         */
        id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/downloads/{action}/{kind}/{id}';
};

export type AdminDownloadsActionIdErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
    /**
     * Gateway Timeout
     */
    504: ErrorResponse;
};

export type AdminDownloadsActionIdError = AdminDownloadsActionIdErrors[keyof AdminDownloadsActionIdErrors];

export type AdminDownloadsActionIdResponses = {
    /**
     * Successful Response
     */
    200: DownloadActionResponse;
};

export type AdminDownloadsActionIdResponse = AdminDownloadsActionIdResponses[keyof AdminDownloadsActionIdResponses];

export type AdminHypervisorsListData = {
    body?: never;
    path?: never;
    query?: {
        status?: HypervisorStatus | null;
    };
    url: '/api/v4/admin/items/hypervisors';
};

export type AdminHypervisorsListErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminHypervisorsListError = AdminHypervisorsListErrors[keyof AdminHypervisorsListErrors];

export type AdminHypervisorsListResponses = {
    /**
     * Successful Response
     */
    200: Array<AdminHypervisor>;
};

export type AdminHypervisorsListResponse = AdminHypervisorsListResponses[keyof AdminHypervisorsListResponses];

export type AdminHypervisorsListByStatusData = {
    body?: never;
    path: {
        /**
         * Hypervisor status filter
         */
        status: HypervisorStatus;
    };
    query?: never;
    url: '/api/v4/admin/items/hypervisors/{status}';
};

export type AdminHypervisorsListByStatusErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminHypervisorsListByStatusError = AdminHypervisorsListByStatusErrors[keyof AdminHypervisorsListByStatusErrors];

export type AdminHypervisorsListByStatusResponses = {
    /**
     * Successful Response
     */
    200: Array<AdminHypervisor>;
};

export type AdminHypervisorsListByStatusResponse = AdminHypervisorsListByStatusResponses[keyof AdminHypervisorsListByStatusResponses];

export type AdminHypervisorStatusData = {
    body?: never;
    path: {
        /**
         * Hypervisor ID
         */
        hyper_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/hypervisor/status/{hyper_id}';
};

export type AdminHypervisorStatusErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminHypervisorStatusError = AdminHypervisorStatusErrors[keyof AdminHypervisorStatusErrors];

export type AdminHypervisorStatusResponses = {
    /**
     * Successful Response
     */
    200: AdminHypervisorStatusResponse;
};

export type AdminHypervisorStatusResponse2 = AdminHypervisorStatusResponses[keyof AdminHypervisorStatusResponses];

export type AdminHypervisorCreateData2 = {
    body: AdminHypervisorCreateData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/hypervisor';
};

export type AdminHypervisorCreateErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminHypervisorCreateError = AdminHypervisorCreateErrors[keyof AdminHypervisorCreateErrors];

export type AdminHypervisorCreateResponses = {
    /**
     * Successful Response
     */
    200: AdminHypervisorCreateResponse;
};

export type AdminHypervisorCreateResponse2 = AdminHypervisorCreateResponses[keyof AdminHypervisorCreateResponses];

export type AdminHypervisorDeleteData = {
    body?: never;
    path: {
        /**
         * Hypervisor ID
         */
        hyper_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/hypervisor/{hyper_id}';
};

export type AdminHypervisorDeleteErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminHypervisorDeleteError = AdminHypervisorDeleteErrors[keyof AdminHypervisorDeleteErrors];

export type AdminHypervisorDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminHypervisorDeleteResponse = AdminHypervisorDeleteResponses[keyof AdminHypervisorDeleteResponses];

export type AdminHypervisorEnableData2 = {
    body: AdminHypervisorEnableData;
    path: {
        /**
         * Hypervisor ID
         */
        hyper_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/hypervisor/{hyper_id}';
};

export type AdminHypervisorEnableErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminHypervisorEnableError = AdminHypervisorEnableErrors[keyof AdminHypervisorEnableErrors];

export type AdminHypervisorEnableResponses = {
    /**
     * Successful Response
     */
    200: AdminHypervisorEnableResponse;
};

export type AdminHypervisorEnableResponse2 = AdminHypervisorEnableResponses[keyof AdminHypervisorEnableResponses];

export type AdminHypervisorStopDomainsData = {
    body?: never;
    path: {
        /**
         * Hypervisor ID
         */
        hyper_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/hypervisor/stop/{hyper_id}';
};

export type AdminHypervisorStopDomainsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminHypervisorStopDomainsError = AdminHypervisorStopDomainsErrors[keyof AdminHypervisorStopDomainsErrors];

export type AdminHypervisorStopDomainsResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminHypervisorStopDomainsResponse = AdminHypervisorStopDomainsResponses[keyof AdminHypervisorStopDomainsResponses];

export type AdminHypervisorVpnData = {
    body?: never;
    path: {
        /**
         * Hypervisor ID
         */
        hyper_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/hypervisor_vpn/{hyper_id}';
};

export type AdminHypervisorVpnErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminHypervisorVpnError = AdminHypervisorVpnErrors[keyof AdminHypervisorVpnErrors];

export type AdminHypervisorVpnResponses = {
    /**
     * Successful Response
     */
    200: AdminHypervisorVpnResponse;
};

export type AdminHypervisorVpnResponse2 = AdminHypervisorVpnResponses[keyof AdminHypervisorVpnResponses];

export type AdminHypervisorWgAddrData2 = {
    body: AdminHypervisorWgAddrData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/hypervisor/vm/wg_addr';
};

export type AdminHypervisorWgAddrErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminHypervisorWgAddrError = AdminHypervisorWgAddrErrors[keyof AdminHypervisorWgAddrErrors];

export type AdminHypervisorWgAddrResponses = {
    /**
     * Successful Response
     */
    200: AdminHypervisorWgAddrResponse;
};

export type AdminHypervisorWgAddrResponse2 = AdminHypervisorWgAddrResponses[keyof AdminHypervisorWgAddrResponses];

export type AdminHypervisorMediaFoundData2 = {
    body: AdminHypervisorMediaFoundData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/hypervisor/media_found';
};

export type AdminHypervisorMediaFoundErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminHypervisorMediaFoundError = AdminHypervisorMediaFoundErrors[keyof AdminHypervisorMediaFoundErrors];

export type AdminHypervisorMediaFoundResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminHypervisorMediaFoundResponse = AdminHypervisorMediaFoundResponses[keyof AdminHypervisorMediaFoundResponses];

export type AdminHypervisorDisksFoundData2 = {
    body: AdminHypervisorDisksFoundData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/hypervisor/disks_found';
};

export type AdminHypervisorDisksFoundErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminHypervisorDisksFoundError = AdminHypervisorDisksFoundErrors[keyof AdminHypervisorDisksFoundErrors];

export type AdminHypervisorDisksFoundResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminHypervisorDisksFoundResponse = AdminHypervisorDisksFoundResponses[keyof AdminHypervisorDisksFoundResponses];

export type AdminHypervisorMediaDeleteData2 = {
    body: AdminHypervisorMediaDeleteData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/hypervisor/media_delete';
};

export type AdminHypervisorMediaDeleteErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminHypervisorMediaDeleteError = AdminHypervisorMediaDeleteErrors[keyof AdminHypervisorMediaDeleteErrors];

export type AdminHypervisorMediaDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminHypervisorMediaDeleteResponse = AdminHypervisorMediaDeleteResponses[keyof AdminHypervisorMediaDeleteResponses];

export type AdminOrchestratorHypervisorsListData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/orchestrator/hypervisors';
};

export type AdminOrchestratorHypervisorsListErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminOrchestratorHypervisorsListError = AdminOrchestratorHypervisorsListErrors[keyof AdminOrchestratorHypervisorsListErrors];

export type AdminOrchestratorHypervisorsListResponses = {
    /**
     * Successful Response
     */
    200: Array<OrchestratorHypervisor>;
};

export type AdminOrchestratorHypervisorsListResponse = AdminOrchestratorHypervisorsListResponses[keyof AdminOrchestratorHypervisorsListResponses];

export type AdminOrchestratorHypervisorGetData = {
    body?: never;
    path: {
        /**
         * Hypervisor ID
         */
        hypervisor_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/orchestrator/hypervisor/{hypervisor_id}';
};

export type AdminOrchestratorHypervisorGetErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminOrchestratorHypervisorGetError = AdminOrchestratorHypervisorGetErrors[keyof AdminOrchestratorHypervisorGetErrors];

export type AdminOrchestratorHypervisorGetResponses = {
    /**
     * Successful Response
     */
    200: OrchestratorHypervisor;
};

export type AdminOrchestratorHypervisorGetResponse = AdminOrchestratorHypervisorGetResponses[keyof AdminOrchestratorHypervisorGetResponses];

export type AdminOrchestratorManagedListData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/hypervisors/orchestrator_managed';
};

export type AdminOrchestratorManagedListErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminOrchestratorManagedListError = AdminOrchestratorManagedListErrors[keyof AdminOrchestratorManagedListErrors];

export type AdminOrchestratorManagedListResponses = {
    /**
     * Successful Response
     */
    200: Array<OrchestratorManagedHypervisor>;
};

export type AdminOrchestratorManagedListResponse = AdminOrchestratorManagedListResponses[keyof AdminOrchestratorManagedListResponses];

export type AdminOrchestratorDeadRowResetData = {
    body?: never;
    path: {
        /**
         * Hypervisor ID
         */
        hypervisor_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/orchestrator/hypervisor/{hypervisor_id}/dead_row';
};

export type AdminOrchestratorDeadRowResetErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminOrchestratorDeadRowResetError = AdminOrchestratorDeadRowResetErrors[keyof AdminOrchestratorDeadRowResetErrors];

export type AdminOrchestratorDeadRowResetResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminOrchestratorDeadRowResetResponse = AdminOrchestratorDeadRowResetResponses[keyof AdminOrchestratorDeadRowResetResponses];

export type AdminOrchestratorDeadRowSetData = {
    body?: never;
    path: {
        /**
         * Hypervisor ID
         */
        hypervisor_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/orchestrator/hypervisor/{hypervisor_id}/dead_row';
};

export type AdminOrchestratorDeadRowSetErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminOrchestratorDeadRowSetError = AdminOrchestratorDeadRowSetErrors[keyof AdminOrchestratorDeadRowSetErrors];

export type AdminOrchestratorDeadRowSetResponses = {
    /**
     * Successful Response
     */
    200: DeadRowSetResponse;
};

export type AdminOrchestratorDeadRowSetResponse = AdminOrchestratorDeadRowSetResponses[keyof AdminOrchestratorDeadRowSetResponses];

export type AdminOrchestratorOnlyForcedSetData = {
    body: OrchestratorOnlyForcedData;
    path: {
        /**
         * Hypervisor ID
         */
        hypervisor_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/orchestrator/hypervisor/{hypervisor_id}/only_forced';
};

export type AdminOrchestratorOnlyForcedSetErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminOrchestratorOnlyForcedSetError = AdminOrchestratorOnlyForcedSetErrors[keyof AdminOrchestratorOnlyForcedSetErrors];

export type AdminOrchestratorOnlyForcedSetResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminOrchestratorOnlyForcedSetResponse = AdminOrchestratorOnlyForcedSetResponses[keyof AdminOrchestratorOnlyForcedSetResponses];

export type AdminOrchestratorStopDesktopsData = {
    body?: never;
    path: {
        /**
         * Hypervisor ID
         */
        hypervisor_id: string;
    };
    query?: never;
    url: '/api/v4/admin/items/orchestrator/hypervisor/{hypervisor_id}/desktops';
};

export type AdminOrchestratorStopDesktopsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminOrchestratorStopDesktopsError = AdminOrchestratorStopDesktopsErrors[keyof AdminOrchestratorStopDesktopsErrors];

export type AdminOrchestratorStopDesktopsResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminOrchestratorStopDesktopsResponse = AdminOrchestratorStopDesktopsResponses[keyof AdminOrchestratorStopDesktopsResponses];

export type AdminOrchestratorManageUnsetData = {
    body?: never;
    path: {
        /**
         * Hypervisor ID
         */
        hypervisor_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/orchestrator/hypervisor/{hypervisor_id}/manage';
};

export type AdminOrchestratorManageUnsetErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminOrchestratorManageUnsetError = AdminOrchestratorManageUnsetErrors[keyof AdminOrchestratorManageUnsetErrors];

export type AdminOrchestratorManageUnsetResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminOrchestratorManageUnsetResponse = AdminOrchestratorManageUnsetResponses[keyof AdminOrchestratorManageUnsetResponses];

export type AdminOrchestratorManageSetData = {
    body?: never;
    path: {
        /**
         * Hypervisor ID
         */
        hypervisor_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/orchestrator/hypervisor/{hypervisor_id}/manage';
};

export type AdminOrchestratorManageSetErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminOrchestratorManageSetError = AdminOrchestratorManageSetErrors[keyof AdminOrchestratorManageSetErrors];

export type AdminOrchestratorManageSetResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminOrchestratorManageSetResponse = AdminOrchestratorManageSetResponses[keyof AdminOrchestratorManageSetResponses];

export type AdminHypervisorVirtPoolsGetData = {
    body?: never;
    path: {
        /**
         * Hypervisor ID
         */
        hyper_id: string;
    };
    query?: never;
    url: '/api/v4/admin/items/hypervisor/{hyper_id}/virt_pools';
};

export type AdminHypervisorVirtPoolsGetErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminHypervisorVirtPoolsGetError = AdminHypervisorVirtPoolsGetErrors[keyof AdminHypervisorVirtPoolsGetErrors];

export type AdminHypervisorVirtPoolsGetResponses = {
    /**
     * Successful Response
     */
    200: Array<AdminHypervisorVirtPool>;
};

export type AdminHypervisorVirtPoolsGetResponse = AdminHypervisorVirtPoolsGetResponses[keyof AdminHypervisorVirtPoolsGetResponses];

export type AdminHypervisorVirtPoolsUpdateData = {
    body: AdminHypervisorVirtPoolUpdateData;
    path: {
        /**
         * Hypervisor ID
         */
        hyper_id: string;
    };
    query?: never;
    url: '/api/v4/admin/items/hypervisor/{hyper_id}/virt_pools';
};

export type AdminHypervisorVirtPoolsUpdateErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminHypervisorVirtPoolsUpdateError = AdminHypervisorVirtPoolsUpdateErrors[keyof AdminHypervisorVirtPoolsUpdateErrors];

export type AdminHypervisorVirtPoolsUpdateResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminHypervisorVirtPoolsUpdateResponse = AdminHypervisorVirtPoolsUpdateResponses[keyof AdminHypervisorVirtPoolsUpdateResponses];

export type AdminHypervisorMountpointsData = {
    body?: never;
    path: {
        /**
         * Hypervisor ID
         */
        hyper_id: string;
    };
    query?: never;
    url: '/api/v4/admin/items/hypervisor/mountpoints/{hyper_id}';
};

export type AdminHypervisorMountpointsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminHypervisorMountpointsError = AdminHypervisorMountpointsErrors[keyof AdminHypervisorMountpointsErrors];

export type AdminHypervisorMountpointsResponses = {
    /**
     * Successful Response
     */
    200: Array<AdminHypervisorMountpoint>;
};

export type AdminHypervisorMountpointsResponse = AdminHypervisorMountpointsResponses[keyof AdminHypervisorMountpointsResponses];

export type AdminHypervisorStartedDomainsData = {
    body?: never;
    path: {
        /**
         * Hypervisor ID
         */
        hyper_id: string;
    };
    query?: never;
    url: '/api/v4/admin/items/hypervisor/started_domains/{hyper_id}';
};

export type AdminHypervisorStartedDomainsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminHypervisorStartedDomainsError = AdminHypervisorStartedDomainsErrors[keyof AdminHypervisorStartedDomainsErrors];

export type AdminHypervisorStartedDomainsResponses = {
    /**
     * Successful Response
     */
    200: Array<AdminHypervisorStartedDomain>;
};

export type AdminHypervisorStartedDomainsResponse = AdminHypervisorStartedDomainsResponses[keyof AdminHypervisorStartedDomainsResponses];

export type AdminRegisterVlansData = {
    body: AdminRegisterVlansRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/vlans';
};

export type AdminRegisterVlansErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminRegisterVlansError = AdminRegisterVlansErrors[keyof AdminRegisterVlansErrors];

export type AdminRegisterVlansResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminRegisterVlansResponse = AdminRegisterVlansResponses[keyof AdminRegisterVlansResponses];

export type AdminHypervisorBootProgressData = {
    body: AdminBootProgressRequest;
    path: {
        hyper_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/hypervisor/{hyper_id}/boot_progress';
};

export type AdminHypervisorBootProgressErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminHypervisorBootProgressError = AdminHypervisorBootProgressErrors[keyof AdminHypervisorBootProgressErrors];

export type AdminHypervisorBootProgressResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminHypervisorBootProgressResponse = AdminHypervisorBootProgressResponses[keyof AdminHypervisorBootProgressResponses];

export type AdminHypervisorHealthData = {
    body: AdminHypervisorHealthRequest;
    path: {
        hyper_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/hypervisor/{hyper_id}/health';
};

export type AdminHypervisorHealthErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminHypervisorHealthError = AdminHypervisorHealthErrors[keyof AdminHypervisorHealthErrors];

export type AdminHypervisorHealthResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminHypervisorHealthResponse = AdminHypervisorHealthResponses[keyof AdminHypervisorHealthResponses];

export type AdminSharedStorageStallData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/shared-storage-stall';
};

export type AdminSharedStorageStallErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminSharedStorageStallError = AdminSharedStorageStallErrors[keyof AdminSharedStorageStallErrors];

export type AdminSharedStorageStallResponses = {
    /**
     * Successful Response
     */
    200: SharedStorageStallResponse;
};

export type AdminSharedStorageStallResponse = AdminSharedStorageStallResponses[keyof AdminSharedStorageStallResponses];

export type AdminHypervisorGpuAppliedData = {
    body: AdminGpuAppliedRequest;
    path: {
        hyper_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/hypervisor/{hyper_id}/gpu_applied';
};

export type AdminHypervisorGpuAppliedErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminHypervisorGpuAppliedError = AdminHypervisorGpuAppliedErrors[keyof AdminHypervisorGpuAppliedErrors];

export type AdminHypervisorGpuAppliedResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminHypervisorGpuAppliedResponse = AdminHypervisorGpuAppliedResponses[keyof AdminHypervisorGpuAppliedResponses];

export type AdminGpuForceProfilePreviewData = {
    body: AdminGpuForceProfilePreviewRequest;
    path: {
        card_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/hypervisor/gpus/{card_id}/force_profile_preview';
};

export type AdminGpuForceProfilePreviewErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGpuForceProfilePreviewError = AdminGpuForceProfilePreviewErrors[keyof AdminGpuForceProfilePreviewErrors];

export type AdminGpuForceProfilePreviewResponses = {
    /**
     * Successful Response
     */
    200: AdminGpuForceProfilePreviewResponse;
};

export type AdminGpuForceProfilePreviewResponse2 = AdminGpuForceProfilePreviewResponses[keyof AdminGpuForceProfilePreviewResponses];

export type AdminLoginNotificationUpdateData = {
    body: LoginNotificationUpdateRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/login_config/notification';
};

export type AdminLoginNotificationUpdateErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminLoginNotificationUpdateError = AdminLoginNotificationUpdateErrors[keyof AdminLoginNotificationUpdateErrors];

export type AdminLoginNotificationUpdateResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminLoginNotificationUpdateResponse = AdminLoginNotificationUpdateResponses[keyof AdminLoginNotificationUpdateResponses];

export type AdminLoginNotificationCoverEnableData = {
    body: LoginNotificationEnableRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/login_config/notification/cover/enable';
};

export type AdminLoginNotificationCoverEnableErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminLoginNotificationCoverEnableError = AdminLoginNotificationCoverEnableErrors[keyof AdminLoginNotificationCoverEnableErrors];

export type AdminLoginNotificationCoverEnableResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminLoginNotificationCoverEnableResponse = AdminLoginNotificationCoverEnableResponses[keyof AdminLoginNotificationCoverEnableResponses];

export type AdminLoginNotificationFormEnableData = {
    body: LoginNotificationEnableRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/login_config/notification/form/enable';
};

export type AdminLoginNotificationFormEnableErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminLoginNotificationFormEnableError = AdminLoginNotificationFormEnableErrors[keyof AdminLoginNotificationFormEnableErrors];

export type AdminLoginNotificationFormEnableResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminLoginNotificationFormEnableResponse = AdminLoginNotificationFormEnableResponses[keyof AdminLoginNotificationFormEnableResponses];

export type AdminCreateNotificationTemplateData = {
    body: TemplateCreateRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/notifications/template';
};

export type AdminCreateNotificationTemplateErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminCreateNotificationTemplateError = AdminCreateNotificationTemplateErrors[keyof AdminCreateNotificationTemplateErrors];

export type AdminCreateNotificationTemplateResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type AdminCreateNotificationTemplateResponse = AdminCreateNotificationTemplateResponses[keyof AdminCreateNotificationTemplateResponses];

export type AdminListNotificationTemplatesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/notifications/templates';
};

export type AdminListNotificationTemplatesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminListNotificationTemplatesError = AdminListNotificationTemplatesErrors[keyof AdminListNotificationTemplatesErrors];

export type AdminListNotificationTemplatesResponses = {
    /**
     * Successful Response
     */
    200: TemplateListResponse;
};

export type AdminListNotificationTemplatesResponse = AdminListNotificationTemplatesResponses[keyof AdminListNotificationTemplatesResponses];

export type AdminListCustomNotificationTemplatesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/notifications/templates/custom';
};

export type AdminListCustomNotificationTemplatesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminListCustomNotificationTemplatesError = AdminListCustomNotificationTemplatesErrors[keyof AdminListCustomNotificationTemplatesErrors];

export type AdminListCustomNotificationTemplatesResponses = {
    /**
     * Successful Response
     */
    200: TemplateListResponse;
};

export type AdminListCustomNotificationTemplatesResponse = AdminListCustomNotificationTemplatesResponses[keyof AdminListCustomNotificationTemplatesResponses];

export type AdminListSystemNotificationTemplatesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/notifications/templates/system';
};

export type AdminListSystemNotificationTemplatesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminListSystemNotificationTemplatesError = AdminListSystemNotificationTemplatesErrors[keyof AdminListSystemNotificationTemplatesErrors];

export type AdminListSystemNotificationTemplatesResponses = {
    /**
     * Successful Response
     */
    200: TemplateListResponse;
};

export type AdminListSystemNotificationTemplatesResponse = AdminListSystemNotificationTemplatesResponses[keyof AdminListSystemNotificationTemplatesResponses];

export type AdminDeleteNotificationTemplateData = {
    body?: never;
    path: {
        template_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/notifications/template/{template_id}';
};

export type AdminDeleteNotificationTemplateErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDeleteNotificationTemplateError = AdminDeleteNotificationTemplateErrors[keyof AdminDeleteNotificationTemplateErrors];

export type AdminDeleteNotificationTemplateResponses = {
    /**
     * Successful Response
     */
    200: DeleteResponse;
};

export type AdminDeleteNotificationTemplateResponse = AdminDeleteNotificationTemplateResponses[keyof AdminDeleteNotificationTemplateResponses];

export type AdminGetNotificationTemplateData = {
    body?: never;
    path: {
        template_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/notifications/template/{template_id}';
};

export type AdminGetNotificationTemplateErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetNotificationTemplateError = AdminGetNotificationTemplateErrors[keyof AdminGetNotificationTemplateErrors];

export type AdminGetNotificationTemplateResponses = {
    /**
     * Successful Response
     */
    200: TemplateResponse;
};

export type AdminGetNotificationTemplateResponse = AdminGetNotificationTemplateResponses[keyof AdminGetNotificationTemplateResponses];

export type AdminUpdateNotificationTemplateData = {
    body: TemplateUpdateRequest;
    path: {
        template_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/notifications/template/{template_id}';
};

export type AdminUpdateNotificationTemplateErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUpdateNotificationTemplateError = AdminUpdateNotificationTemplateErrors[keyof AdminUpdateNotificationTemplateErrors];

export type AdminUpdateNotificationTemplateResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminUpdateNotificationTemplateResponse = AdminUpdateNotificationTemplateResponses[keyof AdminUpdateNotificationTemplateResponses];

export type AdminPreviewNotificationTemplateData = {
    body: TemplatePreviewRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/notifications/template/preview';
};

export type AdminPreviewNotificationTemplateErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminPreviewNotificationTemplateError = AdminPreviewNotificationTemplateErrors[keyof AdminPreviewNotificationTemplateErrors];

export type AdminPreviewNotificationTemplateResponses = {
    /**
     * Successful Response
     */
    200: TemplatePreviewResponse;
};

export type AdminPreviewNotificationTemplateResponse = AdminPreviewNotificationTemplateResponses[keyof AdminPreviewNotificationTemplateResponses];

export type AdminListNotificationsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/notifications';
};

export type AdminListNotificationsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminListNotificationsError = AdminListNotificationsErrors[keyof AdminListNotificationsErrors];

export type AdminListNotificationsResponses = {
    /**
     * Successful Response
     */
    200: NotificationListResponse;
};

export type AdminListNotificationsResponse = AdminListNotificationsResponses[keyof AdminListNotificationsResponses];

export type AdminCreateNotificationData = {
    body: NotificationCreateRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/notification';
};

export type AdminCreateNotificationErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminCreateNotificationError = AdminCreateNotificationErrors[keyof AdminCreateNotificationErrors];

export type AdminCreateNotificationResponses = {
    /**
     * Successful Response
     */
    200: NotificationResponse;
};

export type AdminCreateNotificationResponse = AdminCreateNotificationResponses[keyof AdminCreateNotificationResponses];

export type AdminListNotificationActionsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/notification/actions';
};

export type AdminListNotificationActionsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminListNotificationActionsError = AdminListNotificationActionsErrors[keyof AdminListNotificationActionsErrors];

export type AdminListNotificationActionsResponses = {
    /**
     * Successful Response
     */
    200: NotificationActionsResponse;
};

export type AdminListNotificationActionsResponse = AdminListNotificationActionsResponses[keyof AdminListNotificationActionsResponses];

export type AdminDeleteNotificationData = {
    body: NotificationDeleteRequest;
    path: {
        notification_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/notification/{notification_id}';
};

export type AdminDeleteNotificationErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDeleteNotificationError = AdminDeleteNotificationErrors[keyof AdminDeleteNotificationErrors];

export type AdminDeleteNotificationResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminDeleteNotificationResponse = AdminDeleteNotificationResponses[keyof AdminDeleteNotificationResponses];

export type AdminGetNotificationData = {
    body?: never;
    path: {
        notification_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/notification/{notification_id}';
};

export type AdminGetNotificationErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetNotificationError = AdminGetNotificationErrors[keyof AdminGetNotificationErrors];

export type AdminGetNotificationResponses = {
    /**
     * Successful Response
     */
    200: NotificationDetailResponse;
};

export type AdminGetNotificationResponse = AdminGetNotificationResponses[keyof AdminGetNotificationResponses];

export type AdminUpdateNotificationData = {
    body: NotificationUpdateRequest;
    path: {
        notification_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/notification/{notification_id}';
};

export type AdminUpdateNotificationErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUpdateNotificationError = AdminUpdateNotificationErrors[keyof AdminUpdateNotificationErrors];

export type AdminUpdateNotificationResponses = {
    /**
     * Successful Response
     */
    200: NotificationResponse;
};

export type AdminUpdateNotificationResponse = AdminUpdateNotificationResponses[keyof AdminUpdateNotificationResponses];

export type AdminGetNotificationsDataByStatusData = {
    body?: never;
    path: {
        status: string;
        user_id: string;
    };
    query?: never;
    url: '/api/v4/admin/items/notifications/data/status/{status}/user/{user_id}';
};

export type AdminGetNotificationsDataByStatusErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetNotificationsDataByStatusError = AdminGetNotificationsDataByStatusErrors[keyof AdminGetNotificationsDataByStatusErrors];

export type AdminGetNotificationsDataByStatusResponses = {
    /**
     * Successful Response
     */
    200: NotificationDataListResponse;
};

export type AdminGetNotificationsDataByStatusResponse = AdminGetNotificationsDataByStatusResponses[keyof AdminGetNotificationsDataByStatusResponses];

export type AdminGetNotificationStatusesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/notifications/statuses';
};

export type AdminGetNotificationStatusesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetNotificationStatusesError = AdminGetNotificationStatusesErrors[keyof AdminGetNotificationStatusesErrors];

export type AdminGetNotificationStatusesResponses = {
    /**
     * Successful Response
     */
    200: NotificationStatusesResponse;
};

export type AdminGetNotificationStatusesResponse = AdminGetNotificationStatusesResponses[keyof AdminGetNotificationStatusesResponses];

export type AdminGetNotificationsGroupedByStatusData = {
    body?: never;
    path: {
        status: 'pending' | 'notified';
    };
    query?: never;
    url: '/api/v4/admin/items/notifications/data/by_status/{status}';
};

export type AdminGetNotificationsGroupedByStatusErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetNotificationsGroupedByStatusError = AdminGetNotificationsGroupedByStatusErrors[keyof AdminGetNotificationsGroupedByStatusErrors];

export type AdminGetNotificationsGroupedByStatusResponses = {
    /**
     * Successful Response
     */
    200: NotificationGroupedDataResponse;
};

export type AdminGetNotificationsGroupedByStatusResponse = AdminGetNotificationsGroupedByStatusResponses[keyof AdminGetNotificationsGroupedByStatusResponses];

export type AdminDeleteUserNotificationDataData = {
    body?: never;
    path: {
        user_id: string;
    };
    query?: never;
    url: '/api/v4/admin/items/notifications/data/user/{user_id}';
};

export type AdminDeleteUserNotificationDataErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDeleteUserNotificationDataError = AdminDeleteUserNotificationDataErrors[keyof AdminDeleteUserNotificationDataErrors];

export type AdminDeleteUserNotificationDataResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminDeleteUserNotificationDataResponse = AdminDeleteUserNotificationDataResponses[keyof AdminDeleteUserNotificationDataResponses];

export type AdminDeleteNotificationDataData = {
    body?: never;
    path: {
        notification_data_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/notifications/data/{notification_data_id}';
};

export type AdminDeleteNotificationDataErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDeleteNotificationDataError = AdminDeleteNotificationDataErrors[keyof AdminDeleteNotificationDataErrors];

export type AdminDeleteNotificationDataResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminDeleteNotificationDataResponse = AdminDeleteNotificationDataResponses[keyof AdminDeleteNotificationDataResponses];

export type AdminDeleteAllNotificationDataData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/notifications/data';
};

export type AdminDeleteAllNotificationDataErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDeleteAllNotificationDataError = AdminDeleteAllNotificationDataErrors[keyof AdminDeleteAllNotificationDataErrors];

export type AdminDeleteAllNotificationDataResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminDeleteAllNotificationDataResponse = AdminDeleteAllNotificationDataResponses[keyof AdminDeleteAllNotificationDataResponses];

export type AdminGetUserNotificationDisplaysData = {
    body?: never;
    path: {
        user_id: string;
        trigger: NotificationTriggerEnum;
    };
    query?: never;
    url: '/api/v4/admin/items/notifications/user/displays/{user_id}/{trigger}';
};

export type AdminGetUserNotificationDisplaysErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetUserNotificationDisplaysError = AdminGetUserNotificationDisplaysErrors[keyof AdminGetUserNotificationDisplaysErrors];

export type AdminGetUserNotificationDisplaysResponses = {
    /**
     * Successful Response
     */
    200: AdminUserDisplaysResponse;
};

export type AdminGetUserNotificationDisplaysResponse = AdminGetUserNotificationDisplaysResponses[keyof AdminGetUserNotificationDisplaysResponses];

export type DeleteExpiredUserNotificationsDataData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/notifications/expired';
};

export type DeleteExpiredUserNotificationsDataErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
};

export type DeleteExpiredUserNotificationsDataError = DeleteExpiredUserNotificationsDataErrors[keyof DeleteExpiredUserNotificationsDataErrors];

export type DeleteExpiredUserNotificationsDataResponses = {
    /**
     * Successful Response
     */
    200: DeleteResponse;
};

export type DeleteExpiredUserNotificationsDataResponse = DeleteExpiredUserNotificationsDataResponses[keyof DeleteExpiredUserNotificationsDataResponses];

export type AdminNotifyUserDesktopData = {
    body: NotifyUserDesktopRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/notify/user/desktop';
};

export type AdminNotifyUserDesktopErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminNotifyUserDesktopError = AdminNotifyUserDesktopErrors[keyof AdminNotifyUserDesktopErrors];

export type AdminNotifyUserDesktopResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminNotifyUserDesktopResponse = AdminNotifyUserDesktopResponses[keyof AdminNotifyUserDesktopResponses];

export type AdminNotifyDesktopData = {
    body: NotifyDesktopRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/notify/desktop';
};

export type AdminNotifyDesktopErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminNotifyDesktopError = AdminNotifyDesktopErrors[keyof AdminNotifyDesktopErrors];

export type AdminNotifyDesktopResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminNotifyDesktopResponse = AdminNotifyDesktopResponses[keyof AdminNotifyDesktopResponses];

export type AdminNotifyDesktopQueueData = {
    body: AdminNotifyDesktopsQueueRequest;
    path: {
        hyp_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/notify/desktops/queue/{hyp_id}';
};

export type AdminNotifyDesktopQueueErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminNotifyDesktopQueueError = AdminNotifyDesktopQueueErrors[keyof AdminNotifyDesktopQueueErrors];

export type AdminNotifyDesktopQueueResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminNotifyDesktopQueueResponse = AdminNotifyDesktopQueueResponses[keyof AdminNotifyDesktopQueueResponses];

export type AdminOperationsHypervisorsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/operations/hypervisors';
};

export type AdminOperationsHypervisorsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminOperationsHypervisorsError = AdminOperationsHypervisorsErrors[keyof AdminOperationsHypervisorsErrors];

export type AdminOperationsHypervisorsResponses = {
    /**
     * Successful Response
     */
    200: Array<OperationsHypervisorResponse>;
};

export type AdminOperationsHypervisorsResponse = AdminOperationsHypervisorsResponses[keyof AdminOperationsHypervisorsResponses];

export type AdminOperationsHypervisorStopData = {
    body?: never;
    path: {
        hypervisor_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/operations/hypervisor/{hypervisor_id}';
};

export type AdminOperationsHypervisorStopErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminOperationsHypervisorStopError = AdminOperationsHypervisorStopErrors[keyof AdminOperationsHypervisorStopErrors];

export type AdminOperationsHypervisorStopResponses = {
    /**
     * Successful Response
     */
    200: HypervisorActionResponse;
};

export type AdminOperationsHypervisorStopResponse = AdminOperationsHypervisorStopResponses[keyof AdminOperationsHypervisorStopResponses];

export type AdminOperationsHypervisorStartData = {
    body?: never;
    path: {
        hypervisor_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/operations/hypervisor/{hypervisor_id}';
};

export type AdminOperationsHypervisorStartErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminOperationsHypervisorStartError = AdminOperationsHypervisorStartErrors[keyof AdminOperationsHypervisorStartErrors];

export type AdminOperationsHypervisorStartResponses = {
    /**
     * Successful Response
     */
    200: HypervisorActionResponse;
};

export type AdminOperationsHypervisorStartResponse = AdminOperationsHypervisorStartResponses[keyof AdminOperationsHypervisorStartResponses];

export type AdminOrchestratorConfigData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/orchestrator-config';
};

export type AdminOrchestratorConfigErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminOrchestratorConfigError = AdminOrchestratorConfigErrors[keyof AdminOrchestratorConfigErrors];

export type AdminOrchestratorConfigResponses = {
    /**
     * Successful Response
     */
    200: OrchestratorConfigResponse;
};

export type AdminOrchestratorConfigResponse = AdminOrchestratorConfigResponses[keyof AdminOrchestratorConfigResponses];

export type AdminOrchestratorConfigUpdateData = {
    body: OrchestratorConfigUpdateRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/orchestrator-config';
};

export type AdminOrchestratorConfigUpdateErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminOrchestratorConfigUpdateError = AdminOrchestratorConfigUpdateErrors[keyof AdminOrchestratorConfigUpdateErrors];

export type AdminOrchestratorConfigUpdateResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminOrchestratorConfigUpdateResponse = AdminOrchestratorConfigUpdateResponses[keyof AdminOrchestratorConfigUpdateResponses];

export type AdminQueuesJobsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/queues';
};

export type AdminQueuesJobsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminQueuesJobsError = AdminQueuesJobsErrors[keyof AdminQueuesJobsErrors];

export type AdminQueuesJobsResponses = {
    /**
     * Successful Response
     */
    200: Array<QueueJobsResponse>;
};

export type AdminQueuesJobsResponse = AdminQueuesJobsResponses[keyof AdminQueuesJobsResponses];

export type AdminQueuesConsumersData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/queues/consumers';
};

export type AdminQueuesConsumersErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminQueuesConsumersError = AdminQueuesConsumersErrors[keyof AdminQueuesConsumersErrors];

export type AdminQueuesConsumersResponses = {
    /**
     * Successful Response
     */
    200: Array<QueueConsumerResponse>;
};

export type AdminQueuesConsumersResponse = AdminQueuesConsumersResponses[keyof AdminQueuesConsumersResponses];

export type AdminQueuesLaneHealthData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/queues/lane-health';
};

export type AdminQueuesLaneHealthErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminQueuesLaneHealthError = AdminQueuesLaneHealthErrors[keyof AdminQueuesLaneHealthErrors];

export type AdminQueuesLaneHealthResponses = {
    /**
     * Successful Response
     */
    200: StorageLaneHealthResponse;
};

export type AdminQueuesLaneHealthResponse = AdminQueuesLaneHealthResponses[keyof AdminQueuesLaneHealthResponses];

export type AdminQueuesGovernorData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/queues/governor';
};

export type AdminQueuesGovernorErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminQueuesGovernorError = AdminQueuesGovernorErrors[keyof AdminQueuesGovernorErrors];

export type AdminQueuesGovernorResponses = {
    /**
     * Successful Response
     */
    200: GovernorGaugesResponse;
};

export type AdminQueuesGovernorResponse = AdminQueuesGovernorResponses[keyof AdminQueuesGovernorResponses];

export type AdminQueuesBacklogData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/queues/backlog';
};

export type AdminQueuesBacklogErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminQueuesBacklogError = AdminQueuesBacklogErrors[keyof AdminQueuesBacklogErrors];

export type AdminQueuesBacklogResponses = {
    /**
     * Successful Response
     */
    200: Array<BacklogRollupRow>;
};

export type AdminQueuesBacklogResponse = AdminQueuesBacklogResponses[keyof AdminQueuesBacklogResponses];

export type AdminQueuesProblemTasksData = {
    body?: never;
    path?: never;
    query?: {
        kind?: 'failed' | 'deferred_orphan' | 'stuck_running' | 'all';
        pool?: string | null;
        category_id?: string | null;
        tier?: 'interactive' | 'standard' | 'template' | 'bulk' | 'maintenance' | 'reclaim' | null;
        limit?: number;
        offset?: number;
    };
    url: '/api/v4/admin/items/queues/tasks/problems';
};

export type AdminQueuesProblemTasksErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminQueuesProblemTasksError = AdminQueuesProblemTasksErrors[keyof AdminQueuesProblemTasksErrors];

export type AdminQueuesProblemTasksResponses = {
    /**
     * Successful Response
     */
    200: ProblemTasksResponse;
};

export type AdminQueuesProblemTasksResponse = AdminQueuesProblemTasksResponses[keyof AdminQueuesProblemTasksResponses];

export type AdminGetOldTasksConfigData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/queues/old_tasks/config';
};

export type AdminGetOldTasksConfigErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetOldTasksConfigError = AdminGetOldTasksConfigErrors[keyof AdminGetOldTasksConfigErrors];

export type AdminGetOldTasksConfigResponses = {
    /**
     * Successful Response
     */
    200: AutoDeleteConfigResponse;
};

export type AdminGetOldTasksConfigResponse = AdminGetOldTasksConfigResponses[keyof AdminGetOldTasksConfigResponses];

export type AdminGetOldTasksData = {
    body?: never;
    path: {
        older_than: number;
    };
    query?: never;
    url: '/api/v4/admin/items/queues/old_tasks/{older_than}';
};

export type AdminGetOldTasksErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetOldTasksError = AdminGetOldTasksErrors[keyof AdminGetOldTasksErrors];

export type AdminGetOldTasksResponses = {
    /**
     * Successful Response
     */
    200: Array<string>;
};

export type AdminGetOldTasksResponse = AdminGetOldTasksResponses[keyof AdminGetOldTasksResponses];

export type AdminDeleteOldTasksData = {
    body: DeleteOldTasksRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/queues/old_tasks';
};

export type AdminDeleteOldTasksErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDeleteOldTasksError = AdminDeleteOldTasksErrors[keyof AdminDeleteOldTasksErrors];

export type AdminDeleteOldTasksResponses = {
    /**
     * Successful Response
     */
    200: DeleteOldTasksResult;
};

export type AdminDeleteOldTasksResponse = AdminDeleteOldTasksResponses[keyof AdminDeleteOldTasksResponses];

export type AdminSetOldTasksMaxTimeData = {
    body?: never;
    path: {
        max_time: number;
    };
    query?: never;
    url: '/api/v4/admin/item/queues/old_tasks/config/max_time/{max_time}';
};

export type AdminSetOldTasksMaxTimeErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminSetOldTasksMaxTimeError = AdminSetOldTasksMaxTimeErrors[keyof AdminSetOldTasksMaxTimeErrors];

export type AdminSetOldTasksMaxTimeResponses = {
    /**
     * Successful Response
     */
    200: AutoDeleteMaxTimeResponse;
};

export type AdminSetOldTasksMaxTimeResponse = AdminSetOldTasksMaxTimeResponses[keyof AdminSetOldTasksMaxTimeResponses];

export type AdminGetStorageSchedulerConfigData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/queues/storage_scheduler/config';
};

export type AdminGetStorageSchedulerConfigErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetStorageSchedulerConfigError = AdminGetStorageSchedulerConfigErrors[keyof AdminGetStorageSchedulerConfigErrors];

export type AdminGetStorageSchedulerConfigResponses = {
    /**
     * Successful Response
     */
    200: StorageSchedulerConfigResponse;
};

export type AdminGetStorageSchedulerConfigResponse = AdminGetStorageSchedulerConfigResponses[keyof AdminGetStorageSchedulerConfigResponses];

export type AdminSetStorageSchedulerConfigData = {
    body: StorageSchedulerConfigRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/queues/storage_scheduler/config';
};

export type AdminSetStorageSchedulerConfigErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminSetStorageSchedulerConfigError = AdminSetStorageSchedulerConfigErrors[keyof AdminSetStorageSchedulerConfigErrors];

export type AdminSetStorageSchedulerConfigResponses = {
    /**
     * Successful Response
     */
    200: StorageSchedulerConfigResponse;
};

export type AdminSetStorageSchedulerConfigResponse = AdminSetStorageSchedulerConfigResponses[keyof AdminSetStorageSchedulerConfigResponses];

export type AdminSetOldTasksQueueRegistriesData = {
    body: QueueRegistriesRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/queues/old_tasks/config/queue_registries';
};

export type AdminSetOldTasksQueueRegistriesErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminSetOldTasksQueueRegistriesError = AdminSetOldTasksQueueRegistriesErrors[keyof AdminSetOldTasksQueueRegistriesErrors];

export type AdminSetOldTasksQueueRegistriesResponses = {
    /**
     * Successful Response
     */
    200: AutoDeleteQueueRegistriesResponse;
};

export type AdminSetOldTasksQueueRegistriesResponse = AdminSetOldTasksQueueRegistriesResponses[keyof AdminSetOldTasksQueueRegistriesResponses];

export type AdminSetOldTasksEnabledData = {
    body: AutoDeleteEnabledRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/queues/old_tasks/config/enabled';
};

export type AdminSetOldTasksEnabledErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminSetOldTasksEnabledError = AdminSetOldTasksEnabledErrors[keyof AdminSetOldTasksEnabledErrors];

export type AdminSetOldTasksEnabledResponses = {
    /**
     * Successful Response
     */
    200: AutoDeleteEnabledResponse;
};

export type AdminSetOldTasksEnabledResponse = AdminSetOldTasksEnabledResponses[keyof AdminSetOldTasksEnabledResponses];

export type AdminDeleteOldTasksAutoData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/queues/old_tasks/auto';
};

export type AdminDeleteOldTasksAutoErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDeleteOldTasksAutoError = AdminDeleteOldTasksAutoErrors[keyof AdminDeleteOldTasksAutoErrors];

export type AdminDeleteOldTasksAutoResponses = {
    /**
     * Successful Response
     */
    200: DeleteOldTasksResult;
};

export type AdminDeleteOldTasksAutoResponse = AdminDeleteOldTasksAutoResponses[keyof AdminDeleteOldTasksAutoResponses];

export type AdminRemoteVpnWithOsData = {
    body?: never;
    path: {
        /**
         * VPN ID
         */
        vpn_id: string;
        /**
         * VPN data kind: config or install
         */
        kind: string;
        /**
         * Operating system
         */
        os: string;
    };
    query?: never;
    url: '/api/v4/admin/item/remote_vpn/{vpn_id}/{kind}/{os}';
};

export type AdminRemoteVpnWithOsErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminRemoteVpnWithOsError = AdminRemoteVpnWithOsErrors[keyof AdminRemoteVpnWithOsErrors];

export type AdminRemoteVpnWithOsResponses = {
    /**
     * Successful Response
     */
    200: RemoteVpnResponse;
};

export type AdminRemoteVpnWithOsResponse = AdminRemoteVpnWithOsResponses[keyof AdminRemoteVpnWithOsResponses];

export type AdminRemoteVpnData = {
    body?: never;
    path: {
        /**
         * VPN ID
         */
        vpn_id: string;
        /**
         * VPN data kind: config or install
         */
        kind: string;
    };
    query?: never;
    url: '/api/v4/admin/item/remote_vpn/{vpn_id}/{kind}';
};

export type AdminRemoteVpnErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminRemoteVpnError = AdminRemoteVpnErrors[keyof AdminRemoteVpnErrors];

export type AdminRemoteVpnResponses = {
    /**
     * Successful Response
     */
    200: RemoteVpnResponse;
};

export type AdminRemoteVpnResponse = AdminRemoteVpnResponses[keyof AdminRemoteVpnResponses];

export type AdminQosDiskAddData = {
    body: QosDiskCreateRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/qos_disk';
};

export type AdminQosDiskAddErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Conflict
     */
    409: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminQosDiskAddError = AdminQosDiskAddErrors[keyof AdminQosDiskAddErrors];

export type AdminQosDiskAddResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminQosDiskAddResponse = AdminQosDiskAddResponses[keyof AdminQosDiskAddResponses];

export type AdminQosDiskUpdateData = {
    body: QosDiskUpdateRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/qos_disk';
};

export type AdminQosDiskUpdateErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Conflict
     */
    409: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminQosDiskUpdateError = AdminQosDiskUpdateErrors[keyof AdminQosDiskUpdateErrors];

export type AdminQosDiskUpdateResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminQosDiskUpdateResponse = AdminQosDiskUpdateResponses[keyof AdminQosDiskUpdateResponses];

export type AdminGetRoleData = {
    body?: never;
    path: {
        role_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/role/{role_id}';
};

export type AdminGetRoleErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetRoleError = AdminGetRoleErrors[keyof AdminGetRoleErrors];

export type AdminGetRoleResponses = {
    /**
     * Successful Response
     */
    200: RoleResponse;
};

export type AdminGetRoleResponse = AdminGetRoleResponses[keyof AdminGetRoleResponses];

export type AdminUpdateRoleData = {
    body: AdminRoleUpdateData;
    path: {
        role_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/role/{role_id}';
};

export type AdminUpdateRoleErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUpdateRoleError = AdminUpdateRoleErrors[keyof AdminUpdateRoleErrors];

export type AdminUpdateRoleResponses = {
    /**
     * Role updated
     */
    204: void;
};

export type AdminUpdateRoleResponse = AdminUpdateRoleResponses[keyof AdminUpdateRoleResponses];

export type AdminSchedulerJobsSystemData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/scheduler/jobs/system';
};

export type AdminSchedulerJobsSystemErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminSchedulerJobsSystemError = AdminSchedulerJobsSystemErrors[keyof AdminSchedulerJobsSystemErrors];

export type AdminSchedulerJobsSystemResponses = {
    /**
     * Successful Response
     */
    200: Array<SchedulerSystemJob>;
};

export type AdminSchedulerJobsSystemResponse = AdminSchedulerJobsSystemResponses[keyof AdminSchedulerJobsSystemResponses];

export type AdminSchedulerJobsBookingsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/scheduler/jobs/bookings';
};

export type AdminSchedulerJobsBookingsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminSchedulerJobsBookingsError = AdminSchedulerJobsBookingsErrors[keyof AdminSchedulerJobsBookingsErrors];

export type AdminSchedulerJobsBookingsResponses = {
    /**
     * Successful Response
     */
    200: Array<SchedulerBookingsJob>;
};

export type AdminSchedulerJobsBookingsResponse = AdminSchedulerJobsBookingsResponses[keyof AdminSchedulerJobsBookingsResponses];

export type AdminSmtpGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/smtp';
};

export type AdminSmtpGetErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminSmtpGetError = AdminSmtpGetErrors[keyof AdminSmtpGetErrors];

export type AdminSmtpGetResponses = {
    /**
     * Successful Response
     */
    200: SmtpConfigResponse;
};

export type AdminSmtpGetResponse = AdminSmtpGetResponses[keyof AdminSmtpGetResponses];

export type AdminSmtpPutData = {
    body: SmtpConfigRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/smtp';
};

export type AdminSmtpPutErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminSmtpPutError = AdminSmtpPutErrors[keyof AdminSmtpPutErrors];

export type AdminSmtpPutResponses = {
    /**
     * Successful Response
     */
    200: SmtpConfigResponse;
};

export type AdminSmtpPutResponse = AdminSmtpPutResponses[keyof AdminSmtpPutResponses];

export type AdminSmtpEnabledGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/smtp/enabled';
};

export type AdminSmtpEnabledGetErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminSmtpEnabledGetError = AdminSmtpEnabledGetErrors[keyof AdminSmtpEnabledGetErrors];

export type AdminSmtpEnabledGetResponses = {
    /**
     * Successful Response
     */
    200: boolean;
};

export type AdminSmtpEnabledGetResponse = AdminSmtpEnabledGetResponses[keyof AdminSmtpEnabledGetResponses];

export type AdminSmtpTestPostData = {
    body: SmtpConfigRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/smtp/test';
};

export type AdminSmtpTestPostErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminSmtpTestPostError = AdminSmtpTestPostErrors[keyof AdminSmtpTestPostErrors];

export type AdminSmtpTestPostResponses = {
    /**
     * Successful Response
     */
    200: SmtpTestResponse;
};

export type AdminSmtpTestPostResponse = AdminSmtpTestPostResponses[keyof AdminSmtpTestPostResponses];

export type AdminEmitSocketioData = {
    body: AdminSocketioEmitRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/socketio';
};

export type AdminEmitSocketioErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminEmitSocketioError = AdminEmitSocketioErrors[keyof AdminEmitSocketioErrors];

export type AdminEmitSocketioResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminEmitSocketioResponse = AdminEmitSocketioResponses[keyof AdminEmitSocketioResponses];

export type StatsDesktopsStatusData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/stats/desktops/status';
};

export type StatsDesktopsStatusErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type StatsDesktopsStatusError = StatsDesktopsStatusErrors[keyof StatsDesktopsStatusErrors];

export type StatsDesktopsStatusResponses = {
    /**
     * Successful Response
     */
    200: StatsGenericResponse;
};

export type StatsDesktopsStatusResponse = StatsDesktopsStatusResponses[keyof StatsDesktopsStatusResponses];

export type StatsDomainsStatusData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/stats/domains/status';
};

export type StatsDomainsStatusErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type StatsDomainsStatusError = StatsDomainsStatusErrors[keyof StatsDomainsStatusErrors];

export type StatsDomainsStatusResponses = {
    /**
     * Successful Response
     */
    200: StatsDomainsStatusResponse;
};

export type StatsDomainsStatusResponse2 = StatsDomainsStatusResponses[keyof StatsDomainsStatusResponses];

export type StatsCategoriesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/stats/categories';
};

export type StatsCategoriesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type StatsCategoriesError = StatsCategoriesErrors[keyof StatsCategoriesErrors];

export type StatsCategoriesResponses = {
    /**
     * Successful Response
     */
    200: StatsCategoriesResponse;
};

export type StatsCategoriesResponse2 = StatsCategoriesResponses[keyof StatsCategoriesResponses];

export type StatsCategoriesDeploymentsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/stats/categories/deployments';
};

export type StatsCategoriesDeploymentsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type StatsCategoriesDeploymentsError = StatsCategoriesDeploymentsErrors[keyof StatsCategoriesDeploymentsErrors];

export type StatsCategoriesDeploymentsResponses = {
    /**
     * Successful Response
     */
    200: StatsCategoriesDeploymentsResponse;
};

export type StatsCategoriesDeploymentsResponse2 = StatsCategoriesDeploymentsResponses[keyof StatsCategoriesDeploymentsResponses];

export type StatsUsersData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/stats/users';
};

export type StatsUsersErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type StatsUsersError = StatsUsersErrors[keyof StatsUsersErrors];

export type StatsUsersResponses = {
    /**
     * Successful Response
     */
    200: Array<StatsKindUser>;
};

export type StatsUsersResponse = StatsUsersResponses[keyof StatsUsersResponses];

export type StatsDesktopsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/stats/desktops';
};

export type StatsDesktopsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type StatsDesktopsError = StatsDesktopsErrors[keyof StatsDesktopsErrors];

export type StatsDesktopsResponses = {
    /**
     * Successful Response
     */
    200: Array<StatsKindDesktop>;
};

export type StatsDesktopsResponse = StatsDesktopsResponses[keyof StatsDesktopsResponses];

export type StatsTemplatesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/stats/templates';
};

export type StatsTemplatesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type StatsTemplatesError = StatsTemplatesErrors[keyof StatsTemplatesErrors];

export type StatsTemplatesResponses = {
    /**
     * Successful Response
     */
    200: Array<StatsKindTemplate>;
};

export type StatsTemplatesResponse = StatsTemplatesResponses[keyof StatsTemplatesResponses];

export type StatsHypervisorsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/stats/hypervisors';
};

export type StatsHypervisorsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type StatsHypervisorsError = StatsHypervisorsErrors[keyof StatsHypervisorsErrors];

export type StatsHypervisorsResponses = {
    /**
     * Successful Response
     */
    200: Array<StatsKindHypervisor>;
};

export type StatsHypervisorsResponse = StatsHypervisorsResponses[keyof StatsHypervisorsResponses];

export type AdminDomainsStartedCountData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/domains/started-count';
};

export type AdminDomainsStartedCountErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDomainsStartedCountError = AdminDomainsStartedCountErrors[keyof AdminDomainsStartedCountErrors];

export type AdminDomainsStartedCountResponses = {
    /**
     * Successful Response
     */
    200: Array<StatsGenericResponse>;
};

export type AdminDomainsStartedCountResponse = AdminDomainsStartedCountResponses[keyof AdminDomainsStartedCountResponses];

export type AdminStorageByRoleData = {
    body?: never;
    path: {
        /**
         * User role to filter by
         */
        role: string;
    };
    query?: never;
    url: '/api/v4/admin/items/storage/by-role/{role}';
};

export type AdminStorageByRoleErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminStorageByRoleError = AdminStorageByRoleErrors[keyof AdminStorageByRoleErrors];

export type AdminStorageByRoleResponses = {
    /**
     * Successful Response
     */
    200: Array<AdminStorageItem>;
};

export type AdminStorageByRoleResponse = AdminStorageByRoleResponses[keyof AdminStorageByRoleResponses];

export type AdminStorageRefreshRunningSizesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/storage/refresh-running-sizes';
};

export type AdminStorageRefreshRunningSizesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminStorageRefreshRunningSizesError = AdminStorageRefreshRunningSizesErrors[keyof AdminStorageRefreshRunningSizesErrors];

export type AdminStorageRefreshRunningSizesResponses = {
    /**
     * Successful Response
     */
    200: AdminStorageRefreshResponse;
};

export type AdminStorageRefreshRunningSizesResponse = AdminStorageRefreshRunningSizesResponses[keyof AdminStorageRefreshRunningSizesResponses];

export type AdminStorageMigrationPlanData = {
    body: MigrationPlanData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/storage/migrations/plan';
};

export type AdminStorageMigrationPlanErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminStorageMigrationPlanError = AdminStorageMigrationPlanErrors[keyof AdminStorageMigrationPlanErrors];

export type AdminStorageMigrationPlanResponses = {
    /**
     * Successful Response
     */
    200: MigrationPlanResponse;
};

export type AdminStorageMigrationPlanResponse = AdminStorageMigrationPlanResponses[keyof AdminStorageMigrationPlanResponses];

export type AdminStorageMigrationListData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/storage/migrations';
};

export type AdminStorageMigrationListErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminStorageMigrationListError = AdminStorageMigrationListErrors[keyof AdminStorageMigrationListErrors];

export type AdminStorageMigrationListResponses = {
    /**
     * Successful Response
     */
    200: MigrationListResponse;
};

export type AdminStorageMigrationListResponse = AdminStorageMigrationListResponses[keyof AdminStorageMigrationListResponses];

export type AdminStorageMigrationCreateData = {
    body: MigrationCreateData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/storage/migrations';
};

export type AdminStorageMigrationCreateErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminStorageMigrationCreateError = AdminStorageMigrationCreateErrors[keyof AdminStorageMigrationCreateErrors];

export type AdminStorageMigrationCreateResponses = {
    /**
     * Successful Response
     */
    200: MigrationResponse;
};

export type AdminStorageMigrationCreateResponse = AdminStorageMigrationCreateResponses[keyof AdminStorageMigrationCreateResponses];

export type AdminStorageMigrationPathPrefixesData = {
    body?: never;
    path?: never;
    query?: {
        src_pool_id?: string | null;
    };
    url: '/api/v4/admin/storage/migrations/path-prefixes';
};

export type AdminStorageMigrationPathPrefixesErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminStorageMigrationPathPrefixesError = AdminStorageMigrationPathPrefixesErrors[keyof AdminStorageMigrationPathPrefixesErrors];

export type AdminStorageMigrationPathPrefixesResponses = {
    /**
     * Successful Response
     */
    200: MigrationPathPrefixesResponse;
};

export type AdminStorageMigrationPathPrefixesResponse = AdminStorageMigrationPathPrefixesResponses[keyof AdminStorageMigrationPathPrefixesResponses];

export type AdminStorageMigrationLogData = {
    body?: never;
    path: {
        migration_id: string;
    };
    query?: {
        format?: 'csv' | 'json';
    };
    url: '/api/v4/admin/storage/migrations/{migration_id}/log';
};

export type AdminStorageMigrationLogErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminStorageMigrationLogError = AdminStorageMigrationLogErrors[keyof AdminStorageMigrationLogErrors];

export type AdminStorageMigrationLogResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type AdminStorageMigrationTreesData = {
    body?: never;
    path: {
        migration_id: string;
    };
    query?: {
        page?: number;
        per_page?: number;
        state?: string | null;
        q?: string | null;
    };
    url: '/api/v4/admin/storage/migrations/{migration_id}/trees';
};

export type AdminStorageMigrationTreesErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminStorageMigrationTreesError = AdminStorageMigrationTreesErrors[keyof AdminStorageMigrationTreesErrors];

export type AdminStorageMigrationTreesResponses = {
    /**
     * Successful Response
     */
    200: MigrationTreesPageResponse;
};

export type AdminStorageMigrationTreesResponse = AdminStorageMigrationTreesResponses[keyof AdminStorageMigrationTreesResponses];

export type AdminStorageMigrationItemsData = {
    body?: never;
    path: {
        migration_id: string;
    };
    query?: {
        page?: number;
        per_page?: number;
        state?: string | null;
        tree_id?: string | null;
        q?: string | null;
    };
    url: '/api/v4/admin/storage/migrations/{migration_id}/items';
};

export type AdminStorageMigrationItemsErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminStorageMigrationItemsError = AdminStorageMigrationItemsErrors[keyof AdminStorageMigrationItemsErrors];

export type AdminStorageMigrationItemsResponses = {
    /**
     * Successful Response
     */
    200: MigrationItemsPageResponse;
};

export type AdminStorageMigrationItemsResponse = AdminStorageMigrationItemsResponses[keyof AdminStorageMigrationItemsResponses];

export type AdminStorageMigrationDeleteData = {
    body?: never;
    path: {
        migration_id: string;
    };
    query?: never;
    url: '/api/v4/admin/storage/migrations/{migration_id}';
};

export type AdminStorageMigrationDeleteErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminStorageMigrationDeleteError = AdminStorageMigrationDeleteErrors[keyof AdminStorageMigrationDeleteErrors];

export type AdminStorageMigrationDeleteResponses = {
    /**
     * Successful Response
     */
    200: MigrationDeleteResponse;
};

export type AdminStorageMigrationDeleteResponse = AdminStorageMigrationDeleteResponses[keyof AdminStorageMigrationDeleteResponses];

export type AdminStorageMigrationStatusData = {
    body?: never;
    path: {
        migration_id: string;
    };
    query?: {
        items?: boolean;
    };
    url: '/api/v4/admin/storage/migrations/{migration_id}';
};

export type AdminStorageMigrationStatusErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminStorageMigrationStatusError = AdminStorageMigrationStatusErrors[keyof AdminStorageMigrationStatusErrors];

export type AdminStorageMigrationStatusResponses = {
    /**
     * Successful Response
     */
    200: MigrationStatusResponse;
};

export type AdminStorageMigrationStatusResponse = AdminStorageMigrationStatusResponses[keyof AdminStorageMigrationStatusResponses];

export type AdminStorageMigrationCheckData = {
    body?: never;
    path: {
        migration_id: string;
    };
    query?: never;
    url: '/api/v4/admin/storage/migrations/{migration_id}/check';
};

export type AdminStorageMigrationCheckErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminStorageMigrationCheckError = AdminStorageMigrationCheckErrors[keyof AdminStorageMigrationCheckErrors];

export type AdminStorageMigrationCheckResponses = {
    /**
     * Successful Response
     */
    200: MigrationCheckResponse;
};

export type AdminStorageMigrationCheckResponse = AdminStorageMigrationCheckResponses[keyof AdminStorageMigrationCheckResponses];

export type AdminStorageMigrationActionData = {
    body?: never;
    path: {
        migration_id: string;
        action: 'start' | 'pause' | 'cancel';
    };
    query?: never;
    url: '/api/v4/admin/storage/migrations/{migration_id}/{action}';
};

export type AdminStorageMigrationActionErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminStorageMigrationActionError = AdminStorageMigrationActionErrors[keyof AdminStorageMigrationActionErrors];

export type AdminStorageMigrationActionResponses = {
    /**
     * Successful Response
     */
    200: MigrationResponse;
};

export type AdminStorageMigrationActionResponse = AdminStorageMigrationActionResponses[keyof AdminStorageMigrationActionResponses];

export type AdminStorageMigrationConfigData = {
    body: MigrationConfigUpdateData;
    path: {
        migration_id: string;
    };
    query?: never;
    url: '/api/v4/admin/storage/migrations/{migration_id}/config';
};

export type AdminStorageMigrationConfigErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminStorageMigrationConfigError = AdminStorageMigrationConfigErrors[keyof AdminStorageMigrationConfigErrors];

export type AdminStorageMigrationConfigResponses = {
    /**
     * Successful Response
     */
    200: MigrationResponse;
};

export type AdminStorageMigrationConfigResponse = AdminStorageMigrationConfigResponses[keyof AdminStorageMigrationConfigResponses];

export type AdminStoragePoolMigrationPlanData = {
    body?: never;
    path: {
        pool_id: string;
    };
    query?: never;
    url: '/api/v4/admin/storage-pool/{pool_id}/migration/plan';
};

export type AdminStoragePoolMigrationPlanErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminStoragePoolMigrationPlanError = AdminStoragePoolMigrationPlanErrors[keyof AdminStoragePoolMigrationPlanErrors];

export type AdminStoragePoolMigrationPlanResponses = {
    /**
     * Successful Response
     */
    200: PoolPlanResponse;
};

export type AdminStoragePoolMigrationPlanResponse = AdminStoragePoolMigrationPlanResponses[keyof AdminStoragePoolMigrationPlanResponses];

export type AdminSystemLogoGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/logo';
};

export type AdminSystemLogoGetErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminSystemLogoGetError = AdminSystemLogoGetErrors[keyof AdminSystemLogoGetErrors];

export type AdminSystemLogoGetResponses = {
    /**
     * Successful Response
     */
    200: SystemLogoResponse;
};

export type AdminSystemLogoGetResponse = AdminSystemLogoGetResponses[keyof AdminSystemLogoGetResponses];

export type AdminSystemLogoPutData = {
    body: SystemLogoUpdateData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/logo';
};

export type AdminSystemLogoPutErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminSystemLogoPutError = AdminSystemLogoPutErrors[keyof AdminSystemLogoPutErrors];

export type AdminSystemLogoPutResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminSystemLogoPutResponse = AdminSystemLogoPutResponses[keyof AdminSystemLogoPutResponses];

export type AdminTableInsertData = {
    body: {
        [key: string]: unknown;
    };
    path: {
        /**
         * Database table name
         */
        table: string;
    };
    query?: never;
    url: '/api/v4/admin/item/table/add/{table}';
};

export type AdminTableInsertErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Conflict
     */
    409: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminTableInsertError = AdminTableInsertErrors[keyof AdminTableInsertErrors];

export type AdminTableInsertResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminTableInsertResponse = AdminTableInsertResponses[keyof AdminTableInsertResponses];

export type AdminTableUpdateData = {
    body: {
        [key: string]: unknown;
    };
    path: {
        /**
         * Database table name
         */
        table: string;
    };
    query?: never;
    url: '/api/v4/admin/item/table/update/{table}';
};

export type AdminTableUpdateErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminTableUpdateError = AdminTableUpdateErrors[keyof AdminTableUpdateErrors];

export type AdminTableUpdateResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminTableUpdateResponse = AdminTableUpdateResponses[keyof AdminTableUpdateResponses];

export type AdminTableDeleteData = {
    body?: never;
    path: {
        /**
         * Database table name
         */
        table: string;
        /**
         * Item ID to delete
         */
        item_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/table/{table}/{item_id}';
};

export type AdminTableDeleteErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminTableDeleteError = AdminTableDeleteErrors[keyof AdminTableDeleteErrors];

export type AdminTableDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminTableDeleteResponse = AdminTableDeleteResponses[keyof AdminTableDeleteResponses];

export type AdminUsageConsumersCountData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/usage/consumers';
};

export type AdminUsageConsumersCountErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageConsumersCountError = AdminUsageConsumersCountErrors[keyof AdminUsageConsumersCountErrors];

export type AdminUsageConsumersCountResponses = {
    /**
     * Successful Response
     */
    200: UsageConsumersCountResponse;
};

export type AdminUsageConsumersCountResponse = AdminUsageConsumersCountResponses[keyof AdminUsageConsumersCountResponses];

export type AdminUsageConsolidateData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/usage/consolidate';
};

export type AdminUsageConsolidateErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageConsolidateError = AdminUsageConsolidateErrors[keyof AdminUsageConsolidateErrors];

export type AdminUsageConsolidateResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminUsageConsolidateResponse = AdminUsageConsolidateResponses[keyof AdminUsageConsolidateResponses];

export type AdminUsageConsolidateItemDaysData = {
    body?: never;
    path: {
        item_type: string;
        days: number;
    };
    query?: never;
    url: '/api/v4/admin/items/usage/consolidate/{item_type}/{days}';
};

export type AdminUsageConsolidateItemDaysErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageConsolidateItemDaysError = AdminUsageConsolidateItemDaysErrors[keyof AdminUsageConsolidateItemDaysErrors];

export type AdminUsageConsolidateItemDaysResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminUsageConsolidateItemDaysResponse = AdminUsageConsolidateItemDaysResponses[keyof AdminUsageConsolidateItemDaysResponses];

export type AdminUsageConsolidateItemData = {
    body?: never;
    path: {
        item_type: string;
    };
    query?: never;
    url: '/api/v4/admin/items/usage/consolidate/{item_type}';
};

export type AdminUsageConsolidateItemErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageConsolidateItemError = AdminUsageConsolidateItemErrors[keyof AdminUsageConsolidateItemErrors];

export type AdminUsageConsolidateItemResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminUsageConsolidateItemResponse = AdminUsageConsolidateItemResponses[keyof AdminUsageConsolidateItemResponses];

export type AdminUsageRetentionGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/usage/retention';
};

export type AdminUsageRetentionGetErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageRetentionGetError = AdminUsageRetentionGetErrors[keyof AdminUsageRetentionGetErrors];

export type AdminUsageRetentionGetResponses = {
    /**
     * Successful Response
     */
    200: UsageRetentionConfig;
};

export type AdminUsageRetentionGetResponse = AdminUsageRetentionGetResponses[keyof AdminUsageRetentionGetResponses];

export type AdminUsageRetentionUpdateData = {
    body: UsageRetentionConfig;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/usage/retention';
};

export type AdminUsageRetentionUpdateErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageRetentionUpdateError = AdminUsageRetentionUpdateErrors[keyof AdminUsageRetentionUpdateErrors];

export type AdminUsageRetentionUpdateResponses = {
    /**
     * Successful Response
     */
    200: UsageRetentionConfig;
};

export type AdminUsageRetentionUpdateResponse = AdminUsageRetentionUpdateResponses[keyof AdminUsageRetentionUpdateResponses];

export type AdminUsageParametersListData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/usage/parameters';
};

export type AdminUsageParametersListErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageParametersListError = AdminUsageParametersListErrors[keyof AdminUsageParametersListErrors];

export type AdminUsageParametersListResponses = {
    /**
     * Successful Response
     */
    200: Array<UsageParameterResponse>;
};

export type AdminUsageParametersListResponse = AdminUsageParametersListResponses[keyof AdminUsageParametersListResponses];

export type AdminUsageParametersAddData = {
    body: UsageParameterCreateRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/usage/parameters';
};

export type AdminUsageParametersAddErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageParametersAddError = AdminUsageParametersAddErrors[keyof AdminUsageParametersAddErrors];

export type AdminUsageParametersAddResponses = {
    /**
     * Successful Response
     */
    200: EmptyResponse;
};

export type AdminUsageParametersAddResponse = AdminUsageParametersAddResponses[keyof AdminUsageParametersAddResponses];

export type AdminUsageParametersDeleteData = {
    body?: never;
    path: {
        /**
         * Parameter ID
         */
        parameter_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/usage/parameters/{parameter_id}';
};

export type AdminUsageParametersDeleteErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageParametersDeleteError = AdminUsageParametersDeleteErrors[keyof AdminUsageParametersDeleteErrors];

export type AdminUsageParametersDeleteResponses = {
    /**
     * Successful Response
     */
    200: EmptyResponse;
};

export type AdminUsageParametersDeleteResponse = AdminUsageParametersDeleteResponses[keyof AdminUsageParametersDeleteResponses];

export type AdminUsageParametersUpdateData = {
    body: UsageParameterUpdateRequest;
    path: {
        parameter_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/usage/parameters/{parameter_id}';
};

export type AdminUsageParametersUpdateErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageParametersUpdateError = AdminUsageParametersUpdateErrors[keyof AdminUsageParametersUpdateErrors];

export type AdminUsageParametersUpdateResponses = {
    /**
     * Successful Response
     */
    200: EmptyResponse;
};

export type AdminUsageParametersUpdateResponse = AdminUsageParametersUpdateResponses[keyof AdminUsageParametersUpdateResponses];

export type AdminUsageLimitsListData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/usage/limits';
};

export type AdminUsageLimitsListErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageLimitsListError = AdminUsageLimitsListErrors[keyof AdminUsageLimitsListErrors];

export type AdminUsageLimitsListResponses = {
    /**
     * Successful Response
     */
    200: Array<UsageLimitResponse>;
};

export type AdminUsageLimitsListResponse = AdminUsageLimitsListResponses[keyof AdminUsageLimitsListResponses];

export type AdminUsageLimitsAddData = {
    body: UsageLimitCreateRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/usage/limits';
};

export type AdminUsageLimitsAddErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageLimitsAddError = AdminUsageLimitsAddErrors[keyof AdminUsageLimitsAddErrors];

export type AdminUsageLimitsAddResponses = {
    /**
     * Successful Response
     */
    200: EmptyResponse;
};

export type AdminUsageLimitsAddResponse = AdminUsageLimitsAddResponses[keyof AdminUsageLimitsAddResponses];

export type AdminUsageLimitsDeleteData = {
    body?: never;
    path: {
        /**
         * Limit ID
         */
        limit_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/usage/limits/{limit_id}';
};

export type AdminUsageLimitsDeleteErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageLimitsDeleteError = AdminUsageLimitsDeleteErrors[keyof AdminUsageLimitsDeleteErrors];

export type AdminUsageLimitsDeleteResponses = {
    /**
     * Successful Response
     */
    200: EmptyResponse;
};

export type AdminUsageLimitsDeleteResponse = AdminUsageLimitsDeleteResponses[keyof AdminUsageLimitsDeleteResponses];

export type AdminUsageLimitsUpdateData = {
    body: UsageLimitUpdateRequest;
    path: {
        limit_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/usage/limits/{limit_id}';
};

export type AdminUsageLimitsUpdateErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageLimitsUpdateError = AdminUsageLimitsUpdateErrors[keyof AdminUsageLimitsUpdateErrors];

export type AdminUsageLimitsUpdateResponses = {
    /**
     * Successful Response
     */
    200: EmptyResponse;
};

export type AdminUsageLimitsUpdateResponse = AdminUsageLimitsUpdateResponses[keyof AdminUsageLimitsUpdateResponses];

export type AdminUsageGroupingsListData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/usage/groupings';
};

export type AdminUsageGroupingsListErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageGroupingsListError = AdminUsageGroupingsListErrors[keyof AdminUsageGroupingsListErrors];

export type AdminUsageGroupingsListResponses = {
    /**
     * Successful Response
     */
    200: Array<UsageGroupingResponse>;
};

export type AdminUsageGroupingsListResponse = AdminUsageGroupingsListResponses[keyof AdminUsageGroupingsListResponses];

export type AdminUsageGroupingsAddData = {
    body: UsageGroupingCreateRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/usage/groupings';
};

export type AdminUsageGroupingsAddErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageGroupingsAddError = AdminUsageGroupingsAddErrors[keyof AdminUsageGroupingsAddErrors];

export type AdminUsageGroupingsAddResponses = {
    /**
     * Successful Response
     */
    200: EmptyResponse;
};

export type AdminUsageGroupingsAddResponse = AdminUsageGroupingsAddResponses[keyof AdminUsageGroupingsAddResponses];

export type AdminUsageGroupingsDeleteData = {
    body?: never;
    path: {
        /**
         * Grouping ID
         */
        grouping_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/usage/groupings/{grouping_id}';
};

export type AdminUsageGroupingsDeleteErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageGroupingsDeleteError = AdminUsageGroupingsDeleteErrors[keyof AdminUsageGroupingsDeleteErrors];

export type AdminUsageGroupingsDeleteResponses = {
    /**
     * Successful Response
     */
    200: EmptyResponse;
};

export type AdminUsageGroupingsDeleteResponse = AdminUsageGroupingsDeleteResponses[keyof AdminUsageGroupingsDeleteResponses];

export type AdminUsageGroupingsUpdateData = {
    body: UsageGroupingUpdateRequest;
    path: {
        grouping_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/usage/groupings/{grouping_id}';
};

export type AdminUsageGroupingsUpdateErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageGroupingsUpdateError = AdminUsageGroupingsUpdateErrors[keyof AdminUsageGroupingsUpdateErrors];

export type AdminUsageGroupingsUpdateResponses = {
    /**
     * Successful Response
     */
    200: EmptyResponse;
};

export type AdminUsageGroupingsUpdateResponse = AdminUsageGroupingsUpdateResponses[keyof AdminUsageGroupingsUpdateResponses];

export type AdminUsageAllCreditsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/usage/category_credits';
};

export type AdminUsageAllCreditsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageAllCreditsError = AdminUsageAllCreditsErrors[keyof AdminUsageAllCreditsErrors];

export type AdminUsageAllCreditsResponses = {
    /**
     * Successful Response
     */
    200: Array<UsageCreditResponse>;
};

export type AdminUsageAllCreditsResponse = AdminUsageAllCreditsResponses[keyof AdminUsageAllCreditsResponses];

export type AdminUsageCreditsByIdData = {
    body?: never;
    path: {
        category_credit_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/usage/category_credits/{category_credit_id}';
};

export type AdminUsageCreditsByIdErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageCreditsByIdError = AdminUsageCreditsByIdErrors[keyof AdminUsageCreditsByIdErrors];

export type AdminUsageCreditsByIdResponses = {
    /**
     * Successful Response
     */
    200: UsageCreditResponse;
};

export type AdminUsageCreditsByIdResponse = AdminUsageCreditsByIdResponses[keyof AdminUsageCreditsByIdResponses];

export type AdminUsageCreditsAddData = {
    body: UsageCreditCreateRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/usage/credits';
};

export type AdminUsageCreditsAddErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageCreditsAddError = AdminUsageCreditsAddErrors[keyof AdminUsageCreditsAddErrors];

export type AdminUsageCreditsAddResponses = {
    /**
     * Successful Response
     */
    200: EmptyResponse;
};

export type AdminUsageCreditsAddResponse = AdminUsageCreditsAddResponses[keyof AdminUsageCreditsAddResponses];

export type AdminUsageCreditsDeleteData = {
    body?: never;
    path: {
        /**
         * Credit ID
         */
        credit_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/usage/credits/{credit_id}';
};

export type AdminUsageCreditsDeleteErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageCreditsDeleteError = AdminUsageCreditsDeleteErrors[keyof AdminUsageCreditsDeleteErrors];

export type AdminUsageCreditsDeleteResponses = {
    /**
     * Successful Response
     */
    200: EmptyResponse;
};

export type AdminUsageCreditsDeleteResponse = AdminUsageCreditsDeleteResponses[keyof AdminUsageCreditsDeleteResponses];

export type AdminUsageCreditsUpdateData = {
    body: UsageCreditUpdateRequest;
    path: {
        credit_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/usage/credits/{credit_id}';
};

export type AdminUsageCreditsUpdateErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageCreditsUpdateError = AdminUsageCreditsUpdateErrors[keyof AdminUsageCreditsUpdateErrors];

export type AdminUsageCreditsUpdateResponses = {
    /**
     * Successful Response
     */
    200: EmptyResponse;
};

export type AdminUsageCreditsUpdateResponse = AdminUsageCreditsUpdateResponses[keyof AdminUsageCreditsUpdateResponses];

export type AdminUsageUnifyItemNameData = {
    body?: never;
    path: {
        item_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/usage/unify/{item_id}/item_name';
};

export type AdminUsageUnifyItemNameErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageUnifyItemNameError = AdminUsageUnifyItemNameErrors[keyof AdminUsageUnifyItemNameErrors];

export type AdminUsageUnifyItemNameResponses = {
    /**
     * Successful Response
     */
    200: UsageUnifyItemNameResponse;
};

export type AdminUsageUnifyItemNameResponse = AdminUsageUnifyItemNameResponses[keyof AdminUsageUnifyItemNameResponses];

export type AdminUsageResetDatesAddData = {
    body: UsageResetDatesRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/usage/reset_dates';
};

export type AdminUsageResetDatesAddErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageResetDatesAddError = AdminUsageResetDatesAddErrors[keyof AdminUsageResetDatesAddErrors];

export type AdminUsageResetDatesAddResponses = {
    /**
     * Successful Response
     */
    200: Array<string>;
};

export type AdminUsageResetDatesAddResponse = AdminUsageResetDatesAddResponses[keyof AdminUsageResetDatesAddResponses];

export type AdminUsageDeleteConsumptionDataData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/usage/delete_data';
};

export type AdminUsageDeleteConsumptionDataErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageDeleteConsumptionDataError = AdminUsageDeleteConsumptionDataErrors[keyof AdminUsageDeleteConsumptionDataErrors];

export type AdminUsageDeleteConsumptionDataResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminUsageDeleteConsumptionDataResponse = AdminUsageDeleteConsumptionDataResponses[keyof AdminUsageDeleteConsumptionDataResponses];

export type AdminUsageCheckOverlappingData = {
    body?: never;
    path: {
        credit_id: string;
        start_date: string;
        end_date: string;
    };
    query?: never;
    url: '/api/v4/admin/item/usage/check/overlapping/{credit_id}/{start_date}/{end_date}';
};

export type AdminUsageCheckOverlappingErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUsageCheckOverlappingError = AdminUsageCheckOverlappingErrors[keyof AdminUsageCheckOverlappingErrors];

export type AdminUsageCheckOverlappingResponses = {
    /**
     * Successful Response
     */
    200: UsageOverlappingResponse;
};

export type AdminUsageCheckOverlappingResponse = AdminUsageCheckOverlappingResponses[keyof AdminUsageCheckOverlappingResponses];

export type AdminUserStorageAutoRegisterData = {
    body: UserStorageAutoRegisterRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/user_storage/auto_register';
};

export type AdminUserStorageAutoRegisterErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUserStorageAutoRegisterError = AdminUserStorageAutoRegisterErrors[keyof AdminUserStorageAutoRegisterErrors];

export type AdminUserStorageAutoRegisterResponses = {
    /**
     * Successful Response
     */
    200: UserStorageIdResponse;
};

export type AdminUserStorageAutoRegisterResponse = AdminUserStorageAutoRegisterResponses[keyof AdminUserStorageAutoRegisterResponses];

export type AdminUserStorageTestData = {
    body: UserStorageConnTestRequest;
    path?: never;
    query?: never;
    url: '/api/v4/admin/user_storage/conn_test';
};

export type AdminUserStorageTestErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUserStorageTestError = AdminUserStorageTestErrors[keyof AdminUserStorageTestErrors];

export type AdminUserStorageTestResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminUserStorageTestResponse = AdminUserStorageTestResponses[keyof AdminUserStorageTestResponses];

export type AdminUserStorageLoginAuthData = {
    body?: never;
    path: {
        provider_id: string;
    };
    query?: never;
    url: '/api/v4/admin/user_storage/{provider_id}/login_auth';
};

export type AdminUserStorageLoginAuthErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUserStorageLoginAuthError = AdminUserStorageLoginAuthErrors[keyof AdminUserStorageLoginAuthErrors];

export type AdminUserStorageLoginAuthResponses = {
    /**
     * Successful Response
     */
    200: UserStorageLoginAuthResponse;
};

export type AdminUserStorageLoginAuthResponse = AdminUserStorageLoginAuthResponses[keyof AdminUserStorageLoginAuthResponses];

export type AdminUserStorageListData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/user_storage';
};

export type AdminUserStorageListErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUserStorageListError = AdminUserStorageListErrors[keyof AdminUserStorageListErrors];

export type AdminUserStorageListResponses = {
    /**
     * Successful Response
     */
    200: Array<UserStorageProvider>;
};

export type AdminUserStorageListResponse = AdminUserStorageListResponses[keyof AdminUserStorageListResponses];

export type AdminUserStorageUsersData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/user_storage/users';
};

export type AdminUserStorageUsersErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUserStorageUsersError = AdminUserStorageUsersErrors[keyof AdminUserStorageUsersErrors];

export type AdminUserStorageUsersResponses = {
    /**
     * Successful Response
     */
    200: Array<UserStorageUser>;
};

export type AdminUserStorageUsersResponse = AdminUserStorageUsersResponses[keyof AdminUserStorageUsersResponses];

export type AdminUserStorageRemoveData = {
    body?: never;
    path: {
        provider_id: string;
    };
    query?: never;
    url: '/api/v4/admin/user_storage/{provider_id}';
};

export type AdminUserStorageRemoveErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUserStorageRemoveError = AdminUserStorageRemoveErrors[keyof AdminUserStorageRemoveErrors];

export type AdminUserStorageRemoveResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminUserStorageRemoveResponse = AdminUserStorageRemoveResponses[keyof AdminUserStorageRemoveResponses];

export type AdminUserStorageGetData = {
    body?: never;
    path: {
        provider_id: string;
    };
    query?: never;
    url: '/api/v4/admin/user_storage/{provider_id}';
};

export type AdminUserStorageGetErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUserStorageGetError = AdminUserStorageGetErrors[keyof AdminUserStorageGetErrors];

export type AdminUserStorageGetResponses = {
    /**
     * Successful Response
     */
    200: UserStorageProvider;
};

export type AdminUserStorageGetResponse = AdminUserStorageGetResponses[keyof AdminUserStorageGetResponses];

export type AdminUserStorageResetData = {
    body?: never;
    path: {
        provider_id: string;
    };
    query?: never;
    url: '/api/v4/admin/user_storage/{provider_id}/reset';
};

export type AdminUserStorageResetErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUserStorageResetError = AdminUserStorageResetErrors[keyof AdminUserStorageResetErrors];

export type AdminUserStorageResetResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminUserStorageResetResponse = AdminUserStorageResetResponses[keyof AdminUserStorageResetResponses];

export type AdminUserStorageResetAllData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/user_storage/reset/all';
};

export type AdminUserStorageResetAllErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUserStorageResetAllError = AdminUserStorageResetAllErrors[keyof AdminUserStorageResetAllErrors];

export type AdminUserStorageResetAllResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminUserStorageResetAllResponse = AdminUserStorageResetAllResponses[keyof AdminUserStorageResetAllResponses];

export type AdminUserStorageAddData = {
    body: UserStorageAddRequest;
    path: {
        auth_protocol: string;
    };
    query?: never;
    url: '/api/v4/admin/user_storage/new/{auth_protocol}';
};

export type AdminUserStorageAddErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUserStorageAddError = AdminUserStorageAddErrors[keyof AdminUserStorageAddErrors];

export type AdminUserStorageAddResponses = {
    /**
     * Successful Response
     */
    200: UserStorageIdResponse;
};

export type AdminUserStorageAddResponse = AdminUserStorageAddResponses[keyof AdminUserStorageAddResponses];

export type AdminUserStorageSyncData = {
    body?: never;
    path: {
        provider_id: string;
        item: string;
    };
    query?: never;
    url: '/api/v4/admin/user_storage/{provider_id}/sync/{item}';
};

export type AdminUserStorageSyncErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUserStorageSyncError = AdminUserStorageSyncErrors[keyof AdminUserStorageSyncErrors];

export type AdminUserStorageSyncResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminUserStorageSyncResponse = AdminUserStorageSyncResponses[keyof AdminUserStorageSyncResponses];

export type AdminResetPasswordData = {
    body: AdminPasswordResetData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/users/reset-password';
};

export type AdminResetPasswordErrors = {
    /**
     * Bad Request
     */
    400: PasswordPolicyErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminResetPasswordError = AdminResetPasswordErrors[keyof AdminResetPasswordErrors];

export type AdminResetPasswordResponses = {
    /**
     * Password reset
     */
    200: EmptyResponse;
};

export type AdminResetPasswordResponse = AdminResetPasswordResponses[keyof AdminResetPasswordResponses];

export type AdminCheckPasswordResetRequiredData = {
    body?: never;
    path: {
        user_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/user/required/password-reset/{user_id}';
};

export type AdminCheckPasswordResetRequiredErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminCheckPasswordResetRequiredError = AdminCheckPasswordResetRequiredErrors[keyof AdminCheckPasswordResetRequiredErrors];

export type AdminCheckPasswordResetRequiredResponses = {
    /**
     * Password reset check result
     */
    200: RequiredCheckResponse;
};

export type AdminCheckPasswordResetRequiredResponse = AdminCheckPasswordResetRequiredResponses[keyof AdminCheckPasswordResetRequiredResponses];

export type AdminCheckEmailVerificationData = {
    body?: never;
    path: {
        user_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/user/required/email-verification/{user_id}';
};

export type AdminCheckEmailVerificationErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminCheckEmailVerificationError = AdminCheckEmailVerificationErrors[keyof AdminCheckEmailVerificationErrors];

export type AdminCheckEmailVerificationResponses = {
    /**
     * Email verification check result
     */
    200: RequiredCheckResponse;
};

export type AdminCheckEmailVerificationResponse = AdminCheckEmailVerificationResponses[keyof AdminCheckEmailVerificationResponses];

export type AdminCheckDisclaimerData = {
    body?: never;
    path: {
        user_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/user/required/disclaimer-acknowledgement/{user_id}';
};

export type AdminCheckDisclaimerErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminCheckDisclaimerError = AdminCheckDisclaimerErrors[keyof AdminCheckDisclaimerErrors];

export type AdminCheckDisclaimerResponses = {
    /**
     * Disclaimer acknowledgement check result
     */
    200: RequiredCheckResponse;
};

export type AdminCheckDisclaimerResponse = AdminCheckDisclaimerResponses[keyof AdminCheckDisclaimerResponses];

export type AdminCheckMigrationRequiredData = {
    body?: never;
    path: {
        user_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/user/required/migration/{user_id}';
};

export type AdminCheckMigrationRequiredErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminCheckMigrationRequiredError = AdminCheckMigrationRequiredErrors[keyof AdminCheckMigrationRequiredErrors];

export type AdminCheckMigrationRequiredResponses = {
    /**
     * Migration check result
     */
    200: RequiredCheckResponse;
};

export type AdminCheckMigrationRequiredResponse = AdminCheckMigrationRequiredResponses[keyof AdminCheckMigrationRequiredResponses];

export type AdminListCategoriesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/categories';
};

export type AdminListCategoriesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminListCategoriesError = AdminListCategoriesErrors[keyof AdminListCategoriesErrors];

export type AdminListCategoriesResponses = {
    /**
     * Categories list retrieved
     */
    200: Array<AdminCategoryItem>;
};

export type AdminListCategoriesResponse = AdminListCategoriesResponses[keyof AdminListCategoriesResponses];

export type AdminListCategoriesFrontendData = {
    body?: never;
    path: {
        frontend: string;
    };
    query?: never;
    url: '/api/v4/admin/items/categories/{frontend}';
};

export type AdminListCategoriesFrontendErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminListCategoriesFrontendError = AdminListCategoriesFrontendErrors[keyof AdminListCategoriesFrontendErrors];

export type AdminListCategoriesFrontendResponses = {
    /**
     * Frontend categories list retrieved
     */
    200: Array<AdminCategoryFrontendItem>;
};

export type AdminListCategoriesFrontendResponse = AdminListCategoriesFrontendResponses[keyof AdminListCategoriesFrontendResponses];

export type AdminDeleteCategoryData = {
    body?: never;
    path: {
        category_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/category/{category_id}';
};

export type AdminDeleteCategoryErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDeleteCategoryError = AdminDeleteCategoryErrors[keyof AdminDeleteCategoryErrors];

export type AdminDeleteCategoryResponses = {
    /**
     * Category deleted
     */
    204: void;
};

export type AdminDeleteCategoryResponse = AdminDeleteCategoryResponses[keyof AdminDeleteCategoryResponses];

export type AdminGetCategoryData = {
    body?: never;
    path: {
        category_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/category/{category_id}';
};

export type AdminGetCategoryErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetCategoryError = AdminGetCategoryErrors[keyof AdminGetCategoryErrors];

export type AdminGetCategoryResponses = {
    /**
     * Category data retrieved
     */
    200: AdminCategoryDetailResponse;
};

export type AdminGetCategoryResponse = AdminGetCategoryResponses[keyof AdminGetCategoryResponses];

export type AdminUpdateCategoryData = {
    body: AdminCategoryUpdateData;
    path: {
        category_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/category/{category_id}';
};

export type AdminUpdateCategoryErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUpdateCategoryError = AdminUpdateCategoryErrors[keyof AdminUpdateCategoryErrors];

export type AdminUpdateCategoryResponses = {
    /**
     * Category updated
     */
    204: void;
};

export type AdminUpdateCategoryResponse = AdminUpdateCategoryResponses[keyof AdminUpdateCategoryResponses];

export type AdminCreateCategoryData = {
    body: AdminCategoryCreateData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/category';
};

export type AdminCreateCategoryErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminCreateCategoryError = AdminCreateCategoryErrors[keyof AdminCreateCategoryErrors];

export type AdminCreateCategoryResponses = {
    /**
     * Category created
     */
    200: AdminCategoryDetailResponse;
};

export type AdminCreateCategoryResponse = AdminCreateCategoryResponses[keyof AdminCreateCategoryResponses];

export type AdminUpdateCategoryQuotaData = {
    body: AdminQuotaUpdateData;
    path: {
        category_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/quota/category/{category_id}';
};

export type AdminUpdateCategoryQuotaErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUpdateCategoryQuotaError = AdminUpdateCategoryQuotaErrors[keyof AdminUpdateCategoryQuotaErrors];

export type AdminUpdateCategoryQuotaResponses = {
    /**
     * Category quota updated
     */
    204: void;
};

export type AdminUpdateCategoryQuotaResponse = AdminUpdateCategoryQuotaResponses[keyof AdminUpdateCategoryQuotaResponses];

export type AdminUpdateCategoryLimitsData = {
    body: AdminLimitsUpdateData;
    path: {
        category_id: string;
    };
    query?: never;
    url: '/api/v4/admin/item/limits/category/{category_id}';
};

export type AdminUpdateCategoryLimitsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminUpdateCategoryLimitsError = AdminUpdateCategoryLimitsErrors[keyof AdminUpdateCategoryLimitsErrors];

export type AdminUpdateCategoryLimitsResponses = {
    /**
     * Category limits updated
     */
    204: void;
};

export type AdminUpdateCategoryLimitsResponse = AdminUpdateCategoryLimitsResponses[keyof AdminUpdateCategoryLimitsResponses];

export type AdminGetSecretsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/secrets';
};

export type AdminGetSecretsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetSecretsError = AdminGetSecretsErrors[keyof AdminGetSecretsErrors];

export type AdminGetSecretsResponses = {
    /**
     * Secrets retrieved
     */
    200: Array<AdminSecretItem>;
};

export type AdminGetSecretsResponse = AdminGetSecretsResponses[keyof AdminGetSecretsResponses];

export type AdminCreateSecretData = {
    body: AdminSecretCreateData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/secret';
};

export type AdminCreateSecretErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminCreateSecretError = AdminCreateSecretErrors[keyof AdminCreateSecretErrors];

export type AdminCreateSecretResponses = {
    /**
     * Secret created
     */
    200: AdminSecretCreateResponse;
};

export type AdminCreateSecretResponse = AdminCreateSecretResponses[keyof AdminCreateSecretResponses];

export type AdminDeleteSecretData = {
    body?: never;
    path: {
        kid: string;
    };
    query?: never;
    url: '/api/v4/admin/item/secret/{kid}';
};

export type AdminDeleteSecretErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminDeleteSecretError = AdminDeleteSecretErrors[keyof AdminDeleteSecretErrors];

export type AdminDeleteSecretResponses = {
    /**
     * Secret deleted
     */
    204: void;
};

export type AdminDeleteSecretResponse = AdminDeleteSecretResponses[keyof AdminDeleteSecretResponses];

export type AdminGetUserByEmailCategoryData = {
    body?: never;
    path: {
        email: string;
        category: string;
    };
    query?: never;
    url: '/api/v4/admin/item/user/email-category/{email}/{category}';
};

export type AdminGetUserByEmailCategoryErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminGetUserByEmailCategoryError = AdminGetUserByEmailCategoryErrors[keyof AdminGetUserByEmailCategoryErrors];

export type AdminGetUserByEmailCategoryResponses = {
    /**
     * User ID retrieved
     */
    200: AdminUserIdResponse;
};

export type AdminGetUserByEmailCategoryResponse = AdminGetUserByEmailCategoryResponses[keyof AdminGetUserByEmailCategoryResponses];

export type AdminAutoRegisterData = {
    body: AutoRegisterRequest;
    headers: {
        'Register-Claims': string;
    };
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/user/auto-register';
};

export type AdminAutoRegisterErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Conflict
     */
    409: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminAutoRegisterError = AdminAutoRegisterErrors[keyof AdminAutoRegisterErrors];

export type AdminAutoRegisterResponses = {
    /**
     * User auto-registered
     */
    200: AutoRegisterResponse;
};

export type AdminAutoRegisterResponse = AdminAutoRegisterResponses[keyof AdminAutoRegisterResponses];

export type AdminBroadcastData2 = {
    body: AdminBroadcastData;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/socketio/broadcast';
};

export type AdminBroadcastErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminBroadcastError = AdminBroadcastErrors[keyof AdminBroadcastErrors];

export type AdminBroadcastResponses = {
    /**
     * Message broadcast
     */
    204: void;
};

export type AdminBroadcastResponse = AdminBroadcastResponses[keyof AdminBroadcastResponses];

export type AdminVersionGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/item/version';
};

export type AdminVersionGetErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
};

export type AdminVersionGetError = AdminVersionGetErrors[keyof AdminVersionGetErrors];

export type AdminVersionGetResponses = {
    /**
     * Successful Response
     */
    200: AdminVersionResponse;
};

export type AdminVersionGetResponse = AdminVersionGetResponses[keyof AdminVersionGetResponses];

export type AdminViewersConfigData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/admin/items/viewers-config';
};

export type AdminViewersConfigErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminViewersConfigError = AdminViewersConfigErrors[keyof AdminViewersConfigErrors];

export type AdminViewersConfigResponses = {
    /**
     * Successful Response
     */
    200: Array<ViewerConfigItem>;
};

export type AdminViewersConfigResponse = AdminViewersConfigResponses[keyof AdminViewersConfigResponses];

export type AdminViewersConfigUpdateData = {
    body: ViewerConfigUpdateRequest;
    path: {
        viewer: 'file_rdpgw' | 'file_rdpvpn' | 'file_spice';
    };
    query?: never;
    url: '/api/v4/admin/item/viewers-config/{viewer}';
};

export type AdminViewersConfigUpdateErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminViewersConfigUpdateError = AdminViewersConfigUpdateErrors[keyof AdminViewersConfigUpdateErrors];

export type AdminViewersConfigUpdateResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminViewersConfigUpdateResponse = AdminViewersConfigUpdateResponses[keyof AdminViewersConfigUpdateResponses];

export type AdminViewersConfigResetData = {
    body?: never;
    path: {
        viewer: 'file_rdpgw' | 'file_rdpvpn' | 'file_spice';
    };
    query?: never;
    url: '/api/v4/admin/item/viewers-config/reset/{viewer}';
};

export type AdminViewersConfigResetErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AdminViewersConfigResetError = AdminViewersConfigResetErrors[keyof AdminViewersConfigResetErrors];

export type AdminViewersConfigResetResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminViewersConfigResetResponse = AdminViewersConfigResetResponses[keyof AdminViewersConfigResetResponses];

export type GetAllBookingsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/bookings/all';
};

export type GetAllBookingsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetAllBookingsError = GetAllBookingsErrors[keyof GetAllBookingsErrors];

export type GetAllBookingsResponses = {
    /**
     * Successful Response
     */
    200: Array<AdminBookingResponse>;
};

export type GetAllBookingsResponse = GetAllBookingsResponses[keyof GetAllBookingsResponses];

export type GetUsersPrioritiesData = {
    body: GetUsersPrioritiesRequest;
    path?: never;
    query?: never;
    url: '/api/v4/items/bookings/priorities';
};

export type GetUsersPrioritiesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetUsersPrioritiesError = GetUsersPrioritiesErrors[keyof GetUsersPrioritiesErrors];

export type GetUsersPrioritiesResponses = {
    /**
     * Successful Response
     */
    200: Array<BookingPriorityUser>;
};

export type GetUsersPrioritiesResponse = GetUsersPrioritiesResponses[keyof GetUsersPrioritiesResponses];

export type DeletePriorityData = {
    body?: never;
    path: {
        priority_id: string;
    };
    query?: never;
    url: '/api/v4/item/booking/priority/{priority_id}';
};

export type DeletePriorityErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type DeletePriorityError = DeletePriorityErrors[keyof DeletePriorityErrors];

export type DeletePriorityResponses = {
    /**
     * Successful Response
     */
    200: EmptyResponse;
};

export type DeletePriorityResponse = DeletePriorityResponses[keyof DeletePriorityResponses];

export type ListPriorityRulesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/bookings/priority-rules';
};

export type ListPriorityRulesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type ListPriorityRulesError = ListPriorityRulesErrors[keyof ListPriorityRulesErrors];

export type ListPriorityRulesResponses = {
    /**
     * Successful Response
     */
    200: Array<PriorityRuleResponse>;
};

export type ListPriorityRulesResponse = ListPriorityRulesResponses[keyof ListPriorityRulesResponses];

export type GetGpuBookingsForecastData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/items/bookings/gpu';
};

export type GetGpuBookingsForecastErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetGpuBookingsForecastError = GetGpuBookingsForecastErrors[keyof GetGpuBookingsForecastErrors];

export type GetGpuBookingsForecastResponses = {
    /**
     * Successful Response
     */
    200: Array<GpuForecastProfile>;
};

export type GetGpuBookingsForecastResponse = GetGpuBookingsForecastResponses[keyof GetGpuBookingsForecastResponses];

export type EmptyBookingPlanData = {
    body?: never;
    path: {
        plan_id: string;
    };
    query?: never;
    url: '/api/v4/item/booking/empty/{plan_id}';
};

export type EmptyBookingPlanErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type EmptyBookingPlanError = EmptyBookingPlanErrors[keyof EmptyBookingPlanErrors];

export type EmptyBookingPlanResponses = {
    /**
     * Successful Response
     */
    200: EmptyResponse;
};

export type EmptyBookingPlanResponse = EmptyBookingPlanResponses[keyof EmptyBookingPlanResponses];

export type GetBookingPlansData = {
    body?: never;
    path: {
        booking_id: string;
    };
    query?: never;
    url: '/api/v4/item/booking/{booking_id}/plans';
};

export type GetBookingPlansErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetBookingPlansError = GetBookingPlansErrors[keyof GetBookingPlansErrors];

export type GetBookingPlansResponses = {
    /**
     * Successful Response
     */
    200: Array<BookingPlanResponse>;
};

export type GetBookingPlansResponse = GetBookingPlansResponses[keyof GetBookingPlansResponses];

export type ListProfilesData = {
    body?: never;
    path: {
        /**
         * The reservable type (e.g., 'gpus')
         */
        reservable_type: string;
    };
    query?: never;
    url: '/api/v4/items/reservables/profiles/{reservable_type}';
};

export type ListProfilesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type ListProfilesError = ListProfilesErrors[keyof ListProfilesErrors];

export type ListProfilesResponses = {
    /**
     * Successful Response
     */
    200: Array<ReservableProfileResponse>;
};

export type ListProfilesResponse = ListProfilesResponses[keyof ListProfilesResponses];

export type ListBookablesData = {
    body?: never;
    path: {
        /**
         * The reservable type (e.g., 'gpus')
         */
        reservable_type: string;
    };
    query?: never;
    url: '/api/v4/items/bookables/{reservable_type}';
};

export type ListBookablesErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type ListBookablesError = ListBookablesErrors[keyof ListBookablesErrors];

export type ListBookablesResponses = {
    /**
     * Successful Response
     */
    200: Array<BookableItemResponse>;
};

export type ListBookablesResponse = ListBookablesResponses[keyof ListBookablesResponses];

export type ListReservableSubitemsData = {
    body?: never;
    path: {
        /**
         * The reservable type (e.g., 'gpus', 'usbs')
         */
        reservable_type: string;
        /**
         * The reservable item ID
         */
        item_id: string;
    };
    query?: never;
    url: '/api/v4/items/reservables/{reservable_type}/{item_id}';
};

export type ListReservableSubitemsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type ListReservableSubitemsError = ListReservableSubitemsErrors[keyof ListReservableSubitemsErrors];

export type ListReservableSubitemsResponses = {
    /**
     * Successful Response
     */
    200: Array<{
        [key: string]: unknown;
    }>;
};

export type ListReservableSubitemsResponse = ListReservableSubitemsResponses[keyof ListReservableSubitemsResponses];

export type AddReservableItemData = {
    body: AddReservableItemRequest;
    path: {
        /**
         * The reservable type (e.g., 'gpus')
         */
        reservable_type: string;
    };
    query?: never;
    url: '/api/v4/item/reservable/{reservable_type}';
};

export type AddReservableItemErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type AddReservableItemError = AddReservableItemErrors[keyof AddReservableItemErrors];

export type AddReservableItemResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type AddReservableItemResponse = AddReservableItemResponses[keyof AddReservableItemResponses];

export type EnableReservableSubitemData = {
    body: EnableReservableRequest;
    path: {
        /**
         * The reservable type
         */
        reservable_type: string;
        /**
         * The item ID
         */
        item_id: string;
        /**
         * The subitem ID
         */
        subitem_id: string;
    };
    query?: {
        /**
         * If true, notify every affected user by email before the subitem is disabled. Ignored when ``enabled`` is true.
         */
        notify_user?: boolean;
    };
    url: '/api/v4/item/reservable/enable/{reservable_type}/{item_id}/{subitem_id}';
};

export type EnableReservableSubitemErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type EnableReservableSubitemError = EnableReservableSubitemErrors[keyof EnableReservableSubitemErrors];

export type EnableReservableSubitemResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type EnableReservableSubitemResponse = EnableReservableSubitemResponses[keyof EnableReservableSubitemResponses];

export type CheckLastSubitemData = {
    body?: never;
    path: {
        /**
         * The reservable type
         */
        reservable_type: string;
        /**
         * The subitem ID
         */
        subitem_id: string;
        /**
         * The item ID
         */
        item_id: string;
    };
    query?: never;
    url: '/api/v4/item/reservable/check-last/{reservable_type}/{subitem_id}/{item_id}';
};

export type CheckLastSubitemErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type CheckLastSubitemError = CheckLastSubitemErrors[keyof CheckLastSubitemErrors];

export type CheckLastSubitemResponses = {
    /**
     * Successful Response
     */
    200: CheckLastResponse;
};

export type CheckLastSubitemResponse = CheckLastSubitemResponses[keyof CheckLastSubitemResponses];

export type CheckLastItemData = {
    body?: never;
    path: {
        /**
         * The reservable type
         */
        reservable_type: string;
        /**
         * The item ID
         */
        item_id: string;
    };
    query?: never;
    url: '/api/v4/item/reservable/check-last/{reservable_type}/{item_id}';
};

export type CheckLastItemErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type CheckLastItemError = CheckLastItemErrors[keyof CheckLastItemErrors];

export type CheckLastItemResponses = {
    /**
     * Successful Response
     */
    200: CheckLastResponse;
};

export type CheckLastItemResponse = CheckLastItemResponses[keyof CheckLastItemResponses];

export type DeleteReservableItemData = {
    body?: never;
    path: {
        /**
         * The reservable type
         */
        reservable_type: string;
        /**
         * The item ID
         */
        item_id: string;
    };
    query?: {
        /**
         * If true, fan out a ``deleted-gpu`` email to every user affected by the deletion before removing the item.
         */
        notify_user?: boolean;
    };
    url: '/api/v4/item/reservable/{reservable_type}/{item_id}';
};

export type DeleteReservableItemErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type DeleteReservableItemError = DeleteReservableItemErrors[keyof DeleteReservableItemErrors];

export type DeleteReservableItemResponses = {
    /**
     * Successful Response
     */
    200: EmptyResponse;
};

export type DeleteReservableItemResponse = DeleteReservableItemResponses[keyof DeleteReservableItemResponses];

export type UpdateReservableItemData = {
    body: UpdateReservableItemRequest;
    path: {
        /**
         * The reservable type (e.g., gpus)
         */
        reservable_type: string;
        /**
         * The item ID
         */
        item_id: string;
    };
    query?: never;
    url: '/api/v4/admin/items/reservables/{reservable_type}/{item_id}';
};

export type UpdateReservableItemErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type UpdateReservableItemError = UpdateReservableItemErrors[keyof UpdateReservableItemErrors];

export type UpdateReservableItemResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type UpdateReservableItemResponse = UpdateReservableItemResponses[keyof UpdateReservableItemResponses];

export type CheckIntegrityData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/reservables-planner/check-integrity';
};

export type CheckIntegrityErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type CheckIntegrityError = CheckIntegrityErrors[keyof CheckIntegrityErrors];

export type CheckIntegrityResponses = {
    /**
     * Successful Response
     */
    200: {
        [key: string]: unknown;
    };
};

export type CheckIntegrityResponse = CheckIntegrityResponses[keyof CheckIntegrityResponses];

export type GetActualPlanData = {
    body?: never;
    path: {
        /**
         * The item ID
         */
        item_id: string;
    };
    query?: never;
    url: '/api/v4/item/reservables-planner/actual-plan/{item_id}';
};

export type GetActualPlanErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetActualPlanError = GetActualPlanErrors[keyof GetActualPlanErrors];

export type GetActualPlanResponses = {
    /**
     * Successful Response
     */
    200: {
        [key: string]: unknown;
    };
};

export type GetActualPlanResponse = GetActualPlanResponses[keyof GetActualPlanResponses];

export type GetPlanBookingsData = {
    body?: never;
    path: {
        /**
         * The plan ID
         */
        plan_id: string;
    };
    query?: never;
    url: '/api/v4/item/reservables-planner/{plan_id}/bookings';
};

export type GetPlanBookingsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetPlanBookingsError = GetPlanBookingsErrors[keyof GetPlanBookingsErrors];

export type GetPlanBookingsResponses = {
    /**
     * Successful Response
     */
    200: Array<{
        [key: string]: unknown;
    }>;
};

export type GetPlanBookingsResponse = GetPlanBookingsResponses[keyof GetPlanBookingsResponses];

export type GetAllDesktopsData = {
    body?: never;
    path?: never;
    query?: {
        start_after?: number;
        page_size?: number;
        sort_field?: string;
        sort_order?: string;
        search?: string;
        search_field?: DesktopSearchFields | null;
    };
    url: '/api/v4/admin/items/desktops';
};

export type GetAllDesktopsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetAllDesktopsError = GetAllDesktopsErrors[keyof GetAllDesktopsErrors];

export type GetAllDesktopsResponses = {
    /**
     * Successful Response
     */
    200: DesktopsPaginationResponse;
};

export type GetAllDesktopsResponse = GetAllDesktopsResponses[keyof GetAllDesktopsResponses];

export type RegisterUserData = {
    body: RegisterPostData;
    path?: never;
    query?: never;
    url: '/api/v4/item/user/register';
};

export type RegisterUserErrors = {
    /**
     * Invalid registration data
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Register code not found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Failed to register user
     */
    500: ErrorResponse;
};

export type RegisterUserError = RegisterUserErrors[keyof RegisterUserErrors];

export type RegisterUserResponses = {
    /**
     * Successful Response
     */
    201: SimpleResponse;
};

export type RegisterUserResponse = RegisterUserResponses[keyof RegisterUserResponses];

export type ReRegisterUserData = {
    body: RegisterPostData;
    path?: never;
    query?: never;
    url: '/api/v4/item/user/register';
};

export type ReRegisterUserErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Register code or user not found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Failed to re-register user
     */
    500: ErrorResponse;
};

export type ReRegisterUserError = ReRegisterUserErrors[keyof ReRegisterUserErrors];

export type ReRegisterUserResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type ReRegisterUserResponse = ReRegisterUserResponses[keyof ReRegisterUserResponses];

export type GetUserPasswordPolicyData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/user/get-password-policy';
};

export type GetUserPasswordPolicyErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Failed to retrieve password policy
     */
    500: unknown;
};

export type GetUserPasswordPolicyError = GetUserPasswordPolicyErrors[keyof GetUserPasswordPolicyErrors];

export type GetUserPasswordPolicyResponses = {
    /**
     * Successful Response
     */
    200: UserPasswordPolicyResponse;
};

export type GetUserPasswordPolicyResponse = GetUserPasswordPolicyResponses[keyof GetUserPasswordPolicyResponses];

export type ApiV4DisclaimerData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/disclaimer';
};

export type ApiV4DisclaimerErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
};

export type ApiV4DisclaimerError = ApiV4DisclaimerErrors[keyof ApiV4DisclaimerErrors];

export type ApiV4DisclaimerResponses = {
    /**
     * Successful Response
     */
    200: ApiSchemasLoginDisclaimerResponse;
};

export type ApiV4DisclaimerResponse = ApiV4DisclaimerResponses[keyof ApiV4DisclaimerResponses];

export type GetDisclaimerData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/disclaimer';
};

export type GetDisclaimerErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDisclaimerError = GetDisclaimerErrors[keyof GetDisclaimerErrors];

export type GetDisclaimerResponses = {
    /**
     * Successful Response
     */
    200: ApiSchemasAdminAuthenticationDisclaimerResponse;
};

export type GetDisclaimerResponse = GetDisclaimerResponses[keyof GetDisclaimerResponses];

export type UserOwnsDesktopData = {
    body: UserOwnsDesktopRequest;
    path?: never;
    query?: never;
    url: '/api/v4/item/user/owns-desktop';
};

export type UserOwnsDesktopErrors = {
    /**
     * Bad Request
     */
    400: ErrorResponse;
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type UserOwnsDesktopError = UserOwnsDesktopErrors[keyof UserOwnsDesktopErrors];

export type UserOwnsDesktopResponses = {
    /**
     * Caller owns or may access the desktop viewer
     */
    200: EmptyResponse;
};

export type UserOwnsDesktopResponse = UserOwnsDesktopResponses[keyof UserOwnsDesktopResponses];

export type GetDirectViewerDocsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/desktop/get-direct-viewer-docs';
};

export type GetDirectViewerDocsErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDirectViewerDocsError = GetDirectViewerDocsErrors[keyof GetDirectViewerDocsErrors];

export type GetDirectViewerDocsResponses = {
    /**
     * Successful Response
     */
    200: DirectViewerDocsResponse;
};

export type GetDirectViewerDocsResponse = GetDirectViewerDocsResponses[keyof GetDirectViewerDocsResponses];

export type GetNetworksFromTokenData = {
    body?: never;
    path: {
        /**
         * Code provided for the desktop viewer. Mainly defined when generating the share link.
         */
        token: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/token/{token}/get-networks';
};

export type GetNetworksFromTokenErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetNetworksFromTokenError = GetNetworksFromTokenErrors[keyof GetNetworksFromTokenErrors];

export type GetNetworksFromTokenResponses = {
    /**
     * Successful Response
     */
    200: DesktopNetworksResponse;
};

export type GetNetworksFromTokenResponse = GetNetworksFromTokenResponses[keyof GetNetworksFromTokenResponses];

export type GetDesktopDetailsFromTokenData = {
    body?: never;
    path: {
        /**
         * Code provided for the desktop viewer.
         */
        token: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/token/{token}/get-details';
};

export type GetDesktopDetailsFromTokenErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetDesktopDetailsFromTokenError = GetDesktopDetailsFromTokenErrors[keyof GetDesktopDetailsFromTokenErrors];

export type GetDesktopDetailsFromTokenResponses = {
    /**
     * Successful Response
     */
    200: DesktopDirectViewerDetailsResponse;
};

export type GetDesktopDetailsFromTokenResponse = GetDesktopDetailsFromTokenResponses[keyof GetDesktopDetailsFromTokenResponses];

export type StartDesktopFromTokenData = {
    body?: never;
    path: {
        /**
         * Code provided for the desktop viewer. Mainly defined when generating the share link.
         */
        token: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/token/{token}/start-desktop';
};

export type StartDesktopFromTokenErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type StartDesktopFromTokenError = StartDesktopFromTokenErrors[keyof StartDesktopFromTokenErrors];

export type StartDesktopFromTokenResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type StartDesktopFromTokenResponse = StartDesktopFromTokenResponses[keyof StartDesktopFromTokenResponses];

export type ResetDesktopData = {
    body?: never;
    path: {
        /**
         * Code provided for the desktop viewer. Mainly defined when generating the share link.
         */
        token: string;
    };
    query?: never;
    url: '/api/v4/item/desktop/token/{token}/reset-desktop';
};

export type ResetDesktopErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Precondition Required
     */
    428: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type ResetDesktopError = ResetDesktopErrors[keyof ResetDesktopErrors];

export type ResetDesktopResponses = {
    /**
     * Successful Response
     */
    200: SimpleResponse;
};

export type ResetDesktopResponse = ResetDesktopResponses[keyof ResetDesktopResponses];

export type MigrationExportUserData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v4/item/user-migration/export-user';
};

export type MigrationExportUserErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Forbidden
     */
    403: ErrorResponse;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type MigrationExportUserError = MigrationExportUserErrors[keyof MigrationExportUserErrors];

export type MigrationExportUserResponses = {
    /**
     * Successful Response
     */
    200: MigrationExportResponse;
};

export type MigrationExportUserResponse = MigrationExportUserResponses[keyof MigrationExportUserResponses];

export type GetProviderExportEnabledData = {
    body?: never;
    path: {
        provider_id: string;
    };
    query?: never;
    url: '/api/v4/item/provider/export/{provider_id}';
};

export type GetProviderExportEnabledErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetProviderExportEnabledError = GetProviderExportEnabledErrors[keyof GetProviderExportEnabledErrors];

export type GetProviderExportEnabledResponses = {
    /**
     * Successful Response
     */
    200: MigrationProviderEnabledResponse;
};

export type GetProviderExportEnabledResponse = GetProviderExportEnabledResponses[keyof GetProviderExportEnabledResponses];

export type GetProviderImportEnabledData = {
    body?: never;
    path: {
        provider_id: string;
    };
    query?: never;
    url: '/api/v4/item/provider/import/{provider_id}';
};

export type GetProviderImportEnabledErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * Not Found
     */
    404: ErrorResponse;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Internal Server Error
     */
    500: ErrorResponse;
};

export type GetProviderImportEnabledError = GetProviderImportEnabledErrors[keyof GetProviderImportEnabledErrors];

export type GetProviderImportEnabledResponses = {
    /**
     * Successful Response
     */
    200: MigrationProviderEnabledResponse;
};

export type GetProviderImportEnabledResponse = GetProviderImportEnabledResponses[keyof GetProviderImportEnabledResponses];

export type SetUserEmailData = {
    body: UserSetEmailPutData;
    path?: never;
    query?: never;
    url: '/api/v4/item/user/set-email';
};

export type SetUserEmailErrors = {
    /**
     * Unauthorized
     */
    401: ErrorResponse;
    /**
     * User not found
     */
    404: unknown;
    /**
     * Validation Error
     */
    422: HttpValidationError;
    /**
     * Failed to update email
     */
    500: unknown;
};

export type SetUserEmailError = SetUserEmailErrors[keyof SetUserEmailErrors];

export type SetUserEmailResponses = {
    /**
     * Email set successfully
     */
    200: SimpleResponse;
};

export type SetUserEmailResponse = SetUserEmailResponses[keyof SetUserEmailResponses];
